import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Alert, Button, Card, Collapse, Divider, Form, Input, InputNumber, message, Modal, Space, Table, Typography, Upload, Tabs, Descriptions, Tag, Tooltip, Checkbox } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { CaretDownOutlined, CaretRightOutlined, CopyOutlined, DeleteOutlined, DownloadOutlined, InboxOutlined, QuestionCircleOutlined, ReloadOutlined, ThunderboltOutlined } from '@ant-design/icons';
import request from '../../utils/request';
import { getInventoryUnitDetail } from '../../services/inventory';
import RegularOutboundSnapshotForReview from './components/RegularOutboundSnapshotForReview';
import TimezoneAwareTime from '../../components/TimezoneAwareTime';

const { Title, Text } = Typography;

type AnyObj = Record<string, any>;

function iocContainerTypeText(codeRaw: any): string {
  const code = String(codeRaw || '').trim().toUpperCase();
  if (!code) return '-';
  // 期望格式：IOC-<TypeLetter>-<InstrSeq>-<Seq>
  // 例如：IOC-B-36-1、IOC-SB-12-3
  const parts = code.split('-').filter(Boolean);
  const t = String(parts?.[1] || '').trim().toUpperCase();
  const mp: Record<string, string> = {
    'B': '纸箱',
    'P': '托盘',
    'SB': '物流袋',
    'T': '折叠中转袋',
    'D': '直接存储',
    'C': '移动车',
    'O': '其他',
  };
  return mp[t] || (t ? '其他' : '-');
}

function formatContainerCodeWithType(codeRaw: any, typeRaw?: any): string {
  const code = String(codeRaw || '').trim();
  if (!code || code === '-') return '-';
  const tRaw = String(typeRaw || '').trim();
  const typeText = (tRaw && tRaw !== '-' && tRaw !== '未知') ? tRaw : iocContainerTypeText(code);
  if (!typeText || typeText === '-') return code;
  return `${code}(${typeText})`;
}

function splitContentInfoLines(raw: any): string[] {
  const txt = String(raw || '').trim();
  if (!txt || txt === '-') return [];
  return txt
    .split(/[；;]/)
    .map((x) => String(x || '').trim())
    .filter(Boolean);
}

function resolveRegularOutboundTargetCode(row: AnyObj): string {
  return String(row?.target_code || row?.unit_code || row?.output_code || row?.key || '-').trim() || '-';
}

function resolveRegularOutboundTargetContentInfo(row: AnyObj): string {
  const contentLines = Array.isArray(row?.content_lines) ? row.content_lines : [];
  const kind = String(row?.output_content_kind || '').trim().toLowerCase();
  const hasIocShape = String(row?.output_code || '').trim() !== '' || String(row?.output_contents_detail || '').trim() !== '' || !!kind;
  if (hasIocShape) {
    return kind === 'exception'
      ? '无法操作等异常产品'
      : (String(row?.output_contents_detail || '').trim() || '-');
  }
  if (contentLines.length > 0) {
    return contentLines.map((x: any) => String(x || '').trim()).filter(Boolean).join('；') || '-';
  }
  return '-';
}

function resolveRegularOutboundTargetQtyValue(row: AnyObj): number {
  const n = Number(row?.target_qty);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n));
}

function resolveRegularOutboundDoneQtyValue(row: AnyObj): number {
  const n = Number(row?.done_qty ?? row?.picked_qty);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.floor(n));
}

function calcUnoperableTotalQtyFromIocRows(rowsRaw: AnyObj[], exceptionCodeSet: Set<string>): number {
  try {
    const rows = (Array.isArray(rowsRaw) ? rowsRaw : []).filter((r: any) => String(r?.status || '').trim().toLowerCase() !== 'cancelled');
    const exRows = (() => {
      if (exceptionCodeSet.size > 0) {
        return rows.filter((r: any) => exceptionCodeSet.has(String(r?.code || '').trim()));
      }
      return rows.filter((r: any) => {
        const kind = String(r?.content_kind || '').toLowerCase();
        return !!kind && (kind.includes('exception') || kind.includes('unoperable') || kind.includes('abnormal'));
      });
    })();
    let sum = 0;
    exRows.forEach((r: any) => {
      const contents = Array.isArray(r?.contents) ? r.contents : [];
      contents.forEach((c: any) => {
        const q = Math.floor(Number(c?.quantity || 0) || 0);
        if (q > 0) sum += q;
      });
    });
    return Math.max(0, Math.floor(sum));
  } catch {
    return 0;
  }
}

function resolveBackendFileUrl(rawPath: string): string | undefined {
  if (!rawPath) return undefined;
  if (/^https?:\/\//i.test(rawPath)) return rawPath;
  let backendOrigin = window.location.origin;
  try {
    const u = new URL(backendOrigin);
    if (u.port === '3000') {
      u.port = '8000';
      backendOrigin = u.toString().replace(/\/$/, '');
    }
  } catch {}
  try {
    const anyReq: any = request as any;
    const baseURL = anyReq?.defaults?.baseURL || anyReq?.getUri?.();
    if (baseURL && /^https?:\/\//i.test(baseURL)) {
      const b = new URL(baseURL);
      backendOrigin = `${b.protocol}//${b.host}`;
    }
  } catch {}
  let path = String(rawPath || '');
  if (!path.startsWith('/')) path = '/' + path;
  if (!/^\/media\//i.test(path)) path = '/media' + path;
  return `${backendOrigin}${path}`;
}

function fmtDateTime(v: any): string {
  if (v == null) return '-';
  // 支持 epoch（秒/毫秒）
  try {
    if (typeof v === 'number' && Number.isFinite(v)) {
      const n0 = Math.floor(v);
      const ms = n0 > 1_000_000_000_000 ? n0 : (n0 > 1_000_000_000 ? n0 * 1000 : 0);
      if (ms > 0) {
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          const hh = String(d.getHours()).padStart(2, '0');
          const mm = String(d.getMinutes()).padStart(2, '0');
          const ss = String(d.getSeconds()).padStart(2, '0');
          return `${y}-${m}-${dd} ${hh}:${mm}:${ss}`;
        }
      }
    }
  } catch {}
  const raw = String(v || '').trim();
  if (!raw) return '-';
  try {
    // 纯数字字符串：epoch 秒/毫秒
    if (/^\d{10,16}$/.test(raw)) {
      const n0 = Math.floor(Number(raw));
      const ms = n0 > 1_000_000_000_000 ? n0 : (n0 > 1_000_000_000 ? n0 * 1000 : 0);
      if (ms > 0) {
        const d = new Date(ms);
        if (!Number.isNaN(d.getTime())) {
          const y = d.getFullYear();
          const m = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          const hh = String(d.getHours()).padStart(2, '0');
          const mm = String(d.getMinutes()).padStart(2, '0');
          const ss = String(d.getSeconds()).padStart(2, '0');
          return `${y}-${m}-${dd} ${hh}:${mm}:${ss}`;
        }
      }
    }
  } catch {}
  try {
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return raw;
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${y}-${m}-${dd} ${hh}:${mm}:${ss}`;
  } catch {
    return raw;
  }
}

function stageKeyToCN(k: any): string {
  const s = String(k || '').trim().toUpperCase();
  if (s === 'SORT_COUNT' || s === 'SORT_AND_COUNT') return '分拣清点';
  if (s === 'PICK') return '拣货';
  if (s === 'RELABEL_PACK') return '换标+装箱';
  if (s === 'SINGLE_DROPSHIP_PACK') return '配货|装箱|贴单';
  if (s === 'REGULAR_OUTBOUND_PACK') return '常规出库配货|装箱|贴单';
  if (s === 'WRAP_UP') return '收尾';
  return s || '-';
}

/** 大板块的标题：圆形序号 + 名称 + 一句话说明（和库存非标准操作指令终审页的样式一致） */
const reviewSectionTitle = (no: number, text: string, note: string, color: string) => (
  <Space size={8} align="center">
    <span
      className="final-review-badge"
      style={{ display: 'inline-flex', width: 22, height: 22, borderRadius: '50%', background: color, color: '#fff', fontSize: 13, alignItems: 'center', justifyContent: 'center' }}
    >
      {no}
    </span>
    <span>{text}</span>
    <span style={{ fontSize: 12, fontWeight: 'normal', color: '#8c8c8c' }}>{note}</span>
  </Space>
);

/** 子卡片的小编号（1.1、1.2 …）：方形淡蓝底，和大板块的圆形序号区分开 */
const reviewSubNo = (no: string) => (
  <span
    className="final-review-subno"
    style={{ display: 'inline-block', padding: '0 6px', borderRadius: 4, background: '#e6f4ff', color: '#1677ff', fontSize: 12, fontWeight: 600, lineHeight: '20px' }}
  >
    {no}
  </span>
);

const InstructionFinalReviewPage: React.FC = () => {
  const { id } = useParams();
  const instrId = useMemo(() => Math.floor(Number(id || 0)), [id]);
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [instr, setInstr] = useState<AnyObj | null>(null);
  const [attsLoading, setAttsLoading] = useState(false);
  const [attachments, setAttachments] = useState<AnyObj[]>([]);
  const [finalAttSelectedKeys, setFinalAttSelectedKeys] = useState<string[]>([]);
  const [notes, setNotes] = useState('');
  const [copied, setCopied] = useState(false);

  // 终审计费（策略B）：预览/草稿（仅终审端展示与提交）
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingPreview, setPricingPreview] = useState<AnyObj>({});
  const [pricingFacts, setPricingFacts] = useState<AnyObj>({ by_workorder: {} });
  const [pricingEvidence, setPricingEvidence] = useState<AnyObj>({});
  const [pricingWarnings, setPricingWarnings] = useState<string[]>([]);
  const [pricingBlockingIssues, setPricingBlockingIssues] = useState<AnyObj[]>([]);
  const [pricingEvidenceOpen, setPricingEvidenceOpen] = useState(false);

  // 在线预览：基线文件 / 阶段快照 / IOC装箱数据
  const [baselinesLoading, setBaselinesLoading] = useState(false);
  const [baselineFiles, setBaselineFiles] = useState<AnyObj[]>([]);
  const [stageSubsLoading, setStageSubsLoading] = useState(false);
  const [stageSubs, setStageSubs] = useState<AnyObj[]>([]);
  const [iocLoading, setIocLoading] = useState(false);
  const [iocRows, setIocRows] = useState<AnyObj[]>([]);

  // 明细3：IOC 按新条码聚合（派生统计，数据源=records）
  const [iocNewBarcodeFacetOpen, setIocNewBarcodeFacetOpen] = useState(false);
  const [iocNewBarcodeFacetLoading, setIocNewBarcodeFacetLoading] = useState(false);
  const [iocNewBarcodeFacetRows, setIocNewBarcodeFacetRows] = useState<AnyObj[]>([]);
  const [iocNewBarcodeFacetExporting, setIocNewBarcodeFacetExporting] = useState(false);
  const [iocNewBarcodeFacetAdding, setIocNewBarcodeFacetAdding] = useState(false);
  const [sortCountFinalExporting, setSortCountFinalExporting] = useState(false);
  const [sortCountFinalAddingXlsx, setSortCountFinalAddingXlsx] = useState(false);
  const [sortCountInvPreviewLoading, setSortCountInvPreviewLoading] = useState(false);
  const [sortCountInvApplying, setSortCountInvApplying] = useState(false);
  const [sortCountInvPreview, setSortCountInvPreview] = useState<AnyObj | null>(null);
  const [sortCountInvPreviewError, setSortCountInvPreviewError] = useState('');
  const [inventorySettlementPreview, setInventorySettlementPreview] = useState<AnyObj | null>(null);
  const [inventorySettlementApplied, setInventorySettlementApplied] = useState<AnyObj | null>(null);
  const [regularOutboundPackingOpen, setRegularOutboundPackingOpen] = useState(false);
  const [regularOutboundPackingExporting, setRegularOutboundPackingExporting] = useState(false);
  const [regularOutboundPackingAdding, setRegularOutboundPackingAdding] = useState(false);
  const [relabelPackBoxFinalOpen, setRelabelPackBoxFinalOpen] = useState(false);
  const [relabelPackBoxFinalData, setRelabelPackBoxFinalData] = useState<AnyObj | null>(null);

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewTitle, setPreviewTitle] = useState('预览');
  const [previewKind, setPreviewKind] = useState<'image' | 'pdf' | 'video' | 'table' | 'json' | 'text' | 'other'>('other');
  const [previewUrl, setPreviewUrl] = useState<string>('');
  const [previewTable, setPreviewTable] = useState<{ columns: any[]; data: AnyObj[] } | null>(null);
  const [previewTablePage, setPreviewTablePage] = useState<number>(1);
  const [previewTablePageSize, setPreviewTablePageSize] = useState<number>(20);
  const [previewText, setPreviewText] = useState<string>('');
  const [previewStageReadable, setPreviewStageReadable] = useState<AnyObj | null>(null);
  const previewObjUrlRef = useRef<string>('');
  // Excel 预览：支持多 sheet 切换（仅终审页面预览弹窗）
  const [previewXlsxSheets, setPreviewXlsxSheets] = useState<string[]>([]);
  const [previewXlsxActiveSheet, setPreviewXlsxActiveSheet] = useState<string>('');
  const previewXlsxWbRef = useRef<any>(null);
  const previewXlsxLibRef = useRef<any>(null);
  const previewXlsxCacheRef = useRef<Record<string, { columns: any[]; data: AnyObj[] }>>({});

  // WRAP_UP：步骤4附件表勾选（用于批量下载/批量加入终审附件）
  const [wrapStep4SelectedKeys, setWrapStep4SelectedKeys] = useState<string[]>([]);

  // RELABEL_PACK：操作记录里可能只有 op_iu_id（没有 op_iu_code），终审端按需补拉库存单元系统编码
  const [iuSysCodeById, setIuSysCodeById] = useState<Record<string, string>>({});
  const iuSysLoadingRef = useRef<Set<number>>(new Set());

  // RELABEL_PACK：系统自动生成新标签(PDF) 预览专用弹窗（避免与快照预览弹窗共用 previewOpen 导致互相关闭）
  const [labelPdfOpen, setLabelPdfOpen] = useState(false);
  const [labelPdfTitle, setLabelPdfTitle] = useState('预览');
  const [labelPdfUrl, setLabelPdfUrl] = useState<string>('');
  const labelPdfObjUrlRef = useRef<string>('');
  const [relatedProjectsOpen, setRelatedProjectsOpen] = useState(false);
  const [finalReviewHelpOpen, setFinalReviewHelpOpen] = useState(false);

  // WRAP_UP：步骤4“附件预览”二级弹窗（避免与快照预览弹窗共用 previewOpen 导致互相关闭）
  const [wrapAttOpen, setWrapAttOpen] = useState(false);
  const [wrapAttTitle, setWrapAttTitle] = useState('预览');
  const [wrapAttKind, setWrapAttKind] = useState<'image' | 'pdf' | 'video' | 'other'>('other');
  const [wrapAttUrl, setWrapAttUrl] = useState<string>('');
  const wrapAttObjUrlRef = useRef<string>('');

  // 明细3：IOC 内容物明细弹窗（替代表格 expandable）
  const [iocContentOpen, setIocContentOpen] = useState(false);
  const [iocContentTitle, setIocContentTitle] = useState('内容物明细');
  const [iocContentRows, setIocContentRows] = useState<AnyObj[]>([]);
  const [sortCountNeed3ActiveKeys, setSortCountNeed3ActiveKeys] = useState<string[]>(['mark_group']);
  const [sortCountInvAuditActiveKeys, setSortCountInvAuditActiveKeys] = useState<string[]>(['group_adj']);
  const [inventorySettleActiveKeys, setInventorySettleActiveKeys] = useState<string[]>([]);
  const [detailPanelCollapsedOverride, setDetailPanelCollapsedOverride] = useState<Record<string, boolean>>({});
  const [sortCountDemandHelpOpen, setSortCountDemandHelpOpen] = useState(false);
  const [quantityCorrectionHelpOpen, setQuantityCorrectionHelpOpen] = useState(false);
  const [unoperableToAiuHelpOpen, setUnoperableToAiuHelpOpen] = useState(false);
  const [executionReservationHelpOpen, setExecutionReservationHelpOpen] = useState(false);
  const [groupAdjustmentHelpOpen, setGroupAdjustmentHelpOpen] = useState(false);
  const [sortCountInvPanelCollapsed, setSortCountInvPanelCollapsed] = useState(true);
  // “自动结算结果”下面的调试信息（常规出库-库存数量修正）：默认收起，点击才展开
  const [settlementDebugOpen, setSettlementDebugOpen] = useState(false);
  const [sortCountInvShowEmptyItems, setSortCountInvShowEmptyItems] = useState(false);
  const [sortCountInvPreviewShown, setSortCountInvPreviewShown] = useState(false);
  const [inventoryAutoApplying, setInventoryAutoApplying] = useState(false);
  const inventoryAutoAttemptedRef = useRef<string>('');
  const helpButtonStyle: React.CSSProperties = useMemo(() => ({
    borderColor: '#1677ff',
    color: '#1677ff',
    background: '#fff',
    fontWeight: 500,
  }), []);
  const iocContentStats = useMemo(() => {
    try {
      const rows = Array.isArray(iocContentRows) ? iocContentRows : [];
      const kinds = rows.length;
      const total = rows.reduce((acc: number, r: any) => acc + (Number(r?.quantity ?? 0) || 0), 0);
      return { kinds, total: Math.floor(total) };
    } catch {
      return { kinds: 0, total: 0 };
    }
  }, [iocContentRows]);

  const iocNewBarcodeFacetStats = useMemo(() => {
    try {
      const rows = Array.isArray(iocNewBarcodeFacetRows) ? iocNewBarcodeFacetRows : [];
      const containerSet = new Set<string>();
      const barcodeSet = new Set<string>();
      let totalQty = 0;
      rows.forEach((r: any) => {
        const cc = String(r?.container_code || '').trim();
        const bc = String(r?.new_barcode || '').trim();
        if (cc) containerSet.add(cc);
        if (bc) barcodeSet.add(bc);
        totalQty += (Number(r?.total_quantity ?? 0) || 0);
      });
      return { containers: containerSet.size, barcodes: barcodeSet.size, totalQty: Math.floor(totalQty) };
    } catch {
      return { containers: 0, barcodes: 0, totalQty: 0 };
    }
  }, [iocNewBarcodeFacetRows]);
  const iocBarcodeSummaryRows = useMemo(() => {
    try {
      const m = new Map<string, number>();
      (Array.isArray(iocNewBarcodeFacetRows) ? iocNewBarcodeFacetRows : []).forEach((r: any) => {
        const bc = String(r?.new_barcode || '').trim();
        const q = Math.floor(Number(r?.total_quantity ?? 0) || 0);
        if (!bc || q <= 0) return;
        m.set(bc, (m.get(bc) || 0) + q);
      });
      return Array.from(m.entries())
        .map(([new_barcode, total_quantity], idx) => ({ __k: `bc-${idx}-${new_barcode}`, new_barcode, total_quantity: Math.max(0, Math.floor(Number(total_quantity || 0) || 0)) }))
        .sort((a, b) => (Number(b?.total_quantity || 0) - Number(a?.total_quantity || 0)) || String(a?.new_barcode || '').localeCompare(String(b?.new_barcode || ''), undefined, { numeric: true, sensitivity: 'base' }));
    } catch {
      return [] as AnyObj[];
    }
  }, [iocNewBarcodeFacetRows]);
  const iocBarcodeSummaryStats = useMemo(() => {
    try {
      const rows = Array.isArray(iocBarcodeSummaryRows) ? iocBarcodeSummaryRows : [];
      const barcodeKinds = rows.length;
      const totalQty = rows.reduce((acc: number, r: any) => acc + Math.floor(Number(r?.total_quantity || 0) || 0), 0);
      return { barcodeKinds, totalQty: Math.max(0, Math.floor(totalQty)) };
    } catch {
      return { barcodeKinds: 0, totalQty: 0 };
    }
  }, [iocBarcodeSummaryRows]);
  const [iocIuItemsById, setIocIuItemsById] = useState<Record<string, Array<{ barcode: string; qty: number }>>>({});
  const iocIuLoadingRef = useRef<Set<number>>(new Set());

  const cleanupPreviewObjUrl = useCallback(() => {
    try {
      const u = String(previewObjUrlRef.current || '').trim();
      if (u && u.startsWith('blob:')) {
        URL.revokeObjectURL(u);
      }
    } catch {}
    previewObjUrlRef.current = '';
  }, []);

  const clearPreviewXlsx = useCallback(() => {
    previewXlsxWbRef.current = null;
    previewXlsxLibRef.current = null;
    previewXlsxCacheRef.current = {};
    setPreviewXlsxSheets([]);
    setPreviewXlsxActiveSheet('');
  }, []);

  // Excel/CSV 表格裁剪：去掉末尾无数据的空行/空列（很多 Excel 会因为样式把 !ref 撑大）
  const trimAoa = useCallback((aoaRaw: any[][], opts?: { maxRows?: number; maxCols?: number }): any[][] => {
    const maxRows = Math.max(1, Number(opts?.maxRows || 2000));
    const maxColsCap = Math.max(1, Number(opts?.maxCols || 120));
    const aoa = Array.isArray(aoaRaw) ? aoaRaw.slice(0, maxRows) : [];
    const isEmpty = (v: any): boolean => {
      if (v === null || v === undefined) return true;
      if (typeof v === 'string') return v.trim() === '';
      return false;
    };
    let maxColUsed = 0;
    for (const r of aoa) {
      if (!Array.isArray(r) || r.length === 0) continue;
      let j = Math.min(r.length - 1, maxColsCap - 1);
      for (; j >= 0; j--) {
        if (!isEmpty(r[j])) { maxColUsed = Math.max(maxColUsed, j + 1); break; }
      }
    }
    maxColUsed = Math.min(maxColsCap, Math.max(0, maxColUsed));
    if (maxColUsed <= 0) return [];
    let lastRowUsed = -1;
    for (let i = 0; i < aoa.length; i++) {
      const r = aoa[i];
      if (!Array.isArray(r) || r.length === 0) continue;
      let any = false;
      for (let j = 0; j < maxColUsed; j++) {
        if (!isEmpty(r[j])) { any = true; break; }
      }
      if (any) lastRowUsed = i;
    }
    if (lastRowUsed < 0) return [];
    return aoa.slice(0, lastRowUsed + 1).map((r) => (Array.isArray(r) ? r.slice(0, maxColUsed) : []));
  }, []);

  const aoaToPreviewTable = useCallback((aoaRaw: any[][]) => {
    const aoa = trimAoa(aoaRaw, { maxRows: 2000, maxCols: 120 });
    const head = Array.isArray(aoa?.[0]) ? aoa[0] : [];
    const data = (aoa || []).slice(1).map((r, idx) => {
      const obj: AnyObj = { key: idx };
      head.forEach((h: any, i: number) => { obj[String(h || `C${i + 1}`)] = r?.[i]; });
      return obj;
    });
    const cols = head.map((h: any, i: number) => ({
      title: String(h || `C${i + 1}`),
      dataIndex: String(h || `C${i + 1}`),
      key: String(h || `C${i + 1}`),
      ellipsis: true,
    }));
    return { columns: cols, data };
  }, [trimAoa]);

  const applyXlsxSheetToPreview = useCallback((sheetName: string) => {
    const wb = previewXlsxWbRef.current;
    const XLSX = previewXlsxLibRef.current;
    if (!wb || !XLSX || !sheetName) return;
    const cached = previewXlsxCacheRef.current?.[sheetName];
    if (cached) {
      setPreviewTable(cached);
      return;
    }
    const ws = wb.Sheets?.[sheetName];
    const aoaRaw: any[][] = ws ? (XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', blankrows: false }) as any[][]) : [];
    const table = aoaToPreviewTable(aoaRaw);
    previewXlsxCacheRef.current = { ...(previewXlsxCacheRef.current || {}), [sheetName]: table };
    setPreviewTable(table);
  }, [aoaToPreviewTable]);

  const cleanupLabelPdfObjUrl = useCallback(() => {
    try {
      const u = String(labelPdfObjUrlRef.current || '').trim();
      if (u && u.startsWith('blob:')) {
        URL.revokeObjectURL(u);
      }
    } catch {}
    labelPdfObjUrlRef.current = '';
  }, []);

  const cleanupWrapAttObjUrl = useCallback(() => {
    try {
      const u = String(wrapAttObjUrlRef.current || '').trim();
      if (u && u.startsWith('blob:')) {
        URL.revokeObjectURL(u);
      }
    } catch {}
    wrapAttObjUrlRef.current = '';
  }, []);

  const openLabelPdfPreview = useCallback(async (rawUrl: string, fileName?: string) => {
    const resolved = resolveBackendFileUrl(rawUrl) || rawUrl;
    if (!resolved) return;
    cleanupLabelPdfObjUrl();
    try {
      const resp = await fetch(resolved, { credentials: 'include' as any });
      if (!resp.ok) throw new Error('PDF 加载失败');
      const blob = await resp.blob();
      const objectUrl = URL.createObjectURL(blob);
      labelPdfObjUrlRef.current = objectUrl;
      setLabelPdfTitle(`预览: ${fileName || 'PDF'}`);
      setLabelPdfUrl(objectUrl);
      setLabelPdfOpen(true);
    } catch (e: any) {
      // 兜底：仍然允许以普通方式预览（可能触发下载）
      try {
        setLabelPdfTitle(`预览: ${fileName || 'PDF'}`);
        setLabelPdfUrl(resolved);
        setLabelPdfOpen(true);
      } catch {}
      try { message.error(e?.message || 'PDF 预览失败'); } catch {}
    }
  }, [cleanupLabelPdfObjUrl]);

  const openWrapAttPreview = useCallback(async (rawUrl: string, fileName?: string) => {
    const resolved = resolveBackendFileUrl(rawUrl) || rawUrl;
    if (!resolved) return;
    cleanupWrapAttObjUrl();
    const name = String(fileName || '').trim();
    const ext = (() => {
      const n = name || resolved;
      const m = String(n).toLowerCase().split('?')[0].split('#')[0].split('.').pop();
      return (m || '').trim();
    })();
    // PDF：走 blob 预览，避免 iframe 被拦截
    if (ext === 'pdf') {
      try {
        const resp = await fetch(resolved, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('PDF 加载失败');
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        wrapAttObjUrlRef.current = objectUrl;
        setWrapAttKind('pdf');
        setWrapAttTitle(`预览: ${name || 'PDF'}`);
        setWrapAttUrl(objectUrl);
        setWrapAttOpen(true);
        return;
      } catch (e: any) {
        try { message.error(e?.message || 'PDF 预览失败'); } catch {}
      }
    }
    // 图片
    if (['png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext)) {
      setWrapAttKind('image');
      setWrapAttTitle(`预览: ${name || '图片'}`);
      setWrapAttUrl(resolved);
      setWrapAttOpen(true);
      return;
    }
    // 视频
    if (['mp4', 'webm', 'ogg', 'mov'].includes(ext)) {
      setWrapAttKind('video');
      setWrapAttTitle(`预览: ${name || '视频'}`);
      setWrapAttUrl(resolved);
      setWrapAttOpen(true);
      return;
    }
    // 其他类型：仅提示，可下载
    setWrapAttKind('other');
    setWrapAttTitle(`预览: ${name || '附件'}`);
    setWrapAttUrl(resolved);
    setWrapAttOpen(true);
  }, [cleanupWrapAttObjUrl]);

  const downloadWrapAtt = useCallback(async (rawUrl: string, fileName?: string) => {
    const resolved = resolveBackendFileUrl(rawUrl) || rawUrl;
    if (!resolved) return;
    const name = String(fileName || '').trim();
    const ext = (() => {
      const n = name || resolved;
      const m = String(n).toLowerCase().split('?')[0].split('#')[0].split('.').pop();
      return (m || '').trim();
    })();
    const shouldBlobDownload = ['pdf', 'png', 'jpg', 'jpeg', 'gif', 'webp', 'bmp'].includes(ext);
    // PDF/图片：用 blob 强制下载，避免浏览器打开新窗口/新标签
    if (shouldBlobDownload) {
      try {
        const resp = await fetch(resolved, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('下载失败');
        const blob = await resp.blob();
        const objectUrl = URL.createObjectURL(blob);
        try {
          const a = document.createElement('a');
          a.href = objectUrl;
          a.download = name || `download.${ext || 'bin'}`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
        } finally {
          try { URL.revokeObjectURL(objectUrl); } catch {}
        }
        return;
      } catch (e: any) {
        try { message.error(e?.message || '下载失败'); } catch {}
        // fallback to direct link
      }
    }
    // 兜底：直链下载（可能被浏览器当作打开/预览）
    try {
      const a = document.createElement('a');
      a.href = resolved;
      if (name) a.download = name;
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {}
  }, []);

  // 打开阶段快照预览后，若为 RELABEL_PACK，则预取操作记录里的 op_iu_id 对应系统编码
  useEffect(() => {
    (async () => {
      try {
        if (!previewOpen) return;
        const info = previewStageReadable;
        if (!info) return;
        const sk = String(info?.stage_key || '').toUpperCase();
        if (sk !== 'RELABEL_PACK') return;
        const snap = (info?.snapshot || {}) as AnyObj;
        const inner = (snap?.snapshot && typeof snap.snapshot === 'object') ? (snap.snapshot as AnyObj) : (snap as AnyObj);
        const groups: any[] = Array.isArray(inner?.groups) ? inner.groups : [];
        const g0 = groups[0] || {};
        const records: any[] = Array.isArray(g0?.records_preview) ? g0.records_preview : [];
        const ids = Array.from(new Set(
          (records || [])
            .map((r: any) => Math.floor(Number(r?.op_iu_id || 0) || 0))
            .filter((n: number) => Number.isFinite(n) && n > 0)
        ));
        if (!ids.length) return;
        const missing = ids.filter((id) => !iuSysCodeById[String(id)] && !iuSysLoadingRef.current.has(id));
        if (!missing.length) return;
        // 限制一次最多补拉 200 个，避免卡顿
        const slice = missing.slice(0, 200);
        slice.forEach((id) => iuSysLoadingRef.current.add(id));
        const pairs = await Promise.allSettled(slice.map(async (id) => {
          try {
            const resp: any = await getInventoryUnitDetail(id);
            const d = resp?.data || resp;
            const sys = String(
              d?.system_code
              || d?.systemCode
              || d?.system_barcode
              || d?.systemBarcode
              || d?.code
              || ''
            ).trim();
            return { id, sys: sys || '' };
          } catch {
            return { id, sys: '' };
          }
        }));
        const next: Record<string, string> = {};
        pairs.forEach((p) => {
          if (p.status !== 'fulfilled') return;
          const id = Math.floor(Number((p.value as any)?.id || 0) || 0);
          const sys = String((p.value as any)?.sys || '').trim();
          if (id > 0 && sys) next[String(id)] = sys;
        });
        if (Object.keys(next).length > 0) {
          setIuSysCodeById((prev) => ({ ...prev, ...next }));
        }
        slice.forEach((id) => iuSysLoadingRef.current.delete(id));
      } catch {}
    })();
  }, [previewOpen, previewStageReadable, iuSysCodeById]);

  // 关闭快照弹窗或切换阶段时，清空 WRAP_UP 步骤4勾选
  useEffect(() => {
    try {
      if (!previewOpen) { setWrapStep4SelectedKeys([]); return; }
      const sk = String(previewStageReadable?.stage_key || '').toUpperCase();
      if (sk !== 'WRAP_UP') { setWrapStep4SelectedKeys([]); return; }
    } catch {}
  }, [previewOpen, previewStageReadable?.stage_key]);

  useEffect(() => {
    if (!previewOpen) return;
    if (previewKind !== 'table') return;
    setPreviewTablePage(1);
  }, [previewOpen, previewKind, previewTable, previewXlsxActiveSheet]);

  const load = useCallback(async () => {
    if (!instrId) return;
    setLoading(true);
    try {
      const r = await request.get(`/api/v1/instruction/instructions/${instrId}/`);
      const data = r?.data || null;
      setInstr(data);
      setNotes(String(data?.meta?.final_review_notes || '').trim());
      const st = String(data?.status || '').trim().toLowerCase();
      if (st === 'rejected') {
        message.warning('该指令已驳回，禁止进入终审');
        try {
          const p = String(location.pathname || '');
          const base = p.startsWith('/dashboard/staff')
            ? '/dashboard/staff'
            : (p.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer');
          navigate(`${base}/instructions/list${location.search || ''}`, { replace: true } as any);
        } catch {}
      }
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载指令失败');
      setInstr(null);
    } finally {
      setLoading(false);
    }
  }, [instrId, location.pathname, location.search, navigate]);

  const loadFinalReviewAttachments = useCallback(async () => {
    if (!instrId) return;
    setAttsLoading(true);
    try {
      const r = await request.get('/api/v1/instruction/attachments/', {
        params: {
          instruction: instrId,
          label: 'final_review',
          scope: 'order',
          status: 'active',
          page_size: 200,
        },
      } as any);
      const data = r?.data;
      const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
      setAttachments(list);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载终审附件失败');
      setAttachments([]);
    } finally {
      setAttsLoading(false);
    }
  }, [instrId]);

  const loadBaselines = useCallback(async () => {
    if (!instrId) return;
    setBaselinesLoading(true);
    try {
      const r = await request.get('/api/v1/instruction/baselines/', {
        params: { instruction: instrId, page_size: 500 },
      } as any);
      const data = r?.data;
      const list = Array.isArray(data?.results) ? data.results : (Array.isArray(data) ? data : []);
      const rows: AnyObj[] = [];
      (list || []).forEach((b: AnyObj) => {
        const files = Array.isArray(b?.files) ? b.files : [];
        files.forEach((f: AnyObj) => {
          rows.push({
            baseline_id: b?.id,
            baseline_code: b?.code,
            baseline_name: b?.name,
            baseline_state: b?.state,
            baseline_updated_at: b?.updated_at,
            ...f,
          });
        });
      });
      setBaselineFiles(rows);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载基线失败');
      setBaselineFiles([]);
    } finally {
      setBaselinesLoading(false);
    }
  }, [instrId]);

  const loadStageSubs = useCallback(async () => {
    if (!instrId) return;
    setStageSubsLoading(true);
    try {
      const r = await request.get(`/api/v1/instruction/instructions/${instrId}/stage-submissions`, {
        params: { latest: true },
      } as any);
      const list = Array.isArray(r?.data?.results) ? r.data.results : [];
      setStageSubs(list);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载阶段快照失败');
      setStageSubs([]);
    } finally {
      setStageSubsLoading(false);
    }
  }, [instrId]);

  const loadIoc = useCallback(async () => {
    if (!instrId) return;
    setIocLoading(true);
    try {
      const r = await request.get(`/api/v1/instruction/instructions/${instrId}/ioc/with-contents/`);
      const list = Array.isArray(r?.data?.results) ? r.data.results : [];
      setIocRows(list);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载IOC装箱数据失败');
      setIocRows([]);
    } finally {
      setIocLoading(false);
    }
  }, [instrId]);

  const loadFinalPricingPreview = useCallback(async () => {
    if (!instrId) return;
    setPricingLoading(true);
    try {
      const r = await request.get(`/api/v1/instruction/instructions/${instrId}/final-review/pricing/preview/`);
      const pv = (r?.data?.preview && typeof r.data.preview === 'object') ? (r.data.preview as AnyObj) : {};
      const draft = (r?.data?.draft && typeof r.data.draft === 'object') ? (r.data.draft as AnyObj) : null;
      setPricingPreview(pv);
      setPricingWarnings(Array.isArray(pv?.warnings) ? (pv.warnings as string[]) : []);
      setPricingBlockingIssues(Array.isArray(pv?.blocking_issues) ? (pv.blocking_issues as AnyObj[]) : []);

      const draftFacts = (draft?.facts_json && typeof draft.facts_json === 'object') ? (draft.facts_json as AnyObj) : null;
      const draftEvidence = (draft?.evidence_json && typeof draft.evidence_json === 'object') ? (draft.evidence_json as AnyObj) : null;
      const suggestedFacts = (pv?.suggested_facts_json && typeof pv.suggested_facts_json === 'object') ? (pv.suggested_facts_json as AnyObj) : null;
      const suggestedEvidence = (pv?.evidence_json && typeof pv.evidence_json === 'object') ? (pv.evidence_json as AnyObj) : null;

      const hasDraftFacts = !!draftFacts && Object.keys(draftFacts || {}).length > 0;
      const factsToUse = hasDraftFacts ? (draftFacts as AnyObj) : (suggestedFacts || {});
      setPricingFacts((factsToUse && typeof factsToUse === 'object') ? factsToUse : { by_workorder: {} });

      const hasDraftEvidence = !!draftEvidence && Object.keys(draftEvidence || {}).length > 0;
      // 证据合并策略：
      // - 先用 preview.suggested evidence（含后端最新动态计费说明）
      // - 再让 draft 覆盖同名字段（保留用户草稿）
      // 这样可避免旧 draft 把新增字段（如包材单价/金额）完全覆盖掉。
      const suggestedEvObj = (suggestedEvidence && typeof suggestedEvidence === 'object') ? (suggestedEvidence as AnyObj) : {};
      const draftEvObj = (hasDraftEvidence && draftEvidence && typeof draftEvidence === 'object') ? (draftEvidence as AnyObj) : {};
      const mergeByWo = {
        ...((suggestedEvObj?.by_workorder && typeof suggestedEvObj.by_workorder === 'object') ? suggestedEvObj.by_workorder : {}),
        ...((draftEvObj?.by_workorder && typeof draftEvObj.by_workorder === 'object') ? draftEvObj.by_workorder : {}),
      };
      const evidenceToUse: AnyObj = {
        ...suggestedEvObj,
        ...draftEvObj,
        by_workorder: mergeByWo,
      };
      setPricingEvidence((evidenceToUse && typeof evidenceToUse === 'object') ? evidenceToUse : {});
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载计费预览失败');
      setPricingPreview({});
      setPricingWarnings([]);
      setPricingBlockingIssues([]);
      setPricingFacts({ by_workorder: {} });
      setPricingEvidence({});
    } finally {
      setPricingLoading(false);
    }
  }, [instrId]);

  const saveFinalPricingDraft = useCallback(async () => {
    if (!instrId) return;
    try {
      await request.post(`/api/v1/instruction/instructions/${instrId}/final-review/pricing/save/`, {
        facts_json: pricingFacts || { by_workorder: {} },
        evidence_json: pricingEvidence || {},
      });
      message.success('已保存计费草稿');
      void loadFinalPricingPreview();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '保存计费草稿失败');
    }
  }, [instrId, pricingEvidence, pricingFacts, loadFinalPricingPreview]);

  const pricingEvidenceResolverSummary = useMemo(() => {
    try {
      const ev = (pricingEvidence && typeof pricingEvidence === 'object') ? (pricingEvidence as AnyObj) : {};
      const computed = (ev?.computed && typeof ev.computed === 'object') ? (ev.computed as AnyObj) : {};
      const usedRaw =
        computed?.regular_outbound_resolver_upstream_trace_used ??
        computed?.instruction_output_upstream_trace_used;
      const hopsRaw =
        computed?.regular_outbound_resolver_upstream_trace_hops ??
        computed?.instruction_output_upstream_trace_hops;
      const addedRaw =
        computed?.regular_outbound_resolver_upstream_added_ioc_count ??
        computed?.instruction_output_upstream_added_container_count;

      const used = !!usedRaw;
      const hops = Number(hopsRaw);
      const added = Number(addedRaw);
      return {
        visible: (usedRaw !== undefined) || Number.isFinite(hops) || Number.isFinite(added),
        used,
        hops: Number.isFinite(hops) ? hops : 0,
        added: Number.isFinite(added) ? added : 0,
      };
    } catch {
      return { visible: false, used: false, hops: 0, added: 0 };
    }
  }, [pricingEvidence]);

  const loadIocNewBarcodeFacets = useCallback(async () => {
    if (!instrId) return;
    setIocNewBarcodeFacetLoading(true);
    try {
      const iocList = Array.isArray(iocRows) ? iocRows : [];
      const iocByCode = new Map<string, AnyObj>();
      iocList.forEach((x: any) => {
        const cc = String(x?.code || '').trim();
        if (cc) iocByCode.set(cc, x);
      });
      const fmtKgCeil1 = (gRaw: any): number | null => {
        const g = Number(gRaw);
        if (!Number.isFinite(g) || g <= 0) return null;
        // 向上进位到 1 位小数 kg（例如 0.01kg -> 0.1kg；0.11kg -> 0.2kg）
        // kg*10 = g/100，因此 ceil(g/100)/10
        return Math.ceil(g / 100) / 10;
      };
      const fmtDims = (r: any): string | null => {
        const L = Number(r?.length_cm);
        const W = Number(r?.width_cm);
        const H = Number(r?.height_cm);
        if (![L, W, H].every((x) => Number.isFinite(x) && x > 0)) return null;
        const fmt = (x: number) => (Number.isInteger(x) ? String(x) : String(Math.round(x * 10) / 10));
        return `${fmt(L)}×${fmt(W)}×${fmt(H)}`;
      };
      const isExceptionContainer = (oc: any): boolean => {
        const kind = String(oc?.content_kind || '').toLowerCase();
        if (!kind) return false;
        return kind.includes('exception') || kind.includes('unoperable') || kind.includes('abnormal');
      };
      const toGroupedRows = (rawRows: AnyObj[]): AnyObj[] => {
        const cleaned = (Array.isArray(rawRows) ? rawRows : [])
          .filter((x: any) => {
            if (!x?.container_code || !x?.new_barcode) return false;
            if (!(Number(x?.total_quantity) > 0)) return false;
            const oc = iocByCode.get(String(x?.container_code || '').trim());
            if (!oc) return true;
            return !isExceptionContainer(oc);
          })
          .sort((a: any, b: any) => (
            a.container_code.localeCompare(b.container_code, undefined, { numeric: true, sensitivity: 'base' })
            || a.new_barcode.localeCompare(b.new_barcode, undefined, { numeric: true, sensitivity: 'base' })
          ));
        const byCode = new Map<string, AnyObj[]>();
        cleaned.forEach((r: any) => {
          const cc = String(r?.container_code || '').trim();
          if (!cc) return;
          if (!byCode.has(cc)) byCode.set(cc, []);
          byCode.get(cc)!.push(r);
        });
        const codesOrdered = Array.from(byCode.keys()).sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
        const out: AnyObj[] = [];
        codesOrdered.forEach((cc, gi) => {
          const arr = byCode.get(cc) || [];
          arr.forEach((r: any, idx: number) => {
            out.push({
              ...r,
              __row_span: idx === 0 ? arr.length : 0,
              __group_index: gi,
            });
          });
        });
        return out;
      };
      const aggregateFromIocContents = (): AnyObj[] => {
        const agg = new Map<string, AnyObj>();
        iocList.forEach((oc: any) => {
          const cc = String(oc?.code || '').trim();
          if (!cc) return;
          if (isExceptionContainer(oc)) return;
          const contents = Array.isArray(oc?.contents) ? oc.contents : [];
          contents.forEach((c: any) => {
            const bc = String(c?.inventory_unit_system_barcode || c?.system_barcode || c?.new_barcode || '').trim();
            const qty = Math.max(0, Math.floor(Number(c?.quantity ?? c?.qty ?? 0) || 0));
            if (!bc || !(qty > 0)) return;
            const key = `${cc}__${bc}`;
            if (!agg.has(key)) {
              agg.set(key, {
                __k: key,
                container_code: cc,
                container_type_text: iocContainerTypeText(cc),
                new_barcode: bc,
                total_quantity: 0,
                weight_kg: fmtKgCeil1(oc?.weight_g),
                dims_cm: fmtDims(oc),
              });
            }
            const hit = agg.get(key)!;
            hit.total_quantity = Math.max(0, Number(hit.total_quantity || 0)) + qty;
          });
        });
        return Array.from(agg.values());
      };
      const aggregateFromStageSnapshot = (): AnyObj[] => {
        try {
          const isAdjustPoolContainerCode = (raw: any): boolean => {
            const code = String(raw || '').trim().toUpperCase();
            if (!code) return false;
            return code.startsWith('ADJ-POOL') || code.startsWith('ADJ_POOL');
          };
          const list = Array.isArray(stageSubs) ? stageSubs : [];
          const relabelSubs = list
            .filter((x: any) => String(x?.stage_key || '').trim().toUpperCase() === 'RELABEL_PACK')
            .slice()
            .sort((a: any, b: any) => {
              const ta = Number(new Date(String(a?.submitted_at || 0)).getTime()) || 0;
              const tb = Number(new Date(String(b?.submitted_at || 0)).getTime()) || 0;
              return tb - ta;
            });
          const chosen = relabelSubs[0];
          if (!chosen) return [];
          const root = (chosen?.snapshot && typeof chosen.snapshot === 'object') ? (chosen.snapshot as AnyObj) : {};
          const snap = (root?.snapshot && typeof root.snapshot === 'object') ? (root.snapshot as AnyObj) : root;
          const groups = Array.isArray(snap?.groups) ? snap.groups : [];
          const g0 = groups[0] || {};
          const records = Array.isArray(g0?.records_preview)
            ? g0.records_preview
            : (Array.isArray(snap?.sections?.operation_records?.rows) ? snap.sections.operation_records.rows : []);
          const agg = new Map<string, AnyObj>();
          (Array.isArray(records) ? records : []).forEach((r: any) => {
            const cc = String(r?.container_code || r?.containerCode || '').trim();
            const bc = String(r?.new_code || r?.new_barcode || '').trim().toUpperCase();
            const qty = Math.floor(Number(r?.qty ?? r?.quantity ?? 0) || 0);
            if (!cc || !bc || !Number.isFinite(qty) || qty === 0) return;
            if (isAdjustPoolContainerCode(cc)) return;
            const oc = iocByCode.get(cc);
            if (oc && isExceptionContainer(oc)) return;
            const key = `${cc}__${bc}`;
            if (!agg.has(key)) {
              agg.set(key, {
                __k: key,
                container_code: cc,
                container_type_text: iocContainerTypeText(cc),
                new_barcode: bc,
                total_quantity: 0,
                weight_kg: fmtKgCeil1(oc?.weight_g),
                dims_cm: fmtDims(oc),
              });
            }
            const hit = agg.get(key)!;
            hit.total_quantity = Number(hit.total_quantity || 0) + qty;
          });
          return Array.from(agg.values());
        } catch {
          return [];
        }
      };
      const r = await request.get('/api/v1/storage/output-containers/facets/', {
        params: { instruction_id: instrId, facet_key: 'new_barcode' },
      } as any);
      const rows = Array.isArray(r?.data?.rows) ? r.data.rows : [];
      const fromFacets = rows.map((x: any) => ({
        __k: `${String(x?.container__code || x?.container_code || '')}__${String(x?.facet_value || x?.new_barcode || '')}`,
        container_code: String(x?.container__code || x?.container_code || '').trim(),
        container_type_text: iocContainerTypeText(String(x?.container__code || x?.container_code || '').trim()),
        new_barcode: String(x?.facet_value || x?.new_barcode || '').trim(),
        total_quantity: Number(x?.total_quantity ?? 0) || 0,
        weight_kg: (() => {
          const oc = iocByCode.get(String(x?.container__code || x?.container_code || '').trim());
          const kg = fmtKgCeil1(oc?.weight_g);
          return kg;
        })(),
        dims_cm: (() => {
          const oc = iocByCode.get(String(x?.container__code || x?.container_code || '').trim());
          return fmtDims(oc);
        })(),
      }));
      const outFromFacets = toGroupedRows(fromFacets);
      const outFromStage = toGroupedRows(aggregateFromStageSnapshot());
      const outFromIoc = toGroupedRows(aggregateFromIocContents());
      const hasRelabelStage = (Array.isArray(stageSubs) ? stageSubs : []).some(
        (x: any) => String(x?.stage_key || '').trim().toUpperCase() === 'RELABEL_PACK',
      );
      // defer 模式下 facets 可能为空；换标指令优先使用阶段快照(records.new_code)以避免把 IU 系统编码误当成新条码。
      let out = outFromFacets;
      if (hasRelabelStage) {
        out = outFromStage.length ? outFromStage : (outFromFacets.length ? outFromFacets : outFromIoc);
      } else if (!out.length) {
        out = outFromIoc.length ? outFromIoc : outFromStage;
      }
      setIocNewBarcodeFacetRows(out);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加载按新条码统计失败');
      setIocNewBarcodeFacetRows([]);
    } finally {
      setIocNewBarcodeFacetLoading(false);
    }
  }, [instrId, iocRows, stageSubs]);

  function getWrapUpExceptionContainersForExportRows(): AnyObj[] {
    const out: AnyObj[] = [];
    try {
      const list = Array.isArray(stageSubs) ? stageSubs : [];
      const wrap = list.find((x: any) => String(x?.stage_key || '').toUpperCase() === 'WRAP_UP');
      const snap = (wrap?.snapshot || {}) as AnyObj;
      const inner = (snap?.snapshot && typeof snap.snapshot === 'object') ? (snap.snapshot as AnyObj) : (snap as AnyObj);
      const sections = (inner?.sections || {}) as AnyObj;
      const s1 = (sections?.step1_unoperable || sections?.step1_exception_containers || {}) as AnyObj;
      const arr = Array.isArray((s1 as any)?.exception_containers) ? (s1 as any).exception_containers : [];
      (arr || []).forEach((c: any, idx: number) => {
        const code = String(c?.code || '').trim();
        if (!code) return;
        const allocs = Array.isArray(c?.allocations) ? c.allocations : [];
        const totalByAlloc = allocs.reduce((acc: number, a: any) => {
          const q = Number(a?.quantity ?? a?.qty ?? 0) || 0;
          return acc + Math.max(0, Math.floor(Math.abs(q)));
        }, 0);
        const tq = Number(c?.total_qty ?? 0);
        const totalQty = (Number.isFinite(tq) && tq > 0) ? Math.max(0, Math.floor(tq)) : Math.max(0, Math.floor(totalByAlloc));
        out.push({
          __k: `wrap-exception-export-${idx}-${code}`,
          code,
          total_qty: totalQty,
          allocations: allocs,
          exception_total_weight_g: Number(c?.exception_total_weight_g ?? c?.total_weight_g ?? c?.total_weight ?? 0) || 0,
        });
      });
    } catch {}
    return out;
  }

  const buildIocNewBarcodeXlsx = useCallback(async () => {
    // rows: 使用当前弹窗展示口径组装（仅影响导出展示形式）
    const normalRows = (Array.isArray(iocNewBarcodeFacetRows) ? iocNewBarcodeFacetRows : []).map((r: AnyObj) => ({
      ...r,
      __ioc_kind: 'normal',
      __is_summary: false,
    }));
    const exceptionRows = (() => {
      const wrapRows = getWrapUpExceptionContainersForExportRows();
      const fromWrap = (Array.isArray(wrapRows) ? wrapRows : [])
        .map((c: AnyObj, idx: number) => {
          const cc = String(c?.code || '').trim();
          if (!cc) return null;
          const allocs = Array.isArray(c?.allocations) ? c.allocations : [];
          const byAlloc = allocs.reduce((acc: number, a: any) => acc + Math.max(0, Math.floor(Math.abs(Number(a?.quantity ?? a?.qty ?? 0) || 0))), 0);
          const tqRaw = Number(c?.total_qty ?? 0);
          const total = (Number.isFinite(tqRaw) && tqRaw > 0) ? Math.max(0, Math.floor(tqRaw)) : byAlloc;
          const g = Number(c?.exception_total_weight_g ?? c?.total_weight_g ?? c?.total_weight ?? 0);
          const kg = (Number.isFinite(g) && g > 0) ? Math.ceil((g / 1000) * 10) / 10 : null;
          return {
            __k: `__exception_ioc_export_wrap_${idx}_${cc}`,
            __is_summary: false,
            __ioc_kind: 'exception',
            container_code: cc,
            container_type_text: '异常内容容器',
            new_barcode: '-',
            total_quantity: total,
            weight_kg: kg,
            dims_cm: '',
          } as AnyObj;
        })
        .filter(Boolean) as AnyObj[];
      if (fromWrap.length > 0) return fromWrap;
      return (Array.isArray(iocRows) ? iocRows : [])
        .filter((r: any) => {
          const kind = String(r?.content_kind || '').toLowerCase();
          return !!kind && (kind.includes('exception') || kind.includes('unoperable') || kind.includes('abnormal'));
        })
        .map((oc: AnyObj, idx: number) => {
          const cc = String(oc?.code || '').trim();
          if (!cc) return null;
          const contents = Array.isArray(oc?.contents) ? oc.contents : [];
          const total = contents.reduce((acc: number, c: any) => acc + (Math.floor(Number(c?.quantity || 0) || 0)), 0);
          const g = Number(oc?.weight_g);
          const kg = (Number.isFinite(g) && g > 0) ? Math.ceil((g / 1000) * 10) / 10 : null;
          const L = Number(oc?.length_cm);
          const W = Number(oc?.width_cm);
          const H = Number(oc?.height_cm);
          const dims = ([L, W, H].every((x) => Number.isFinite(x) && x > 0)) ? `${L}×${W}×${H}` : '';
          return {
            __k: `__exception_ioc_export_${idx}_${cc}`,
            __is_summary: false,
            __ioc_kind: 'exception',
            container_code: cc,
            container_type_text: '异常内容容器',
            new_barcode: '-',
            total_quantity: total,
            weight_kg: kg,
            dims_cm: dims,
          } as AnyObj;
        })
        .filter(Boolean) as AnyObj[];
    })();
    const rows: AnyObj[] = [...normalRows, ...exceptionRows];
    const instrCode = String((instr as any)?.system_code || instrId || 'INSTR').trim() || 'INSTR';
    const ymd = (() => {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    })();
    const fileName = `${instrCode}-总包装数据-${ymd}.xlsx`;

    // 使用 exceljs 生成可样式化的 xlsx（实现“水平+垂直居中”）
    let ExcelJS: any = null;
    try {
      ExcelJS = await import('exceljs');
    } catch {}
    if (!ExcelJS) throw new Error('缺少 Excel 生成库（exceljs）');

    const ceil1NonZero = (v: any): number | null => {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return null;
      return Math.ceil(n * 10) / 10;
    };
    const ceil2NonZero = (v: any): number | null => {
      const n = Number(v);
      if (!Number.isFinite(n) || n <= 0) return null;
      return Math.ceil(n * 100) / 100;
    };
    const parseDimsCm = (raw: any): [number, number, number] | null => {
      const s = String(raw || '').trim();
      if (!s) return null;
      const parts = s.split(/[×xX]/g).map((x: string) => Number(String(x).trim()));
      if (parts.length !== 3) return null;
      const ok = parts.every((n) => Number.isFinite(n) && n > 0);
      if (!ok) return null;
      return [parts[0], parts[1], parts[2]];
    };
    const cmToIn = (cm: number) => (cm / 2.54);
    const formatWeightKgLb = (kgRaw: any): string => {
      const kg = ceil1NonZero(kgRaw);
      if (kg == null) return '-';
      const lb = ceil1NonZero(kg * 2.2046226218);
      if (lb == null) return '-';
      return `${kg.toFixed(1)} / ${lb.toFixed(1)}`;
    };
    const formatDimsCmIn = (dimsText: any): string => {
      const dims = parseDimsCm(dimsText);
      if (!dims) return '-';
      const [L, W, H] = dims;
      const Lc = ceil1NonZero(L); const Wc = ceil1NonZero(W); const Hc = ceil1NonZero(H);
      if (Lc == null || Wc == null || Hc == null) return '-';
      const Li = ceil1NonZero(cmToIn(Lc)); const Wi = ceil1NonZero(cmToIn(Wc)); const Hi = ceil1NonZero(cmToIn(Hc));
      if (Li == null || Wi == null || Hi == null) return '-';
      return `${Lc.toFixed(1)}×${Wc.toFixed(1)}×${Hc.toFixed(1)} / ${Li.toFixed(1)}×${Wi.toFixed(1)}×${Hi.toFixed(1)}`;
    };
    const formatVolumeM3Ft3 = (dimsText: any): string => {
      const dims = parseDimsCm(dimsText);
      if (!dims) return '-';
      const [L, W, H] = dims;
      const m3Raw = (L * W * H) / 1_000_000;
      const m3 = ceil2NonZero(m3Raw);
      const ft3 = ceil2NonZero(m3Raw * 35.314666721);
      if (m3 == null || ft3 == null) return '-';
      return `${m3.toFixed(2)} / ${ft3.toFixed(2)}`;
    };

    const normalDetailRows = rows
      .filter((r: any) => !r?.__is_summary && String(r?.__ioc_kind || '') !== 'exception')
      .filter((r: any) => String(r?.container_code || '').trim() && String(r?.new_barcode || '').trim())
      .map((r: any) => ({
        container_code: String(r?.container_code || '').trim(),
        ioc_kind_text: '正常内容容器',
        container_type_text: String(r?.container_type_text || '').trim() || iocContainerTypeText(String(r?.container_code || '').trim()),
        new_barcode: String(r?.new_barcode || '').trim(),
        total_quantity: Math.floor(Number(r?.total_quantity ?? 0) || 0),
        // 向上进位到 1 位小数（<=0 视为缺失，避免 0.0）
        weight_kg: (r?.weight_kg == null || !Number.isFinite(Number(r.weight_kg)) || Number(r.weight_kg) <= 0) ? null : (Math.ceil(Number(r.weight_kg) * 10) / 10),
        dims_cm: String(r?.dims_cm || '').trim(),
      }));

    const unoperableSummaryQty = (() => {
      const v = exceptionRows.reduce((acc: number, r: any) => acc + (Math.floor(Number(r?.total_quantity ?? 0) || 0)), 0);
      return Math.floor(Number(v || 0) || 0);
    })();

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('总装箱数据');
    ws.columns = [
      { header: '容器序号', key: 'idx', width: 10 },
      { header: '指令产出容器(IOC)编码', key: 'container_code', width: 26 },
      { header: 'IOC类型', key: 'ioc_kind', width: 14 },
      { header: '条码', key: 'barcode', width: 28 },
      { header: '产品数量', key: 'qty', width: 12 },
      { header: '容器重量(kg/lb)', key: 'weight', width: 18 },
      { header: '容器尺寸(cm/in)', key: 'dims', width: 26 },
      { header: '容积(m³/ft³)', key: 'volume', width: 18 },
    ];

    // 表头样式（居中）
    try {
      const headerRow = ws.getRow(1);
      headerRow.height = 22;
      headerRow.font = { bold: true };
      headerRow.alignment = { vertical: 'middle', horizontal: 'center' };
    } catch {}

    // 写入数据 + 记录每个 IOC 的行范围用于合并
    const groupRanges: Array<{ start: number; end: number }> = [];
    const weightByContainer = new Map<string, number>();
    // Total 口径：按“每行显示值”累加（确保 Total 严格等于每行显示值之和）
    // - weight：按 0.1 kg / 0.1 lb 的“向上取整显示值”累加（用整数避免浮点误差）
    // - volume：按 0.01 m³ / 0.01 ft³ 的“向上取整显示值”累加（用整数避免浮点误差）
    const weightKgTenthsByContainer = new Map<string, number>(); // kg * 10
    const weightLbTenthsByContainer = new Map<string, number>(); // lb * 10
    const volumeM3HundredthsByContainer = new Map<string, number>(); // m3 * 100
    const volumeFt3HundredthsByContainer = new Map<string, number>(); // ft3 * 100
    let rowCursor = 2; // exceljs 行号从1开始，1是表头
    let i = 0;
    let seq = 1;
    while (i < normalDetailRows.length) {
      const cc = normalDetailRows[i].container_code;
      let j = i;
      while (j < normalDetailRows.length && normalDetailRows[j].container_code === cc) j += 1;
      const span = j - i;
      const start = rowCursor;
      const end = rowCursor + span - 1;
      for (let k = i; k < j; k += 1) {
        const r0 = normalDetailRows[k];
        const isFirst = (k === i);
        const row = ws.addRow({
          // 序号：按“容器”计数，同一容器多行只显示一次（并在后续合并）
          idx: isFirst ? seq : '',
          container_code: isFirst ? formatContainerCodeWithType(r0.container_code, r0.container_type_text) : '',
          ioc_kind: isFirst ? r0.ioc_kind_text : '',
          barcode: r0.new_barcode,
          qty: r0.total_quantity,
          weight: isFirst ? formatWeightKgLb(r0.weight_kg) : '',
          dims: isFirst ? formatDimsCmIn(r0.dims_cm) : '',
          volume: isFirst ? formatVolumeM3Ft3(r0.dims_cm) : '',
        });
        row.height = 20;
        try {
          // IOC类型列（C）：正常内容容器=绿色底
          const kindCell = row.getCell(3);
          kindCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF6FFED' } } as any;
          kindCell.font = { ...(kindCell.font || {}), color: { argb: 'FF389E0D' }, bold: true } as any;
        } catch {}
        rowCursor += 1;
        // 容器重量聚合只计一次（每个容器一条）
        if (isFirst) {
          const w = r0.weight_kg;
          if (w != null && Number.isFinite(Number(w))) {
            weightByContainer.set(r0.container_code, Number(w));
          }
          try {
            const dims = parseDimsCm(r0.dims_cm);
            if (dims) {
              const [L, W, H] = dims;
              const m3Raw = (L * W * H) / 1_000_000; // cm^3 -> m^3
              // === Total：按“显示值”累加 ===
              try {
                // weight：使用当前行的显示口径（kg 向上取整 1 位；lb 由 kg 显示值换算再向上取整 1 位）
                const kgDisp = ceil1NonZero(w);
                if (kgDisp != null) {
                  const kgTenths = Math.round(kgDisp * 10);
                  const lbDisp = ceil1NonZero(kgDisp * 2.2046226218);
                  const lbTenths = (lbDisp == null) ? 0 : Math.round(lbDisp * 10);
                  if (kgTenths > 0) weightKgTenthsByContainer.set(r0.container_code, kgTenths);
                  if (lbTenths > 0) weightLbTenthsByContainer.set(r0.container_code, lbTenths);
                }
              } catch {}
              try {
                // volume：m³/ft³ 都以真实值为基准先向上取整到 2 位，再累加显示值
                if (Number.isFinite(m3Raw) && m3Raw > 0) {
                  const m3Disp = ceil2NonZero(m3Raw);
                  const ft3Disp = ceil2NonZero(m3Raw * 35.314666721);
                  const m3Hund = (m3Disp == null) ? 0 : Math.round(m3Disp * 100);
                  const ft3Hund = (ft3Disp == null) ? 0 : Math.round(ft3Disp * 100);
                  if (m3Hund > 0) volumeM3HundredthsByContainer.set(r0.container_code, m3Hund);
                  if (ft3Hund > 0) volumeFt3HundredthsByContainer.set(r0.container_code, ft3Hund);
                }
              } catch {}
            }
          } catch {}
        }
      }
      if (span > 1) groupRanges.push({ start, end });
      // 序号按容器递增
      seq += 1;
      i = j;
    }

    // 合并单元格：同一 IOC 多行（IOC编码/容器重量/容器尺寸/容积）
    // 序号列不合并；列号从1开始：A=1, B=2, ...
    try {
      groupRanges.forEach(({ start, end }) => {
        ws.mergeCells(`A${start}:A${end}`); // 序号
        ws.mergeCells(`B${start}:B${end}`); // IOC编码
        ws.mergeCells(`C${start}:C${end}`); // IOC类型
        ws.mergeCells(`F${start}:F${end}`); // 容器重量
        ws.mergeCells(`G${start}:G${end}`); // 容器尺寸
        ws.mergeCells(`H${start}:H${end}`); // 容积
      });
    } catch {}

    // 正常内容 IOC 的 Total 行
    let totalRowNumber = 0;
    let packDataBorderLastRow = 0;
    try {
      const totalQty = normalDetailRows.reduce((acc, r) => acc + (Number(r?.total_quantity || 0) || 0), 0);
      // Total：严格按“每行显示值”累加（用整数避免浮点误差）
      const totalKgTenths = Array.from(weightKgTenthsByContainer.values()).reduce((acc, x) => acc + (Number(x || 0) || 0), 0);
      const totalLbTenths = Array.from(weightLbTenthsByContainer.values()).reduce((acc, x) => acc + (Number(x || 0) || 0), 0);
      const totalWeightTxt = (totalKgTenths > 0 && totalLbTenths > 0)
        ? `${(totalKgTenths / 10).toFixed(1)} / ${(totalLbTenths / 10).toFixed(1)}`
        : '-';

      const totalM3Hund = Array.from(volumeM3HundredthsByContainer.values()).reduce((acc, x) => acc + (Number(x || 0) || 0), 0);
      const totalFt3Hund = Array.from(volumeFt3HundredthsByContainer.values()).reduce((acc, x) => acc + (Number(x || 0) || 0), 0);
      const totalVolTxt = (totalM3Hund > 0 && totalFt3Hund > 0)
        ? `${(totalM3Hund / 100).toFixed(2)} / ${(totalFt3Hund / 100).toFixed(2)}`
        : '-';
      const totalRow = ws.addRow({
        idx: '',
        container_code: '正常产品总数',
        ioc_kind: '',
        barcode: '',
        qty: Math.floor(totalQty),
        weight: totalWeightTxt,
        dims: '',
        volume: totalVolTxt,
      });
      totalRow.height = 22;
      try { totalRowNumber = Number(totalRow?.number || 0) || 0; } catch { totalRowNumber = 0; }
      packDataBorderLastRow = totalRowNumber;
      // 产品数量列=E(5)，重量列=F(6)，容积列=H(8)
      const qtyCell = totalRow.getCell(5);
      const wCell = totalRow.getCell(6);
      const vCell = totalRow.getCell(8);
      const style = {
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF6FFED' } }, // 绿色
        font: { color: { argb: 'FF389E0D' }, bold: true }, // 绿色
        alignment: { vertical: 'middle', horizontal: 'center' },
      } as any;
      const titleCell = totalRow.getCell(2);
      titleCell.style = { ...(titleCell.style || {}), font: { color: { argb: 'FF389E0D' }, bold: true } };
      qtyCell.style = { ...(qtyCell.style || {}), ...style };
      wCell.style = { ...(wCell.style || {}), ...style };
      vCell.style = { ...(vCell.style || {}), ...style };

    } catch {}

    // 正常内容 IOC 区域边框：从第1行到「正常产品总数量」行，从 A 列到最后一列
    try {
      const lastCol = Number(ws.columnCount || 0) || 8;
      const lastRow = Number(packDataBorderLastRow || totalRowNumber || 0) || 0;
      if (lastRow > 0 && lastCol > 0) {
        const border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        } as any;
        for (let r = 1; r <= lastRow; r += 1) {
          const row = ws.getRow(r);
          for (let c = 1; c <= lastCol; c += 1) {
            const cell = row.getCell(c);
            cell.border = border;
          }
        }
      }
    } catch {}

    // 仅保留“无法操作产品总数”尾行（不导出异常内容容器明细）
    try {
      // 空一行
      ws.addRow({ idx: '', container_code: '', ioc_kind: '', barcode: '', qty: '', weight: '', dims: '', volume: '' });
      // 无法操作产品总数
      const unRowAfterException = ws.addRow({
        idx: '',
        container_code: '无法操作产品总数',
        ioc_kind: '',
        barcode: '',
        qty: Math.floor(Number(unoperableSummaryQty || 0) || 0),
        weight: '',
        dims: '',
        volume: '',
      });
      unRowAfterException.height = 22;
      try {
        const unTitle = unRowAfterException.getCell(2);
        unTitle.style = { ...(unTitle.style || {}), font: { color: { argb: 'FFD4380D' }, bold: true } };
        const unQty = unRowAfterException.getCell(5);
        unQty.style = {
          ...(unQty.style || {}),
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2E8' } },
          font: { color: { argb: 'FFFA541C' }, bold: true },
          alignment: { vertical: 'middle', horizontal: 'center' },
        } as any;
      } catch {}
      // “无法操作产品总数”行补边框
      try {
        const border = {
          top: { style: 'thin' },
          left: { style: 'thin' },
          bottom: { style: 'thin' },
          right: { style: 'thin' },
        } as any;
        const rn = Number(unRowAfterException?.number || 0) || 0;
        if (rn > 0) {
          const row = ws.getRow(rn);
          for (let c = 1; c <= 8; c += 1) {
            const cell = row.getCell(c);
            cell.border = border;
          }
        }
      } catch {}
    } catch {}

    // 全表居中
    try {
      ws.eachRow({ includeEmpty: false }, (row: any, rowNumber: number) => {
        row.eachCell({ includeEmpty: true }, (cell: any) => {
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
        });
      });
    } catch {}

    // 另一个表：按条码汇总（仅统计“正常 IOC”，与总装箱数据口径一致）
    try {
      const ws2 = wb.addWorksheet('按条码汇总');
      ws2.columns = [
        { header: '序号', key: 'idx', width: 8 },
        { header: '条码', key: 'barcode', width: 28 },
        { header: '产品数量', key: 'qty', width: 14 },
      ];
      // 表头样式
      try {
        const headerRow2 = ws2.getRow(1);
        headerRow2.height = 22;
        headerRow2.font = { bold: true };
        headerRow2.alignment = { vertical: 'middle', horizontal: 'center' };
      } catch {}

      const m = new Map<string, number>();
      normalDetailRows.forEach((r) => {
        const bc = String((r as any)?.new_barcode || '').trim();
        if (!bc) return;
        const q = Number((r as any)?.total_quantity ?? 0) || 0;
        if (!Number.isFinite(q) || q <= 0) return;
        m.set(bc, (m.get(bc) || 0) + Math.floor(q));
      });
      const items = Array.from(m.entries())
        .map(([barcode, qty]) => ({ barcode, qty: Math.floor(qty) }))
        .sort((a, b) => (b.qty - a.qty) || a.barcode.localeCompare(b.barcode, undefined, { numeric: true, sensitivity: 'base' }));

      let idx2 = 1;
      items.forEach((it) => {
        const row = ws2.addRow({ idx: idx2, barcode: it.barcode, qty: it.qty });
        row.height = 20;
        idx2 += 1;
      });

      // Total 行（可读性更强）
      const totalQty2 = items.reduce((acc, x) => acc + (Number(x.qty || 0) || 0), 0);
      const totalRow2 = ws2.addRow({ idx: '', barcode: 'Total', qty: Math.floor(totalQty2) });
      totalRow2.height = 22;
      // Total 值单元格样式：与第一个 sheet “总装箱数据”的 Total 数值单元格保持一致（黄色底 + 红色加粗）
      try {
        const qtyCell2 = totalRow2.getCell(3); // C列=产品数量
        const style2 = {
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE58F' } }, // 黄色
          font: { color: { argb: 'FFCF1322' }, bold: true }, // 红色加粗
          alignment: { vertical: 'middle', horizontal: 'center' },
        } as any;
        qtyCell2.style = { ...(qtyCell2.style || {}), ...style2 };
      } catch {}

      const unRow2 = ws2.addRow({
        idx: '',
        barcode: '无法操作产品总数',
        qty: Math.floor(Number(unoperableSummaryQty || 0) || 0),
      });
      unRow2.height = 22;
      try {
        const barCell2 = unRow2.getCell(2);
        barCell2.style = { ...(barCell2.style || {}), font: { color: { argb: 'FFD4380D' }, bold: true } } as any;
        const qtyUn2 = unRow2.getCell(3);
        const styleUn2 = {
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2E8' } },
          font: { color: { argb: 'FFFA541C' }, bold: true },
          alignment: { vertical: 'middle', horizontal: 'center' },
        } as any;
        qtyUn2.style = { ...(qtyUn2.style || {}), ...styleUn2 };
      } catch {}

      // 全表居中 + 边框（从第1行到 Total + 无法操作产品总数 行，A-C）
      const lastRow2 = Number(unRow2?.number || 0) || Number(totalRow2?.number || 0) || 0;
      const border2 = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      } as any;
      for (let r = 1; r <= lastRow2; r += 1) {
        const row = ws2.getRow(r);
        for (let c = 1; c <= 3; c += 1) {
          const cell = row.getCell(c);
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          cell.border = border2;
        }
      }
    } catch {}

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf as any], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    return { blob, fileName };
  }, [iocNewBarcodeFacetRows, iocRows, instr, instrId, stageSubs]);

  const downloadBlobAsFile = useCallback(async (blob: Blob, fileName: string) => {
    try {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.rel = 'noreferrer';
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => {
        try { URL.revokeObjectURL(url); } catch {}
      }, 1000);
    } catch {
      message.error('下载失败');
    }
  }, []);

  const downloadFinalReviewAttachment = useCallback(async (att: AnyObj) => {
    const raw = String(att?.file || att?.file_path || att?.file_url || '').trim();
    const url = resolveBackendFileUrl(raw) || raw;
    const name = String(att?.file_name || att?.file || att?.file_path || 'download').trim() || 'download';
    if (!url) return;
    try {
      const resp = await fetch(url, { credentials: 'include' as any });
      if (!resp.ok) throw new Error('download_failed');
      const blob = await resp.blob();
      await downloadBlobAsFile(blob, name);
    } catch {
      message.error('下载失败');
    }
  }, [downloadBlobAsFile]);

  const doBatchDownloadFinalAtts = useCallback(async () => {
    const keys = Array.from(new Set((finalAttSelectedKeys || []).map((k) => String(k || '')).filter(Boolean)));
    if (!keys.length) { message.warning('请先勾选附件'); return; }
    const sel = (attachments || []).filter((a: any) => keys.includes(String(a?.id || '')));
    if (!sel.length) { message.warning('未选择可下载的附件'); return; }
    for (const a of sel) {
      await downloadFinalReviewAttachment(a);
    }
  }, [attachments, finalAttSelectedKeys, downloadFinalReviewAttachment]);

  const doBatchDeleteFinalAtts = useCallback(async () => {
    const keys = Array.from(new Set((finalAttSelectedKeys || []).map((k) => String(k || '')).filter(Boolean)));
    if (!keys.length) { message.warning('请先勾选附件'); return; }
    Modal.confirm({
      title: '批量删除终审附件',
      okText: '删除',
      cancelText: '取消',
      okButtonProps: { danger: true },
      content: `将删除已勾选的 ${keys.length} 个终审附件记录（仅影响终审留档，不影响执行快照）。`,
      onOk: async () => {
        for (const idStr of keys) {
          try { await request.delete(`/api/v1/instruction/attachments/${idStr}/`); } catch {}
        }
        message.success('已删除');
        setFinalAttSelectedKeys([]);
        await loadFinalReviewAttachments();
      },
    });
  }, [finalAttSelectedKeys, loadFinalReviewAttachments]);

  const downloadIocNewBarcodeExcel = useCallback(async () => {
    if (!instrId) return;
    try {
      setIocNewBarcodeFacetExporting(true);
      const { blob, fileName } = await buildIocNewBarcodeXlsx();
      await downloadBlobAsFile(blob, fileName);
    } catch (e: any) {
      message.error(e?.message || e?.response?.data?.detail || '生成Excel失败');
    } finally {
      setIocNewBarcodeFacetExporting(false);
    }
  }, [instrId, buildIocNewBarcodeXlsx, downloadBlobAsFile]);

  const addIocNewBarcodeExcelToFinalReview = useCallback(async () => {
    if (!instrId) return;
    try {
      setIocNewBarcodeFacetAdding(true);
      const { blob, fileName } = await buildIocNewBarcodeXlsx();
      const file = new File([blob], fileName, { type: blob.type });
      const fd = new FormData();
      fd.append('instruction', String(instrId));
      fd.append('label', 'final_review');
      fd.append('scope', 'order');
      fd.append('status', 'active');
      // 后端模型层 file_path 为必填；这里显式传入，避免 DRF 校验阶段 400
      fd.append('file_path', fileName);
      fd.append('file', file);
      fd.append('file_name', fileName);
      // 注意：本项目的 request 封装会默认走 JSON；这里必须显式声明 multipart 才会把 file 放进 request.FILES
      await request.post('/api/v1/instruction/attachments/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        // 避免封装层 transformRequest 将 FormData JSON 化
        transformRequest: [(d: any) => d],
      } as any);
      message.success('已加入终审附件（总包装数据Excel）');
      await loadFinalReviewAttachments();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || JSON.stringify(e?.response?.data || {}) || e?.message || '加入终审附件失败');
    } finally {
      setIocNewBarcodeFacetAdding(false);
    }
  }, [instrId, buildIocNewBarcodeXlsx, loadFinalReviewAttachments]);


  useEffect(() => {
    // 兼容并纠正旧URL：/dashboard/admin/admin/instructions/:id/final-review -> /dashboard/admin/instructions/:id/final-review
    try {
      const p = String(location.pathname || '');
      if (p.startsWith('/dashboard/admin/admin/instructions/') && p.includes('/final-review')) {
        const fixed = p.replace('/dashboard/admin/admin/instructions/', '/dashboard/admin/instructions/');
        navigate(`${fixed}${location.search || ''}`, { replace: true } as any);
        return;
      }
    } catch {}
    void load();
    void loadFinalReviewAttachments();
    void loadBaselines();
    void loadStageSubs();
    void loadIoc();
    void loadFinalPricingPreview();
  }, [load, loadFinalReviewAttachments, loadBaselines, loadStageSubs, loadIoc, loadFinalPricingPreview, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!instrId) return;
    void loadBaselines();
    void loadStageSubs();
    void loadIoc();
    void loadFinalPricingPreview();
  }, [instrId, loadBaselines, loadStageSubs, loadIoc, loadFinalPricingPreview]);

  useEffect(() => {
    setSortCountInvPreview(null);
    setSortCountInvPreviewShown(false);
    inventoryAutoAttemptedRef.current = '';
  }, [instrId]);

  const getExtLower = useCallback((name?: string) => {
    const s = String(name || '').trim();
    const i = s.lastIndexOf('.');
    if (i < 0) return '';
    return s.slice(i + 1).trim().toLowerCase();
  }, []);

  const openPreviewOther = useCallback((url?: string) => {
    if (!url) return;
    try { window.open(url, '_blank'); } catch {}
  }, []);

  const openPreview = useCallback(async (rawUrl: string, fileName?: string, forceKind?: 'json' | 'text') => {
    const resolved = resolveBackendFileUrl(rawUrl) || rawUrl;
    const ext = getExtLower(fileName || rawUrl);
    setPreviewTable(null);
    setPreviewText('');
    cleanupPreviewObjUrl();
    clearPreviewXlsx();
    setPreviewUrl(resolved || '');

    if (forceKind === 'json' || ext === 'json') {
      try {
        const txt = await fetch(resolved).then(r => r.text());
        let obj: any = null;
        try { obj = JSON.parse(txt); } catch { obj = txt; }
        setPreviewKind('json');
        setPreviewTitle(`预览: ${fileName || 'JSON'}`);
        setPreviewText(typeof obj === 'string' ? obj : JSON.stringify(obj, null, 2));
        setPreviewStageReadable(null);
        setPreviewOpen(true);
        return;
      } catch {
        openPreviewOther(resolved);
        return;
      }
    }

    if (['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext)) {
      setPreviewKind('image');
      setPreviewTitle(`预览: ${fileName || '图片'}`);
      setPreviewStageReadable(null);
      setPreviewOpen(true);
      return;
    }
    if (ext === 'pdf') {
      // 避免 iframe 跨端口被 X-Frame-Options/SameSite 拦截：拉取为 blob 再预览
      try {
        const resp = await fetch(resolved, { credentials: 'include' as any });
        if (!resp.ok) throw new Error('fetch pdf failed');
        const blob = await resp.blob();
        const objUrl = URL.createObjectURL(blob);
        previewObjUrlRef.current = objUrl;
        setPreviewUrl(objUrl);
        setPreviewKind('pdf');
        setPreviewTitle(`预览: ${fileName || 'PDF'}`);
        setPreviewStageReadable(null);
        setPreviewOpen(true);
        return;
      } catch {
        openPreviewOther(resolved);
        return;
      }
    }
    if (['mp4', 'mov', 'avi', 'webm'].includes(ext)) {
      setPreviewKind('video');
      setPreviewTitle(`预览: ${fileName || '视频'}`);
      setPreviewStageReadable(null);
      setPreviewOpen(true);
      return;
    }

    if (['xlsx', 'xls', 'csv'].includes(ext)) {
      try {
        if (ext === 'csv') {
          const Papa = await (async () => {
            try { return (await import('papaparse')).default; } catch { return null; }
          })();
          const txt = await fetch(resolved).then(r => r.text());
          const parsed: any = Papa ? Papa.parse(txt, { skipEmptyLines: true }) : null;
          const aoaRaw: any[][] = (parsed && Array.isArray(parsed.data)) ? parsed.data : txt.split(/\r?\n/).filter(Boolean).map(line => line.split(','));
          const { columns: cols, data } = aoaToPreviewTable(aoaRaw);
          setPreviewKind('table');
          setPreviewTitle(`预览: ${fileName || 'CSV'}`);
          setPreviewTable({ columns: cols, data });
        setPreviewStageReadable(null);
          setPreviewOpen(true);
          return;
        }
        const XLSX = await import('xlsx');
        const buf = await fetch(resolved).then(r => r.arrayBuffer());
        const wb = XLSX.read(buf, { type: 'array' });
        const sheetNames = Array.isArray(wb?.SheetNames) ? (wb.SheetNames as string[]) : [];
        const sheetName = String(sheetNames?.[0] || '').trim();
        previewXlsxWbRef.current = wb;
        previewXlsxLibRef.current = XLSX;
        setPreviewXlsxSheets(sheetNames);
        setPreviewXlsxActiveSheet(sheetName);
        const ws = sheetName ? wb.Sheets?.[sheetName] : null;
        const aoaRaw: any[][] = ws ? (XLSX.utils.sheet_to_json(ws, { header: 1, defval: '', blankrows: false }) as any[][]) : [];
        const { columns: cols, data } = aoaToPreviewTable(aoaRaw);
        previewXlsxCacheRef.current = sheetName ? { [sheetName]: { columns: cols, data } } : {};
        setPreviewKind('table');
        setPreviewTitle(`预览: ${fileName || 'Excel'}`);
        setPreviewTable({ columns: cols, data });
        setPreviewStageReadable(null);
        setPreviewOpen(true);
        return;
      } catch {
        openPreviewOther(resolved);
        return;
      }
    }

    if (forceKind === 'text') {
      try {
        const txt = await fetch(resolved).then(r => r.text());
        setPreviewKind('text');
        setPreviewTitle(`预览: ${fileName || '文本'}`);
        setPreviewText(String(txt || ''));
        setPreviewStageReadable(null);
        setPreviewOpen(true);
        return;
      } catch {}
    }

    openPreviewOther(resolved);
  }, [aoaToPreviewTable, cleanupPreviewObjUrl, clearPreviewXlsx, getExtLower, openPreviewOther]);

  const addStageSnapshotToFinalReview = useCallback(async (stageSubmissionId: number) => {
    if (!instrId || !stageSubmissionId) return;
    try {
      await request.post(`/api/v1/instruction/instructions/${instrId}/final-review/attachments/from-stage-submission/`, {
        stage_submission_id: stageSubmissionId,
      });
      message.success('已加入终审附件（快照JSON）');
      await loadFinalReviewAttachments();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加入失败');
    }
  }, [instrId, loadFinalReviewAttachments]);

  const addStepAttachmentToFinalReview = useCallback(async (stepAttachmentId: number) => {
    if (!instrId || !stepAttachmentId) return;
    try {
      await request.post(`/api/v1/instruction/instructions/${instrId}/final-review/attachments/from-step-attachments/`, {
        step_attachment_ids: [stepAttachmentId],
      });
      message.success('已加入终审附件（现场附件）');
      await loadFinalReviewAttachments();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加入失败');
    }
  }, [instrId, loadFinalReviewAttachments]);

  const addStepAttachmentBatchToFinalReview = useCallback(async (stepAttachmentIds: number[]) => {
    if (!instrId) return;
    const ids = Array.from(new Set((stepAttachmentIds || []).map((x) => Math.floor(Number(x || 0) || 0)).filter((n) => n > 0)));
    if (!ids.length) { message.warning('未选择可加入的附件'); return; }
    try {
      await request.post(`/api/v1/instruction/instructions/${instrId}/final-review/attachments/from-step-attachments/`, {
        step_attachment_ids: ids,
      });
      message.success(`已批量加入终审附件（${ids.length}个）`);
      await loadFinalReviewAttachments();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '批量加入失败');
    }
  }, [instrId, loadFinalReviewAttachments]);

  const addIocJsonToFinalReview = useCallback(async () => {
    if (!instrId) return;
    try {
      await request.post(`/api/v1/instruction/instructions/${instrId}/final-review/attachments/from-ioc-json/`, {});
      message.success('已加入终审附件（IOC装箱JSON）');
      await loadFinalReviewAttachments();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || '加入失败');
    }
  }, [instrId, loadFinalReviewAttachments]);

  const buildSortCountFinalResultXlsx = useCallback(async () => {
    if (!instrId) throw new Error('缺少指令ID');
    let ExcelJS: any = null;
    try {
      ExcelJS = await import('exceljs');
    } catch {}
    if (!ExcelJS) throw new Error('缺少 Excel 生成库（exceljs）');
    const wb = new ExcelJS.Workbook();
    const now = new Date();
    const pad2 = (n: number) => String(n).padStart(2, '0');
    const ymd = `${now.getFullYear()}${pad2(now.getMonth() + 1)}${pad2(now.getDate())}`;
    const code = String((instr as any)?.system_code || instrId);
    const fileName = `${code}-分拣最终结果-${ymd}.xlsx`;
    const report = sortCountFinalReportData || ({} as AnyObj);
    const wsResult = wb.addWorksheet('已完成分拣结果汇总');
    wsResult.columns = [
      { header: '序号', key: 'idx', width: 8 },
      { header: '预设分组编号', key: 'group_code', width: 26 },
      { header: '分组名称', key: 'group_name', width: 28 },
      { header: '分拣标识', key: 'sorting_mark', width: 24 },
      { header: '已分拣数量', key: 'total_qty', width: 12 },
    ];
    const presetGroups = Array.isArray(report?.presetGroups) ? report.presetGroups : [];
    const presetGroupColumns = Array.isArray(report?.presetGroupColumns) ? report.presetGroupColumns : [];
    const presetGroupNoDataIndex = String(
      presetGroupColumns.find((c: AnyObj) => String(c?.title || '').includes('分组编号'))?.dataIndex
      || presetGroupColumns.find((c: AnyObj) => String(c?.title || '').includes('分组编码'))?.dataIndex
      || ''
    ).trim();
    const presetGroupNameDataIndex = String(
      presetGroupColumns.find((c: AnyObj) => String(c?.title || '').includes('分组名称'))?.dataIndex
      || ''
    ).trim();
    const presetGroupMarkDataIndex = String(
      presetGroupColumns.find((c: AnyObj) => String(c?.title || '').includes('分拣标识'))?.dataIndex
      || ''
    ).trim();
    const byMarkRows = Array.isArray(report?.byMarkRows) ? report.byMarkRows : [];
    const byMarkMap = new Map<string, AnyObj>();
    byMarkRows.forEach((r: AnyObj) => {
      const mark = String(r?.sorting_mark || r?.mark || '').trim();
      if (!mark) return;
      byMarkMap.set(mark.toUpperCase(), r);
    });
    const consumedMarks = new Set<string>();
    let rowIdx = 1;
    let totalSortedQty = 0;
    presetGroups.forEach((r: AnyObj) => {
      const sortingMark = String(
        r?.sorting_mark
        || r?.mark
        || (presetGroupMarkDataIndex ? r?.[presetGroupMarkDataIndex] : '')
        || ''
      ).trim() || '-';
      const match = byMarkMap.get(sortingMark.trim().toUpperCase()) || {};
      if (sortingMark !== '-') consumedMarks.add(sortingMark.trim().toUpperCase());
      const qty = Number(match?.total_qty ?? match?.qty ?? r?.total_qty ?? r?.qty ?? 0);
      totalSortedQty += Math.max(0, Number.isFinite(qty) ? qty : 0);
      const added = wsResult.addRow({
        idx: rowIdx++,
        group_code: String(
          r?.group_code
          || r?.preset_group_no
          || r?.__preset_group_display_no
          || (presetGroupNoDataIndex ? r?.[presetGroupNoDataIndex] : '')
          || '-'
        ),
        group_name: String(
          r?.group_name
          || r?.name
          || (presetGroupNameDataIndex ? r?.[presetGroupNameDataIndex] : '')
          || '-'
        ),
        sorting_mark: sortingMark,
        total_qty: qty,
      });
      if (Math.max(0, Math.floor(Number(qty || 0))) === 0) {
        const qtyCell = added.getCell('total_qty');
        qtyCell.font = { ...(qtyCell.font || {}), color: { argb: 'FFCF1322' }, bold: true } as any;
      }
    });
    byMarkRows.forEach((r: AnyObj) => {
      const sortingMark = String(r?.sorting_mark || r?.mark || '-');
      const markKey = sortingMark.trim().toUpperCase();
      if (markKey && consumedMarks.has(markKey)) return;
      const qty = Number(r?.total_qty ?? r?.qty ?? 0);
      totalSortedQty += Math.max(0, Number.isFinite(qty) ? qty : 0);
      const added = wsResult.addRow({
        idx: rowIdx++,
        group_code: String(r?.group_code || '-'),
        group_name: String(r?.group_name || '-'),
        sorting_mark: String(r?.sorting_mark || r?.mark || '-'),
        total_qty: qty,
      });
      if (Math.max(0, Math.floor(Number(qty || 0))) === 0) {
        const qtyCell = added.getCell('total_qty');
        qtyCell.font = { ...(qtyCell.font || {}), color: { argb: 'FFCF1322' }, bold: true } as any;
      }
    });
    const totalRow = wsResult.addRow({
      idx: '',
      group_code: '合计',
      group_name: '-',
      sorting_mark: '-',
      total_qty: Math.floor(Math.max(0, totalSortedQty)),
    });
    totalRow.eachCell((cell: any) => {
      cell.style = {
        ...(cell.style || {}),
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2E8' } },
        font: { color: { argb: 'FFD4380D' }, bold: true },
        alignment: { vertical: 'middle', horizontal: 'center' },
      };
    });
    wsResult.eachRow((row: any) => {
      row.eachCell((cell: any) => {
        cell.alignment = { vertical: 'middle', horizontal: 'center' };
      });
    });
    const wsException = wb.addWorksheet('无法操作汇总');
    const exceptionRows = Array.isArray(report?.exceptionRows) ? report.exceptionRows : [];
    const exceptionTotalQty = exceptionRows.reduce((sum: number, r: AnyObj) => sum + Math.max(0, Number(r?.qty ?? 0) || 0), 0);
    wsException.getColumn(1).width = 48;
    wsException.getCell('A1').value = `无法操作汇总数量: ${Math.floor(Math.max(0, exceptionTotalQty))}`;
    wsException.getCell('A1').alignment = { vertical: 'middle', horizontal: 'center' };
    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    return { blob, fileName };
  }, [instrId, instr, stageSubs]);

  const addSortCountFinalResultExcelToFinalReview = useCallback(async () => {
    if (!instrId) return;
    try {
      setSortCountFinalAddingXlsx(true);
      const { blob, fileName } = await buildSortCountFinalResultXlsx();
      const file = new File([blob], fileName, { type: blob.type });
      const fd = new FormData();
      fd.append('instruction', String(instrId));
      fd.append('label', 'final_review');
      fd.append('scope', 'order');
      fd.append('status', 'active');
      fd.append('file_path', fileName);
      fd.append('file', file);
      fd.append('file_name', fileName);
      await request.post('/api/v1/instruction/attachments/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        transformRequest: [(d: any) => d],
      } as any);
      message.success('已加入终审附件（分拣清点最终操作结果Excel）');
      await loadFinalReviewAttachments();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || JSON.stringify(e?.response?.data || {}) || e?.message || '加入终审附件失败');
    } finally {
      setSortCountFinalAddingXlsx(false);
    }
  }, [instrId, buildSortCountFinalResultXlsx, loadFinalReviewAttachments]);

  const downloadSortCountFinalResultExcel = useCallback(async () => {
    if (!instrId) return;
    try {
      setSortCountFinalExporting(true);
      const { blob, fileName } = await buildSortCountFinalResultXlsx();
      await downloadBlobAsFile(blob, fileName);
    } catch (e: any) {
      message.error(e?.response?.data?.detail || e?.message || '导出失败');
    } finally {
      setSortCountFinalExporting(false);
    }
  }, [instrId, buildSortCountFinalResultXlsx, downloadBlobAsFile]);

  const sortCountInventoryWriteback = useMemo(() => {
    const meta = (instr?.meta && typeof instr.meta === 'object') ? (instr.meta as AnyObj) : {};
    const wb = (meta?.sort_count_inventory_writeback && typeof meta.sort_count_inventory_writeback === 'object')
      ? (meta.sort_count_inventory_writeback as AnyObj)
      : null;
    return wb;
  }, [instr]);


  const effectiveInventorySettlement = useMemo<AnyObj | null>(() => {
    if (sortCountInvPreviewShown && inventorySettlementPreview && typeof inventorySettlementPreview === 'object') return inventorySettlementPreview;
    if (inventorySettlementApplied && typeof inventorySettlementApplied === 'object') return inventorySettlementApplied;
    if (inventorySettlementPreview && typeof inventorySettlementPreview === 'object') return inventorySettlementPreview;
    try {
      const meta = ((instr as any)?.meta && typeof (instr as any).meta === 'object') ? ((instr as any).meta as AnyObj) : {};
      const persisted = (meta?.final_review_inventory_settlement && typeof meta.final_review_inventory_settlement === 'object')
        ? (meta.final_review_inventory_settlement as AnyObj)
        : null;
      if (persisted) return persisted;
    } catch {}
    return null;
  }, [sortCountInvPreviewShown, inventorySettlementApplied, inventorySettlementPreview, instr]);
  const effectiveInventorySettlementForStatus = useMemo<AnyObj | null>(() => {
    const isAppliedSuccess = (v: any): boolean => {
      const s = String((v as AnyObj)?.summary?.status || '').trim().toLowerCase();
      return s === 'applied' || s === 'success';
    };
    if (inventorySettlementApplied && typeof inventorySettlementApplied === 'object' && isAppliedSuccess(inventorySettlementApplied)) {
      return inventorySettlementApplied as AnyObj;
    }
    try {
      const meta = ((instr as any)?.meta && typeof (instr as any).meta === 'object') ? ((instr as any).meta as AnyObj) : {};
      const persisted = (meta?.final_review_inventory_settlement && typeof meta.final_review_inventory_settlement === 'object')
        ? (meta.final_review_inventory_settlement as AnyObj)
        : null;
      if (persisted && isAppliedSuccess(persisted)) return persisted;
    } catch {}
    return effectiveInventorySettlement;
  }, [inventorySettlementApplied, instr, effectiveInventorySettlement]);
  const sortCountInventoryWritebackStatusText = useMemo(() => {
    const s = String((effectiveInventorySettlementForStatus as AnyObj)?.summary?.status || '').trim().toLowerCase();
    if (s === 'applied' || s === 'success') return '成功';
    if (s === 'failed') return '失败';
    return '未执行';
  }, [effectiveInventorySettlementForStatus]);
  const sortCountInventoryWritebackStatusRaw = useMemo(
    () => String((effectiveInventorySettlementForStatus as AnyObj)?.summary?.status || '').trim().toLowerCase(),
    [effectiveInventorySettlementForStatus],
  );
  const settlementTemplateFamilyRaw = useMemo(
    () => String((effectiveInventorySettlement as AnyObj)?.template_family || '').trim().toLowerCase(),
    [effectiveInventorySettlement],
  );
  const settlementDebugBuildTag = useMemo(
    () => String(
      (effectiveInventorySettlement as AnyObj)?.debug_build_tag
      || ((effectiveInventorySettlement as AnyObj)?.summary?.debug_build_tag)
      || '',
    ).trim(),
    [effectiveInventorySettlement],
  );
  const isLegacyUnknownSettlement = useMemo(
    () => settlementTemplateFamilyRaw === 'unknown' || !settlementDebugBuildTag,
    [settlementTemplateFamilyRaw, settlementDebugBuildTag],
  );
  const sortCountInvAppliedSuccess = (sortCountInventoryWritebackStatusRaw === 'success' || sortCountInventoryWritebackStatusRaw === 'applied')
    && !isLegacyUnknownSettlement;
  const sortCountInvPreviewDisabledReason = sortCountInvAppliedSuccess
    ? '已执行库存落库，预览已关闭（避免重复预检歧义）'
    : '';
  const sortCountInvApplyDetail = useMemo(() => {
    return (sortCountInventoryWriteback?.apply_detail && typeof sortCountInventoryWriteback.apply_detail === 'object')
      ? (sortCountInventoryWriteback.apply_detail as AnyObj)
      : {};
  }, [sortCountInventoryWriteback]);
  const inventorySettlementActionMap = useMemo(() => {
    const cur = (effectiveInventorySettlement && typeof effectiveInventorySettlement === 'object')
      ? effectiveInventorySettlement
      : {};
    const actions = Array.isArray((cur as AnyObj)?.actions) ? ((cur as AnyObj).actions as AnyObj[]) : [];
    const m: Record<string, AnyObj> = {};
    actions.forEach((a: any) => {
      const k = String(a?.action_key || '').trim();
      if (!k) return;
      m[k] = a;
    });
    return m;
  }, [effectiveInventorySettlement]);
  const sortCountInvGroupAdjustRows = useMemo(() => (
    Array.isArray((inventorySettlementActionMap?.group_adjustment as AnyObj)?.rows)
      ? (((inventorySettlementActionMap?.group_adjustment as AnyObj)?.rows) as AnyObj[])
      : (Array.isArray((sortCountInvApplyDetail as AnyObj)?.group_adjustments?.rows)
        ? ((sortCountInvApplyDetail as AnyObj).group_adjustments.rows as AnyObj[])
        : [])
  ), [sortCountInvApplyDetail, inventorySettlementActionMap]);
  const sortCountInvGeneratedInboundRows = useMemo(() => (
    Array.isArray((inventorySettlementActionMap?.generated_unit_inbound as AnyObj)?.rows)
      ? (((inventorySettlementActionMap?.generated_unit_inbound as AnyObj)?.rows) as AnyObj[])
      : (Array.isArray((sortCountInvApplyDetail as AnyObj)?.generated_unit_inbound?.rows)
        ? ((sortCountInvApplyDetail as AnyObj).generated_unit_inbound.rows as AnyObj[])
        : [])
  ), [sortCountInvApplyDetail, inventorySettlementActionMap]);
  const sortCountInvNeed1AdjustRows = useMemo(() => (
    Array.isArray((inventorySettlementActionMap?.need1_qty_adjustment as AnyObj)?.rows)
      ? (((inventorySettlementActionMap?.need1_qty_adjustment as AnyObj)?.rows) as AnyObj[])
      : (Array.isArray((sortCountInvApplyDetail as AnyObj)?.need1_qty_adjustment?.rows)
        ? ((sortCountInvApplyDetail as AnyObj).need1_qty_adjustment.rows as AnyObj[])
        : [])
  ), [sortCountInvApplyDetail, inventorySettlementActionMap]);
  const sortCountInvNeed1GroupCleanupRows = useMemo(() => (
    Array.isArray((inventorySettlementActionMap?.need1_group_cleanup as AnyObj)?.rows)
      ? (((inventorySettlementActionMap?.need1_group_cleanup as AnyObj)?.rows) as AnyObj[])
      : (Array.isArray((sortCountInvApplyDetail as AnyObj)?.need1_group_cleanup?.rows)
        ? ((sortCountInvApplyDetail as AnyObj).need1_group_cleanup.rows as AnyObj[])
        : [])
  ), [sortCountInvApplyDetail, inventorySettlementActionMap]);
  const sortCountInvNeed1ReservationReleaseRows = useMemo(() => (
    Array.isArray((inventorySettlementActionMap?.need1_reservation_release as AnyObj)?.rows)
      ? (((inventorySettlementActionMap?.need1_reservation_release as AnyObj)?.rows) as AnyObj[])
      : (Array.isArray((sortCountInvApplyDetail as AnyObj)?.need1_reservation_release?.rows)
        ? ((sortCountInvApplyDetail as AnyObj).need1_reservation_release.rows as AnyObj[])
        : [])
  ), [sortCountInvApplyDetail, inventorySettlementActionMap]);
  const sortCountInvUnoperableToAiuRows = useMemo(() => (
    Array.isArray((inventorySettlementActionMap?.unoperable_to_aiu as AnyObj)?.rows)
      ? (((inventorySettlementActionMap?.unoperable_to_aiu as AnyObj)?.rows) as AnyObj[])
      : (Array.isArray((sortCountInvApplyDetail as AnyObj)?.unoperable_to_aiu?.rows)
        ? ((sortCountInvApplyDetail as AnyObj).unoperable_to_aiu.rows as AnyObj[])
        : [])
  ), [sortCountInvApplyDetail, inventorySettlementActionMap]);
  const sortCountInvInsufficientRows = useMemo(() => {
    if (Array.isArray((inventorySettlementActionMap?.insufficient as AnyObj)?.rows)) {
      return (inventorySettlementActionMap?.insufficient as AnyObj).rows as AnyObj[];
    }
    const p = (sortCountInvPreview && typeof sortCountInvPreview === 'object') ? (sortCountInvPreview as AnyObj) : {};
    return Array.isArray(p?.insufficient_rows) ? (p.insufficient_rows as AnyObj[]) : [];
  }, [sortCountInvPreview, inventorySettlementActionMap]);
  const sortCountInvWeightedAgeRows = useMemo(() => {
    if (Array.isArray((inventorySettlementActionMap?.fifo_weighted_age as AnyObj)?.rows)) {
      return (inventorySettlementActionMap?.fifo_weighted_age as AnyObj).rows as AnyObj[];
    }
    const p = (sortCountInvPreview && typeof sortCountInvPreview === 'object') ? (sortCountInvPreview as AnyObj) : {};
    return Array.isArray(p?.weighted_source_age_components) ? (p.weighted_source_age_components as AnyObj[]) : [];
  }, [sortCountInvPreview, inventorySettlementActionMap]);
  const sortCountInvSourceDeductionRows = useMemo(() => {
    if (Array.isArray((inventorySettlementActionMap?.source_deduction as AnyObj)?.rows)) {
      return (inventorySettlementActionMap?.source_deduction as AnyObj).rows as AnyObj[];
    }
    const p = (sortCountInvPreview && typeof sortCountInvPreview === 'object') ? (sortCountInvPreview as AnyObj) : {};
    return Array.isArray(p?.source_deduction_rows) ? (p.source_deduction_rows as AnyObj[]) : [];
  }, [sortCountInvPreview, inventorySettlementActionMap]);
  const sortCountInvTargetCodeByIuId = useMemo(() => {
    const m: Record<string, string> = {};
    (Array.isArray(sortCountInvSourceDeductionRows) ? sortCountInvSourceDeductionRows : []).forEach((r: any) => {
      const id = String(r?.inventory_unit_id ?? '').trim();
      const code = String(r?.target_code || '').trim();
      if (!id || !code) return;
      if (!m[id]) m[id] = code;
    });
    return m;
  }, [sortCountInvSourceDeductionRows]);
  const sortCountInvWeightedAgeFormulaText = useMemo(() => {
    const p = (sortCountInvPreview && typeof sortCountInvPreview === 'object') ? (sortCountInvPreview as AnyObj) : {};
    const summary = (p?.summary && typeof p.summary === 'object') ? (p.summary as AnyObj) : {};
    const weighted = summary?.weighted_source_age_days == null ? null : Number(summary?.weighted_source_age_days || 0);
    const rows = Array.isArray(sortCountInvWeightedAgeRows) ? sortCountInvWeightedAgeRows : [];
    const byAge = new Map<number, number>();
    rows.forEach((r: any) => {
      const age = Number(r?.layer_age_days);
      const qty = Number(r?.taken_qty);
      if (!Number.isFinite(age) || !Number.isFinite(qty) || qty <= 0) return;
      const ageInt = Math.max(0, Math.round(age));
      byAge.set(ageInt, (byAge.get(ageInt) || 0) + Math.max(0, Math.floor(qty)));
    });
    const entries = Array.from(byAge.entries()).sort((a, b) => b[0] - a[0]);
    const totalQty = entries.reduce((acc, [, q]) => acc + q, 0);
    if (entries.length <= 0 || totalQty <= 0 || weighted == null) return '暂无可用公式明细';
    const terms = entries.map(([age, qty]) => `${qty}件 × ${age}天`);
    return `(${terms.join(' + ')}) ÷ ${totalQty}件 = ${Math.round(weighted)}天`;
  }, [sortCountInvPreview, sortCountInvWeightedAgeRows]);
  const renderSortCountInvAuditLabel = useCallback((key: string, title: string, count: number, color: string) => (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        fontWeight: 700,
        color,
      }}
    >
      <span>{`${title}(${count})`}</span>
      {sortCountInvAuditActiveKeys.includes(key) ? <CaretDownOutlined /> : <CaretRightOutlined />}
    </div>
  ), [sortCountInvAuditActiveKeys]);

  const previewSortCountInventoryWriteback = useCallback(async (opts?: { silent?: boolean }) => {
    if (!instrId) return;
    const silent = !!opts?.silent;
    try {
      setSortCountInvPreviewShown(true);
      setSortCountInvPreviewLoading(true);
      setSortCountInvPreviewError('');
      const r = await request.post(`/api/v1/instruction/instructions/${instrId}/final-review/sort-count-inventory/preview/`, {});
      const data = (r?.data && typeof r.data === 'object') ? (r.data as AnyObj) : {};
      const settlement = (data?.inventory_settlement && typeof data.inventory_settlement === 'object')
        ? (data.inventory_settlement as AnyObj)
        : null;
      const plan = (data?.plan && typeof data.plan === 'object') ? (data.plan as AnyObj) : null;
      setSortCountInvPreview(plan);
      setInventorySettlementPreview(settlement);
      if (!plan) {
        // 非分拣模板在统一返回下可能不再提供 plan，允许通过 inventory_settlement 渲染。
        if (!settlement) {
          setSortCountInvPreviewError('未获取到库存落库预览结果');
          if (!silent) message.warning('未获取到库存落库预览结果');
          return;
        }
      }
      if (!silent) message.success('库存落库预览完成');
    } catch (e: any) {
      const errText = e?.response?.data?.detail || e?.message || '库存落库预览失败';
      setSortCountInvPreviewError(String(errText || '库存落库预览失败'));
      const settlement = (e?.response?.data?.inventory_settlement && typeof e.response.data.inventory_settlement === 'object')
        ? (e.response.data.inventory_settlement as AnyObj)
        : null;
      if (settlement) setInventorySettlementPreview(settlement);
      if (!silent) message.error(errText);
    } finally {
      setSortCountInvPreviewLoading(false);
    }
  }, [instrId]);

  const applySortCountInventoryWriteback = useCallback(async (opts?: { silent?: boolean; skipReload?: boolean }) => {
    if (!instrId) return;
    const silent = !!opts?.silent;
    const skipReload = !!opts?.skipReload;
    try {
      setSortCountInvApplying(true);
      const idempotencyKey = `sort-count-inv-${instrId}-${Date.now()}`;
      const r = await request.post(`/api/v1/instruction/instructions/${instrId}/final-review/sort-count-inventory/apply/`, {
        idempotency_key: idempotencyKey,
      });
      const data = (r?.data && typeof r.data === 'object') ? (r.data as AnyObj) : {};
      const settlement = (data?.inventory_settlement && typeof data.inventory_settlement === 'object')
        ? (data.inventory_settlement as AnyObj)
        : null;
      if (settlement) setInventorySettlementApplied(settlement);
      setSortCountInvPreviewShown(true);
      setSortCountInvPreviewError('');
      if (!silent) message.success('库存落库执行成功');
      if (!skipReload) await load();
      setSortCountInvPreview(null);
    } catch (e: any) {
      const d = e?.response?.data;
      const settlement = (d?.inventory_settlement && typeof d.inventory_settlement === 'object')
        ? (d.inventory_settlement as AnyObj)
        : null;
      if (settlement) setInventorySettlementApplied(settlement);
      setSortCountInvPreviewShown(true);
      setSortCountInvPreviewError(String(d?.detail || d?.error || e?.message || '库存落库执行失败'));
      if (Array.isArray(d?.insufficient_rows) && d.insufficient_rows.length > 0) {
        if (!silent) message.error(`库存落库失败：源库存锁定不足（${d.insufficient_rows.length}项）`);
      } else {
        if (!silent) message.error(d?.detail || d?.error || e?.message || '库存落库执行失败');
      }
      if (!skipReload) await load();
    } finally {
      setSortCountInvApplying(false);
    }
  }, [instrId, load]);

  useEffect(() => {
    if (!instrId || !instr) return;
    const curStatus = String((instr as any)?.status || '').trim().toLowerCase();
    if (curStatus === 'rejected') return;
    if (curStatus !== 'operation_done' && curStatus !== 'completed') return;
    const attemptKey = `${instrId}:${curStatus}:${String((instr as any)?.final_review_version || 0)}`;
    if (inventoryAutoAttemptedRef.current === attemptKey) return;
    inventoryAutoAttemptedRef.current = attemptKey;

    const meta = (((instr as any)?.meta && typeof (instr as any).meta === 'object') ? ((instr as any).meta as AnyObj) : {}) as AnyObj;
    const persisted = (meta?.final_review_inventory_settlement && typeof meta.final_review_inventory_settlement === 'object')
      ? (meta.final_review_inventory_settlement as AnyObj)
      : null;
    const persistedStatus = String((persisted?.summary && typeof persisted.summary === 'object' ? persisted.summary.status : '') || '').trim().toLowerCase();
    if (persistedStatus === 'applied' || persistedStatus === 'success') {
      setSortCountInvPreviewShown(true);
      return;
    }

    (async () => {
      try {
        setInventoryAutoApplying(true);
        await applySortCountInventoryWriteback({ silent: true, skipReload: true });
      } finally {
        setInventoryAutoApplying(false);
      }
    })();
  }, [instrId, instr, applySortCountInventoryWriteback]);

  const submitFinalReview = useCallback(async () => {
    if (!instrId) return;
    const curStatus = String((instr as any)?.status || '').trim();
    if (curStatus.toLowerCase() === 'rejected') {
      message.warning('该指令已驳回，禁止进入终审');
      return;
    }
    // 分拣清点：首次终审提交前，必须先执行库存落库成功
    const hasSortCountStageForSubmit = (() => {
      try {
        const list = Array.isArray(stageSubs) ? stageSubs : [];
        return list.some((x: any) => {
          const k = String(x?.stage_key || '').trim().toUpperCase();
          return k === 'SORT_COUNT' || k === 'SORT_AND_COUNT';
        });
      } catch {
        return false;
      }
    })();
    if (hasSortCountStageForSubmit && curStatus !== 'completed') {
      const currentSettlement = (() => {
        if (inventorySettlementApplied && typeof inventorySettlementApplied === 'object') return inventorySettlementApplied as AnyObj;
        if (inventorySettlementPreview && typeof inventorySettlementPreview === 'object') return inventorySettlementPreview as AnyObj;
        try {
          const meta = ((instr as any)?.meta && typeof (instr as any).meta === 'object') ? ((instr as any).meta as AnyObj) : {};
          if (meta?.final_review_inventory_settlement && typeof meta.final_review_inventory_settlement === 'object') {
            return meta.final_review_inventory_settlement as AnyObj;
          }
        } catch {}
        return null;
      })();
      const settleStatus = String((currentSettlement as AnyObj)?.summary?.status || '').trim().toLowerCase();
      const settleApplied = settleStatus === 'applied' || settleStatus === 'success';
      if (!settleApplied) {
        message.error('分拣清点指令终审前，必须先执行库存落库并成功');
        return;
      }
    }
    const isEdit = curStatus === 'completed';
    Modal.confirm({
      title: isEdit ? '保存终审结果' : '终审提交执行结果',
      okText: '提交',
      cancelText: '取消',
      content: (
        <div style={{ lineHeight: 1.75 }}>
          <div>将完成以下动作：</div>
          <div style={{ marginTop: 6 }}>
            {isEdit ? (
              <>
                - 保存终审备注（最新口径）<br />
                - 创建/修改终审附件<br />
                - 记录终审编辑日志<br />
              </>
            ) : (
              <>
                - 生成收费记录（如失败可后台重算，不阻断提交）<br />
                - 指令状态置为“已完成”<br />
                - 保存终审备注（最新口径）<br />
                - 创建/修改终审附件<br />
              </>
            )}
          </div>
        </div>
      ),
      onOk: async () => {
        try {
          await request.post(`/api/v1/instruction/instructions/${instrId}/result/submit/`, {
            final_review_notes: String(notes || '').trim() || undefined,
            // 策略B：终审计费事实（优先使用前端当前值；后端仍可回退 draft/auto_suggested）
            pricing_facts: pricingFacts && typeof pricingFacts === 'object' ? pricingFacts : undefined,
            pricing_evidence: pricingEvidence && typeof pricingEvidence === 'object' ? pricingEvidence : undefined,
          });
        } catch (e: any) {
          // 后端可能返回 blocking_issues（口径不一致）等结构化错误，这里尽量透传
          const d = e?.response?.data;
          const detail = d?.detail || d?.error || '提交失败';
          const blocking = Array.isArray(d?.blocking_issues) ? d.blocking_issues : null;
          if (blocking && blocking.length) {
            message.error(`${detail}（blocking_issues=${blocking.length}）`);
          } else {
            message.error(detail);
          }
          throw e;
        }
        message.success(isEdit ? '已保存终审结果' : '已终审提交');
        await load();
        // 提交成功后返回指令列表页（终审/编辑均返回）
        try {
          const w: any = window as any;
          const path = String(w?.location?.pathname || '');
          const base = path.startsWith('/dashboard/staff')
            ? '/dashboard/staff'
            : (path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer');
          // 注意：管理员路由里列表页是 /instructions/list（不是 /instructions）
          navigate(`${base}/instructions/list${location.search || ''}`);
        } catch {
          try { navigate(`/dashboard/admin/instructions/list${location.search || ''}`); } catch {}
        }
      }
    });
  }, [instrId, instr, stageSubs, inventorySettlementApplied, inventorySettlementPreview, notes, pricingFacts, pricingEvidence, load, navigate, location.search]);

  // 显式重算：仅在已完成状态下触发（不推进状态），会覆盖该指令的既有收费记录（策略B）
  const submitFinalReviewWithRecalc = useCallback(async () => {
    if (!instrId) return;
    const curStatus = String((instr as any)?.status || '').trim();
    if (curStatus !== 'completed') {
      // 首次终审提交仍走原逻辑（会自动计费）
      await submitFinalReview();
      return;
    }
    Modal.confirm({
      title: '保存并重算收费记录',
      okText: '确认重算',
      cancelText: '取消',
      content: (
        <div style={{ lineHeight: 1.75 }}>
          <div>将完成以下动作：</div>
          <div style={{ marginTop: 6 }}>
            - 保存终审备注（最新口径）<br />
            - 创建/修改终审附件<br />
            - 使用当前终审计费事实重算，并覆盖该指令已生成的收费记录（策略B）<br />
            - 记录终审编辑日志<br />
          </div>
          <div style={{ marginTop: 10, color: '#cf1322' }}>
            注意：这是“账务影响”操作。若仅修改备注/附件，请使用“保存终审结果”（不重算）。
          </div>
        </div>
      ),
      onOk: async () => {
        try {
          await request.post(`/api/v1/instruction/instructions/${instrId}/result/submit/`, {
            final_review_notes: String(notes || '').trim() || undefined,
            recalc_pricing: true,
            // 策略B：终审计费事实（以当前编辑值为准）
            pricing_facts: pricingFacts && typeof pricingFacts === 'object' ? pricingFacts : undefined,
            pricing_evidence: pricingEvidence && typeof pricingEvidence === 'object' ? pricingEvidence : undefined,
          });
        } catch (e: any) {
          const d = e?.response?.data;
          const detail = d?.detail || d?.error || '提交失败';
          const blocking = Array.isArray(d?.blocking_issues) ? d.blocking_issues : null;
          if (blocking && blocking.length) {
            message.error(`${detail}（blocking_issues=${blocking.length}）`);
          } else {
            message.error(detail);
          }
          throw e;
        }
        message.success('已保存并重算收费记录');
        await load();
        try {
          const w: any = window as any;
          const path = String(w?.location?.pathname || '');
          const base = path.startsWith('/dashboard/staff')
            ? '/dashboard/staff'
            : (path.startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/customer');
          navigate(`${base}/instructions/list${location.search || ''}`);
        } catch {
          try { navigate(`/dashboard/admin/instructions/list${location.search || ''}`); } catch {}
        }
      },
    });
  }, [instrId, instr, notes, pricingFacts, pricingEvidence, load, navigate, location.search, submitFinalReview]);

  const pricingRows = useMemo(() => {
    const wos = Array.isArray(pricingPreview?.work_orders) ? (pricingPreview.work_orders as AnyObj[]) : [];
    const suggestedByWo = (pricingPreview?.suggested_facts_json && typeof pricingPreview.suggested_facts_json === 'object')
      ? ((pricingPreview.suggested_facts_json as AnyObj)?.by_workorder || {})
      : {};
    const byWo = (pricingFacts && typeof pricingFacts === 'object') ? (pricingFacts?.by_workorder || {}) : {};
    const evByWo = (pricingEvidence && typeof pricingEvidence === 'object') ? ((pricingEvidence as AnyObj)?.by_workorder || {}) : {};
    return (wos || []).map((r: AnyObj) => {
      const woId = String(r?.work_order_id ?? '');
      const mk = String(r?.expected_metric_key || 'quantity');
      const suggestedRaw = (suggestedByWo?.[woId] || {})?.[mk];
      const woFacts = (byWo?.[woId] && typeof byWo?.[woId] === 'object') ? (byWo?.[woId] as AnyObj) : {};
      const cur = woFacts?.[mk];
      const hasList = Array.isArray(cur) || Array.isArray(suggestedRaw);
      const suggested = Number(suggestedRaw ?? NaN);
      const curNum = Number(cur ?? NaN);
      const ev = (evByWo?.[woId] && typeof evByWo?.[woId] === 'object') ? (evByWo?.[woId] as AnyObj) : {};
      const from = String(ev?.from || ev?.missing_reason || '').trim();
      const calc = (ev?.calc && typeof ev.calc === 'object') ? (ev.calc as AnyObj) : {};
      const dynamicPricing = String(calc?.dynamic_pricing || '').trim();
      const unitPriceRaw = Number(calc?.unit_price ?? NaN);
      const amountRaw = Number(calc?.amount ?? NaN);
      const woUnitPriceRaw = Number(r?.unit_price ?? NaN);
      const unitPriceBase = Number.isFinite(unitPriceRaw) ? unitPriceRaw : (Number.isFinite(woUnitPriceRaw) ? woUnitPriceRaw : null);
      const overrideRaw = Number(woFacts?.unit_price_override ?? NaN);
      const opSys = String(r?.operation_type_system_code || '').trim().toUpperCase();
      const isSys9 = opSys === 'OPERATIONTYPE-SYS-9' || opSys === 'OPERATIONTYPE_SYS_9';
      const recPkgCode = String(ev?.recommended_packaging?.system_code || '').trim();
      return {
        ...r,
        __woId: woId,
        __metricKey: mk,
        __suggested: Number.isFinite(suggested) ? suggested : null,
        __value: Number.isFinite(curNum) ? curNum : (Number.isFinite(suggested) ? suggested : null),
        __valueList: (Array.isArray(cur) ? cur : (Array.isArray(suggestedRaw) ? suggestedRaw : null)),
        __valueListCount: (Array.isArray(cur) ? cur.length : (Array.isArray(suggestedRaw) ? suggestedRaw.length : 0)),
        __hasList: hasList,
        __from: from || '-',
        __dynamicPricing: dynamicPricing,
        __calcUnitPrice: Number.isFinite(unitPriceRaw) ? unitPriceRaw : null,
        __calcAmount: Number.isFinite(amountRaw) ? amountRaw : null,
        __isSys9: isSys9,
        __unitPriceBase: unitPriceBase,
        __unitPriceOverride: Number.isFinite(overrideRaw) ? overrideRaw : null,
        __recommendedPackagingCode: recPkgCode || '',
      };
    });
  }, [pricingFacts, pricingPreview, pricingEvidence]);

  const pricingEvidenceSummaryFlags = useMemo(() => {
    const usedMetricKeys = new Set<string>();
    const byWo = (pricingEvidence && typeof pricingEvidence === 'object')
      ? (((pricingEvidence as AnyObj)?.by_workorder || {}) as AnyObj)
      : {};
    let usesRelabel = false;
    let usesIocCount = false;
    (pricingRows || []).forEach((r: any) => {
      const mk = String(r?.__metricKey || '').trim();
      if (mk) usedMetricKeys.add(mk);
      const woId = String(r?.__woId || '');
      const ev = (byWo?.[woId] && typeof byWo?.[woId] === 'object') ? (byWo[woId] as AnyObj) : {};
      const fromText = [
        String(ev?.from || ''),
        String(ev?.calc?.from || ''),
        String(ev?.calc?.quantity_from || ''),
      ].join(' ').toLowerCase();
      if (/relabel|new_barcode/.test(fromText)) usesRelabel = true;
      if (/ioc|package_count|container/.test(fromText)) usesIocCount = true;
    });
    return {
      usedMetricKeys,
      usesRelabel,
      usesIocCount,
    };
  }, [pricingRows, pricingEvidence]);

  const pricingEvidenceReadable = useMemo(() => {
    const ev = (pricingEvidence && typeof pricingEvidence === 'object') ? (pricingEvidence as AnyObj) : {};
    const sources = Array.isArray(ev?.sources) ? (ev.sources as AnyObj[]) : [];
    const computed = (ev?.computed && typeof ev.computed === 'object') ? (ev.computed as AnyObj) : {};
    const byWo = (ev?.by_workorder && typeof ev.by_workorder === 'object') ? (ev.by_workorder as AnyObj) : {};
    const woNameById = new Map<string, string>();
    (Array.isArray(pricingRows) ? pricingRows : []).forEach((r: any) => {
      const k = String(r?.__woId || r?.work_order_id || '').trim();
      if (!k) return;
      const n = String(r?.work_order_name || r?.work_order_type_code || '').trim();
      if (n) woNameById.set(k, n);
    });
    const sourceCounts = (() => {
      const m = new Map<string, number>();
      sources.forEach((s: any) => {
        const k = String(s?.kind || 'unknown').trim() || 'unknown';
        m.set(k, (m.get(k) || 0) + 1);
      });
      return Array.from(m.entries())
        .map(([kind, count], idx) => ({ __k: `src-${idx}-${kind}`, kind, count }))
        .sort((a, b) => Number(b.count || 0) - Number(a.count || 0));
    })();
    const byWorkOrderRows = Object.entries(byWo).map(([woId, data], idx) => {
      const d = (data && typeof data === 'object') ? (data as AnyObj) : {};
      const calc = (d?.calc && typeof d.calc === 'object') ? (d.calc as AnyObj) : {};
      return {
        __k: `wo-ev-${idx}-${woId}`,
        work_order_id: String(woId || ''),
        work_order_name: woNameById.get(String(woId || '').trim()) || '-',
        from: String(d?.from || '').trim() || '-',
        metric_key: String(d?.metric_key || '').trim() || '-',
        quantity_semantic: String(d?.quantity_semantic || '').trim() || '-',
        missing_reason: String(d?.missing_reason || '').trim() || '-',
        skipped: !!d?.skipped,
        basis_value: d?.basis_value,
        unit_price: calc?.unit_price ?? null,
        amount: calc?.amount ?? null,
        formula: String(calc?.formula || '').trim() || '-',
      };
    }).sort((a, b) => String(a.work_order_id).localeCompare(String(b.work_order_id), undefined, { numeric: true, sensitivity: 'base' }));
    const sourceAuditRows = Array.isArray(computed?.pricing_source_audit_matrix) ? (computed.pricing_source_audit_matrix as AnyObj[]) : [];
    const sourceAuditNowCountRows = (() => {
      const obj = (computed?.pricing_source_audit_source_now_count && typeof computed.pricing_source_audit_source_now_count === 'object')
        ? (computed.pricing_source_audit_source_now_count as AnyObj)
        : {};
      return Object.entries(obj)
        .map(([source_now, count], idx) => ({ __k: `audit-now-${idx}-${source_now}`, source_now, count: Number(count || 0) || 0 }))
        .sort((a, b) => Number(b.count || 0) - Number(a.count || 0));
    })();
    return {
      sources,
      computed,
      sourceCounts,
      byWorkOrderRows,
      sourceAuditRows,
      sourceAuditNowCountRows,
    };
  }, [pricingEvidence, pricingRows]);

  const enrichPricingValueListWithIuSystemCode = useCallback(async (rawList: any[]) => {
    const list = Array.isArray(rawList) ? rawList : [];
    if (list.length === 0) return [];
    const iuIds = Array.from(new Set(
      list
        .map((x: any) => Math.floor(Number(x?.iu_id || 0) || 0))
        .filter((id: number) => Number.isFinite(id) && id > 0),
    ));
    const missing = iuIds.filter((id) => !String(iuSysCodeById[String(id)] || '').trim() && !iuSysLoadingRef.current.has(id));
    const fetchedMap: Record<string, string> = {};
    if (missing.length > 0) {
      await Promise.all(missing.map(async (id) => {
        try {
          iuSysLoadingRef.current.add(id);
          const resp: any = await getInventoryUnitDetail(id);
          const d = (resp && (resp.data || resp)) || {};
          const code = String(d?.system_barcode || d?.systemBarcode || d?.system_code || d?.code || '').trim();
          if (code) fetchedMap[String(id)] = code;
        } catch {
          fetchedMap[String(id)] = '';
        } finally {
          iuSysLoadingRef.current.delete(id);
        }
      }));
      if (Object.keys(fetchedMap).length > 0) {
        setIuSysCodeById((prev) => ({ ...(prev || {}), ...fetchedMap }));
      }
    }
    const mergedMap: Record<string, string> = { ...(iuSysCodeById || {}), ...fetchedMap };
    const withCode = (item: any) => {
      if (!item || typeof item !== 'object' || Array.isArray(item)) return item;
      const iuId = Math.floor(Number(item?.iu_id || 0) || 0);
      if (!(iuId > 0)) return item;
      const code = String(mergedMap[String(iuId)] || '').trim() || '-';
      const out: AnyObj = {};
      let injected = false;
      Object.entries(item as AnyObj).forEach(([k, v]) => {
        out[k] = v;
        if (!injected && k === 'iu_id') {
          out.iu_system_barcode = code;
          injected = true;
        }
      });
      if (!injected) out.iu_system_barcode = code;
      return out;
    };
    return list.map((x: any) => withCode(x));
  }, [iuSysCodeById]);

  const pricingColumns = useMemo(() => {
    return [
      {
        title: '工单',
        dataIndex: 'work_order_name',
        ellipsis: true,
        render: (_: any, r: any) => {
          const txt = String(r?.work_order_name || r?.work_order_type_code || '-');
          return (
            <Button
              type="link"
              size="small"
              style={{ padding: 0, height: 'auto' }}
              onClick={() => {
                Modal.info({
                  title: '计费行详情',
                  width: 860,
                  content: (
                    <div style={{ marginTop: 8 }}>
                      <pre style={{ maxHeight: 520, overflow: 'auto', background: 'rgba(0,0,0,0.02)', padding: 10 }}>
                        {(() => { try { return JSON.stringify(r || {}, null, 2); } catch { return '{}'; } })()}
                      </pre>
                    </div>
                  ),
                });
              }}
            >
              {txt}
            </Button>
          );
        }
      },
      {
        title: '单位/口径',
        dataIndex: '__uk',
        ellipsis: true,
        render: (_: any, r: any) => (
          <span style={{ fontFamily: 'monospace' }}>
            {String(r?.billing_unit || '-')}/{String(r?.expected_metric_key || '-')}
          </span>
        )
      },
      {
        title: (
          <Tooltip title="按照指令和操作记录的建议计费事实">
            <span>建议值</span>
          </Tooltip>
        ),
        dataIndex: '__suggested',
        render: (_: any, r: any) => {
          if (r?.__hasList) {
            return (
              <Button
                type="link"
                size="small"
                onClick={async () => {
                  const enriched = await enrichPricingValueListWithIuSystemCode(r.__valueList || []);
                  Modal.info({
                    title: '建议值明细',
                    width: 720,
                    content: (
                      <pre style={{ maxHeight: 420, overflow: 'auto', background: 'rgba(0,0,0,0.02)', padding: 10 }}>
                        {(() => { try { return JSON.stringify(enriched || [], null, 2); } catch { return '[]'; } })()}
                      </pre>
                    ),
                  });
                }}
              >
                明细({r.__valueListCount || 0})
              </Button>
            );
          }
          const v = r?.__suggested;
          if (v === null || v === undefined) return <Text type="secondary">-</Text>;
          if (String(r?.__dynamicPricing || '') === 'recommended_warehouse_paid_packaging') {
            const up = r?.__calcUnitPrice;
            const am = r?.__calcAmount;
            const pkgCode = String(r?.__recommendedPackagingCode || '').trim();
            return (
              <div style={{ lineHeight: 1.4 }}>
                <div>{String(v)}</div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {`推荐包材${pkgCode ? `(${pkgCode})` : ''} 单价 ${up == null ? '-' : up}，预计金额 ${am == null ? '-' : am}`}
                </div>
              </div>
            );
          }
          return String(v);
        },
      },
      {
        title: (
          <Tooltip title="调整后的计费事实,可修改,以此来生成真实收费记录">
            <span>终审值</span>
          </Tooltip>
        ),
        dataIndex: '__value',
        render: (_: any, r: any) => {
          const woId = String(r?.__woId || '');
          const mk = String(r?.__metricKey || 'quantity');
          const v0 = r?.__value;
          const value = (v0 === null || v0 === undefined) ? null : Number(v0);
          if (r?.__hasList) {
            return (
              <Button
                type="link"
                size="small"
                onClick={async () => {
                  const enriched = await enrichPricingValueListWithIuSystemCode(r.__valueList || []);
                  Modal.info({
                    title: '明细计费口径',
                    width: 720,
                    content: (
                      <pre style={{ maxHeight: 420, overflow: 'auto', background: 'rgba(0,0,0,0.02)', padding: 10 }}>
                        {(() => { try { return JSON.stringify(enriched || [], null, 2); } catch { return '[]'; } })()}
                      </pre>
                    ),
                  });
                }}
              >
                明细({r.__valueListCount || 0})
              </Button>
            );
          }
          return (
            <InputNumber
              style={{ width: '100%', minWidth: 90 }}
              value={Number.isFinite(Number(value)) ? Number(value) : null}
              min={0}
              step={mk === 'quantity' ? 1 : 0.1}
              onChange={(nv) => {
                const num = Number(nv);
                setPricingFacts((prev) => {
                  const p = (prev && typeof prev === 'object') ? prev : { by_workorder: {} };
                  const by = (p?.by_workorder && typeof p.by_workorder === 'object') ? { ...(p.by_workorder as AnyObj) } : {};
                  const row = (by[woId] && typeof by[woId] === 'object') ? { ...(by[woId] as AnyObj) } : {};
                  if (!Number.isFinite(num)) {
                    try { delete row[mk]; } catch {}
                  } else {
                    row[mk] = num;
                  }
                  by[woId] = row;
                  return { ...(p as AnyObj), by_workorder: by };
                });
              }}
            />
          );
        }
      },
      {
        title: (
          <Tooltip title="可根据现场操作的难易程度调整收费标准,此处显示的是收费系统设置的基准价格">
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span>现场单价</span>
              <QuestionCircleOutlined />
            </span>
          </Tooltip>
        ),
        dataIndex: '__unitPriceOverride',
        width: 140,
        render: (_: any, r: any) => {
          if (!r?.__isSys9) return <Text type="secondary">-</Text>;
          const woId = String(r?.__woId || '');
          const v = r?.__unitPriceOverride;
          const baseV = r?.__unitPriceBase;
          const displayV = Number.isFinite(Number(v)) ? Number(v) : (Number.isFinite(Number(baseV)) ? Number(baseV) : null);
          return (
            <InputNumber
              style={{ width: '100%', minWidth: 100 }}
              value={displayV}
              min={0}
              step={0.1}
              precision={2}
              placeholder={r?.__unitPriceBase == null ? '留空' : `基准:${String(r.__unitPriceBase)}`}
              onChange={(nv) => {
                const num = Number(nv);
                setPricingFacts((prev) => {
                  const p = (prev && typeof prev === 'object') ? prev : { by_workorder: {} };
                  const by = (p?.by_workorder && typeof p.by_workorder === 'object') ? { ...(p.by_workorder as AnyObj) } : {};
                  const row = (by[woId] && typeof by[woId] === 'object') ? { ...(by[woId] as AnyObj) } : {};
                  if (!Number.isFinite(num) || num <= 0) {
                    try { delete row.unit_price_override; } catch {}
                  } else {
                    row.unit_price_override = num;
                  }
                  by[woId] = row;
                  return { ...(p as AnyObj), by_workorder: by };
                });
              }}
            />
          );
        },
      },
      {
        title: '来源',
        dataIndex: '__from',
        ellipsis: true,
        render: (v: any) => (
          <Tooltip title={String(v || '-')}>
            <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>
          </Tooltip>
        ),
      },
    ];
  }, [enrichPricingValueListWithIuSystemCode]);

  const uploadProps: UploadProps = useMemo(() => {
    return {
      multiple: true,
      // 完全隐藏 Upload 组件自带的“已上传/上传中”本地队列回显，只保留下方总列表作为唯一展示
      showUploadList: false,
      accept: [
        '.xlsx,.xls,.csv',
        '.pdf',
        '.png,.jpg,.jpeg,.gif,.webp',
        '.mp4,.mov,.avi',
        '.zip,.rar,.7z',
      ].join(','),
      customRequest: async (opts: any) => {
        const { file, onSuccess, onError } = opts;
        try {
          if (!instrId) {
            message.error('缺少指令ID，无法上传');
            onError?.(new Error('missing instrId'));
            return;
          }
          const fd = new FormData();
          fd.append('instruction', String(instrId));
          fd.append('label', 'final_review');
          fd.append('scope', 'order');
          fd.append('status', 'active');
          fd.append('file', file);
          const fileName = (file as any)?.name || 'upload';
          fd.append('file_name', fileName);
          // 后端模型 file_path 必填；虽然 serializer 会在 multipart 有 file 时兜底补齐，
          // 但这里显式带上，避免因 boundary/解析问题导致 400。
          fd.append('file_path', fileName);
          const resp = await request.post('/api/v1/instruction/attachments/', fd, {
            headers: { 'Content-Type': 'multipart/form-data' },
            // 关键：避免 axios 默认按 JSON 转换 FormData（导致后端收到 application/json）
            transformRequest: [(d: any) => d],
          } as any);
          onSuccess?.(resp?.data);
          message.success('上传成功');
          await loadFinalReviewAttachments();
        } catch (e: any) {
          message.error(e?.response?.data?.detail || e?.response?.data?.error || '上传失败');
          onError?.(e);
        }
      },
    } as UploadProps;
  }, [instrId, loadFinalReviewAttachments]);

  const columns = useMemo(() => {
    return [
      {
        title: (() => {
          const allKeys = (attachments || []).map((a: any) => String(a?.id || '')).filter(Boolean);
          const total = allKeys.length;
          const sel = finalAttSelectedKeys.length;
          const checked = total > 0 && sel === total;
          const indeterminate = sel > 0 && total > 0 && sel < total;
          return (
            <Checkbox
              checked={checked}
              indeterminate={indeterminate}
              onChange={() => {
                if (checked) setFinalAttSelectedKeys([]);
                else setFinalAttSelectedKeys(allKeys);
              }}
            >
              选择
            </Checkbox>
          );
        })(),
        key: '__sel',
        width: 90,
        render: (_: any, r: AnyObj) => {
          const k = String(r?.id || '');
          const checked = finalAttSelectedKeys.includes(k);
          return (
            <Checkbox
              checked={checked}
              onChange={() => {
                setFinalAttSelectedKeys((prev) => {
                  const s = new Set(prev || []);
                  if (s.has(k)) s.delete(k); else s.add(k);
                  return Array.from(s);
                });
              }}
            />
          );
        },
      },
      {
        title: '文件名',
        key: 'file_name',
        render: (_: any, r: AnyObj) => {
          const name = String(r?.file_name || r?.file || r?.file_path || '').trim() || '-';
          return <span style={{ fontFamily: 'monospace' }}>{name}</span>;
        },
      },
      {
        title: '上传时间',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 180,
        render: (v: any) => <span style={{ color: '#595959', fontFamily: 'monospace' }}>{fmtDateTime(v)}</span>,
      },
      {
        title: '操作',
        key: 'ops',
        width: 240,
        render: (_: any, r: AnyObj) => {
          const raw = String(r?.file || r?.file_path || '').trim();
          const url = resolveBackendFileUrl(raw) || raw;
          const name = String(r?.file_name || r?.file || r?.file_path || '').trim();
          return (
            <Space size="small">
              <Button
                size="small"
                onClick={() => {
                  if (!url) return;
                  void openPreview(url, name || undefined);
                }}
                disabled={!url}
              >
                预览
              </Button>
              <Button size="small" icon={<DownloadOutlined />} onClick={() => { void downloadFinalReviewAttachment(r); }} disabled={!url}>
                下载
              </Button>
              <Button
                size="small"
                danger
                onClick={async () => {
                  Modal.confirm({
                    title: '删除附件',
                    okText: '删除',
                    cancelText: '取消',
                    content: '将删除该终审附件记录（仅影响终审留档，不影响执行快照）。',
                    onOk: async () => {
                      await request.delete(`/api/v1/instruction/attachments/${r?.id}/`);
                      message.success('已删除');
                      setFinalAttSelectedKeys((prev) => prev.filter((k) => k !== String(r?.id || '')));
                      await loadFinalReviewAttachments();
                    }
                  });
                }}
              >
                删除
              </Button>
            </Space>
          );
        },
      },
    ];
  }, [attachments, finalAttSelectedKeys, loadFinalReviewAttachments, downloadFinalReviewAttachment, openPreview]);

  const baselineColumns = useMemo(() => {
    return [
      {
        title: '基线文件',
        key: 'baseline',
        width: 160,
        render: (_: any, r: AnyObj) => <span style={{ fontFamily: 'monospace' }}>{String(r?.baseline_code || '-')}</span>,
      },
      {
        title: '文件名',
        key: 'file_name',
        render: (_: any, r: AnyObj) => <span style={{ fontFamily: 'monospace' }}>{String(r?.file_name || r?.file || '-')}</span>,
      },
      {
        title: '操作',
        key: 'ops',
        width: 120,
        render: (_: any, r: AnyObj) => {
          const raw = String(r?.file || '').trim();
          const url = resolveBackendFileUrl(raw) || raw;
          const name = String(r?.file_name || r?.file || '').trim();
          return (
            <Space size="small">
              <Button size="small" onClick={() => openPreview(url, name)} disabled={!url}>预览</Button>
            </Space>
          );
        },
      },
    ];
  }, [openPreview]);

  const stageColumns = useMemo(() => {
    return [
      {
        title: '阶段',
        key: 'stage',
        width: 160,
        render: (_: any, r: AnyObj) => (
          <span style={{ fontFamily: 'monospace' }}>
            {stageKeyToCN(r?.stage_key)}
          </span>
        ),
      },
      {
        title: '提交时间',
        dataIndex: 'submitted_at',
        key: 'submitted_at',
        width: 190,
        // 和非标准操作指令终审页的"提交时间"列一致：显示用户时区的本地时间，鼠标悬停显示 UTC 时间、用户时区和本地时间
        render: (v: any) => (
          v && !Number.isNaN(new Date(v).getTime())
            ? <TimezoneAwareTime time={v} format="YYYY-MM-DD HH:mm" showTimezone={false} />
            : <span style={{ color: '#595959', fontFamily: 'monospace' }}>{fmtDateTime(v)}</span>
        ),
      },
      {
        title: '操作',
        key: 'ops',
        width: 120,
        render: (_: any, r: AnyObj) => (
          <Space size="small">
            <Button
              size="small"
              onClick={() => {
                const stageKey = String(r?.stage_key || '').trim().toUpperCase();
                // 注意：StageSubmission.snapshot 本身就是“阶段快照骨架”（含 stats / submitted_at 等）
                // 对于 relabel_pack/wrap_up，骨架内部可能还包含 snapshot 字段（阶段自定义内容）。
                // 这里必须传骨架给“可读视图”，否则会丢失 stats 等字段。
                const snap = (r?.snapshot || {}) as AnyObj;
                setPreviewKind('json');
                setPreviewTitle(`阶段快照: ${stageKeyToCN(stageKey)}`);
                setPreviewUrl('');
                setPreviewTable(null);
                setPreviewText(JSON.stringify(snap || {}, null, 2));
                setPreviewStageReadable({ stage_key: stageKey, snapshot: snap, row: r });
                setPreviewOpen(true);
              }}
            >
              查看快照
            </Button>
          </Space>
        ),
      },
    ];
  }, [instrId]);

  const iocColumns = useMemo(() => {
    return [
      {
        title: '容器序号',
        key: '__idx',
        width: 90,
        render: (_: any, r: AnyObj, idx: number) => {
          if (r?.__is_summary) return <span />;
          const seq = Math.floor(Number((r as any)?.__container_seq ?? 0) || 0);
          return <span style={{ fontFamily: 'monospace' }}>{String(seq > 0 ? seq : ((idx ?? 0) + 1))}</span>;
        },
      },
      {
        title: <div style={{ whiteSpace: 'normal', lineHeight: 1.15 }}>IOC编码</div>,
        key: 'code',
        width: 180,
        onHeaderCell: () => ({ style: { whiteSpace: 'normal', lineHeight: 1.15 } }),
        render: (_: any, r: AnyObj) => {
          const code = String(r?.code || `IOC#${r?.id}`);
          if (r?.__is_summary) {
            if (r?.__is_normal_summary) return <span style={{ fontWeight: 800, color: '#389e0d' }}>{code}</span>;
            return <span style={{ fontWeight: 800, color: '#d4380d' }}>{code}</span>;
          }
          const openContent = async () => {
            // 1) 优先使用当前页面已聚合好的事实口径（避免再次依赖 facets 物化表）
            try {
              const currentRows = (Array.isArray(iocNewBarcodeFacetRows) ? iocNewBarcodeFacetRows : [])
                .filter((x: any) => !x?.__is_summary && String(x?.container_code || '').trim() === String(code).trim())
                .map((x: any, idx: number) => ({
                  __k: `${code}_local_${idx}_${String(x?.new_barcode || '').trim()}`,
                  new_code: String(x?.new_barcode || '').trim(),
                  quantity: Math.floor(Number(x?.total_quantity ?? 0) || 0),
                }))
                .filter((x: any) => !!x.new_code && Number(x.quantity) > 0)
                .sort((a: any, b: any) => a.new_code.localeCompare(b.new_code, undefined, { numeric: true, sensitivity: 'base' }));
              if (currentRows.length > 0) {
                setIocContentTitle(`内容物明细 - ${code}`);
                setIocContentRows(currentRows);
                setIocContentOpen(true);
                return;
              }
            } catch {}

            // 2) 兼容层：调用 facets 接口（后端已做 defer 回退到阶段快照）
            try {
              if (instrId && code) {
                const resp = await request.get('/api/v1/storage/output-containers/facets/', {
                  params: { instruction_id: instrId, facet_key: 'new_barcode', codes: code },
                } as any);
                const rowsRaw = Array.isArray(resp?.data?.rows) ? resp.data.rows : [];
                const rows = rowsRaw
                  .filter((x: any) => String(x?.container__code || x?.container_code || '').trim() === String(code).trim())
                  .map((x: any, idx: number) => ({
                    __k: `${code}_${idx}_${String(x?.facet_value || x?.new_barcode || '').trim()}`,
                    new_code: String(x?.facet_value || x?.new_barcode || '').trim(),
                    quantity: Math.floor(Number(x?.total_quantity ?? 0) || 0),
                  }))
                  .filter((x: any) => !!x.new_code && Number(x.quantity) > 0)
                  .sort((a: any, b: any) => a.new_code.localeCompare(b.new_code, undefined, { numeric: true, sensitivity: 'base' }));
                if (rows.length > 0) {
                  setIocContentTitle(`内容物明细 - ${code}`);
                  setIocContentRows(rows);
                  setIocContentOpen(true);
                  return;
                }
              }
            } catch {}

            // 3) 最后兜底：按 IOC contents + IU 详情解析（可能展示为 IU 系统编码）
            const contents = Array.isArray(r?.contents) ? r.contents : [];
            const iuIds = Array.from(new Set(
              contents.map((x: AnyObj) => Math.floor(Number(x?.inventory_unit_id || 0) || 0)).filter((n: number) => n > 0)
            ));
            // 预取缺失 IU 详情并缓存“新条码+数量”
            const missing = iuIds.filter((id) => !iocIuItemsById[String(id)] && !iocIuLoadingRef.current.has(id));
            if (missing.length) {
              const slice = missing.slice(0, 200);
              slice.forEach((id) => iocIuLoadingRef.current.add(id));
              const pairs = await Promise.allSettled(slice.map(async (id) => {
                try {
                  const resp: any = await getInventoryUnitDetail(id);
                  const d = resp?.data || resp;
                  const arr = Array.isArray(d?.content?.items) ? d.content.items : (Array.isArray(d?.content_items) ? d.content_items : []);
                  const items = (arr || []).map((it: any) => {
                    const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
                    let barcode = '';
                    try {
                      const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                      if (sc.length > 0) {
                        const codes = sc.map((c: any) => String(c?.code || '').trim()).filter(Boolean);
                        if (codes.length > 0) barcode = codes.join('/');
                      }
                    } catch {}
                    if (!barcode) {
                      barcode = String(
                        it?.product_system_code
                        || it?.system_code
                        || it?.product_code
                        || it?.new_code
                        || ''
                      ).trim();
                    }
                    return { barcode, qty };
                  }).filter((x: any) => (x.barcode && x.qty) || x.barcode);
                  return { id, items };
                } catch {
                  return { id, items: [] as any[] };
                }
              }));
              const next: Record<string, Array<{ barcode: string; qty: number }>> = {};
              pairs.forEach((p) => {
                if (p.status !== 'fulfilled') return;
                const id = Math.floor(Number((p.value as any)?.id || 0) || 0);
                const items = Array.isArray((p.value as any)?.items) ? (p.value as any).items : [];
                if (id > 0) next[String(id)] = items;
              });
              if (Object.keys(next).length) setIocIuItemsById((prev) => ({ ...prev, ...next }));
              slice.forEach((id) => iocIuLoadingRef.current.delete(id));
            }

            // 组装弹窗数据：按新条码汇总数量（不展示 IU 信息）
            const sum = new Map<string, number>();
            contents.forEach((c: AnyObj) => {
              const iuId = Math.floor(Number(c?.inventory_unit_id || 0) || 0);
              const cached = iuId > 0 ? (iocIuItemsById[String(iuId)] || []) : [];
              if (cached.length > 0) {
                // 重要：InstructionOutputContainerContent.quantity 是该 IU 在该 IOC 中的数量
                // IU 详情里的 content.items.qty 是 IU 内容项数量；最终按新条码汇总需要乘以该行数量
                const mulRaw = Number(c?.quantity ?? 1);
                const mul = (Number.isFinite(mulRaw) && mulRaw > 0) ? Math.floor(mulRaw) : 1;
                cached.forEach((it) => {
                  const bc = String(it?.barcode || '').trim();
                  if (!bc) return;
                  const q = Number(it?.qty || 0) || 0;
                  sum.set(bc, (sum.get(bc) || 0) + ((q || 0) * mul));
                });
                return;
              }
              // 无法解析新条码时不展示 IU，避免误导
            });
            const rows = Array.from(sum.entries())
              .map(([barcode, qty], idx) => ({ __k: `${code}_${idx}_${barcode}`, new_code: barcode, quantity: Math.floor(qty) }))
              .sort((a, b) => a.new_code.localeCompare(b.new_code, undefined, { numeric: true, sensitivity: 'base' }));
            setIocContentTitle(`内容物明细 - ${code}`);
            setIocContentRows(rows);
            setIocContentOpen(true);
          };
          return (
            <Space size="small">
              <span
                style={{ fontFamily: 'monospace', fontWeight: 800, color: '#1677ff', cursor: 'pointer' }}
                onClick={() => { void openContent(); }}
                title="点击查看内容物"
              >
                {code}
              </span>
              <Button
                size="small"
                type="text"
                aria-label="复制IOC编码"
                icon={<CopyOutlined />}
                onClick={() => {
                  const txt = String(code || '').trim();
                  if (!txt) return;
                  const run = async () => {
                    try {
                      await navigator.clipboard.writeText(txt);
                      message.success('已复制');
                      return;
                    } catch {}
                    try {
                      const ta = document.createElement('textarea');
                      ta.value = txt;
                      ta.style.position = 'fixed';
                      ta.style.left = '-9999px';
                      document.body.appendChild(ta);
                      ta.focus();
                      ta.select();
                      document.execCommand('copy');
                      document.body.removeChild(ta);
                      message.success('已复制');
                    } catch {
                      message.error('复制失败');
                    }
                  };
                  void run();
                }}
              />
            </Space>
          );
        },
      },
      {
        title: 'IOC类型',
        key: 'ioc_kind',
        width: 110,
        render: (_: any, r: AnyObj) => {
          if (r?.__is_summary) return <span style={{ color: '#999' }}>-</span>;
          const kind = String(r?.__ioc_kind || '').trim();
          if (kind === 'exception') {
            return (
              <Tag style={{ marginInlineEnd: 0, background: '#fa541c', borderColor: '#fa541c', color: '#fff', fontWeight: 700 }}>
                异常内容容器
              </Tag>
            );
          }
          return (
            <Tag style={{ marginInlineEnd: 0, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 700 }}>
              正常内容容器
            </Tag>
          );
        },
      },
      {
        title: (
          <Tooltip title="仅统计容器内直接装载的操作对象(库存单元或历史指令IOC),不统计容器内子容器或者历史指令IOC容器中的内容">
            <div style={{ whiteSpace: 'normal', lineHeight: 1.15, cursor: 'help' }}>直接装载操作对象条目数</div>
          </Tooltip>
        ),
        key: 'cnt',
        width: 70,
        onHeaderCell: () => ({ style: { whiteSpace: 'normal', lineHeight: 1.15 } }),
        render: (_: any, r: AnyObj) => {
          if (r?.__is_summary) return <span style={{ color: '#999' }}>-</span>;
          return <span>{Array.isArray(r?.contents) ? r.contents.length : 0}</span>;
        },
      },
      {
        title: (
          <Tooltip title="仅统计容器内直接装载的操作对象(库存单元或历史指令IOC),不统计容器内子容器或者历史指令IOC容器中的内容">
            <div style={{ whiteSpace: 'normal', lineHeight: 1.15, cursor: 'help' }}>直接装载操作对象总数</div>
          </Tooltip>
        ),
        key: 'sum_qty',
        width: 80,
        onHeaderCell: () => ({ style: { whiteSpace: 'normal', lineHeight: 1.15 } }),
        render: (_: any, r: AnyObj) => {
          if (r?.__is_summary) {
            if (r?.__is_normal_summary) {
              const n = Math.floor(Number(r?.__normal_total_qty ?? 0) || 0);
              return <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#389e0d' }}>{String(n)}</span>;
            }
            const n = Math.floor(Number(r?.__unoperable_total_qty ?? 0) || 0);
            return <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#fa541c' }}>{String(n)}</span>;
          }
          const rows = Array.isArray(r?.contents) ? r.contents : [];
          const sum = rows.reduce((acc: number, x: any) => acc + (Math.floor(Number(x?.quantity || 0) || 0)), 0);
          return <span style={{ fontFamily: 'monospace' }}>{String(sum)}</span>;
        }
      },
      {
        title: '重量(kg)',
        key: 'weight',
        width: 90,
        render: (_: any, r: AnyObj) => {
          if (r?.__is_summary) return <span style={{ color: '#999' }}>-</span>;
          const g = Number(r?.weight_g);
          if (!Number.isFinite(g) || g <= 0) return <span style={{ color: '#999' }}>-</span>;
          const kg = g / 1000;
          return <span style={{ fontFamily: 'monospace' }}>{kg.toFixed(3)}</span>;
        }
      },
      {
        title: '尺寸(cm)',
        key: 'dims',
        width: 180,
        ellipsis: true,
        render: (_: any, r: AnyObj) => {
          if (r?.__is_summary) return <span style={{ color: '#999' }}>-</span>;
          const L = Number(r?.length_cm);
          const W = Number(r?.width_cm);
          const H = Number(r?.height_cm);
          if (![L, W, H].every((x) => Number.isFinite(x) && x > 0)) return <span style={{ color: '#999' }}>-</span>;
          const fmt = (x: number) => (Number.isInteger(x) ? String(x) : String(Math.round(x * 10) / 10));
          return <span style={{ fontFamily: 'monospace' }}>{fmt(L)}×{fmt(W)}×{fmt(H)}</span>;
        }
      },
      {
        title: '仓位',
        key: 'loc',
        ellipsis: true,
        render: (_: any, r: AnyObj) => {
          if (r?.__is_summary) return <span style={{ color: '#999' }}>-</span>;
          return <span>{String(r?.location_code || r?.location_text || '-')}</span>;
        },
      },
    ];
  }, [instrId, iocIuItemsById, iocNewBarcodeFacetRows]);

  // 明细3：只展示“指令产出容器(正常内容)”的 IOC
  const normalIocCodeSet = useMemo(() => {
    const s = new Set<string>();
    try {
      const list = Array.isArray(stageSubs) ? stageSubs : [];
      const wrap = list.find((x: any) => String(x?.stage_key || '').toUpperCase() === 'WRAP_UP');
      const snap = (wrap?.snapshot || {}) as AnyObj;
      const inner = (snap?.snapshot && typeof snap.snapshot === 'object') ? (snap.snapshot as AnyObj) : (snap as AnyObj);
      const sections = (inner?.sections || {}) as AnyObj;
      const s3 = (sections?.step3_put_away || {}) as AnyObj;
      const arr = Array.isArray((s3 as any)?.ioc_normal_codes) ? (s3 as any).ioc_normal_codes : [];
      (arr || []).forEach((c: any) => {
        const cc = String(c || '').trim();
        if (cc) s.add(cc);
      });
    } catch {}
    return s;
  }, [stageSubs]);

  // 明细3：异常内容 IOC（未操作/无法操作产品）容器 codes（来自收尾快照 step1）
  const exceptionIocCodeSet = useMemo(() => {
    const s = new Set<string>();
    try {
      const list = Array.isArray(stageSubs) ? stageSubs : [];
      const wrap = list.find((x: any) => String(x?.stage_key || '').toUpperCase() === 'WRAP_UP');
      const snap = (wrap?.snapshot || {}) as AnyObj;
      const inner = (snap?.snapshot && typeof snap.snapshot === 'object') ? (snap.snapshot as AnyObj) : (snap as AnyObj);
      const sections = (inner?.sections || {}) as AnyObj;
      const s1 = (sections?.step1_unoperable || sections?.step1_exception_containers || {}) as AnyObj;
      const arr = Array.isArray((s1 as any)?.exception_containers) ? (s1 as any).exception_containers : [];
      (arr || []).forEach((r: any) => {
        const cc = String(r?.code || '').trim();
        if (cc) s.add(cc);
      });
    } catch {}
    return s;
  }, [stageSubs]);

  const wrapUpExceptionContainersForDetail3 = useMemo(() => {
    const out: AnyObj[] = [];
    try {
      const list = Array.isArray(stageSubs) ? stageSubs : [];
      const wrap = list.find((x: any) => String(x?.stage_key || '').toUpperCase() === 'WRAP_UP');
      const snap = (wrap?.snapshot || {}) as AnyObj;
      const inner = (snap?.snapshot && typeof snap.snapshot === 'object') ? (snap.snapshot as AnyObj) : (snap as AnyObj);
      const sections = (inner?.sections || {}) as AnyObj;
      const s1 = (sections?.step1_unoperable || sections?.step1_exception_containers || {}) as AnyObj;
      const arr = Array.isArray((s1 as any)?.exception_containers) ? (s1 as any).exception_containers : [];
      (arr || []).forEach((c: any, idx: number) => {
        const code = String(c?.code || '').trim();
        if (!code) return;
        const allocs = Array.isArray(c?.allocations) ? c.allocations : [];
        const totalByAlloc = allocs.reduce((acc: number, a: any) => {
          const q = Number(a?.quantity ?? a?.qty ?? 0) || 0;
          return acc + Math.max(0, Math.floor(Math.abs(q)));
        }, 0);
        const totalQty = (() => {
          const tq = Number(c?.total_qty ?? 0);
          if (Number.isFinite(tq) && tq > 0) return Math.max(0, Math.floor(tq));
          return Math.max(0, Math.floor(totalByAlloc));
        })();
        out.push({
          __k: `wrap-exception-${idx}-${code}`,
          code,
          total_qty: totalQty,
          allocations: allocs,
          exception_total_weight_g: Number(c?.exception_total_weight_g ?? c?.total_weight_g ?? c?.total_weight ?? 0) || 0,
        });
      });
    } catch {}
    return out;
  }, [stageSubs]);

  const hasSortCountStage = useMemo(() => {
    try {
      const list = Array.isArray(stageSubs) ? stageSubs : [];
      return list.some((x: any) => {
        const k = String(x?.stage_key || '').trim().toUpperCase();
        return k === 'SORT_COUNT' || k === 'SORT_AND_COUNT';
      });
    } catch {
      return false;
    }
  }, [stageSubs]);
  const sortCountFinalReportData = useMemo(() => {
    const empty = {
      stageKey: 'SORT_COUNT',
      demandMode: '',
      demandModeText: '-',
      headerDisplay: '',
      submittedAt: '',
      stats: {},
      presetGroups: [] as AnyObj[],
      presetGroupColumns: [] as AnyObj[],
      operationTargets: [] as AnyObj[],
      sortDatabaseRows: [] as AnyObj[],
      operationRecords: [] as AnyObj[],
      byMarkRows: [] as AnyObj[],
      generatedContainers: [] as AnyObj[],
      generatedDetailRows: [] as AnyObj[],
      newUnitRows: [] as AnyObj[],
      newProductRows: [] as AnyObj[],
      consumptionByTargetRows: [] as AnyObj[],
      initQtyByUnitRows: [] as AnyObj[],
      agingRows: [] as AnyObj[],
      exceptionRows: [] as AnyObj[],
      wrapUpExceptionContainers: [] as AnyObj[],
    };
    try {
      const list = Array.isArray(stageSubs) ? stageSubs : [];
      const sortSub = list.find((x: any) => {
        const k = String(x?.stage_key || '').trim().toUpperCase();
        return k === 'SORT_COUNT' || k === 'SORT_AND_COUNT';
      });
      if (!sortSub) return empty;
      const stageKey = String(sortSub?.stage_key || 'SORT_COUNT').trim().toUpperCase();
      const root = (sortSub?.snapshot && typeof sortSub.snapshot === 'object') ? (sortSub.snapshot as AnyObj) : {};
      const real = (root?.snapshot && typeof root.snapshot === 'object') ? (root.snapshot as AnyObj) : root;
      const ctx = (real?.instruction_context && typeof real.instruction_context === 'object') ? (real.instruction_context as AnyObj) : {};
      const sections = (real?.sections && typeof real.sections === 'object') ? (real.sections as AnyObj) : {};
      const stats = (root?.stats && typeof root.stats === 'object') ? (root.stats as AnyObj) : ((real?.stats || {}) as AnyObj);
      const demandMode = String(ctx?.demand_mode || '').trim();
      const demandModeText = demandMode === 'need1' ? '需求1' : demandMode === 'need2' ? '需求2' : demandMode === 'need3' ? '需求3' : (demandMode || '-');
      const presetGroups = Array.isArray(sections?.preset_group_list?.rows) ? sections.preset_group_list.rows : [];
      const presetGroupColumns = Array.isArray(sections?.preset_group_list?.columns) ? sections.preset_group_list.columns : [];
      const operationTargets = Array.isArray(sections?.operation_targets?.rows) ? sections.operation_targets.rows : [];
      const sortDatabaseRows = Array.isArray(sections?.sort_database_rows?.rows) ? sections.sort_database_rows.rows : [];
      const operationRecords = Array.isArray(sections?.operation_records?.rows) ? sections.operation_records.rows : [];
      const byMarkRows = Array.isArray(sections?.stats_by_mark?.rows) ? sections.stats_by_mark.rows : [];
      const generatedContainers = Array.isArray(sections?.generated_containers?.rows) ? sections.generated_containers.rows : [];
      const generatedDetailRows = Array.isArray(sections?.generated_containers?.detail_rows) ? sections.generated_containers.detail_rows : [];
      const newUnitMap = new Map<string, { unit_code: string; total_qty: number; sorting_marks: Set<string>; containers: Set<string> }>();
      (generatedDetailRows || []).forEach((r: any) => {
        const unitCode = String(r?.unit_code || '').trim();
        if (!unitCode || unitCode === '-') return;
        const qty = Math.max(0, Number(r?.qty || 0) || 0);
        if (!newUnitMap.has(unitCode)) {
          newUnitMap.set(unitCode, { unit_code: unitCode, total_qty: 0, sorting_marks: new Set<string>(), containers: new Set<string>() });
        }
        const hit = newUnitMap.get(unitCode)!;
        hit.total_qty += qty;
        const sm = String(r?.sorting_mark || '').trim();
        if (sm) hit.sorting_marks.add(sm);
        const cc = String(r?.system_code || '').trim();
        if (cc) hit.containers.add(cc);
      });
      const newUnitRows = Array.from(newUnitMap.values()).map((x: any, idx: number) => ({
        __k: `new-unit-${idx}-${x.unit_code}`,
        unit_code: x.unit_code,
        initial_qty: Math.max(0, Math.floor(Number(x.total_qty || 0))),
        sorting_marks: Array.from(x.sorting_marks).join('、') || '-',
        container_codes: Array.from(x.containers).join('、') || '-',
      })).sort((a: AnyObj, b: AnyObj) => String(a?.unit_code || '').localeCompare(String(b?.unit_code || ''), undefined, { numeric: true, sensitivity: 'base' }));
      const newProductMap = new Map<string, number>();
      (byMarkRows || []).forEach((r: any) => {
        const mark = String(r?.sorting_mark || r?.mark || '').trim();
        if (!mark) return;
        const q = Math.max(0, Number(r?.total_qty ?? r?.qty ?? 0) || 0);
        newProductMap.set(mark, (newProductMap.get(mark) || 0) + q);
      });
      const newProductRows = Array.from(newProductMap.entries()).map(([sorting_mark, total_qty], idx: number) => ({
        __k: `new-product-${idx}-${sorting_mark}`,
        sorting_mark,
        total_qty: Math.max(0, Math.floor(Number(total_qty || 0))),
      })).sort((a: AnyObj, b: AnyObj) => Number(b?.total_qty || 0) - Number(a?.total_qty || 0));
      const consumptionByTargetMap = new Map<string, number>();
      (operationRecords || []).forEach((r: any) => {
        const target = String(r?.target_code || '').trim();
        if (!target) return;
        const qty = Math.max(0, Number(r?.qty || 0) || 0);
        consumptionByTargetMap.set(target, (consumptionByTargetMap.get(target) || 0) + qty);
      });
      const consumptionByTargetRows = Array.from(consumptionByTargetMap.entries()).map(([target_code, consumed_qty], idx: number) => ({
        __k: `target-consume-${idx}-${target_code}`,
        target_code,
        consumed_qty: Math.max(0, Math.floor(Number(consumed_qty || 0))),
      })).sort((a: AnyObj, b: AnyObj) => Number(b?.consumed_qty || 0) - Number(a?.consumed_qty || 0));
      const initQtyByUnitRows = (newUnitRows || []).map((r: AnyObj, idx: number) => ({
        __k: `unit-init-${idx}-${String(r?.unit_code || '')}`,
        unit_code: String(r?.unit_code || ''),
        initial_qty: Math.max(0, Math.floor(Number(r?.initial_qty || 0))),
      }));
      const agingRows = (generatedContainers || []).map((r: AnyObj, idx: number) => ({
        __k: `aging-${idx}-${String(r?.system_code || '')}`,
        container_code: String(r?.system_code || '-'),
        initial_stock_age_days: r?.initial_stock_age_days ?? r?.initial_aging_days ?? r?.initial_age_days ?? null,
        initial_stock_date: r?.initial_stock_date ?? r?.aging_start_date ?? '',
      }));
      const wrapSub = list.find((x: any) => String(x?.stage_key || '').trim().toUpperCase() === 'WRAP_UP');
      const wrapRoot = (wrapSub?.snapshot && typeof wrapSub.snapshot === 'object') ? (wrapSub.snapshot as AnyObj) : {};
      const wrapReal = (wrapRoot?.snapshot && typeof wrapRoot.snapshot === 'object') ? (wrapRoot.snapshot as AnyObj) : wrapRoot;
      const wrapSections = (wrapReal?.sections && typeof wrapReal.sections === 'object') ? (wrapReal.sections as AnyObj) : {};
      const wrapStep1 = (wrapSections?.step1_unoperable || wrapSections?.step1_exception_containers || {}) as AnyObj;
      const exceptionContainers = Array.isArray(wrapStep1?.exception_containers) ? wrapStep1.exception_containers : [];
      const exceptionRows: AnyObj[] = [];
      exceptionContainers.forEach((c: AnyObj, idx: number) => {
        const allocs = Array.isArray(c?.allocations) ? c.allocations : [];
        if (!allocs.length) {
          exceptionRows.push({
            __k: `exception-${idx}-${String(c?.code || '')}-0`,
            container_code: String(c?.code || '-'),
            sorting_mark: '-',
            qty: Math.max(0, Math.floor(Number(c?.total_qty || 0) || 0)),
            reason: String(c?.remark || c?.reason || '无法操作产品'),
          });
          return;
        }
        allocs.forEach((a: AnyObj, j: number) => {
          exceptionRows.push({
            __k: `exception-${idx}-${j}-${String(c?.code || '')}`,
            container_code: String(c?.code || '-'),
            sorting_mark: String(a?.old_code || a?.sorting_mark || '-'),
            qty: Math.max(0, Math.floor(Number(a?.quantity || a?.qty || 0) || 0)),
            reason: String(a?.reason || c?.remark || '无法操作产品'),
          });
        });
      });
      return {
        stageKey,
        demandMode,
        demandModeText,
        headerDisplay: String(ctx?.header_display || '').trim(),
        submittedAt: String(root?.submitted_at || real?.submitted_at || sortSub?.submitted_at || '').trim(),
        stats,
        presetGroups,
        presetGroupColumns,
        operationTargets,
        sortDatabaseRows,
        operationRecords,
        byMarkRows,
        generatedContainers,
        generatedDetailRows,
        newUnitRows,
        newProductRows,
        consumptionByTargetRows,
        initQtyByUnitRows,
        agingRows,
        exceptionRows,
        wrapUpExceptionContainers: exceptionContainers,
      };
    } catch {
      return empty;
    }
  }, [stageSubs]);
  const sortCountOperationTargetRows = useMemo(() => (
    Array.isArray(sortCountFinalReportData?.operationTargets) ? sortCountFinalReportData.operationTargets : []
  ), [sortCountFinalReportData]);
  const sortCountOperationTargetKindCount = useMemo(() => {
    const s = new Set<string>();
    (Array.isArray(sortCountOperationTargetRows) ? sortCountOperationTargetRows : []).forEach((r: any) => {
      const code = String(
        r?.inventory_unit_system_barcode
        || r?.snapshot?.system_barcode
        || r?.snapshot?.system_code
        || r?.inventory_unit_code
        || r?.inventory_unit_id
        || ''
      ).trim();
      if (code) s.add(code);
    });
    return s.size;
  }, [sortCountOperationTargetRows]);
  const sortCountPresetGroupRows = useMemo(() => (
    Array.isArray(sortCountFinalReportData?.presetGroups) ? sortCountFinalReportData.presetGroups : []
  ), [sortCountFinalReportData]);
  const sortCountPresetGroupColumns = useMemo(() => (
    Array.isArray(sortCountFinalReportData?.presetGroupColumns) ? sortCountFinalReportData.presetGroupColumns : []
  ), [sortCountFinalReportData]);
  const sortCountPresetGroupCount = useMemo(() => {
    const cols = Array.isArray(sortCountPresetGroupColumns) ? sortCountPresetGroupColumns : [];
    const groupNoCol = cols.find((c: any) => String(c?.title || '').includes('分组编号'));
    const groupNoDataIndex = String(groupNoCol?.dataIndex || '').trim();
    const s = new Set<string>();
    (Array.isArray(sortCountPresetGroupRows) ? sortCountPresetGroupRows : []).forEach((r: any) => {
      const code = String(
        r?.__preset_group_display_no
        || (groupNoDataIndex ? r?.[groupNoDataIndex] : '')
        || r?.group_code
        || r?.preset_group_no
        || r?.group_id
        || ''
      ).trim();
      if (code) s.add(code);
    });
    return s.size;
  }, [sortCountPresetGroupRows, sortCountPresetGroupColumns]);
  const sortCountPresetGroupNoDataIndex = useMemo(() => {
    const cols = Array.isArray(sortCountPresetGroupColumns) ? sortCountPresetGroupColumns : [];
    const hit = cols.find((c: any) => String(c?.title || '').includes('分组编号'));
    return String(hit?.dataIndex || '').trim();
  }, [sortCountPresetGroupColumns]);
  const sortCountPresetGroupMarkDataIndex = useMemo(() => {
    const cols = Array.isArray(sortCountPresetGroupColumns) ? sortCountPresetGroupColumns : [];
    const hit = cols.find((c: any) => String(c?.title || '').includes('分拣标识'));
    return String(hit?.dataIndex || '').trim();
  }, [sortCountPresetGroupColumns]);

  // “总装箱数据”入口分流：
  // - RELABEL_PACK：沿用既有“按新条码汇总”弹窗（保持不变）
  // - REGULAR_OUTBOUND_PACK：启用常规出库专用弹窗（独立样式/列表）
  const hasRelabelPackStage = useMemo(() => {
    try {
      const list = Array.isArray(stageSubs) ? stageSubs : [];
      return list.some((x: any) => String(x?.stage_key || '').trim().toUpperCase() === 'RELABEL_PACK');
    } catch {
      return false;
    }
  }, [stageSubs]);
  const hasRegularOutboundPackStage = useMemo(() => {
    try {
      const list = Array.isArray(stageSubs) ? stageSubs : [];
      return list.some((x: any) => String(x?.stage_key || '').trim().toUpperCase() === 'REGULAR_OUTBOUND_PACK');
    } catch {
      return false;
    }
  }, [stageSubs]);
  const isRegularOutboundTemplate = useMemo(() => {
    try {
      const tplName = String((instr as any)?.template_detail?.name || (instr as any)?.template_snapshot?.name || '').trim().toLowerCase();
      const tplCode = String((instr as any)?.template_detail?.system_code || (instr as any)?.template_snapshot?.system_code || (instr as any)?.template_detail?.code || (instr as any)?.template_snapshot?.code || '').trim().toLowerCase();
      return tplName.includes('常规出库') || (tplCode.includes('regular') && tplCode.includes('outbound'));
    } catch {
      return false;
    }
  }, [instr]);
  const isRelabelTemplate = useMemo(() => {
    try {
      const tplName = String((instr as any)?.template_detail?.name || (instr as any)?.template_snapshot?.name || '').trim().toLowerCase();
      const tplCode = String((instr as any)?.template_detail?.system_code || (instr as any)?.template_snapshot?.system_code || (instr as any)?.template_detail?.code || (instr as any)?.template_snapshot?.code || '').trim().toLowerCase();
      return tplName.includes('换标') || (tplCode.includes('relabel') && tplCode.includes('pack'));
    } catch {
      return false;
    }
  }, [instr]);
  const totalPackingMode = useMemo<'none' | 'relabel' | 'regular_outbound'>(() => {
    if (isRegularOutboundTemplate && hasRegularOutboundPackStage) return 'regular_outbound';
    if (isRelabelTemplate && hasRelabelPackStage) return 'relabel';
    if (hasRegularOutboundPackStage && !hasRelabelPackStage) return 'regular_outbound';
    if (hasRelabelPackStage && !hasRegularOutboundPackStage) return 'relabel';
    if (hasRegularOutboundPackStage) return 'regular_outbound';
    if (hasRelabelPackStage) return 'relabel';
    return 'none';
  }, [hasRelabelPackStage, hasRegularOutboundPackStage, isRegularOutboundTemplate, isRelabelTemplate]);
  const canUseTotalPackingData = totalPackingMode !== 'none';

  const regularOutboundPackingPayload = useMemo(() => {
    try {
      const isAdjustPoolContainerCode = (raw: any): boolean => {
        const code = String(raw || '').trim().toUpperCase();
        if (!code) return false;
        return code.startsWith('ADJ-POOL') || code.startsWith('ADJ_POOL');
      };
      const list = Array.isArray(stageSubs) ? stageSubs : [];
      const cands = list.filter((x: any) => String(x?.stage_key || '').trim().toUpperCase() === 'REGULAR_OUTBOUND_PACK');
      if (!cands.length) return null;
      const chosen = cands
        .slice()
        .sort((a: any, b: any) => {
          const ta = Number(new Date(String(a?.submitted_at || 0)).getTime()) || 0;
          const tb = Number(new Date(String(b?.submitted_at || 0)).getTime()) || 0;
          return tb - ta;
        })[0];
      const root = (chosen?.snapshot || {}) as AnyObj;
      const snap = (root?.snapshot && typeof root.snapshot === 'object') ? (root.snapshot as AnyObj) : root;
      const sections = (snap?.sections || {}) as AnyObj;
      const metrics = (snap?.metrics || {}) as AnyObj;
      const progressTabs = (sections?.progress_tabs || metrics?.progress_tabs_snapshot || {}) as AnyObj;
      const opRows = Array.isArray(sections?.operation_records?.rows)
        ? sections.operation_records.rows
        : (Array.isArray(metrics?.operation_records) ? metrics.operation_records : []);
      const byContainerRowsRaw = Array.isArray(progressTabs?.by_container_rows) ? progressTabs.by_container_rows : [];
      // 终审“总包装数据”中不展示调整池容器
      const byContainerRows = byContainerRowsRaw.filter((r: any) => {
        const code = String(r?.container_code || r?.code || '').trim();
        return !isAdjustPoolContainerCode(code);
      });
      const byTargetRows = Array.isArray(progressTabs?.by_target_rows) ? progressTabs.by_target_rows : [];
      const byHierarchyRows = Array.isArray(progressTabs?.by_hierarchy_rows) ? progressTabs.by_hierarchy_rows : [];
      const childCodeSet = new Set<string>(
        (Array.isArray(byHierarchyRows) ? byHierarchyRows : [])
          .map((ln: any) => String(ln?.child_code || '').trim())
          .filter((x: string) => !!x),
      );
      const rootContainerRows = (Array.isArray(byContainerRows) ? byContainerRows : []).filter((r: any) => {
        const code = String(r?.container_code || r?.code || '').trim();
        if (!code) return false;
        return !childCodeSet.has(code);
      });
      const stats = {
        containers: byContainerRows.length,
        activeContainers: byContainerRows.filter((r: any) => String(r?.status || '').trim().toLowerCase() !== 'cancelled').length,
        cancelledContainers: byContainerRows.filter((r: any) => String(r?.status || '').trim().toLowerCase() === 'cancelled').length,
        directQty: byContainerRows.reduce((s: number, r: any) => s + Math.max(0, Math.floor(Number(r?.direct_qty ?? r?.qty ?? 0) || 0)), 0),
        // 避免重复：含子容器汇总仅按“根容器（父+独立，非子容器）”统计
        aggregatedQty: rootContainerRows.reduce((s: number, r: any) => s + Math.max(0, Math.floor(Number(r?.aggregated_qty ?? r?.qty ?? 0) || 0)), 0),
        targetRows: byTargetRows.length,
        operationRows: opRows.length,
      };
      return { byContainerRows, byTargetRows, byHierarchyRows, opRows, stats };
    } catch {
      return null;
    }
  }, [stageSubs]);
  const regularOutboundContainerTable = useMemo(() => {
    try {
      const payload = (regularOutboundPackingPayload && typeof regularOutboundPackingPayload === 'object')
        ? regularOutboundPackingPayload
        : ({} as AnyObj);
      const byContainerRowsRaw = Array.isArray(payload?.byContainerRows) ? payload.byContainerRows : [];
      const byHierarchyRowsRaw = Array.isArray(payload?.byHierarchyRows) ? payload.byHierarchyRows : [];
      const byTargetRowsRaw = Array.isArray(payload?.byTargetRows) ? payload.byTargetRows : [];
      const opRows = Array.isArray(payload?.opRows) ? payload.opRows : [];
      if (!byContainerRowsRaw.length) {
        return {
          rows: [] as AnyObj[],
          hasHierarchy: false,
          totalQty: 0,
          totalWeightKg: 0,
        };
      }

      // 1) 不显示已作废容器
      const byContainerRows = byContainerRowsRaw.filter((r: AnyObj) => {
        const st = String(r?.status || '').trim().toLowerCase();
        return st !== 'cancelled';
      });
      if (!byContainerRows.length) {
        return {
          rows: [] as AnyObj[],
          hasHierarchy: false,
          totalQty: 0,
          totalWeightKg: 0,
        };
      }

      const boxMap = new Map<string, AnyObj>();
      byContainerRows.forEach((r: AnyObj) => {
        const code = String(r?.container_code || r?.code || '').trim();
        if (code) boxMap.set(code, r);
      });

      const childrenByParent = new Map<string, string[]>();
      const childSet = new Set<string>();
      byHierarchyRowsRaw.forEach((ln: AnyObj) => {
        const p = String(ln?.parent_code || '').trim();
        const c = String(ln?.child_code || '').trim();
        if (!p || !c) return;
        if (!boxMap.has(p) || !boxMap.has(c)) return;
        if (p === c) return;
        if (!childrenByParent.has(p)) childrenByParent.set(p, []);
        childrenByParent.get(p)!.push(c);
        childSet.add(c);
      });
      childrenByParent.forEach((arr, p) => {
        const uniq = Array.from(new Set(arr.filter((x) => !!x && x !== p)));
        uniq.sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
        childrenByParent.set(p, uniq);
      });
      const hasHierarchy = childrenByParent.size > 0;

      const byContainerTarget = new Map<string, Map<string, number>>();
      opRows.forEach((r: AnyObj) => {
        const cc = String(r?.container_code || '').trim();
        if (!cc) return;
        if (!boxMap.has(cc)) return;
        const target = String(r?.target || r?.target_code || r?.code || '').trim().toUpperCase();
        if (!target || target === '-') return;
        const qRaw = Number(r?.qty ?? r?.quantity ?? 0) || 0;
        const qAbs = Math.max(0, Math.floor(Math.abs(qRaw)));
        if (qAbs <= 0) return;
        const ev = String(r?.event || r?.action || '').trim();
        const isOut = /取出|冲销|移除|移出/.test(ev);
        const delta = isOut ? -qAbs : qAbs;
        if (!byContainerTarget.has(cc)) byContainerTarget.set(cc, new Map<string, number>());
        const mm = byContainerTarget.get(cc)!;
        mm.set(target, (mm.get(target) || 0) + delta);
      });

      const targetContentInfoByCode = new Map<string, { text: string; mode: 'inventory' | 'ioc' | 'unknown' }>();
      byTargetRowsRaw.forEach((row: AnyObj) => {
        const codeRaw = resolveRegularOutboundTargetCode(row);
        const code = codeRaw.toUpperCase();
        if (!code) return;
        const kind = String(row?.output_content_kind || '').trim().toLowerCase();
        const text = resolveRegularOutboundTargetContentInfo(row);
        if (String(row?.output_code || '').trim() !== '' || String(row?.output_contents_detail || '').trim() !== '' || !!kind) {
          targetContentInfoByCode.set(code, { text, mode: 'ioc' });
          return;
        }
        if (text && text !== '-') {
          targetContentInfoByCode.set(code, { text, mode: 'inventory' });
          return;
        }
        targetContentInfoByCode.set(code, { text: '-', mode: 'unknown' });
      });

      const directRowsByContainer = new Map<string, Array<{ target: string; qty: number }>>();
      byContainerTarget.forEach((mm, cc) => {
        const rows = Array.from(mm.entries())
          .map(([target, qty]) => ({ target, qty: Math.max(0, Math.floor(Number(qty || 0) || 0)) }))
          .filter((x) => x.qty > 0)
          .sort((a, b) => (b.qty - a.qty) || a.target.localeCompare(b.target, undefined, { numeric: true, sensitivity: 'base' }));
        directRowsByContainer.set(cc, rows);
      });

      const roots = byContainerRows
        .map((r: AnyObj) => String(r?.container_code || r?.code || '').trim())
        .filter((c: string) => !!c && !childSet.has(c))
        .sort((a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
      const topLevelCodes = roots.length
        ? roots
        : byContainerRows
          .map((r: AnyObj) => String(r?.container_code || r?.code || '').trim())
          .filter((c: string) => !!c)
          .sort((a: string, b: string) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));

      const out: AnyObj[] = [];
      let idx = 0;
      let totalQty = 0;
      const parseWeightKg = (weightDisplay: any): number => {
        const txt = String(weightDisplay || '').trim();
        if (!txt || txt === '-') return 0;
        const left = txt.split('|')[0] || '';
        const num = Number(String(left).replace(/[^\d.-]/g, ''));
        if (!Number.isFinite(num) || num <= 0) return 0;
        return Math.floor(num);
      };
      let totalWeightKg = 0;
      const makeLines = (containerCode: string): Array<{ target: string; qty: number }> => {
        const arr = directRowsByContainer.get(containerCode) || [];
        if (arr.length > 0) return arr;
        return [{ target: '-', qty: 0 }];
      };
      topLevelCodes.forEach((code) => {
        const parentCode = String(code || '').trim();
        if (!parentCode) return;
        const parent = boxMap.get(parentCode) || {};
        totalWeightKg += parseWeightKg(parent?.weight_display);
        const children = hasHierarchy ? (childrenByParent.get(parentCode) || []) : [];
        const hasChildren = children.length > 0;

        type ChildGroup = {
          childCode: string;
          childLabel: string;
          sourceCode: string;
          childType: string;
          isDirectOfParent?: boolean;
        };
        const groups: ChildGroup[] = [];

        if (hasChildren) {
          children.forEach((cc) => {
            const child = boxMap.get(cc) || {};
            groups.push({
              childCode: cc,
              childLabel: cc,
              sourceCode: cc,
              childType: String(child?.container_type_display || child?.container_type_text || child?.container_type || '-'),
            });
          });
          // 2) 父容器直接装载内容放在子容器后面
          const parentDirect = directRowsByContainer.get(parentCode) || [];
          if (parentDirect.length > 0) {
            groups.push({
              childCode: '__PARENT_DIRECT__',
              childLabel: '直接装载',
              sourceCode: parentCode,
              childType: String(parent?.container_type_display || parent?.container_type_text || parent?.container_type || '-'),
              isDirectOfParent: true,
            });
          }
        } else {
          // 3) 独立容器同级展示，子容器列固定为 "-"
          groups.push({
            childCode: '__INDEPENDENT__',
            childLabel: '-',
            sourceCode: parentCode,
            childType: String(parent?.container_type_display || parent?.container_type_text || parent?.container_type || '-'),
          });
        }

        const rowsForParent: AnyObj[] = [];
        groups.forEach((g) => {
          const lines = makeLines(g.sourceCode);
          const childSpan = lines.length;
          lines.forEach((ln, lineIdx) => {
            rowsForParent.push({
              __k: `${idx}-${parentCode}-${g.childCode}-${lineIdx}`,
              __group_index: idx,
              __parent_row_span: 0,
              __child_row_span: lineIdx === 0 ? childSpan : 0,
              __is_first_child_row: lineIdx === 0,
              parent_code: hasHierarchy ? (g.childCode === '__INDEPENDENT__' ? '-' : parentCode) : parentCode,
              parent_weight: String(parent?.weight_display || '-'),
              parent_dims: String(parent?.dims_display || '-'),
              outer_weight: (g.childCode === '__INDEPENDENT__')
                ? '-'
                : String(parent?.weight_display || '-'),
              outer_dims: (g.childCode === '__INDEPENDENT__')
                ? '-'
                : String(parent?.dims_display || '-'),
              parent_status: String(parent?.status || ''),
              parent_type: String(parent?.container_type_display || parent?.container_type_text || parent?.container_type || '-'),
              child_code: g.childCode,
              child_label: hasHierarchy ? (g.childCode === '__INDEPENDENT__' ? parentCode : g.childLabel) : g.childLabel,
              child_type: g.childType,
              inner_weight: g.childCode === '__INDEPENDENT__'
                ? String(parent?.weight_display || '-')
                : (g.isDirectOfParent || g.childCode === '__EMPTY__')
                  ? '-'
                  : String((boxMap.get(g.sourceCode) || {})?.weight_display || '-'),
              inner_dims: g.childCode === '__INDEPENDENT__'
                ? String(parent?.dims_display || '-')
                : (g.isDirectOfParent || g.childCode === '__EMPTY__')
                  ? '-'
                  : String((boxMap.get(g.sourceCode) || {})?.dims_display || '-'),
              is_parent_direct: !!g.isDirectOfParent,
              target_code: ln.target,
              target_qty: Math.max(0, Math.floor(Number(ln.qty || 0) || 0)),
              target_content_info: String(targetContentInfoByCode.get(String(ln.target || '').trim().toUpperCase())?.text || '-'),
              target_content_mode: String(targetContentInfoByCode.get(String(ln.target || '').trim().toUpperCase())?.mode || 'unknown'),
            });
            totalQty += Math.max(0, Math.floor(Number(ln.qty || 0) || 0));
          });
        });

        const parentSpan = rowsForParent.length || 1;
        rowsForParent.forEach((r, ridx) => {
          r.__parent_row_span = ridx === 0 ? parentSpan : 0;
          out.push(r);
        });
        if (rowsForParent.length === 0) {
          out.push({
            __k: `${idx}-${parentCode}-empty`,
            __group_index: idx,
            __parent_row_span: 1,
            __child_row_span: 1,
            __is_first_child_row: true,
            parent_code: parentCode,
            parent_weight: String(parent?.weight_display || '-'),
            parent_dims: String(parent?.dims_display || '-'),
            outer_weight: String(parent?.weight_display || '-'),
            outer_dims: String(parent?.dims_display || '-'),
            parent_status: String(parent?.status || ''),
            parent_type: String(parent?.container_type_display || parent?.container_type_text || parent?.container_type || '-'),
            child_code: '__EMPTY__',
            child_label: '-',
            child_type: '-',
            inner_weight: '-',
            inner_dims: '-',
            is_parent_direct: false,
            target_code: '-',
            target_qty: 0,
            target_content_info: '-',
            target_content_mode: 'unknown',
          });
        }
        idx += 1;
      });
      return {
        rows: out,
        hasHierarchy,
        totalQty,
        totalWeightKg,
      };
    } catch {
      return {
        rows: [] as AnyObj[],
        hasHierarchy: false,
        totalQty: 0,
        totalWeightKg: 0,
      };
    }
  }, [regularOutboundPackingPayload]);

  const buildRegularOutboundPackingXlsx = useCallback(async () => {
    const rows = Array.isArray(regularOutboundContainerTable?.rows) ? regularOutboundContainerTable.rows : [];
    const hasHierarchy = !!regularOutboundContainerTable?.hasHierarchy;
    const unoperableTotalQty = (
      Array.isArray(wrapUpExceptionContainersForDetail3) && wrapUpExceptionContainersForDetail3.length > 0
    )
      ? wrapUpExceptionContainersForDetail3.reduce((sum: number, c: AnyObj) => {
          const tq = Number(c?.total_qty ?? 0);
          if (Number.isFinite(tq) && tq > 0) return sum + Math.floor(tq);
          const allocs = Array.isArray(c?.allocations) ? c.allocations : [];
          const byAlloc = allocs.reduce((acc: number, a: any) => acc + Math.max(0, Math.floor(Math.abs(Number(a?.quantity ?? a?.qty ?? 0) || 0))), 0);
          return sum + byAlloc;
        }, 0)
      : calcUnoperableTotalQtyFromIocRows(Array.isArray(iocRows) ? iocRows : [], exceptionIocCodeSet);
    const instrCode = String((instr as any)?.system_code || instrId || 'INSTR').trim() || 'INSTR';
    const ymd = (() => {
      const d = new Date();
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dd}`;
    })();
    const fileName = `${instrCode}-常规出库总包装数据-${ymd}.xlsx`;

    let ExcelJS: any = null;
    try {
      ExcelJS = await import('exceljs');
    } catch {}
    if (!ExcelJS) throw new Error('缺少 Excel 生成库（exceljs）');

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('按容器统计');

    ws.columns = hasHierarchy
      ? [
        { header: '外层容器编码', key: 'parent_code', width: 26 },
        { header: '外层容器重量(kg | lb)', key: 'outer_weight', width: 18 },
        { header: '外层容器尺寸(cm | inch)', key: 'outer_dims', width: 26 },
        { header: '内层容器编码', key: 'child_code', width: 22 },
        { header: '内层容器重量(kg | lb)', key: 'inner_weight', width: 18 },
        { header: '内层容器尺寸(cm | inch)', key: 'inner_dims', width: 26 },
        { header: '操作对象', key: 'target_code', width: 24 },
        { header: '操作对象所含内容物', key: 'target_content_info', width: 34 },
        { header: '操作对象数量', key: 'target_qty', width: 12 },
      ]
      : [
        { header: '容器编码', key: 'parent_code', width: 26 },
        { header: '容器重量(kg | lb)', key: 'outer_weight', width: 18 },
        { header: '容器尺寸(cm | inch)', key: 'outer_dims', width: 26 },
        { header: '操作对象', key: 'target_code', width: 24 },
        { header: '操作对象所含内容物', key: 'target_content_info', width: 34 },
        { header: '操作对象数量', key: 'target_qty', width: 12 },
      ];

    try {
      const header = ws.getRow(1);
      header.height = 22;
      header.font = { bold: true };
      header.alignment = { vertical: 'middle', horizontal: 'center' };
    } catch {}

    let totalQty = 0;
    rows.forEach((r: AnyObj) => {
      const qty = Math.max(0, Math.floor(Number(r?.target_qty || 0) || 0));
      totalQty += qty;
      const parentRowSpan = Math.max(0, Math.floor(Number(r?.__parent_row_span ?? 1) || 0));
      const childRowSpan = Math.max(0, Math.floor(Number(r?.__child_row_span ?? 1) || 0));
      const childText = (() => {
        if (!hasHierarchy) return '';
        if (r?.is_parent_direct) return '直接装载';
        return formatContainerCodeWithType(String(r?.child_label || '-'), r?.child_type);
      })();
      const row = ws.addRow(hasHierarchy
        ? {
          parent_code: parentRowSpan > 0 ? formatContainerCodeWithType(String(r?.parent_code || '-'), r?.parent_type) : '',
          outer_weight: parentRowSpan > 0 ? String(r?.outer_weight || '-') : '',
          outer_dims: parentRowSpan > 0 ? String(r?.outer_dims || '-') : '',
          child_code: childRowSpan > 0 ? childText : '',
          inner_weight: childRowSpan > 0 ? String(r?.inner_weight || '-') : '',
          inner_dims: childRowSpan > 0 ? String(r?.inner_dims || '-') : '',
          target_code: String(r?.target_code || '-'),
          target_content_info: String(r?.target_content_info || '-'),
          target_qty: qty,
        }
        : {
          parent_code: parentRowSpan > 0 ? formatContainerCodeWithType(String(r?.parent_code || '-'), r?.parent_type) : '',
          outer_weight: parentRowSpan > 0 ? String((((r?.outer_weight && String(r?.outer_weight) !== '-') ? r?.outer_weight : r?.inner_weight) || '-')) : '',
          outer_dims: parentRowSpan > 0 ? String((((r?.outer_dims && String(r?.outer_dims) !== '-') ? r?.outer_dims : r?.inner_dims) || '-')) : '',
          target_code: String(r?.target_code || '-'),
          target_content_info: String(r?.target_content_info || '-'),
          target_qty: qty,
        });
      row.height = 20;
      try {
        const gi = Number(r?.__group_index || 0) || 0;
        const bg = gi % 2 === 0 ? 'FFF0F5FF' : 'FFF6FFED';
        row.eachCell({ includeEmpty: true }, (cell: any) => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } } as any;
        });
      } catch {}
    });

    // 合并同组共享的垂直单元格（并配合居中显示）
    try {
      rows.forEach((r: AnyObj, idx: number) => {
        const excelRow = idx + 2; // 表头占1行
        const parentRowSpan = Math.max(0, Math.floor(Number(r?.__parent_row_span ?? 0) || 0));
        const childRowSpan = Math.max(0, Math.floor(Number(r?.__child_row_span ?? 0) || 0));
        if (parentRowSpan > 1) {
          ws.mergeCells(excelRow, 1, excelRow + parentRowSpan - 1, 1); // parent_code
          ws.mergeCells(excelRow, 2, excelRow + parentRowSpan - 1, 2); // outer_weight
          ws.mergeCells(excelRow, 3, excelRow + parentRowSpan - 1, 3); // outer_dims
        }
        if (hasHierarchy && childRowSpan > 1) {
          ws.mergeCells(excelRow, 4, excelRow + childRowSpan - 1, 4); // child_code
          ws.mergeCells(excelRow, 5, excelRow + childRowSpan - 1, 5); // inner_weight
          ws.mergeCells(excelRow, 6, excelRow + childRowSpan - 1, 6); // inner_dims
        }
      });
    } catch {}

    const totalRow = ws.addRow(hasHierarchy
      ? {
        parent_code: 'TOTAL',
        outer_weight: '',
        outer_dims: '',
        child_code: '',
        inner_weight: '',
        inner_dims: '',
        target_code: '',
        target_content_info: '',
        target_qty: totalQty,
      }
      : {
        parent_code: 'TOTAL',
        outer_weight: '',
        outer_dims: '',
        target_code: '',
        target_content_info: '',
        target_qty: totalQty,
      });
    totalRow.height = 22;
    const unoperableRow = ws.addRow(hasHierarchy
      ? {
        parent_code: '无法操作产品总数',
        outer_weight: '',
        outer_dims: '',
        child_code: '',
        inner_weight: '',
        inner_dims: '',
        target_code: '',
        target_content_info: '',
        target_qty: unoperableTotalQty,
      }
      : {
        parent_code: '无法操作产品总数',
        outer_weight: '',
        outer_dims: '',
        target_code: '',
        target_content_info: '',
        target_qty: unoperableTotalQty,
      });
    unoperableRow.height = 22;
    try {
      const qtyCol = hasHierarchy ? 9 : 6;
      const qtyCell = totalRow.getCell(qtyCol);
      qtyCell.style = {
        ...(qtyCell.style || {}),
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE58F' } },
        font: { color: { argb: 'FFCF1322' }, bold: true },
        alignment: { vertical: 'middle', horizontal: 'center' },
      };
      const titleCell = totalRow.getCell(1);
      titleCell.font = { ...(titleCell.font || {}), bold: true };
      const unTitleCell = unoperableRow.getCell(1);
      unTitleCell.font = { ...(unTitleCell.font || {}), color: { argb: 'FFD4380D' }, bold: true } as any;
      const unQtyCell = unoperableRow.getCell(qtyCol);
      unQtyCell.style = {
        ...(unQtyCell.style || {}),
        fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2E8' } },
        font: { color: { argb: 'FFFA541C' }, bold: true },
        alignment: { vertical: 'middle', horizontal: 'center' },
      };
    } catch {}

    try {
      const border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      } as any;
      for (let r = 1; r <= ws.rowCount; r += 1) {
        const row = ws.getRow(r);
        for (let c = 1; c <= ws.columnCount; c += 1) {
          const cell = row.getCell(c);
          cell.alignment = { vertical: 'middle', horizontal: 'center' };
          cell.border = border;
        }
      }
    } catch {}

    // 独立 sheet：按操作对象统计（与弹窗 tab 对齐）
    try {
      const wsTarget = wb.addWorksheet('按操作对象统计');
      wsTarget.columns = [
        { header: '操作对象编码', key: 'target_code', width: 34 },
        { header: '操作对象所含内容物', key: 'target_content_info', width: 48 },
        { header: '目标数量', key: 'target_qty', width: 14 },
        { header: '已装入容器', key: 'done_qty', width: 16 },
      ];
      const payload = (regularOutboundPackingPayload && typeof regularOutboundPackingPayload === 'object')
        ? regularOutboundPackingPayload
        : ({} as AnyObj);
      const targetRowsRaw = Array.isArray(payload?.byTargetRows) ? payload.byTargetRows : [];
      let targetQtyTotal = 0;
      let doneQtyTotal = 0;
      const targetRows = targetRowsRaw.map((r: AnyObj, idx: number) => ({
        __k: String(r?.key || `${resolveRegularOutboundTargetCode(r)}-${idx}`),
        target_code: resolveRegularOutboundTargetCode(r),
        target_content_info: resolveRegularOutboundTargetContentInfo(r),
        target_qty: String(r?.target_qty ?? '-'),
        done_qty: String(r?.done_qty ?? r?.picked_qty ?? '-'),
        __target_qty_num: resolveRegularOutboundTargetQtyValue(r),
        __done_qty_num: resolveRegularOutboundDoneQtyValue(r),
      }));
      targetRows.forEach((r: AnyObj) => {
        targetQtyTotal += Math.max(0, Math.floor(Number(r?.__target_qty_num || 0) || 0));
        doneQtyTotal += Math.max(0, Math.floor(Number(r?.__done_qty_num || 0) || 0));
      });
      targetRows.forEach((r: AnyObj) => wsTarget.addRow(r));
      const totalRow = wsTarget.addRow({
        target_code: 'TOTAL',
        target_content_info: '',
        target_qty: targetQtyTotal,
        done_qty: doneQtyTotal,
      });
      totalRow.height = 22;
      const unoperableRow = wsTarget.addRow({
        target_code: '无法操作产品总数',
        target_content_info: '',
        target_qty: '',
        done_qty: unoperableTotalQty,
      });
      unoperableRow.height = 22;
      const border = {
        top: { style: 'thin' },
        left: { style: 'thin' },
        bottom: { style: 'thin' },
        right: { style: 'thin' },
      } as any;
      for (let rr = 1; rr <= wsTarget.rowCount; rr += 1) {
        const row = wsTarget.getRow(rr);
        for (let cc = 1; cc <= wsTarget.columnCount; cc += 1) {
          const cell = row.getCell(cc);
          cell.alignment = { vertical: 'middle', horizontal: cc >= 3 ? 'center' : 'left', wrapText: cc === 2 };
          cell.border = border;
        }
      }
      const header = wsTarget.getRow(1);
      header.height = 22;
      header.font = { bold: true };
      header.alignment = { vertical: 'middle', horizontal: 'center' };
      try {
        const titleCell = totalRow.getCell(1);
        titleCell.font = { ...(titleCell.font || {}), bold: true };
        const qtyCell = totalRow.getCell(3);
        qtyCell.style = {
          ...(qtyCell.style || {}),
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFE58F' } },
          font: { color: { argb: 'FFCF1322' }, bold: true },
          alignment: { vertical: 'middle', horizontal: 'center' },
        };
        const doneCell = totalRow.getCell(4);
        doneCell.style = {
          ...(doneCell.style || {}),
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE6F4FF' } },
          font: { color: { argb: 'FF0958D9' }, bold: true },
          alignment: { vertical: 'middle', horizontal: 'center' },
        };
        const unTitleCell = unoperableRow.getCell(1);
        unTitleCell.font = { ...(unTitleCell.font || {}), color: { argb: 'FFD4380D' }, bold: true } as any;
        const unDoneCell = unoperableRow.getCell(4);
        unDoneCell.style = {
          ...(unDoneCell.style || {}),
          fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFFF2E8' } },
          font: { color: { argb: 'FFFA541C' }, bold: true },
          alignment: { vertical: 'middle', horizontal: 'center' },
        };
      } catch {}
    } catch {}

    const buf = await wb.xlsx.writeBuffer();
    const blob = new Blob([buf as any], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    return { blob, fileName };
  }, [regularOutboundContainerTable, regularOutboundPackingPayload, iocRows, exceptionIocCodeSet, instr, instrId, wrapUpExceptionContainersForDetail3]);

  const downloadRegularOutboundPackingExcel = useCallback(async () => {
    if (!instrId) return;
    try {
      setRegularOutboundPackingExporting(true);
      const { blob, fileName } = await buildRegularOutboundPackingXlsx();
      await downloadBlobAsFile(blob, fileName);
    } catch (e: any) {
      message.error(e?.message || e?.response?.data?.detail || '生成Excel失败');
    } finally {
      setRegularOutboundPackingExporting(false);
    }
  }, [instrId, buildRegularOutboundPackingXlsx, downloadBlobAsFile]);

  const addRegularOutboundPackingExcelToFinalReview = useCallback(async () => {
    if (!instrId) return;
    try {
      setRegularOutboundPackingAdding(true);
      const { blob, fileName } = await buildRegularOutboundPackingXlsx();
      const file = new File([blob], fileName, { type: blob.type });
      const fd = new FormData();
      fd.append('instruction', String(instrId));
      fd.append('label', 'final_review');
      fd.append('scope', 'order');
      fd.append('status', 'active');
      fd.append('file_path', fileName);
      fd.append('file', file);
      fd.append('file_name', fileName);
      await request.post('/api/v1/instruction/attachments/', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        transformRequest: [(d: any) => d],
      } as any);
      message.success('已加入终审附件（常规出库总包装数据Excel）');
      await loadFinalReviewAttachments();
    } catch (e: any) {
      message.error(e?.response?.data?.detail || JSON.stringify(e?.response?.data || {}) || e?.message || '加入终审附件失败');
    } finally {
      setRegularOutboundPackingAdding(false);
    }
  }, [instrId, buildRegularOutboundPackingXlsx, loadFinalReviewAttachments]);

  const iocRowsActive = useMemo(() => {
    const rows = Array.isArray(iocRows) ? iocRows : [];
    return rows.filter((r: any) => String(r?.status || '').trim().toLowerCase() !== 'cancelled');
  }, [iocRows]);

  const iocRowsNormalOnly = useMemo(() => {
    const rows = Array.isArray(iocRowsActive) ? iocRowsActive : [];
    // 优先使用收尾快照里固化的 ioc_normal_codes（更贴合“正常内容”口径）
    if (normalIocCodeSet.size > 0) {
      return rows.filter((r: any) => normalIocCodeSet.has(String(r?.code || '').trim()));
    }
    // 兜底：若拿不到 ioc_normal_codes，则按后端 content_kind 排除异常内容
    return rows.filter((r: any) => {
      const kind = String(r?.content_kind || '').toLowerCase();
      if (!kind) return true;
      return !(kind.includes('exception') || kind.includes('unoperable') || kind.includes('abnormal'));
    });
  }, [iocRowsActive, normalIocCodeSet]);

  // 异常内容 IOC：用于“未操作产品总数”统计（来自异常 IOC 容器中的内容物数量总和）
  const exceptionIocTotalQty = useMemo(() => {
    if (Array.isArray(wrapUpExceptionContainersForDetail3) && wrapUpExceptionContainersForDetail3.length > 0) {
      return wrapUpExceptionContainersForDetail3.reduce((sum: number, c: any) => {
        const tq = Number(c?.total_qty ?? 0);
        if (Number.isFinite(tq) && tq > 0) return sum + Math.floor(tq);
        const allocs = Array.isArray(c?.allocations) ? c.allocations : [];
        const byAlloc = allocs.reduce((acc: number, a: any) => acc + Math.max(0, Math.floor(Math.abs(Number(a?.quantity ?? a?.qty ?? 0) || 0))), 0);
        return sum + byAlloc;
      }, 0);
    }
    const rows = Array.isArray(iocRowsActive) ? iocRowsActive : [];
    const exRows = (() => {
      if (exceptionIocCodeSet.size > 0) {
        return rows.filter((r: any) => exceptionIocCodeSet.has(String(r?.code || '').trim()));
      }
      // 兜底：按 content_kind 判断异常
      return rows.filter((r: any) => {
        const kind = String(r?.content_kind || '').toLowerCase();
        return !!kind && (kind.includes('exception') || kind.includes('unoperable') || kind.includes('abnormal'));
      });
    })();
    let sum = 0;
    try {
      exRows.forEach((r: any) => {
        const contents = Array.isArray(r?.contents) ? r.contents : [];
        contents.forEach((c: any) => {
          const q = Math.floor(Number(c?.quantity || 0) || 0);
          if (q > 0) sum += q;
        });
      });
    } catch {}
    return sum;
  }, [iocRowsActive, exceptionIocCodeSet, wrapUpExceptionContainersForDetail3]);

  const normalIocTotalQty = useMemo(() => {
    const rows = Array.isArray(iocRowsNormalOnly) ? iocRowsNormalOnly : [];
    let sum = 0;
    try {
      rows.forEach((r: any) => {
        const contents = Array.isArray(r?.contents) ? r.contents : [];
        contents.forEach((c: any) => {
          const q = Math.floor(Number(c?.quantity || 0) || 0);
          if (q > 0) sum += q;
        });
      });
    } catch {}
    return sum;
  }, [iocRowsNormalOnly]);

  const iocNewBarcodeFacetRowsWithUnoperableSummary = useMemo(() => {
    const rows = (Array.isArray(iocNewBarcodeFacetRows) ? iocNewBarcodeFacetRows : []).map((r: AnyObj) => ({
      ...r,
      __ioc_kind: 'normal',
      __container_seq: Math.floor(Number(r?.__group_index ?? 0) || 0) + 1,
    }));
    const allRows = Array.isArray(iocRowsActive) ? iocRowsActive : [];
    const exceptionIocRows = (() => {
      const baseFromWrap = (Array.isArray(wrapUpExceptionContainersForDetail3) ? wrapUpExceptionContainersForDetail3 : [])
        .map((c: AnyObj) => ({
          code: String(c?.code || '').trim(),
          total_qty: Math.max(0, Math.floor(Number(c?.total_qty || 0) || 0)),
          weight_g: Number(c?.exception_total_weight_g || 0) || 0,
          contents: Array.isArray(c?.allocations) ? c.allocations.map((a: any) => ({
            quantity: Math.max(0, Math.floor(Math.abs(Number(a?.quantity ?? a?.qty ?? 0) || 0))),
          })) : [],
        }))
        .filter((x: AnyObj) => !!String(x?.code || '').trim());
      const base = baseFromWrap.length > 0
        ? baseFromWrap
        : (
          (exceptionIocCodeSet.size > 0)
            ? allRows.filter((r: any) => exceptionIocCodeSet.has(String(r?.code || '').trim()))
            : allRows.filter((r: any) => {
                const kind = String(r?.content_kind || '').toLowerCase();
                return !!kind && (kind.includes('exception') || kind.includes('unoperable') || kind.includes('abnormal'));
              })
        );
      const normalContainerCount = Array.from(new Set(
        rows.map((x: AnyObj) => Math.floor(Number(x?.__container_seq ?? 0) || 0)).filter((n: number) => n > 0)
      )).length;
      return base
        .map((oc: AnyObj, idx: number) => {
          const cc = String(oc?.code || '').trim();
          if (!cc) return null;
          const contents = Array.isArray(oc?.contents) ? oc.contents : [];
          const totalRaw = Number(oc?.total_qty ?? 0);
          const total = (Number.isFinite(totalRaw) && totalRaw > 0)
            ? Math.max(0, Math.floor(totalRaw))
            : contents.reduce((acc: number, c: any) => acc + (Math.floor(Number(c?.quantity || 0) || 0)), 0);
          const g = Number(oc?.weight_g);
          const kg = (Number.isFinite(g) && g > 0) ? Math.ceil((g / 1000) * 10) / 10 : null;
          const L = Number(oc?.length_cm);
          const W = Number(oc?.width_cm);
          const H = Number(oc?.height_cm);
          const dims = ([L, W, H].every((x) => Number.isFinite(x) && x > 0))
            ? `${L}×${W}×${H}`
            : '';
          return {
            __k: `__exception_ioc_${idx}_${cc}`,
            __is_exception_ioc: true,
            __row_span: 1,
            __group_index: 100000 + idx,
            __ioc_kind: 'exception',
            __container_seq: normalContainerCount + idx + 1,
            container_code: cc,
            container_type_text: '异常内容容器',
            new_barcode: '-',
            total_quantity: total,
            weight_kg: kg,
            dims_cm: dims,
          } as AnyObj;
        })
        .filter(Boolean) as AnyObj[];
    })();
    return [...rows, ...exceptionIocRows];
  }, [iocNewBarcodeFacetRows, iocRowsActive, exceptionIocCodeSet, wrapUpExceptionContainersForDetail3]);

  const iocRowsNormalWithUnoperableSummary = useMemo(() => {
    const normalList = (Array.isArray(iocRowsNormalOnly) ? iocRowsNormalOnly : []).map((r: AnyObj, idx: number) => ({ ...r, __ioc_kind: 'normal', __container_seq: idx + 1 }));
    const allRows = Array.isArray(iocRowsActive) ? iocRowsActive : [];
    const exceptionList = (() => {
      if (Array.isArray(wrapUpExceptionContainersForDetail3) && wrapUpExceptionContainersForDetail3.length > 0) {
        return wrapUpExceptionContainersForDetail3.map((c: AnyObj) => ({
          code: String(c?.code || '').trim(),
          contents: Array.isArray(c?.allocations) ? c.allocations.map((a: any) => ({
            quantity: Math.max(0, Math.floor(Math.abs(Number(a?.quantity ?? a?.qty ?? 0) || 0))),
            inventory_unit_code: String(a?.unit_code || a?.inventory_unit_code || '').trim(),
          })) : [],
          total_qty: Math.max(0, Math.floor(Number(c?.total_qty || 0) || 0)),
          content_kind: 'exception',
        }));
      }
      if (exceptionIocCodeSet.size > 0) {
        return allRows.filter((r: any) => exceptionIocCodeSet.has(String(r?.code || '').trim()));
      }
      // 兜底：按 content_kind 判断异常
      return allRows.filter((r: any) => {
        const kind = String(r?.content_kind || '').toLowerCase();
        return !!kind && (kind.includes('exception') || kind.includes('unoperable') || kind.includes('abnormal'));
      });
    })().map((r: AnyObj, idx: number) => ({ ...r, __ioc_kind: 'exception', __container_seq: normalList.length + idx + 1 }));
    return [...normalList, ...exceptionList];
  }, [iocRowsNormalOnly, iocRowsActive, exceptionIocCodeSet, wrapUpExceptionContainersForDetail3]);

  const statusTextCN = useMemo(() => {
    const st = String((instr as any)?.status || '').trim().toLowerCase();
    if (!st) return '-';
    if (st === 'operation_done') return '操作阶段已完成 待终审';
    if (st === 'pending') return '待处理';
    if (st === 'in_progress') return '进行中';
    if (st === 'done') return '已完成';
    return String((instr as any)?.status || '-');
  }, [instr]);
  const templateDisplayText = useMemo(() => {
    const name = String((instr as any)?.template_detail?.name || (instr as any)?.template_snapshot?.name || '').trim();
    const code = String((instr as any)?.template_detail?.system_code || (instr as any)?.template_snapshot?.system_code || (instr as any)?.template_detail?.code || (instr as any)?.template_snapshot?.code || '').trim();
    if (!name && !code) return '';
    if (name && code) return `${name} · ${code}`;
    return name || code;
  }, [instr]);
  const finalReviewTemplateFamily = useMemo(() => {
    const tplName = String((instr as any)?.template_detail?.name || (instr as any)?.template_snapshot?.name || '').trim().toLowerCase();
    const tplCode = String((instr as any)?.template_detail?.system_code || (instr as any)?.template_snapshot?.system_code || (instr as any)?.template_detail?.code || (instr as any)?.template_snapshot?.code || '').trim().toLowerCase();
    const merged = `${tplName} ${tplCode}`;
    if (merged.includes('sort_count') || merged.includes('sort-and-count') || merged.includes('sort_and_count') || merged.includes('分拣清点')) return 'sort_count';
    if (merged.includes('relabel') || (merged.includes('换标') && merged.includes('装箱'))) return 'relabel_pack';
    if (merged.includes('single_dropship') || merged.includes('一件代发')) return 'single_dropship';
    if (merged.includes('regular') && merged.includes('outbound')) return 'regular_outbound';
    if (merged.includes('批量') || merged.includes('batch')) return 'batch';
    return 'unknown';
  }, [instr]);
  const finalReviewTemplateFamilyText = useMemo(() => {
    if (finalReviewTemplateFamily === 'sort_count') return '分拣清点';
    if (finalReviewTemplateFamily === 'relabel_pack') return '换标装箱';
    if (finalReviewTemplateFamily === 'single_dropship') return '一件代发';
    if (finalReviewTemplateFamily === 'regular_outbound') return '常规出库';
    if (finalReviewTemplateFamily === 'batch') return '批量处理';
    return '未识别';
  }, [finalReviewTemplateFamily]);
  const inventorySettlementCurrent = effectiveInventorySettlement;
  const finalReviewInventoryActionRows = useMemo(() => {
    const settlement = (inventorySettlementCurrent && typeof inventorySettlementCurrent === 'object') ? inventorySettlementCurrent : {};
    const actionsRaw = Array.isArray(settlement?.actions) ? settlement.actions : [];
    const toSummaryText = (v: any): string => {
      if (!v || typeof v !== 'object') return '-';
      const pairs = Object.entries(v)
        .filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
        .slice(0, 4)
        .map(([k, value]) => `${k}:${String(value)}`);
      return pairs.length > 0 ? pairs.join(' | ') : '-';
    };
    const statusRank = (sRaw: string): number => {
      const s = String(sRaw || '').trim().toLowerCase();
      if (s === 'failed' || s === 'blocked') return 5;
      if (s === 'applied' || s === 'success') return 4;
      if (s === 'applicable' || s === 'preview_ready') return 3;
      if (s === 'skipped') return 2;
      if (s === 'not_applicable') return 1;
      return 0;
    };
    const statusText = (sRaw: string): string => {
      const s = String(sRaw || '').trim().toLowerCase();
      if (s === 'failed') return '执行失败';
      if (s === 'blocked') return '预览阻断';
      if (s === 'applied' || s === 'success') return '已执行';
      if (s === 'applicable' || s === 'preview_ready') return '预览通过';
      if (s === 'skipped' || s === 'not_applicable') return '已跳过';
      return '待预览';
    };
    const pickByKeys = (keys: string[]): AnyObj[] => {
      const keySet = new Set((keys || []).map((k) => String(k || '').trim()).filter(Boolean));
      return (actionsRaw as AnyObj[]).filter((a: any) => keySet.has(String(a?.action_key || '').trim()));
    };
    const buildRowFromMatches = (cfg: { key: string; title: string; keys: string[]; emptyNote: string }) => {
      const matched = pickByKeys(cfg.keys);
      if (!matched.length) {
        return {
          __k: cfg.key,
          action: cfg.title,
          status: '待预览',
          summary: cfg.emptyNote,
          blocked_reason: '-',
          detail_rows: [] as AnyObj[],
        };
      }
      const st = matched
        .map((a: any) => String(a?.status || '').trim())
        .sort((a: string, b: string) => statusRank(b) - statusRank(a))[0] || '';
      const summary = matched
        .map((a: any) => {
          const head = String(a?.title || a?.action_key || '').trim();
          const body = toSummaryText(a?.summary);
          return head ? `${head}: ${body}` : body;
        })
        .filter(Boolean)
        .join(' | ');
      const blocked = matched.map((a: any) => String(a?.blocked_reason || '').trim()).filter(Boolean).join(' | ');
      return {
        __k: cfg.key,
        action: cfg.title,
        status: statusText(st),
        summary: summary || '-',
        blocked_reason: blocked || '-',
        detail_rows: matched.flatMap((a: any) => (Array.isArray(a?.rows) ? a.rows : [])),
      };
    };

    const rows: AnyObj[] = [
      buildRowFromMatches({
        key: 'quantity_adjustment',
        title: '库存数量修正',
        keys: ['quantity_correction', 'quantity_adjustment', 'need1_qty_adjustment'],
        emptyNote: '点击“预览库存落库”后展示结果',
      }),
      buildRowFromMatches({
        key: 'unoperable_to_aiu',
        title: '无法操作产品转AIU',
        keys: ['unoperable_to_aiu'],
        emptyNote: '点击“预览库存落库”后展示结果',
      }),
      buildRowFromMatches({
        key: 'execution_reservation_settlement',
        title: '执行期占用结算',
        keys: hasSortCountStage
          ? ['reservation_finalize', 'need1_reservation_release', 'ioc_reservation_finalize', 'source_deduction']
          : ['reservation_finalize', 'need1_reservation_release', 'ioc_reservation_finalize'],
        emptyNote: '点击“预览库存落库”后展示结果',
      }),
    ];
    if (hasSortCountStage) {
      rows.push(
        buildRowFromMatches({
          key: 'group_adjustment',
          title: '归组调整',
          keys: ['group_adjustment'],
          emptyNote: '分拣清点专属项',
        }),
        buildRowFromMatches({
          key: 'generated_unit_inbound',
          title: '新建IU初始入库',
          keys: ['generated_unit_inbound'],
          emptyNote: '分拣清点专属项（含初始分拣组/初始库龄）',
        }),
        buildRowFromMatches({
          key: 'fifo_weighted_age',
          title: '来源IU库龄桶切桶',
          keys: ['fifo_weighted_age'],
          emptyNote: '仅分拣需求2/3适用',
        }),
      );
    }
    return rows;
  }, [inventorySettlementCurrent, hasSortCountStage]);
  const inventoryPreviewSummary = useMemo(() => {
    if (!sortCountInvPreviewShown) return null;
    const statusSettlement = (effectiveInventorySettlementForStatus && typeof effectiveInventorySettlementForStatus === 'object')
      ? (effectiveInventorySettlementForStatus as AnyObj)
      : {};
    const settlement = (inventorySettlementCurrent && typeof inventorySettlementCurrent === 'object')
      ? (inventorySettlementCurrent as AnyObj)
      : {};
    const summary = (statusSettlement?.summary && typeof statusSettlement.summary === 'object')
      ? (statusSettlement.summary as AnyObj)
      : {};
    const actions = Array.isArray(settlement?.actions) ? (settlement.actions as AnyObj[]) : [];
    const blockedReasons = actions
      .filter((a: any) => {
        const s = String(a?.status || '').trim().toLowerCase();
        return s === 'blocked' || s === 'failed';
      })
      .map((a: any) => String(a?.blocked_reason || '').trim())
      .filter((x: string) => !!x);
    const merged = Array.from(new Set(blockedReasons));
    const statusRaw = String(summary?.status || '').trim().toLowerCase();
    const canApplyRaw = typeof summary?.can_apply === 'boolean'
      ? !!summary.can_apply
      : (statusRaw === 'applied' || statusRaw === 'success' || statusRaw === 'applicable' || statusRaw === 'preview_ready');
    const canApplyText = (statusRaw === 'applied' || statusRaw === 'success')
      ? '已执行'
      : (canApplyRaw === true ? '可执行' : '不可执行');
    const errorText = String(sortCountInvPreviewError || '').trim();
    return {
      canApplyText,
      canApplyRaw,
      statusRaw,
      blockedReasonText: (statusRaw === 'applied' || statusRaw === 'success') ? '' : merged.join(' | '),
      errorText,
    };
  }, [sortCountInvPreviewShown, inventorySettlementCurrent, effectiveInventorySettlementForStatus, sortCountInvPreviewError]);
  const quantityCorrectionDebugView = useMemo(() => {
    const settlement = (inventorySettlementCurrent && typeof inventorySettlementCurrent === 'object')
      ? (inventorySettlementCurrent as AnyObj)
      : {};
    const actions = Array.isArray(settlement?.actions) ? (settlement.actions as AnyObj[]) : [];
    const qtyAction = actions.find((a: any) => {
      const k = String(a?.action_key || '').trim();
      return k === 'quantity_correction' || k === 'quantity_adjustment' || k === 'need1_qty_adjustment';
    }) || null;
    const qtyRows = Array.isArray(qtyAction?.rows) ? (qtyAction?.rows as AnyObj[]) : [];
    const reservationAction = actions.find((a: any) => {
      const k = String(a?.action_key || '').trim();
      return k === 'reservation_finalize';
    }) || null;
    const reservationRows = Array.isArray(reservationAction?.rows) ? (reservationAction?.rows as AnyObj[]) : [];
    const reservationByStatus = reservationRows.reduce((acc: Record<string, number>, row: any) => {
      const k = String(row?.status || row?.row_status || '-').trim() || '-';
      acc[k] = Number(acc[k] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    const byStatus = qtyRows.reduce((acc: Record<string, number>, row: any) => {
      const k = String(row?.status || row?.row_status || '-').trim() || '-';
      acc[k] = Number(acc[k] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return {
      visible: true,
      templateFamily: finalReviewTemplateFamily,
      settlementDebugBuildTag: String(
        settlement?.debug_build_tag
        || ((settlement?.summary && typeof settlement.summary === 'object') ? settlement.summary?.debug_build_tag : '')
        || '-',
      ).trim() || '-',
      actionStatus: String(qtyAction?.status || '-').trim() || '-',
      blockedReason: String(qtyAction?.blocked_reason || '-').trim() || '-',
      summary: (qtyAction?.summary && typeof qtyAction.summary === 'object') ? (qtyAction.summary as AnyObj) : {},
      rowCount: qtyRows.length,
      byStatus,
      rows: qtyRows.slice(0, 200),
      reservationActionStatus: String(reservationAction?.status || '-').trim() || '-',
      reservationBlockedReason: String(reservationAction?.blocked_reason || '-').trim() || '-',
      reservationSummary: (reservationAction?.summary && typeof reservationAction.summary === 'object') ? (reservationAction.summary as AnyObj) : {},
      reservationRowCount: reservationRows.length,
      reservationByStatus,
      reservationRows: reservationRows.slice(0, 300),
    };
  }, [inventorySettlementCurrent, finalReviewTemplateFamily]);
  const defaultCollapseSettlementPanel = finalReviewInventoryActionRows.length <= 0;
  const defaultCollapseBaselinePanel = !baselinesLoading && (!Array.isArray(baselineFiles) || baselineFiles.length <= 0);
  const defaultCollapseStagePanel = !stageSubsLoading && (!Array.isArray(stageSubs) || stageSubs.length <= 0);
  const defaultCollapseDetail3Panel = hasSortCountStage
    ? ((Array.isArray(sortCountOperationTargetRows) ? sortCountOperationTargetRows.length : 0) <= 0
      && (Array.isArray(sortCountPresetGroupRows) ? sortCountPresetGroupRows.length : 0) <= 0)
    : (!iocLoading && (!Array.isArray(iocRowsActive) || iocRowsActive.length <= 0));
  const isDetailPanelCollapsed = useCallback((key: string, defaultCollapsed: boolean): boolean => {
    const ov = detailPanelCollapsedOverride[key];
    if (typeof ov === 'boolean') return ov;
    return defaultCollapsed;
  }, [detailPanelCollapsedOverride]);
  const toggleDetailPanelCollapsed = useCallback((key: string, defaultCollapsed: boolean) => {
    setDetailPanelCollapsedOverride((prev) => {
      const current = (typeof prev[key] === 'boolean') ? !!prev[key] : !!defaultCollapsed;
      return { ...prev, [key]: !current };
    });
  }, []);
  const settlementPanelCollapsed = isDetailPanelCollapsed('settlement', defaultCollapseSettlementPanel);
  const baselinePanelCollapsed = isDetailPanelCollapsed('baseline', defaultCollapseBaselinePanel);
  const stagePanelCollapsed = isDetailPanelCollapsed('stage', defaultCollapseStagePanel);
  const detail3PanelCollapsed = isDetailPanelCollapsed('detail3', defaultCollapseDetail3Panel);
  const showLegacySortCountInvInDetail3 = false;
  const renderSortCountInventorySettlementPanel = () => (
    <div style={{ marginBottom: 18, padding: '8px 12px', borderRadius: 8, border: '1px solid #4096ff', background: '#f0f9ff' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <Space size={12} wrap>
          <span style={{ color: '#595959' }}>库存落库状态:</span>
          <Tag color={sortCountInventoryWritebackStatusText === '成功' ? 'success' : (sortCountInventoryWritebackStatusText === '失败' ? 'error' : 'default')}>
            {sortCountInventoryWritebackStatusText}
          </Tag>
        </Space>
        <Space size={8} wrap style={{ justifyContent: 'flex-end' }}>
          <Tooltip title={sortCountInvPreviewDisabledReason}>
            <span style={{ display: 'inline-flex' }}>
              <Button
                size="small"
                icon={<ReloadOutlined />}
                loading={sortCountInvPreviewLoading}
                onClick={() => { void previewSortCountInventoryWriteback(); }}
                disabled={!instrId || sortCountInvAppliedSuccess}
              >
                预览库存落库
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            type={sortCountInvAppliedSuccess ? 'default' : 'primary'}
            loading={sortCountInvApplying}
            onClick={() => { void applySortCountInventoryWriteback(); }}
            disabled={!instrId || sortCountInvApplying || sortCountInvAppliedSuccess}
          >
            {sortCountInvAppliedSuccess
              ? '库存落库已执行'
              : (sortCountInventoryWritebackStatusRaw === 'failed' ? '重试库存落库' : '执行库存落库')}
          </Button>
          <span style={{ cursor: 'pointer', color: '#1677ff', display: 'inline-flex', alignItems: 'center', padding: '0 2px' }} onClick={() => setSortCountInvPanelCollapsed((v) => !v)}>
            {sortCountInvPanelCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
          </span>
        </Space>
      </div>
      {!sortCountInvPanelCollapsed ? (
        <>
          <div style={{ marginTop: 8, padding: '8px 10px', border: '1px solid #d9d9d9', borderRadius: 6, background: '#fafafa' }}>
            <div style={{ color: '#595959', fontSize: 12, marginBottom: 4, fontWeight: 600 }}>指令事实信息</div>
            <div style={{ color: '#8c8c8c', fontSize: 12, lineHeight: 1.8 }}>
              {(() => {
                const st = (inventorySettlementCurrent && typeof inventorySettlementCurrent === 'object')
                  ? (inventorySettlementCurrent as AnyObj)
                  : {};
                const ss = (st?.summary && typeof st.summary === 'object') ? (st.summary as AnyObj) : {};
                const statusRaw = String(ss?.status || '').trim().toLowerCase();
                const modeRaw = String(ss?.mode || '').trim();
                const atRaw = String(ss?.applied_at || '').trim();
                const atText = atRaw ? String(fmtDateTime(atRaw) || '-') : '-';
                const statusText = statusRaw === 'applied'
                  ? '成功'
                  : (statusRaw === 'failed' ? '失败' : (statusRaw === 'preview_ready' ? '已预览' : '未执行'));
                if (statusRaw !== 'applied' && statusRaw !== 'failed' && statusRaw !== 'preview_ready') {
                  return '尚未执行库存落库。';
                }
                const line1 = `最近状态: ${statusText} | 执行时间: ${atText} | 结算模式: ${modeRaw || '-'}`;
                const actions = Array.isArray(st?.actions) ? (st.actions as AnyObj[]) : [];
                const mapped = actions
                  .slice(0, 4)
                  .map((a: any) => `${String(a?.title || a?.action_key || '-')}(${String(a?.status || '-')})`)
                  .join(' | ');
                const line2 = `动作结果: ${mapped || '-'}`;
                return (
                  <span>
                    <span>{line1}</span>
                    <span> | {line2}</span>
                  </span>
                );
              })()}
            </div>
          </div>
          {sortCountInvPreviewShown ? (
            <div style={{ marginTop: 8, padding: '8px 10px', border: '1px solid #b7eb8f', borderRadius: 6, background: '#f6ffed' }}>
              <div style={{ color: '#237804', fontSize: 12, marginBottom: 4, fontWeight: 600 }}>库存落库预览结果</div>
              <div style={{ color: '#595959', fontSize: 12, lineHeight: 1.8 }}>
                {(() => {
                  const ssrc = (inventorySettlementCurrent && typeof inventorySettlementCurrent === 'object')
                    ? (inventorySettlementCurrent as AnyObj)
                    : {};
                  const s = (ssrc?.summary && typeof ssrc.summary === 'object') ? (ssrc.summary as AnyObj) : {};
                  const canApply = s?.can_apply === true ? '可执行' : (s?.can_apply === false ? '不可执行' : '-');
                  const mode = String(s?.mode || '-');
                  const weighted = s?.weighted_source_age_days == null ? '-' : `${Number(s?.weighted_source_age_days || 0)}天`;
                  const insuff = Array.isArray((inventorySettlementActionMap?.insufficient as AnyObj)?.rows)
                    ? (inventorySettlementActionMap?.insufficient as AnyObj).rows.length
                    : 0;
                  const weightedAge = s?.weighted_source_age_days == null ? '-' : Number(s?.weighted_source_age_days || 0);
                  return (
                    <span>
                      {`预估扣减总量:${Number(s?.estimated_source_deduction_total || 0)} | 分拣结果总量:${Number(s?.sorted_total_qty || 0)} | 异常数量:${Number(s?.exception_total_qty || 0)} | 加权库龄:`}
                      {weightedAge === '-' ? '-' : (
                        <Tooltip title={sortCountInvWeightedAgeFormulaText}>
                          <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>{`${weightedAge}天`}</span>
                        </Tooltip>
                      )}
                      {` | 预览结果:${canApply} | 需求模式:${mode} | 不足项:${insuff}`}
                      {weighted === '-' ? '' : ' '}
                    </span>
                  );
                })()}
              </div>
            </div>
          ) : (
            <div style={{ marginTop: 8, color: '#8c8c8c', fontSize: 12 }}>
              点击“预览库存落库”后显示预览结果。
            </div>
          )}
          {sortCountInvPreviewShown && sortCountInvPreviewError ? (
            <div style={{ marginTop: 6, color: '#cf1322', whiteSpace: 'normal', wordBreak: 'break-word' }}>
              预览错误: {sortCountInvPreviewError}
            </div>
          ) : null}
          {quantityCorrectionDebugView.visible ? (
            <div style={{ marginTop: 8, padding: '8px 10px', border: '1px dashed #faad14', borderRadius: 6, background: '#fffbe6' }}>
              <div style={{ color: '#ad6800', fontSize: 12, marginBottom: 4, fontWeight: 600 }}>
                调试信息（常规出库-库存数量修正）
              </div>
              <div style={{ color: '#595959', fontSize: 12, lineHeight: 1.8 }}>
                {`模板=${quantityCorrectionDebugView.templateFamily} | action_status=${quantityCorrectionDebugView.actionStatus} | rows=${quantityCorrectionDebugView.rowCount} | by_status=${JSON.stringify(quantityCorrectionDebugView.byStatus)} | blocked_reason=${quantityCorrectionDebugView.blockedReason}`}
              </div>
              <div style={{ marginTop: 6 }}>
                <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>summary:</div>
                <pre style={{ margin: 0, padding: 8, background: '#fff', border: '1px solid #f0f0f0', borderRadius: 4, maxHeight: 140, overflow: 'auto', fontSize: 12, lineHeight: 1.5 }}>
                  {JSON.stringify(quantityCorrectionDebugView.summary, null, 2)}
                </pre>
              </div>
              <div style={{ marginTop: 6 }}>
                <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>rows(最多展示200条):</div>
                <pre style={{ margin: 0, padding: 8, background: '#fff', border: '1px solid #f0f0f0', borderRadius: 4, maxHeight: 260, overflow: 'auto', fontSize: 12, lineHeight: 1.5 }}>
                  {JSON.stringify(quantityCorrectionDebugView.rows, null, 2)}
                </pre>
              </div>
            </div>
          ) : null}
          {sortCountInvPreviewShown && sortCountInvInsufficientRows.length > 0 ? (
            <div style={{ marginTop: 6, color: '#cf1322', whiteSpace: 'normal', wordBreak: 'break-word' }}>
              预检提示: 源库存锁定不足 {sortCountInvInsufficientRows.length} 项，执行库存落库会被阻断。
            </div>
          ) : null}
          {String(sortCountInventoryWriteback?.status || '').trim().toLowerCase() === 'failed' ? (
            <div style={{ marginTop: 6, color: '#cf1322', whiteSpace: 'normal', wordBreak: 'break-word' }}>
              错误: {String((inventorySettlementCurrent as AnyObj)?.summary?.error || sortCountInventoryWriteback?.error || '-')}
            </div>
          ) : null}
          <div style={{ marginTop: 8 }}>
            <div style={{ marginBottom: 8 }}>
              <Checkbox checked={sortCountInvShowEmptyItems} onChange={(e) => setSortCountInvShowEmptyItems(!!e?.target?.checked)}>
                显示空子项
              </Checkbox>
            </div>
            {(() => {
              const allItems = [
                {
                  key: 'group_adj',
                  showArrow: false,
                  label: renderSortCountInvAuditLabel('group_adj', '归组调整明细', sortCountInvGroupAdjustRows.length, '#0958d9'),
                  children: (
                    <Table
                      size="small"
                      rowKey={(r: any, idx?: number) => String(r?.inventory_unit_id || r?.inventory_unit_code || idx || 0)}
                      dataSource={sortCountInvGroupAdjustRows}
                      pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                      columns={[
                        { title: '状态', dataIndex: 'status', key: 'status', width: 90 },
                        { title: '库存单元', key: 'iu', width: 200, ellipsis: true, render: (_: any, row: any) => <span style={{ fontFamily: 'monospace' }}>{String(row?.inventory_unit_code || `IU#${row?.inventory_unit_id || '-'}`)}</span> },
                        {
                          title: '最终归属分拣组',
                          key: 'final_group_code',
                          width: 280,
                          ellipsis: true,
                          render: (_: any, row: any) => {
                            const finalCode = String(
                              row?.target_group_system_code
                              || row?.final_group_system_code
                              || row?.to_group_system_code
                              || row?.group_code
                              || '',
                            ).trim();
                            const finalName = String(
                              row?.target_group_name
                              || row?.final_group_name
                              || row?.to_group_name
                              || row?.group_name
                              || '',
                            ).trim();
                            const shown = finalCode
                              ? (finalName ? `${finalCode} (${finalName})` : finalCode)
                              : '-';
                            return <span style={{ fontFamily: 'monospace' }}>{shown}</span>;
                          },
                        },
                      ]}
                      scroll={{ x: 'max-content' as any }}
                      locale={{ emptyText: '暂无归组调整明细' }}
                    />
                  ),
                },
                {
                  key: 'need1_group_cleanup',
                  showArrow: false,
                  label: renderSortCountInvAuditLabel('need1_group_cleanup', '分拣需求1空分组清理明细', sortCountInvNeed1GroupCleanupRows.length, '#d46b08'),
                  children: (
                    <Table
                      size="small"
                      rowKey={(r: any, idx?: number) => String(r?.group_id || r?.group_system_code || idx || 0)}
                      dataSource={sortCountInvNeed1GroupCleanupRows}
                      pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                      columns={[
                        { title: '状态', dataIndex: 'status', key: 'status', width: 100 },
                        { title: '分组ID', dataIndex: 'group_id', key: 'group_id', width: 100, render: (v: any) => String(v ?? '-') },
                        { title: '分组编码', dataIndex: 'group_system_code', key: 'group_system_code', width: 200, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        { title: '库存单元数', dataIndex: 'unit_count', key: 'unit_count', width: 110, align: 'right' as const, render: (v: any) => String(v ?? '-') },
                        { title: '原因', dataIndex: 'reason', key: 'reason', ellipsis: true, render: (v: any) => String(v || '-') },
                      ]}
                      scroll={{ x: 'max-content' as any }}
                      locale={{ emptyText: '暂无need1空分组清理明细' }}
                    />
                  ),
                },
                {
                  key: 'need1_res_release',
                  showArrow: false,
                  label: renderSortCountInvAuditLabel('need1_res_release', '分拣需求1占用释放明细', sortCountInvNeed1ReservationReleaseRows.length, '#7c3aed'),
                  children: (
                    <Table
                      size="small"
                      rowKey={(r: any, idx?: number) => String(r?.reservation_id || idx || 0)}
                      dataSource={sortCountInvNeed1ReservationReleaseRows}
                      pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                      columns={[
                        { title: '状态', dataIndex: 'status', key: 'status', width: 100 },
                        { title: '锁定ID', dataIndex: 'reservation_id', key: 'reservation_id', width: 120, render: (v: any) => String(v ?? '-') },
                        { title: '库存单元ID', dataIndex: 'inventory_unit_id', key: 'inventory_unit_id', width: 130, render: (v: any) => String(v ?? '-') },
                        { title: '释放数量', dataIndex: 'released_qty', key: 'released_qty', width: 110, align: 'right' as const, render: (v: any) => String(v ?? '-') },
                      ]}
                      scroll={{ x: 'max-content' as any }}
                      locale={{ emptyText: '暂无need1占用释放明细' }}
                    />
                  ),
                },
                {
                  key: 'gen_inbound',
                  showArrow: false,
                  label: renderSortCountInvAuditLabel('gen_inbound', '新IU初始入库明细', sortCountInvGeneratedInboundRows.length, '#389e0d'),
                  children: (
                    <Table
                      size="small"
                      rowKey={(r: any, idx?: number) => String(r?.unit_code || idx || 0)}
                      dataSource={sortCountInvGeneratedInboundRows}
                      pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                      columns={[
                        { title: '状态', dataIndex: 'status', key: 'status', width: 90 },
                        { title: '新库存单元', dataIndex: 'unit_code', key: 'unit_code', width: 220, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        { title: '数量', dataIndex: 'qty', key: 'qty', width: 100, align: 'right' as const },
                        { title: '初始时间', dataIndex: 'received_at', key: 'received_at', width: 190, ellipsis: true, render: (v: any) => String(v ? fmtDateTime(v) : '-') },
                        { title: '原因', dataIndex: 'reason', key: 'reason', ellipsis: true, render: (v: any) => String(v || '-') },
                      ]}
                      scroll={{ x: 'max-content' as any }}
                      locale={{ emptyText: '暂无新IU入库明细' }}
                    />
                  ),
                },
                {
                  key: 'need1_adj',
                  showArrow: false,
                  label: renderSortCountInvAuditLabel('need1_adj', '分拣需求1数量修正明细', sortCountInvNeed1AdjustRows.length, '#ad6800'),
                  children: (
                    <Table
                      size="small"
                      rowKey={(r: any, idx?: number) => String(r?.inventory_unit_id || r?.target_code || idx || 0)}
                      dataSource={sortCountInvNeed1AdjustRows}
                      pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                      columns={[
                        { title: '状态', dataIndex: 'status', key: 'status', width: 90 },
                        { title: '目标库存单元', dataIndex: 'target_code', key: 'target_code', width: 220, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        { title: '修正数量', dataIndex: 'delta_qty', key: 'delta_qty', width: 120, align: 'right' as const },
                        { title: '原因', dataIndex: 'reason', key: 'reason', ellipsis: true, render: (v: any) => String(v || '-') },
                      ]}
                      scroll={{ x: 'max-content' as any }}
                      locale={{ emptyText: '暂无need1数量修正明细' }}
                    />
                  ),
                },
                {
                  key: 'unoperable_to_aiu',
                  showArrow: false,
                  label: renderSortCountInvAuditLabel('unoperable_to_aiu', '无法操作转AIU明细', sortCountInvUnoperableToAiuRows.length, '#722ed1'),
                  children: (
                    <Table
                      size="small"
                      rowKey={(r: any, idx?: number) => String(r?.source_inventory_unit_id || r?.inventory_unit_id || idx || 0)}
                      dataSource={sortCountInvUnoperableToAiuRows}
                      pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                      columns={[
                        { title: '状态', dataIndex: 'status', key: 'status', width: 90 },
                        {
                          title: '来源库存单元',
                          key: 'src',
                          width: 200,
                          ellipsis: true,
                          render: (_: any, row: any) => <span style={{ fontFamily: 'monospace' }}>{String(row?.source_inventory_unit_code || `IU#${row?.source_inventory_unit_id || row?.inventory_unit_id || '-'}`)}</span>,
                        },
                        {
                          title: 'AIU库存单元',
                          key: 'aiu',
                          width: 200,
                          ellipsis: true,
                          render: (_: any, row: any) => <span style={{ fontFamily: 'monospace' }}>{String(row?.aiu_unit_code || `IU#${row?.aiu_inventory_unit_id || '-'}`)}</span>,
                        },
                        { title: '转换数量', dataIndex: 'qty', key: 'qty', width: 110, align: 'right' as const, render: (v: any) => String(v ?? '-') },
                        { title: '原因', dataIndex: 'reason', key: 'reason', ellipsis: true, render: (v: any) => String(v || '-') },
                      ]}
                      scroll={{ x: 'max-content' as any }}
                      locale={{ emptyText: '暂无无法操作转AIU明细' }}
                    />
                  ),
                },
                {
                  key: 'insufficient',
                  showArrow: false,
                  label: renderSortCountInvAuditLabel('insufficient', '预检不足项', sortCountInvInsufficientRows.length, '#cf1322'),
                  children: (
                    <Table
                      size="small"
                      rowKey={(r: any, idx?: number) => String(r?.inventory_unit_id || r?.target_code || idx || 0)}
                      dataSource={sortCountInvInsufficientRows}
                      pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                      columns={[
                        { title: '库存单元ID', dataIndex: 'inventory_unit_id', key: 'inventory_unit_id', width: 130, render: (v: any) => String(v ?? '-') },
                        { title: '需扣减', dataIndex: 'required_qty', key: 'required_qty', width: 100, align: 'right' as const },
                        { title: '已锁定', dataIndex: 'reserved_qty', key: 'reserved_qty', width: 100, align: 'right' as const },
                        { title: '差额', dataIndex: 'gap_qty', key: 'gap_qty', width: 100, align: 'right' as const },
                        { title: '原因', dataIndex: 'reason', key: 'reason', ellipsis: true, render: (v: any) => String(v || '-') },
                      ]}
                      scroll={{ x: 'max-content' as any }}
                      locale={{ emptyText: '暂无不足项' }}
                    />
                  ),
                },
                {
                  key: 'weighted_age',
                  showArrow: false,
                  label: renderSortCountInvAuditLabel('weighted_age', 'FIFO库龄切桶明细', sortCountInvWeightedAgeRows.length, '#531dab'),
                  children: (
                    <Table
                      size="small"
                      rowKey={(_: any, idx?: number) => String(idx || 0)}
                      dataSource={sortCountInvWeightedAgeRows}
                      pagination={{ defaultPageSize: 5, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50'] }}
                      columns={[
                        {
                          title: '操作目标库存单元',
                          key: 'inventory_unit_code',
                          width: 180,
                          ellipsis: true,
                          render: (_: any, row: any) => {
                            const idKey = String(row?.inventory_unit_id ?? '').trim();
                            const code = idKey ? String(sortCountInvTargetCodeByIuId[idKey] || '').trim() : '';
                            return <span style={{ fontFamily: 'monospace' }}>{code || '-'}</span>;
                          },
                        },
                        { title: '层ID', dataIndex: 'layer_id', key: 'layer_id', width: 90, render: (v: any) => String(v ?? '-') },
                        { title: '层时间', dataIndex: 'layer_received_at', key: 'layer_received_at', width: 190, ellipsis: true, render: (v: any) => String(v ? fmtDateTime(v) : '-') },
                        { title: '层库龄(天)', dataIndex: 'layer_age_days', key: 'layer_age_days', width: 100, align: 'right' as const, render: (v: any) => String(v ?? '-') },
                        { title: '层剩余', dataIndex: 'layer_remaining_qty', key: 'layer_remaining_qty', width: 90, align: 'right' as const, render: (v: any) => String(v ?? '-') },
                        { title: '本次取用', dataIndex: 'taken_qty', key: 'taken_qty', width: 90, align: 'right' as const, render: (v: any) => String(v ?? '-') },
                        { title: '需处理', dataIndex: 'needed_qty', key: 'needed_qty', width: 90, align: 'right' as const, render: (v: any) => String(v ?? '-') },
                        { title: '缺口', dataIndex: 'missing_qty', key: 'missing_qty', width: 90, align: 'right' as const, render: (v: any) => String(v ?? '-') },
                        { title: '原因', dataIndex: 'reason', key: 'reason', ellipsis: true, render: (v: any) => String(v || '-') },
                      ]}
                      scroll={{ x: 'max-content' as any }}
                      locale={{ emptyText: '暂无FIFO库龄切桶明细' }}
                    />
                  ),
                },
              ];
              const items = allItems.filter((x: any) => {
                const k = String(x?.key || '');
                if (!sortCountInvPreviewShown && k === 'insufficient') return false;
                if (sortCountInvShowEmptyItems) return true;
                if (k === 'group_adj') return sortCountInvGroupAdjustRows.length > 0;
                if (k === 'need1_group_cleanup') return sortCountInvNeed1GroupCleanupRows.length > 0;
                if (k === 'need1_res_release') return sortCountInvNeed1ReservationReleaseRows.length > 0;
                if (k === 'gen_inbound') return sortCountInvGeneratedInboundRows.length > 0;
                if (k === 'need1_adj') return sortCountInvNeed1AdjustRows.length > 0;
                if (k === 'unoperable_to_aiu') return sortCountInvUnoperableToAiuRows.length > 0;
                if (k === 'insufficient') return sortCountInvInsufficientRows.length > 0;
                if (k === 'weighted_age') return sortCountInvWeightedAgeRows.length > 0;
                return false;
              });
              if (items.length <= 0) {
                return <div style={{ color: '#8c8c8c', fontSize: 12 }}>当前无可展示子项（可勾选“显示空子项”查看全部）。</div>;
              }
              return (
                <Collapse
                  style={{ background: 'transparent' }}
                  activeKey={sortCountInvAuditActiveKeys}
                  onChange={(keys) => {
                    if (Array.isArray(keys)) {
                      setSortCountInvAuditActiveKeys(keys.map((k) => String(k || '')));
                    } else if (keys == null) {
                      setSortCountInvAuditActiveKeys([]);
                    } else {
                      setSortCountInvAuditActiveKeys([String(keys || '')]);
                    }
                  }}
                  items={items as any}
                />
              );
            })()}
          </div>
        </>
      ) : null}
    </div>
  );
  const relatedProjects = useMemo(() => {
    const rawRows = (() => {
      const arr = (instr as any)?.projects_detail;
      if (Array.isArray(arr) && arr.length > 0) return arr;
      const single = (instr as any)?.project_detail || (instr as any)?.project;
      return single ? [single] : [];
    })();
    const seen = new Set<string>();
    const rows: AnyObj[] = [];
    (Array.isArray(rawRows) ? rawRows : []).forEach((r: any, idx: number) => {
      const id = Number(r?.id);
      const idText = Number.isFinite(id) && id > 0 ? String(id) : String(r?.id || '').trim();
      const code = String(r?.project_code || r?.code || '').trim();
      const name = String(r?.name || '').trim();
      const key = `${idText}::${code}::${name}::${idx}`;
      if (seen.has(key)) return;
      seen.add(key);
      rows.push({
        __k: key,
        id: idText || '-',
        project_code: code || '-',
        name: name || '-',
      });
    });
    if (rows.length > 0) return rows;
    const pid = Number((instr as any)?.project_id);
    if (Number.isFinite(pid) && pid > 0) {
      return [{ __k: `fallback-${pid}`, id: String(pid), project_code: '-', name: '-' }];
    }
    return [] as AnyObj[];
  }, [instr]);
  const relatedProjectFirstCode = useMemo(() => {
    const first = (Array.isArray(relatedProjects) && relatedProjects.length > 0) ? relatedProjects[0] : null;
    if (!first) return '-';
    return String(first?.project_code || first?.id || '-');
  }, [relatedProjects]);
  const relatedProjectRestCount = Math.max(0, Number(relatedProjects.length || 0) - 1);

  return (
    <div className="final-review-workbench" style={{ padding: 16, width: '100%' }}>
      <style>{`
        .final-review-workbench *:not(.final-review-main-title):not(.final-review-main-title *) {
          font-weight: 400 !important;
        }
        .final-review-workbench .final-review-main-title {
          font-weight: 700 !important;
        }
      `}</style>

      <Card loading={loading} style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Title level={4} className="final-review-main-title" style={{ margin: 0 }}>
            指令终审工作台
          </Title>
        </div>
        <div style={{ marginTop: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <div style={{ lineHeight: 1.8 }}>
              <Text type="secondary">状态</Text>：<Text strong>{statusTextCN}</Text>
              <Text type="secondary" style={{ marginLeft: 12 }}>关联项目</Text>：
              {relatedProjects.length > 0 ? (
                <>
                  <Tag color="blue" style={{ marginInlineEnd: 0, marginLeft: 4 }}>
                    {relatedProjectFirstCode}
                  </Tag>
                  {relatedProjectRestCount > 0 ? (
                    <Button
                      type="link"
                      size="small"
                      style={{ marginLeft: 2, paddingInline: 4 }}
                      onClick={() => setRelatedProjectsOpen(true)}
                    >
                      +{relatedProjectRestCount}个
                    </Button>
                  ) : null}
                </>
              ) : (
                <Text type="secondary" style={{ marginLeft: 4 }}>-</Text>
              )}
              <Text type="secondary" style={{ marginLeft: 12 }}>指令</Text>：
              <Text
                style={{
                  fontFamily: 'monospace',
                  fontWeight: 900,
                  fontSize: 18,
                  lineHeight: '22px',
                  color: '#1677ff',
                }}
              >
                {String((instr as any)?.system_code || instrId || '-')}
              </Text>
              <Button
                size="small"
                type="text"
                aria-label="复制指令编码"
                icon={copied ? <span style={{ fontWeight: 900 }}>√</span> : <CopyOutlined />}
                onClick={() => {
                  const code = String((instr as any)?.system_code || instrId || '').trim();
                  if (!code) return;
                  const ok = async () => {
                    try {
                      await navigator.clipboard.writeText(code);
                      message.success('已复制');
                      setCopied(true);
                      try { window.setTimeout(() => setCopied(false), 1200); } catch {}
                    } catch {
                      try {
                        const ta = document.createElement('textarea');
                        ta.value = code;
                        ta.style.position = 'fixed';
                        ta.style.left = '-9999px';
                        document.body.appendChild(ta);
                        ta.focus();
                        ta.select();
                        document.execCommand('copy');
                        document.body.removeChild(ta);
                        message.success('已复制');
                        setCopied(true);
                        try { window.setTimeout(() => setCopied(false), 1200); } catch {}
                      } catch {
                        message.error('复制失败');
                        setCopied(false);
                      }
                    }
                  };
                  void ok();
                }}
                style={{ marginLeft: 6 }}
              />
              <Button
                type="default"
                size="small"
                style={{
                  marginLeft: 8,
                  color: '#1677ff',
                  borderColor: '#1677ff',
                  background: '#ffffff',
                }}
                onClick={() => {
                  // 目标：在“系统内标签页”打开，而不是打开新的浏览器窗口/标签
                  const base = String(location.pathname || '').startsWith('/dashboard/admin') ? '/dashboard/admin' : '/dashboard/staff';
                  const path = `${base}/instructions/${instrId}`;
                  try {
                    const w: any = window as any;
                    if (w?.tabsManager?.openTabByPath && typeof w.tabsManager.openTabByPath === 'function') {
                      w.tabsManager.openTabByPath(path, { forceNew: true });
                      return;
                    }
                  } catch {}
                  // 兜底：同窗口跳转（不会新开浏览器窗口）
                  try { navigate(`${base}/instructions/${instrId}`); } catch {}
                }}
                disabled={!instrId}
              >
                查看指令详情
              </Button>
            </div>
            <Space>
              {templateDisplayText ? (
                <Text
                  style={{
                    marginRight: 30,
                    color: '#fa541c',
                    fontSize: 16,
                    fontWeight: 400,
                  }}
                >
                  {templateDisplayText}
                </Text>
              ) : null}
              <Button
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={() => { void load(); void loadFinalReviewAttachments(); }}
              >
                刷新
              </Button>
            </Space>
          </div>
        </div>
      </Card>

      {/* 主体：左右两列布局 */}
      <div
        style={{
          display: 'flex',
          gap: 12,
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        {/* 左列：最终执行结果，占一半宽度 */}
        <div style={{ flex: '1 1 560px', minWidth: 520 }}>
          <Card
            className="final-review-result"
            style={{ border: '2px solid #1677ff' }}
            title={reviewSectionTitle(1, '最终执行结果明细', '执行结果与库存结算', '#1677ff')}
            headStyle={{ background: '#e6f4ff', borderColor: '#91caff' }}
            bodyStyle={{ background: '#e6f4ff', borderColor: '#91caff' }}
          >
            <Card
              type="inner"
              size="small"
              title={(
                <Space size={8} align="center">
                  {reviewSubNo('1.1')}
                  <span>库存结算执行</span>
                  <Button
                    size="small"
                    type="default"
                    style={helpButtonStyle}
                    onClick={() => setFinalReviewHelpOpen(true)}
                  >
                    说明
                  </Button>
                </Space>
              )}
              style={{ marginBottom: 10 }}
              headStyle={{ background: 'rgba(255,255,255,0.9)' }}
              bodyStyle={{ background: 'rgba(255,255,255,0.9)' }}
              extra={(
                <Space size={8} wrap>
                  {inventoryAutoApplying ? (
                    <Tag color="processing">自动执行中</Tag>
                  ) : (
                    <Tag color={sortCountInvAppliedSuccess ? 'success' : (sortCountInventoryWritebackStatusRaw === 'failed' ? 'error' : 'default')}>
                      {sortCountInvAppliedSuccess ? '已自动执行' : (sortCountInventoryWritebackStatusRaw === 'failed' ? '自动执行失败' : '待自动执行')}
                    </Tag>
                  )}
                  <Button
                    size="small"
                    type="text"
                    icon={settlementPanelCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
                    onClick={() => toggleDetailPanelCollapsed('settlement', defaultCollapseSettlementPanel)}
                  />
                </Space>
              )}
            >
              {inventoryPreviewSummary ? (
                <div
                  style={{
                    marginBottom: 10,
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: `1px solid ${(inventoryPreviewSummary.statusRaw === 'applied' || inventoryPreviewSummary.statusRaw === 'success')
                      ? '#b7eb8f'
                      : (inventoryPreviewSummary.canApplyRaw === false || !!inventoryPreviewSummary.errorText ? '#ffccc7' : '#b7eb8f')}`,
                    background: (inventoryPreviewSummary.statusRaw === 'applied' || inventoryPreviewSummary.statusRaw === 'success')
                      ? '#f6ffed'
                      : (inventoryPreviewSummary.canApplyRaw === false || !!inventoryPreviewSummary.errorText ? '#fff2f0' : '#f6ffed'),
                  }}
                >
                  <div style={{ fontWeight: 700, marginBottom: (inventoryPreviewSummary.statusRaw === 'applied' || inventoryPreviewSummary.statusRaw === 'success') ? 0 : 4 }}>
                    自动结算结果：{(inventoryPreviewSummary.statusRaw === 'applied' || inventoryPreviewSummary.statusRaw === 'success') ? '已正常执行' : inventoryPreviewSummary.canApplyText}
                  </div>
                  {(inventoryPreviewSummary.statusRaw === 'applied' || inventoryPreviewSummary.statusRaw === 'success') ? null : (
                    inventoryPreviewSummary.canApplyRaw === false || !!inventoryPreviewSummary.errorText ? (
                    <div style={{ color: '#cf1322', lineHeight: 1.7 }}>
                      阻断/失败原因：{inventoryPreviewSummary.blockedReasonText || inventoryPreviewSummary.errorText || '未返回具体原因'}
                    </div>
                  ) : (
                    <div style={{ color: '#389e0d', lineHeight: 1.7 }}>
                      自动结算可执行，明细条目展示本次执行结果。
                    </div>
                    )
                  )}
                </div>
              ) : null}
              {quantityCorrectionDebugView.visible ? (
                <div
                  style={{
                    marginBottom: 10,
                    padding: '8px 10px',
                    borderRadius: 6,
                    border: '1px dashed #faad14',
                    background: '#fffbe6',
                  }}
                >
                  <div
                    style={{ color: '#ad6800', fontWeight: 700, marginBottom: settlementDebugOpen ? 4 : 0, cursor: 'pointer', userSelect: 'none' }}
                    onClick={() => setSettlementDebugOpen((v) => !v)}
                  >
                    {settlementDebugOpen ? <CaretDownOutlined style={{ marginRight: 6 }} /> : <CaretRightOutlined style={{ marginRight: 6 }} />}
                    调试信息（常规出库-库存数量修正）{settlementDebugOpen ? '' : '（点击展开）'}
                  </div>
                  {settlementDebugOpen ? (
                  <>
                  <div style={{ color: '#595959', fontSize: 12, lineHeight: 1.8 }}>
                    {`debug_build_tag=${quantityCorrectionDebugView.settlementDebugBuildTag} | template_family=${quantityCorrectionDebugView.templateFamily} | action_status=${quantityCorrectionDebugView.actionStatus} | rows=${quantityCorrectionDebugView.rowCount} | by_status=${JSON.stringify(quantityCorrectionDebugView.byStatus)} | blocked_reason=${quantityCorrectionDebugView.blockedReason}`}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>summary</div>
                    <pre style={{ margin: 0, padding: 8, background: '#fff', border: '1px solid #f0f0f0', borderRadius: 4, maxHeight: 140, overflow: 'auto', fontSize: 12, lineHeight: 1.5 }}>
                      {JSON.stringify(quantityCorrectionDebugView.summary, null, 2)}
                    </pre>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>rows（最多200条）</div>
                    <pre style={{ margin: 0, padding: 8, background: '#fff', border: '1px solid #f0f0f0', borderRadius: 4, maxHeight: 260, overflow: 'auto', fontSize: 12, lineHeight: 1.5 }}>
                      {JSON.stringify(quantityCorrectionDebugView.rows, null, 2)}
                    </pre>
                  </div>
                  <div style={{ marginTop: 10, color: '#595959', fontSize: 12, lineHeight: 1.8 }}>
                    {`执行期占用结算: action_status=${quantityCorrectionDebugView.reservationActionStatus} | rows=${quantityCorrectionDebugView.reservationRowCount} | by_status=${JSON.stringify(quantityCorrectionDebugView.reservationByStatus)} | blocked_reason=${quantityCorrectionDebugView.reservationBlockedReason}`}
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>reservation_summary</div>
                    <pre style={{ margin: 0, padding: 8, background: '#fff', border: '1px solid #f0f0f0', borderRadius: 4, maxHeight: 220, overflow: 'auto', fontSize: 12, lineHeight: 1.5 }}>
                      {JSON.stringify(quantityCorrectionDebugView.reservationSummary, null, 2)}
                    </pre>
                  </div>
                  <div style={{ marginTop: 6 }}>
                    <div style={{ color: '#8c8c8c', fontSize: 12, marginBottom: 4 }}>reservation_rows（最多300条）</div>
                    <pre style={{ margin: 0, padding: 8, background: '#fff', border: '1px solid #f0f0f0', borderRadius: 4, maxHeight: 260, overflow: 'auto', fontSize: 12, lineHeight: 1.5 }}>
                      {JSON.stringify(quantityCorrectionDebugView.reservationRows, null, 2)}
                    </pre>
                  </div>
                  </>
                  ) : null}
                </div>
              ) : null}
              {!settlementPanelCollapsed ? (
              <Collapse
                bordered
                activeKey={inventorySettleActiveKeys}
                onChange={(keys) => {
                  if (Array.isArray(keys)) {
                    setInventorySettleActiveKeys(keys.map((k) => String(k)));
                    return;
                  }
                  const one = String(keys || '').trim();
                  setInventorySettleActiveKeys(one ? [one] : []);
                }}
                items={(finalReviewInventoryActionRows || []).map((r: any) => {
                  const itemKey = String(r?.__k || '');
                  const isExecutionReservationSettlement = itemKey === 'execution_reservation_settlement' || itemKey === 'reservation_finalize';
                  const expanded = inventorySettleActiveKeys.includes(itemKey);
                  const detailRows = Array.isArray(r?.detail_rows) ? (r.detail_rows as AnyObj[]) : [];
                  const reservationFinalizeGroups = isExecutionReservationSettlement
                    ? (() => {
                        type ReservationGroup = {
                          iuCode: string;
                          lines: string[];
                          hasFailed: boolean;
                        };
                        const normalizeReservationModeText = (raw: any): string => {
                          const s = String(raw || '').trim();
                          const sl = s.toLowerCase();
                          if (!s) return '-';
                          if (s === '转消耗' || sl === 'consume_reserved' || sl === 'consume') return '转消耗';
                          if (s === '释放执行期占用' || sl === 'release_reserved' || sl === 'release') return '释放执行期占用';
                          if (s === '转业务留存占用' || sl === 'transfer_to_business_hold' || sl === 'business_hold' || sl === 'hold') return '转业务留存占用';
                          return s;
                        };
                        const pushGroupLine = (
                          groupMap: Map<string, ReservationGroup>,
                          iuCodeRaw: any,
                          lineText: string,
                          failed: boolean,
                        ) => {
                          const iuCode = String(iuCodeRaw || '-').trim() || '-';
                          const cur = groupMap.get(iuCode) || { iuCode, lines: [], hasFailed: false };
                          cur.lines.push(lineText);
                          cur.hasFailed = cur.hasFailed || failed;
                          groupMap.set(iuCode, cur);
                        };
                        if (Array.isArray(detailRows) && detailRows.length > 0) {
                          const groupMap = new Map<string, ReservationGroup>();
                          detailRows.forEach((x: any) => {
                            const status = String(x?.status || '').trim().toLowerCase();
                            const modeText = normalizeReservationModeText(x?.mode_text || x?.mode || '-');
                            const iuCode = String(x?.inventory_unit_code || '-').trim() || '-';
                            const qty = Number(x?.qty ?? 0);
                            const qtyText = Number.isFinite(qty) ? `${Math.trunc(qty)}件` : '-';
                            const reason = String(x?.reason || '').trim();
                            if (status === 'failed') {
                              pushGroupLine(groupMap, iuCode, `失败: × ${qtyText} | ${modeText} | 原因=${reason || '-'}`, true);
                              return;
                            }
                            pushGroupLine(groupMap, iuCode, `结算: × ${qtyText} | ${modeText}`, false);
                          });
                          return Array.from(groupMap.values());
                        }
                        const sm = (r?.summary && typeof r.summary === 'object') ? (r.summary as AnyObj) : {};
                        const groupMap = new Map<string, ReservationGroup>();
                        const modeText = normalizeReservationModeText(sm?.mode);
                        const consumedQty = Number(sm?.consumed_qty || 0);
                        const releasedQty = Number(sm?.released_qty || 0);
                        const transferredQty = Number(sm?.transferred_qty || 0);
                        if (Number.isFinite(consumedQty) && consumedQty > 0) pushGroupLine(groupMap, '-', `结算: × ${Math.trunc(consumedQty)}件 | ${modeText || '转消耗'}`, false);
                        if (Number.isFinite(releasedQty) && releasedQty > 0) pushGroupLine(groupMap, '-', `结算: × ${Math.trunc(releasedQty)}件 | ${modeText || '释放执行期占用'}`, false);
                        if (Number.isFinite(transferredQty) && transferredQty > 0) pushGroupLine(groupMap, '-', `结算: × ${Math.trunc(transferredQty)}件 | ${modeText || '转业务留存占用'}`, false);
                        const blockedText = String(r?.blocked_reason || '').trim();
                        if (blockedText) pushGroupLine(groupMap, '-', `失败原因: ${blockedText}`, true);
                        return Array.from(groupMap.values());
                      })()
                    : [];
                  const unoperableToAiuRows = itemKey === 'unoperable_to_aiu'
                    ? detailRows
                    : [];
                  const generatedInboundRows = itemKey === 'generated_unit_inbound'
                    ? detailRows
                    : [];
                  const groupAdjustmentRows = itemKey === 'group_adjustment'
                    ? detailRows
                    : [];
                  const quantityCorrectionRows = itemKey === 'quantity_adjustment'
                    ? detailRows.filter((x: any) => {
                        const s = String(x?.status || x?.row_status || '').trim().toLowerCase();
                        const delta = Number(x?.diff_qty ?? x?.delta_qty ?? 0);
                        return (s === 'applied' || s === 'success') && Number.isFinite(delta) && Math.abs(delta) > 0;
                      })
                    : [];
                  const recordCount = itemKey === 'quantity_adjustment'
                    ? quantityCorrectionRows.length
                    : (isExecutionReservationSettlement
                      ? reservationFinalizeGroups.reduce((sum: number, g: any) => sum + (Array.isArray(g?.lines) ? g.lines.length : 0), 0)
                      : detailRows.length);
                  return {
                    key: itemKey,
                    showArrow: false,
                    label: (
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, width: '100%' }}>
                        <Space size={10} wrap>
                          <span style={{ fontWeight: 600 }}>{String(r?.action || '-')}</span>
                          {itemKey === 'quantity_adjustment' ? (
                            <Button
                              size="small"
                              type="default"
                              style={helpButtonStyle}
                              onClick={(e) => {
                                e.stopPropagation();
                                setQuantityCorrectionHelpOpen(true);
                              }}
                            >
                              说明
                            </Button>
                          ) : null}
                          {itemKey === 'unoperable_to_aiu' ? (
                            <Button
                              size="small"
                              type="default"
                              style={helpButtonStyle}
                              onClick={(e) => {
                                e.stopPropagation();
                                setUnoperableToAiuHelpOpen(true);
                              }}
                            >
                              说明
                            </Button>
                          ) : null}
                          {isExecutionReservationSettlement ? (
                            <Button
                              size="small"
                              type="default"
                              style={helpButtonStyle}
                              onClick={(e) => {
                                e.stopPropagation();
                                setExecutionReservationHelpOpen(true);
                              }}
                            >
                              说明
                            </Button>
                          ) : null}
                          {itemKey === 'group_adjustment' ? (
                            <Button
                              size="small"
                              type="default"
                              style={helpButtonStyle}
                              onClick={(e) => {
                                e.stopPropagation();
                                setGroupAdjustmentHelpOpen(true);
                              }}
                            >
                              说明
                            </Button>
                          ) : null}
                          <span style={{ color: '#1677ff', display: 'inline-flex', alignItems: 'center' }}>
                            {expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                          </span>
                        </Space>
                        {recordCount > 0 ? <span style={{ color: '#8c8c8c' }}>{`${recordCount}条记录`}</span> : null}
                      </div>
                    ),
                    children: (
                      <div style={{ display: 'grid', gap: 6 }}>
                        {(itemKey !== 'quantity_adjustment' && itemKey !== 'unoperable_to_aiu' && itemKey !== 'group_adjustment' && !isExecutionReservationSettlement) ? (
                          <div><span style={{ color: '#8c8c8c' }}>摘要：</span>{String(r?.summary || '-')}</div>
                        ) : null}
                        <div><span style={{ color: '#8c8c8c' }}>阻断原因：</span>{String(r?.blocked_reason || '-')}</div>
                        {isExecutionReservationSettlement ? (
                          <div>
                            <div style={{ color: '#8c8c8c', marginBottom: 4 }}>
                              结算明细：{recordCount > 0 ? `${recordCount}条` : '无'}
                            </div>
                            {reservationFinalizeGroups.length > 0 ? (
                              <div style={{ display: 'grid', gap: 4 }}>
                                {reservationFinalizeGroups.map((grp: any, idx: number) => (
                                  <div key={`reservation-finalize-group-${idx}`} style={{ border: '1px solid #f0f0f0', borderRadius: 6, padding: '6px 8px' }}>
                                    <div style={{ fontFamily: 'monospace', fontSize: 13, fontWeight: 600, marginBottom: 2 }}>
                                      {`IU: ${String(grp?.iuCode || '-')}`}
                                    </div>
                                    <div style={{ display: 'grid', gap: 2 }}>
                                      {(Array.isArray(grp?.lines) ? grp.lines : []).map((txt: string, i2: number) => (
                                        <div
                                          key={`reservation-finalize-line-${idx}-${i2}`}
                                          style={{
                                            fontFamily: 'monospace',
                                            fontSize: 13,
                                            color: String(txt || '').startsWith('失败') ? '#cf1322' : '#262626',
                                          }}
                                        >
                                          {txt}
                                        </div>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        {itemKey === 'unoperable_to_aiu' ? (
                          <div>
                            <div style={{ color: '#8c8c8c', marginBottom: 4 }}>
                              转换明细：{unoperableToAiuRows.length > 0 ? `${unoperableToAiuRows.length}条` : '无'}
                            </div>
                            {unoperableToAiuRows.length > 0 ? (
                              <div style={{ display: 'grid', gap: 4 }}>
                                {unoperableToAiuRows.map((x: any, idx: number) => {
                                  const status = String(x?.status || '').trim().toLowerCase();
                                  const srcCode = String(x?.source_inventory_unit_code || x?.inventory_unit_code || '-').trim() || '-';
                                  const aiuCode = String(x?.aiu_unit_code || '-').trim() || '-';
                                  if (status === 'applied') {
                                    return (
                                      <div key={`unop-aiu-row-${idx}`} style={{ fontFamily: 'monospace', fontSize: 13, color: '#262626' }}>
                                        {`转换: ${srcCode} × ${String(x?.qty ?? '-')}件 | 单重${String(x?.unit_weight_g ?? '-')}g 总重${String(x?.weight_g ?? '-')}g`}
                                      </div>
                                    );
                                  }
                                  if (status === 'aiu_inbound_aggregated') {
                                    return (
                                      <div key={`unop-aiu-row-${idx}`} style={{ fontFamily: 'monospace', fontSize: 13, color: '#1677ff', fontWeight: 600 }}>
                                        {`AIU汇总入库: ${aiuCode} × ${String(x?.qty ?? '-')}件 | 总重${String(x?.weight_g ?? '-')}g`}
                                      </div>
                                    );
                                  }
                                  if (status === 'aiu_content_finalized') {
                                    return (
                                      <div key={`unop-aiu-row-${idx}`} style={{ fontFamily: 'monospace', fontSize: 13, color: '#595959' }}>
                                        {`容器内容固化: AIU=${aiuCode} | draft=${String(x?.draft_rewritten_count ?? 0)} | ioc=${String(x?.ioc_rewritten_count ?? 0)}`}
                                      </div>
                                    );
                                  }
                                  if (status === 'failed') {
                                    return (
                                      <div key={`unop-aiu-row-${idx}`} style={{ fontFamily: 'monospace', fontSize: 13, color: '#cf1322' }}>
                                        {`失败: 来源IU=${srcCode} | 数量=${String(x?.qty ?? '-')} | 原因=${String(x?.reason || x?.reason_text || '-')}`}
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={`unop-aiu-row-${idx}`} style={{ fontFamily: 'monospace', fontSize: 13, color: '#595959' }}>
                                      {`记录: 状态=${String(x?.status || '-')} | 来源IU=${srcCode} | AIU=${aiuCode} | 数量=${String(x?.qty ?? '-')}`}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        {itemKey === 'group_adjustment' ? (
                          <div>
                            <div style={{ color: '#8c8c8c', marginBottom: 4 }}>
                              归组调整明细：{groupAdjustmentRows.length > 0 ? `${groupAdjustmentRows.length}条` : '无'}
                            </div>
                            {groupAdjustmentRows.length > 0 ? (
                              <div style={{ display: 'grid', gap: 4 }}>
                                {groupAdjustmentRows.map((x: any, idx: number) => {
                                  const status = String(x?.status || '').trim().toLowerCase();
                                  const iuCode = String(x?.inventory_unit_code || '-').trim() || '-';
                                  const groupCode = String(x?.group_code || '-').trim() || '-';
                                  const toGroup = String(x?.target_group_system_code || '-').trim() || '-';
                                  const reason = String(x?.reason || '-').trim() || '-';
                                  if (status === 'updated') {
                                    return (
                                      <div key={`group-adjustment-row-${idx}`} style={{ fontFamily: 'monospace', fontSize: 13, color: '#262626' }}>
                                        {`归组: ${iuCode} | 临时组=${groupCode} | 目标组=${toGroup}`}
                                      </div>
                                    );
                                  }
                                  if (status === 'noop') {
                                    return (
                                      <div key={`group-adjustment-row-${idx}`} style={{ fontFamily: 'monospace', fontSize: 13, color: '#595959' }}>
                                        {`跳过(无变化): ${iuCode} | 临时组=${groupCode} | 目标组=${toGroup}`}
                                      </div>
                                    );
                                  }
                                  if (status === 'skipped') {
                                    return (
                                      <div key={`group-adjustment-row-${idx}`} style={{ fontFamily: 'monospace', fontSize: 13, color: '#cf1322' }}>
                                        {`跳过: ${iuCode} | 临时组=${groupCode} | 原因=${reason}`}
                                      </div>
                                    );
                                  }
                                  return (
                                    <div key={`group-adjustment-row-${idx}`} style={{ fontFamily: 'monospace', fontSize: 13, color: '#595959' }}>
                                      {`记录: IU=${iuCode} | 状态=${String(x?.status || '-')} | 临时组=${groupCode} | 目标组=${toGroup}`}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        {itemKey === 'generated_unit_inbound' ? (
                          <div>
                            <div style={{ color: '#8c8c8c', marginBottom: 4 }}>
                              新建IU初始入库明细：{generatedInboundRows.length > 0 ? `${generatedInboundRows.length}条` : '无'}
                            </div>
                            {generatedInboundRows.length > 0 ? (
                              <div style={{ display: 'grid', gap: 4 }}>
                                {generatedInboundRows.map((x: any, idx: number) => {
                                  const status = String(x?.status || '').trim().toLowerCase();
                                  const unitCode = String(x?.unit_code || '-').trim() || '-';
                                  const qty = String(x?.qty ?? '-');
                                  const receivedAt = String(x?.received_at || '').trim();
                                  const reason = String(x?.reason || '-').trim() || '-';
                                  const receivedText = receivedAt ? fmtDateTime(receivedAt) : '-';
                                  const color = status === 'applied' ? '#262626' : (status === 'skipped' ? '#cf1322' : '#595959');
                                  return (
                                    <div key={`generated-inbound-row-${idx}`} style={{ fontFamily: 'monospace', fontSize: 13, color }}>
                                      {`入库: ${unitCode} × ${qty}件 | 初始时间=${receivedText}${status === 'skipped' ? ` | 原因=${reason}` : ''}`}
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                        {String(r?.__k || '') === 'fifo_weighted_age' && Array.isArray(r?.detail_rows) && r.detail_rows.length > 0 ? (
                          <div>
                            <div style={{ color: '#8c8c8c', marginBottom: 4 }}>切桶记录（前10条）：</div>
                            <div style={{ display: 'grid', gap: 2 }}>
                              {(r.detail_rows as AnyObj[]).slice(0, 10).map((x: any, idx: number) => (
                                <div key={`fifo-row-${idx}`} style={{ fontFamily: 'monospace', fontSize: 12, color: '#595959' }}>
                                  {`层ID:${String(x?.layer_id ?? '-')} | 层库龄:${String(x?.layer_age_days ?? '-')}天 | 本次取用:${String(x?.taken_qty ?? '-')} | 缺口:${String(x?.missing_qty ?? '-')} | 原因:${String(x?.reason || '-')}`}
                                </div>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        {itemKey === 'quantity_adjustment' ? (
                          <div>
                            <div style={{ color: '#8c8c8c', marginBottom: 4 }}>
                              数量修正明细：{quantityCorrectionRows.length > 0 ? `${quantityCorrectionRows.length}条` : '无'}
                            </div>
                            {quantityCorrectionRows.length > 0 ? (
                              <div style={{ display: 'grid', gap: 2 }}>
                                {quantityCorrectionRows.map((x: any, idx: number) => {
                                  const code = String(
                                    x?.inventory_unit_code
                                    || x?.target_code
                                    || x?.source_inventory_unit_code
                                    || x?.sku_code
                                    || '-',
                                  ).trim() || '-';
                                  const deltaRaw = Number(x?.diff_qty ?? x?.delta_qty ?? 0);
                                  const deltaText = Number.isFinite(deltaRaw)
                                    ? `${deltaRaw >= 0 ? '+' : ''}${Math.trunc(deltaRaw)}`
                                    : '-';
                                  return (
                                    <div
                                      key={`qty-correction-row-${idx}`}
                                      style={{
                                        fontFamily: 'monospace',
                                        fontSize: 14,
                                        color: deltaRaw >= 0 ? '#237804' : '#a8071a',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0,
                                      }}
                                    >
                                      <span>{`${code} 修正=${deltaText}件`}</span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : null}
                          </div>
                        ) : null}
                      </div>
                    ),
                  };
                })}
              />
              ) : null}
            </Card>

            <Card
              type="inner"
              size="small"
              title={<Space size={8} align="center">{reviewSubNo('1.2')}<span>基线文件</span></Space>}
              style={{ marginBottom: 10 }}
              headStyle={{ background: 'rgba(255,255,255,0.9)' }}
              bodyStyle={{ background: 'rgba(255,255,255,0.9)' }}
              loading={baselinesLoading}
              extra={(
                <Button
                  size="small"
                  type="text"
                  icon={baselinePanelCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
                  onClick={() => toggleDetailPanelCollapsed('baseline', defaultCollapseBaselinePanel)}
                />
              )}
            >
              {!baselinePanelCollapsed ? (
              <Table
                size="small"
                rowKey={(r: any) => String(r?.id || '')}
                columns={baselineColumns as any}
                dataSource={baselineFiles}
                pagination={{
                  defaultPageSize: 8,
                  showSizeChanger: true,
                  pageSizeOptions: ['8', '20', '50', '100'],
                  showQuickJumper: true,
                  showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                }}
                tableLayout="fixed"
              />
              ) : null}
            </Card>

            <Card
              type="inner"
              size="small"
              title={<Space size={8} align="center">{reviewSubNo('1.3')}<span>仓库操作各阶段快照</span></Space>}
              style={{ marginBottom: 10 }}
              headStyle={{ background: 'rgba(255,255,255,0.9)' }}
              bodyStyle={{ background: 'rgba(255,255,255,0.9)' }}
              loading={stageSubsLoading}
              extra={(
                <Button
                  size="small"
                  type="text"
                  icon={stagePanelCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
                  onClick={() => toggleDetailPanelCollapsed('stage', defaultCollapseStagePanel)}
                />
              )}
            >
              {!stagePanelCollapsed ? (
              <Table
                size="small"
                rowKey={(r: any) => String(r?.id || '')}
                columns={stageColumns as any}
                dataSource={stageSubs}
                pagination={false}
              />
              ) : null}
            </Card>

            <Card
              type="inner"
              size="small"
              title={hasSortCountStage ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, whiteSpace: 'normal', lineHeight: 1.2 }}>
                  {reviewSubNo('1.4')}
                  <span>分拣清点最终操作结果</span>
                  <Button size="small" type="default" style={helpButtonStyle} onClick={() => setSortCountDemandHelpOpen(true)}>说明</Button>
                </div>
              ) : (
                <div style={{ display:'flex', alignItems:'center', gap: 12, whiteSpace: 'normal', lineHeight: 1.2 }}>
                  {reviewSubNo('1.4')}
                  <span>最终包装数据及无法操作统计</span>
                  <span style={{ marginLeft: 8, fontWeight: 800, color: '#262626' }}>
                    <span style={{ color:'#389e0d' }}>{`正常${normalIocTotalQty}件`}</span>
                    <span style={{ color:'#bfbfbf', margin: '0 8px' }}>|</span>
                    <span style={{ color:'#fa541c' }}>{`异常${exceptionIocTotalQty}件`}</span>
                    <span style={{ color:'#bfbfbf', margin: '0 8px' }}>|</span>
                    <span>{`共${normalIocTotalQty + exceptionIocTotalQty}件`}</span>
                  </span>
                </div>
              )}
              style={{ maxWidth: '100%' }}
              headStyle={{ background: 'rgba(255,255,255,0.9)', flexWrap: 'wrap', rowGap: 6 }}
              bodyStyle={{ background: 'rgba(255,255,255,0.9)', overflowX: 'hidden' }}
              loading={iocLoading}
              extra={
                hasSortCountStage ? (
                  <Space>
                    <Button
                      size="small"
                      icon={<DownloadOutlined />}
                      loading={sortCountFinalExporting}
                      onClick={() => { void downloadSortCountFinalResultExcel(); }}
                      disabled={!instrId}
                    >
                      下载结果
                    </Button>
                    <Button
                      size="small"
                      type="primary"
                      onClick={() => { void addSortCountFinalResultExcelToFinalReview(); }}
                      loading={sortCountFinalAddingXlsx}
                      disabled={!instrId}
                    >
                      加入终审附件
                    </Button>
                    <Button
                      size="small"
                      type="text"
                      icon={detail3PanelCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
                      onClick={() => toggleDetailPanelCollapsed('detail3', defaultCollapseDetail3Panel)}
                    />
                  </Space>
                ) : (
                  <Space>
                    <Button
                      size="small"
                      type="primary"
                      style={{
                        fontWeight: 700,
                        paddingTop: 6,
                        paddingBottom: 6,
                        height: 'auto',
                        background: (!instrId || !canUseTotalPackingData) ? '#f5f5f5' : '#1677ff',
                        borderColor: (!instrId || !canUseTotalPackingData) ? '#d9d9d9' : '#1677ff',
                        color: (!instrId || !canUseTotalPackingData) ? '#bfbfbf' : '#ffffff',
                      }}
                      onClick={() => {
                        if (totalPackingMode === 'regular_outbound') {
                          setRegularOutboundPackingOpen(true);
                          return;
                        }
                        if (totalPackingMode === 'relabel') {
                        setIocNewBarcodeFacetOpen(true);
                        void loadIocNewBarcodeFacets();
                        }
                      }}
                      disabled={!instrId || !canUseTotalPackingData}
                    >
                      总包装数据
                    </Button>
                    <Button
                      size="small"
                      type="text"
                      icon={detail3PanelCollapsed ? <CaretRightOutlined /> : <CaretDownOutlined />}
                      onClick={() => toggleDetailPanelCollapsed('detail3', defaultCollapseDetail3Panel)}
                    />
                  </Space>
                )
              }
            >
              {!detail3PanelCollapsed ? (
              hasSortCountStage ? (
                <div style={{ paddingRight: 8 }}>
                  {showLegacySortCountInvInDetail3 ? renderSortCountInventorySettlementPanel() : null}
                  <Modal
                    open={sortCountDemandHelpOpen}
                    title="分拣需求说明"
                    onCancel={() => setSortCountDemandHelpOpen(false)}
                    onOk={() => setSortCountDemandHelpOpen(false)}
                    okText="关闭"
                    cancelButtonProps={{ style: { display: 'none' } }}
                  >
                    <div style={{ display: 'grid', gap: 12, lineHeight: 1.9 }}>
                      <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, padding: '8px 10px', background: '#fafafa' }}>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>总体分拣需求说明</div>
                        <div>分拣需求1:分拣混装入库的库存单元(LIU/BIU/PIU)到独立分拣组,便于后续独立操作</div>
                        <div>分拣需求2:拆开箱库存单元/托盘库存单元并分拣出可独立操作的产品</div>
                        <div>分拣需求3:将某款散货库存单元按照规则实现细分</div>
                      </div>
                      <div style={{ border: '1px solid #91caff', borderRadius: 8, padding: '8px 10px', background: '#f0f9ff' }}>
                        <div style={{ fontWeight: 700, marginBottom: 4 }}>本指令分拣需求及预估执行结果</div>
                        {(() => {
                          const mode = String(sortCountFinalReportData?.demandMode || '').trim();
                          const demandText = (() => {
                            const txt = String(sortCountFinalReportData?.headerDisplay || '').trim();
                            if (!txt) {
                              if (mode === 'need1') return '分拣需求1:需求1（混装LIU->分装LIU）';
                              if (mode === 'need2') return '分拣需求2:需求2（BIU/PIU->LIU）';
                              if (mode === 'need3') return '分拣需求3:需求3（散货库存单元LIU按产品特性细分）';
                              return '分拣需求:-';
                            }
                            const parts = txt
                              .split('|')
                              .map((x) => String(x || '').trim())
                              .filter((x) => !!x && !x.includes('库存转换'));
                            const raw = parts.length <= 0 ? txt : parts.join(' | ');
                            const prefix = mode === 'need1' ? '分拣需求1:'
                              : mode === 'need2' ? '分拣需求2:'
                              : mode === 'need3' ? '分拣需求3:'
                              : '分拣需求:';
                            if (raw.startsWith('分拣需求1:') || raw.startsWith('分拣需求2:') || raw.startsWith('分拣需求3:')) return raw;
                            if (raw.startsWith('分拣需求:')) return `${prefix}${raw.slice('分拣需求:'.length)}`;
                            return `${prefix}${raw}`;
                          })();
                          const resultText = mode === 'need1'
                            ? '预估执行结果:一般不会生成新产品和新库存单元LIU,需按照分拣模式调整分拣结果LIU的入库分拣组;需按照分拣结果调整库存数量,库龄不变;'
                            : mode === 'need2'
                              ? '预估执行结果:可能生成新产品和新库存单元LIU,按照分拣模式生成对应的入库分拣组;无论是否新增LIU,分拣出的所有LIU按照分拣结果增加库存数量,库龄继承自操作对象的库龄加权结果'
                              : mode === 'need3'
                                ? '预估执行结果:必生成变体产品和新库存单元LIU,按照分拣模式生成对应的入库分拣组;初始库龄继承自操作对象的库龄加权结果;初始库存数量来自分拣结果'
                                : '预估执行结果:-';
                          return (
                            <div style={{ color: '#595959', display: 'grid', gap: 4 }}>
                              <div>{demandText}</div>
                              <div>{resultText}</div>
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </Modal>
                  <div style={{ marginTop: 4, border: '1px solid #73d13d', background: '#f6ffed', borderRadius: 8, padding: '10px 10px 6px', width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
                    <div style={{ marginBottom: 8, fontWeight: 700, color: '#237804' }}>指令概览与操作结果</div>
                    <Tabs
                      size="small"
                      items={[
                      {
                        key: 'operation_targets',
                        label: `操作对象(${Number(sortCountOperationTargetKindCount || 0)})`,
                        children: (
                          <Table
                            size="small"
                            rowKey={(r: any, idx?: number) => String(
                              r?.id
                              || r?.target_id
                              || r?.inventory_unit_id
                              || r?.inventory_unit_system_barcode
                              || idx
                              || 0,
                            )}
                            dataSource={sortCountOperationTargetRows}
                            columns={[
                              {
                                title: '序号',
                                key: '__idx',
                                width: 70,
                                align: 'center' as const,
                                render: (_: any, __: any, idx: number) => idx + 1,
                              },
                              {
                                title: '库存单元系统编码',
                                key: 'inventory_unit',
                                width: 220,
                                align: 'center' as const,
                                render: (_: any, row: any) => {
                                  const code = String(
                                    row?.inventory_unit_system_barcode
                                    || row?.snapshot?.system_barcode
                                    || row?.snapshot?.system_code
                                    || row?.inventory_unit_code
                                    || row?.inventory_unit_id
                                    || '-',
                                  ).trim();
                                  return <span style={{ fontFamily: 'monospace' }}>{code || '-'}</span>;
                                },
                              },
                              {
                                title: '入库分拣组',
                                key: 'sorting_group',
                                width: 220,
                                align: 'center' as const,
                                render: (_: any, row: any) => {
                                  const sgCode = String(
                                    row?.sorting_group_info?.system_code
                                    || row?.sorting_group_code
                                    || row?.snapshot?.sorting_group_info?.system_code
                                    || '',
                                  ).trim();
                                  const sgName = String(
                                    row?.sorting_group_info?.name
                                    || row?.sorting_group_name
                                    || row?.snapshot?.sorting_group_info?.name
                                    || '',
                                  ).trim();
                                  if (sgCode && sgName) return `${sgCode} (${sgName})`;
                                  return sgCode || sgName || '-';
                                },
                              },
                              {
                                title: '库存单元内容项',
                                key: 'inventory_unit_items',
                                width: 420,
                                render: (_: any, row: any) => {
                                  const items = Array.isArray(row?.inventory_content_items) ? row.inventory_content_items : [];
                                  if (!items.length) return <span style={{ color: '#999' }}>-</span>;
                                  return (
                                    <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                      {items.map((it: any, i: number) => {
                                        const qty = Math.max(0, Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0);
                                        const productName = String(it?.product_name || it?.productName || it?.name || '').trim();
                                        const customCode = String(it?.product_custom_code || it?.customCode || it?.product_code || '').trim();
                                        const barcode = String(
                                          it?.product_sorting_barcode
                                          || it?.sorting_barcode
                                          || it?.barcode
                                          || it?.product_system_code
                                          || it?.system_code
                                          || it?.product_code
                                          || ''
                                        ).trim();
                                        const meta = [customCode, barcode].filter(Boolean).join(' ');
                                        const head = productName && meta ? `${productName}(${meta})` : (productName || meta || '-');
                                        return <div key={`target-item-${i}`}>{`${head} × ${qty}`}</div>;
                                      })}
                                    </div>
                                  );
                                },
                              },
                              {
                                title: '预估数量',
                                key: 'expected_qty',
                                width: 120,
                                align: 'center' as const,
                                render: (_: any, row: any) => String(row?.expected_qty ?? row?.target_qty ?? 0),
                              },
                            ]}
                            pagination={{
                              defaultPageSize: 10,
                              showSizeChanger: true,
                              pageSizeOptions: ['10', '20', '50', '100'],
                              showQuickJumper: true,
                              showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                            }}
                            locale={{ emptyText: '暂无操作对象数据' }}
                            scroll={{ x: 'max-content' as any }}
                            tableLayout="fixed"
                          />
                        ),
                      },
                      {
                        key: 'preset_groups',
                        label: `预设分组列表(${Number(sortCountPresetGroupCount || 0)})`,
                        children: (
                          <div style={{ width: '100%', overflowX: 'auto' }}>
                            <Table
                              size="small"
                              rowKey={(r: any, idx?: number) => String((r?.__idx != null ? r.__idx : r?.__k) || idx || 0)}
                              dataSource={sortCountPresetGroupRows}
                              columns={(sortCountPresetGroupColumns || []).flatMap((col: any) => {
                                const titleText = String(col?.title || '').trim();
                                const dataIndexText = String(col?.dataIndex || '').trim();
                                const isSortMarkCol = titleText.includes('分拣标识') || (sortCountPresetGroupMarkDataIndex && dataIndexText === sortCountPresetGroupMarkDataIndex);
                                const isGroupNoCol = titleText.includes('分组编号') || (sortCountPresetGroupNoDataIndex && dataIndexText === sortCountPresetGroupNoDataIndex);
                                const baseCol = {
                                  ...col,
                                  render: (v: any, row: any) => {
                                    if (isGroupNoCol) return String(row?.__preset_group_display_no ?? v ?? '');
                                    if (isSortMarkCol) return <span style={{ fontFamily: 'monospace' }}>{String(v ?? '')}</span>;
                                    return String(v ?? '');
                                  },
                                  onCell: (row: any) => ({
                                    rowSpan: isGroupNoCol ? Number(row?.__preset_group_row_span ?? 1) : 1,
                                    style: { whiteSpace: 'pre-wrap', wordBreak: 'break-word', textAlign: 'center', verticalAlign: 'middle' },
                                  }),
                                } as any;
                                return [baseCol];
                              })}
                              pagination={{
                                defaultPageSize: 10,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                showQuickJumper: true,
                                showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                              }}
                              locale={{ emptyText: '暂无预设分组列表数据' }}
                              scroll={{ x: 'max-content' as any }}
                              tableLayout="fixed"
                            />
                          </div>
                        ),
                      },
                      {
                        key: 'result_detail',
                        label: '操作结果明细',
                        children: (
                          (() => {
                            const mode = String(sortCountFinalReportData?.demandMode || '').trim();
                            if (mode === '__deprecated_need1__') {
                              return (
                                <div style={{ display: 'grid', gap: 10 }}>
                                  <Table
                                    size="small"
                                    rowKey={(r: any, idx?: number) => String(r?.__k || r?.sorting_mark || idx || 0)}
                                    dataSource={Array.isArray(sortCountFinalReportData?.byMarkRows) ? sortCountFinalReportData.byMarkRows : []}
                                    columns={[
                                      { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', width: 220, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                                      { title: '数量', dataIndex: 'total_qty', key: 'total_qty', width: 110, render: (v: any) => String(v ?? 0) },
                                      { title: '操作次数', dataIndex: 'operation_count', key: 'operation_count', width: 110, render: (v: any) => String(v ?? 0) },
                                      { title: '最近时间', dataIndex: 'last_time', key: 'last_time', width: 170, render: (v: any) => String(v || '-') },
                                    ]}
                                    pagination={{ defaultPageSize: 10 }}
                                    locale={{ emptyText: '暂无操作结果记录' }}
                                  />
                                  <Table
                                    size="small"
                                    rowKey={(r: any, idx?: number) => String(r?.__k || idx || 0)}
                                    dataSource={Array.isArray(sortCountFinalReportData?.exceptionRows) ? sortCountFinalReportData.exceptionRows : []}
                                    columns={[
                                      { title: '异常容器', dataIndex: 'container_code', key: 'container_code', width: 180, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                                      { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', width: 180, ellipsis: true },
                                      { title: '数量', dataIndex: 'qty', key: 'qty', width: 100, render: (v: any) => String(v ?? 0) },
                                      { title: '原因', dataIndex: 'reason', key: 'reason', ellipsis: true },
                                    ]}
                                    pagination={{ defaultPageSize: 8 }}
                                    locale={{ emptyText: '暂无异常产品记录' }}
                                  />
                                </div>
                              );
                            }
                            if (mode === 'need3' || mode === 'need2' || mode === 'need1') {
                              const normalizeMarkKey = (v: any) => String(v || '').trim().toUpperCase();
                              const sortDbRows = Array.isArray(sortCountFinalReportData?.sortDatabaseRows) ? sortCountFinalReportData.sortDatabaseRows : [];
                              const byMarkRows = Array.isArray(sortCountFinalReportData?.byMarkRows) ? sortCountFinalReportData.byMarkRows : [];
                              const operationRecords = Array.isArray(sortCountFinalReportData?.operationRecords) ? sortCountFinalReportData.operationRecords : [];
                              const generatedDetailRows = Array.isArray(sortCountFinalReportData?.generatedDetailRows) ? sortCountFinalReportData.generatedDetailRows : [];
                              const wrapUpExceptionContainers = Array.isArray(sortCountFinalReportData?.wrapUpExceptionContainers)
                                ? sortCountFinalReportData.wrapUpExceptionContainers
                                : [];
                              const sortDbMarkCount = Array.from(new Set(
                                sortDbRows
                                  .map((r: any) => String(r?.sorting_mark || '').trim())
                                  .filter(Boolean),
                              )).length;
                              const sortDbGroupCount = Array.from(new Set(
                                sortDbRows
                                  .map((r: any) => String(r?.group_code || '').trim())
                                  .filter(Boolean),
                              )).length;
                              const byMarkCount = Array.from(new Set(
                                byMarkRows
                                  .map((r: any) => String(r?.sorting_mark || r?.mark || r?.sorting_code || '').trim())
                                  .filter(Boolean),
                              )).length;
                              const byMarkTotalQty = byMarkRows.reduce((sum: number, r: any) => (
                                sum + Math.max(0, Number(r?.qty ?? r?.total_qty ?? r?.count ?? 0) || 0)
                              ), 0);
                              const byMarkQtyMap = new Map<string, number>();
                              byMarkRows.forEach((r: any) => {
                                const mk = normalizeMarkKey(r?.sorting_mark || r?.mark || r?.sorting_code);
                                if (!mk) return;
                                const q = Math.max(0, Number(r?.qty ?? r?.total_qty ?? r?.count ?? 0) || 0);
                                byMarkQtyMap.set(mk, (byMarkQtyMap.get(mk) || 0) + q);
                              });
                              const markUnitCodeMap = new Map<string, Set<string>>();
                              const pushMarkUnitCode = (markRaw: any, codeRaw: any) => {
                                const mk = String(markRaw || '').trim();
                                const code = String(codeRaw || '').trim();
                                if (!mk || !code || code === '-') return;
                                if (!markUnitCodeMap.has(mk)) markUnitCodeMap.set(mk, new Set<string>());
                                markUnitCodeMap.get(mk)!.add(code);
                              };
                              (operationRecords || []).forEach((r: any) => {
                                pushMarkUnitCode(r?.sorting_mark || r?.mark || r?.sorting_code, r?.target_code || r?.target || r?.inventory_unit_code);
                              });
                              (generatedDetailRows || []).forEach((r: any) => {
                                pushMarkUnitCode(r?.sorting_mark || r?.mark || r?.sorting_code, r?.unit_code || r?.inventory_unit_code);
                              });
                              const exceptionIocCount = wrapUpExceptionContainers.length;
                              const exceptionTotalQty = (() => {
                                let sum = 0;
                                try {
                                  wrapUpExceptionContainers.forEach((c: any) => {
                                    const tq = Number(c?.total_qty);
                                    if (Number.isFinite(tq) && tq > 0) {
                                      sum += tq;
                                      return;
                                    }
                                    const arr = Array.isArray(c?.allocations) ? c.allocations : [];
                                    arr.forEach((a: any) => {
                                      const q = Number(a?.quantity ?? a?.qty ?? 0) || 0;
                                      if (q) sum += Math.abs(q);
                                    });
                                  });
                                } catch {}
                                return Math.max(0, Math.floor(sum));
                              })();
                              const sortMarkBadgeStyle: React.CSSProperties = {
                                display: 'inline-block',
                                minWidth: 64,
                                padding: '2px 10px',
                                borderRadius: 6,
                                background: '#e6f7ff',
                                border: '1px solid #91d5ff',
                                color: '#0050b3',
                                fontWeight: 700,
                                lineHeight: 1.2,
                                maxWidth: '100%',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                verticalAlign: 'middle',
                              };
                              const renderSortMarkBadge = (v: any) => {
                                const txt = String(v ?? '').trim().toUpperCase();
                                if (!txt) return '-';
                                const shown = txt.length > 20 ? `${txt.slice(0, 10)}...${txt.slice(-10)}` : txt;
                                return (
                                  <span style={sortMarkBadgeStyle} title={txt}>
                                    {shown}
                                  </span>
                                );
                              };
                              return (
                                <div style={{ display: 'grid', gap: 10, width: '100%', minWidth: 0 }}>
                                  <Collapse
                                    style={{ background: 'transparent', width: '100%', minWidth: 0 }}
                                    activeKey={sortCountNeed3ActiveKeys}
                                    onChange={(keys) => {
                                      if (Array.isArray(keys)) {
                                        setSortCountNeed3ActiveKeys(keys.map((k) => String(k || '')));
                                      } else if (keys == null) {
                                        setSortCountNeed3ActiveKeys([]);
                                      } else {
                                        setSortCountNeed3ActiveKeys([String(keys || '')]);
                                      }
                                    }}
                                    items={[
                                      {
                                        key: 'mark_group',
                                        showArrow: false,
                                        label: (
                                          <div
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              gap: 8,
                                              flexWrap: 'wrap',
                                              padding: '6px 10px',
                                              border: '1px solid #ffd666',
                                              background: '#fffbe6',
                                              borderRadius: 6,
                                            }}
                                          >
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 800, color: '#ad6800' }}>
                                              <span>已完成分拣结果汇总</span>
                                              {sortCountNeed3ActiveKeys.includes('mark_group') ? <CaretDownOutlined /> : <CaretRightOutlined />}
                                            </div>
                                            <div style={{ color: '#595959', fontWeight: 600 }}>
                                              {`分拣标识 ${sortDbMarkCount}个 | 最终分拣组 ${sortDbGroupCount}个 | 最终分拣总数量 ${Math.max(0, Math.floor(byMarkTotalQty))}个`}
                                            </div>
                                          </div>
                                        ),
                                        children: (
                                          <div style={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto' }}>
                                            <Table
                                              size="small"
                                              rowKey={(r: any, idx?: number) => String(r?.id || `${String(r?.sorting_mark || '').trim()}-${String(r?.sort_id || '').trim()}-${idx ?? 0}`)}
                                              dataSource={sortDbRows}
                                              columns={[
                                                {
                                                  title: '分拣ID',
                                                  dataIndex: 'sort_id',
                                                  key: 'sort_id',
                                                  width: 110,
                                                  align: 'center' as const,
                                                  render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>,
                                                },
                                                {
                                                  title: '分拣标识',
                                                  dataIndex: 'sorting_mark',
                                                  key: 'sorting_mark',
                                                  width: 220,
                                                  ellipsis: true,
                                                  render: (v: any) => renderSortMarkBadge(v),
                                                },
                                                {
                                                  title: '库存单元',
                                                  key: 'unit_code',
                                                  width: 220,
                                                  ellipsis: true,
                                                  render: (_: any, row: any) => {
                                                    const mk = String(row?.sorting_mark || '').trim();
                                                    const codes = mk && markUnitCodeMap.has(mk)
                                                      ? Array.from(markUnitCodeMap.get(mk) || [])
                                                      : [];
                                                    const txt = codes.length > 0 ? codes.join('、') : '-';
                                                    return <span style={{ fontFamily: 'monospace' }} title={txt}>{txt}</span>;
                                                  },
                                                },
                                                {
                                                  title: '已分拣数量',
                                                  key: 'total_qty',
                                                  width: 120,
                                                  align: 'right' as const,
                                                  render: (_: any, row: any) => {
                                                    const mk = normalizeMarkKey(row?.sorting_mark);
                                                    const q = mk ? Number(byMarkQtyMap.get(mk) || 0) : 0;
                                                    return String(Math.max(0, Math.floor(q)));
                                                  },
                                                },
                                                {
                                                  title: '操作阶段临时分拣组',
                                                  dataIndex: 'group_code',
                                                  key: 'group_code',
                                                  width: 120,
                                                  align: 'center' as const,
                                                  render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>,
                                                },
                                                {
                                                  title: '客户指令预设分组编号',
                                                  dataIndex: 'preset_group_no',
                                                  key: 'preset_group_no',
                                                  width: 140,
                                                  align: 'center' as const,
                                                  render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>,
                                                },
                                              ]}
                                              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50', '100'], showQuickJumper: true }}
                                              scroll={{ y: 320, x: 'max-content' as any }}
                                              locale={{ emptyText: '暂无分拣标识与最终分拣组记录' }}
                                            />
                                          </div>
                                        ),
                                      },
                                      {
                                        key: 'unoperable',
                                        showArrow: false,
                                        label: (
                                          <div
                                            style={{
                                              display: 'flex',
                                              alignItems: 'center',
                                              justifyContent: 'space-between',
                                              gap: 8,
                                              flexWrap: 'wrap',
                                              padding: '6px 10px',
                                              border: '1px solid #ffccc7',
                                              background: '#fff1f0',
                                              borderRadius: 6,
                                            }}
                                          >
                                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 800, color: '#cf1322' }}>
                                              <span>无法操作汇总</span>
                                              {sortCountNeed3ActiveKeys.includes('unoperable') ? <CaretDownOutlined /> : <CaretRightOutlined />}
                                            </div>
                                            <div style={{ color: '#595959', fontWeight: 600 }}>
                                              {`异常容器IOC ${exceptionIocCount}个 | 无法操作总数量 ${exceptionTotalQty}个`}
                                            </div>
                                          </div>
                                        ),
                                        children: (
                                          <div style={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto' }}>
                                            <Table
                                              size="small"
                                              rowKey={(_x: any, idx?: number) => String(idx || 0)}
                                              dataSource={wrapUpExceptionContainers}
                                              columns={[
                                                {
                                                  title: '容器',
                                                  dataIndex: 'code',
                                                  key: 'code',
                                                  width: 180,
                                                  ellipsis: true,
                                                  render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>,
                                                },
                                                {
                                                  title: '总数量',
                                                  dataIndex: 'total_qty',
                                                  key: 'total_qty',
                                                  width: 100,
                                                  render: (v: any) => String(v ?? '-'),
                                                },
                                                {
                                                  title: '分配明细',
                                                  key: 'alloc',
                                                  width: 680,
                                                  render: (_: any, r: any) => (
                                                    <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
                                                      {(Array.isArray(r?.allocations) ? r.allocations : []).map((x: any, i: number) => (
                                                        <Tag key={i} style={{ marginInlineEnd: 0, borderRadius: 999 }}>
                                                          <span style={{ fontFamily: 'monospace' }}>{String(x?.unit_code || `IU#${x?.inventory_unit_id}`)}</span>
                                                          <span style={{ marginLeft: 6, fontWeight: 800, color: '#1677ff' }}>{String(x?.quantity ?? '-')}</span>
                                                        </Tag>
                                                      ))}
                                                    </span>
                                                  ),
                                                },
                                              ]}
                                              pagination={{ defaultPageSize: 10, showSizeChanger: true, pageSizeOptions: ['5', '10', '20', '50', '100'], showQuickJumper: true }}
                                              scroll={{ x: 'max-content' as any }}
                                              locale={{ emptyText: '暂无无法操作产品分配明细' }}
                                            />
                                          </div>
                                        ),
                                      },
                                    ]}
                                  />
                                </div>
                              );
                            }
                            if (mode === 'need2') {
                              return (
                                <div style={{ display: 'grid', gap: 10 }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)', gap: 10 }}>
                                    <div style={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
                                      <Table
                                        size="small"
                                        rowKey={(r: any, idx?: number) => String(r?.__k || r?.target_code || idx || 0)}
                                        dataSource={Array.isArray(sortCountFinalReportData?.consumptionByTargetRows) ? sortCountFinalReportData.consumptionByTargetRows : []}
                                        columns={[
                                          { title: '操作目标库存单元', dataIndex: 'target_code', key: 'target_code', ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                                          { title: '消耗数量', dataIndex: 'consumed_qty', key: 'consumed_qty', width: 120, render: (v: any) => String(v ?? 0) },
                                        ]}
                                        pagination={{ defaultPageSize: 8 }}
                                        scroll={{ x: 'max-content' as any }}
                                        locale={{ emptyText: '暂无目标消耗记录' }}
                                      />
                                    </div>
                                    <div style={{ width: '100%', minWidth: 0, overflowX: 'auto' }}>
                                      <Table
                                        size="small"
                                        rowKey={(r: any, idx?: number) => String(r?.__k || r?.unit_code || idx || 0)}
                                        dataSource={Array.isArray(sortCountFinalReportData?.newUnitRows) ? sortCountFinalReportData.newUnitRows : []}
                                        columns={[
                                          { title: '新库存单元', dataIndex: 'unit_code', key: 'unit_code', ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                                          { title: '初始数量', dataIndex: 'initial_qty', key: 'initial_qty', width: 120, render: (v: any) => String(v ?? 0) },
                                        ]}
                                        pagination={{ defaultPageSize: 8 }}
                                        scroll={{ x: 'max-content' as any }}
                                        locale={{ emptyText: '暂无新库存单元记录' }}
                                      />
                                    </div>
                                  </div>
                                  <Table
                                    size="small"
                                    rowKey={(r: any, idx?: number) => String(r?.__k || r?.sorting_mark || idx || 0)}
                                    dataSource={Array.isArray(sortCountFinalReportData?.newProductRows) ? sortCountFinalReportData.newProductRows : []}
                                    columns={[
                                      { title: '新产品标识', dataIndex: 'sorting_mark', key: 'sorting_mark', width: 260, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                                      { title: '数量', dataIndex: 'total_qty', key: 'total_qty', width: 120, render: (v: any) => String(v ?? 0) },
                                    ]}
                                    pagination={{ defaultPageSize: 8 }}
                                    scroll={{ x: 'max-content' as any }}
                                    locale={{ emptyText: '暂无新产品记录' }}
                                  />
                                  <Table
                                    size="small"
                                    rowKey={(r: any, idx?: number) => String(r?.__k || idx || 0)}
                                    dataSource={Array.isArray(sortCountFinalReportData?.exceptionRows) ? sortCountFinalReportData.exceptionRows : []}
                                    columns={[
                                      { title: '异常容器', dataIndex: 'container_code', key: 'container_code', width: 180, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                                      { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', width: 180, ellipsis: true },
                                      { title: '数量', dataIndex: 'qty', key: 'qty', width: 100, render: (v: any) => String(v ?? 0) },
                                      { title: '原因', dataIndex: 'reason', key: 'reason', ellipsis: true },
                                    ]}
                                    pagination={{ defaultPageSize: 8 }}
                                    scroll={{ x: 'max-content' as any }}
                                    locale={{ emptyText: '暂无异常产品记录' }}
                                  />
                                </div>
                              );
                            }
                            return (
                              <Alert
                                type="warning"
                                showIcon
                                message="未识别分拣需求，暂按通用明细展示。"
                                description="请确认分拣需求题答案已正确保存。"
                              />
                            );
                          })()
                        ),
                      },
                      ]}
                    />
                  </div>
                </div>
              ) : (
                <>
                  {/* 右侧留一点空隙，避免行背景/内容贴边（左侧默认已有视觉留白） */}
                  <div style={{ paddingRight: 8 }}>
                    <Table
                      size="small"
                      rowKey={(r: any) => String(r?.id || r?.__k || r?.code || '')}
                      columns={iocColumns as any}
                      dataSource={iocRowsNormalWithUnoperableSummary}
                      onRow={(r: any) => {
                        if (r?.__is_summary) return {};
                        return String(r?.__ioc_kind || '') === 'exception'
                          ? { style: { background: '#fff2e8' } }
                          : {};
                      }}
                      scroll={{ x: 1200 }}
                      pagination={{
                        defaultPageSize: 10,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100', '200'],
                        showQuickJumper: true,
                        showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                      }}
                      tableLayout="auto"
                    />
                  </div>
                </>
              )
              ) : null}
            </Card>
          </Card>
        </div>

        {/* 右列：从上到下依次为 上传附件 / 备注 + 终审提交按钮，占一半宽度 */}
        <div style={{ flex: '1 1 560px', minWidth: 520, display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Card
            className="final-review-attachments"
            style={{ border: '2px solid #52c41a' }}
            title={reviewSectionTitle(2, '终审附件', '上传 Excel/PDF/图片/视频/压缩包等', '#52c41a')}
            headStyle={{ background: '#f6ffed', borderColor: '#b7eb8f' }}
            bodyStyle={{ background: '#f6ffed', borderColor: '#b7eb8f' }}
            extra={
              <Space>
                <Button
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={() => { void doBatchDownloadFinalAtts(); }}
                  disabled={finalAttSelectedKeys.length === 0}
                >
                  批量下载
                </Button>
                <Button
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => { void doBatchDeleteFinalAtts(); }}
                  disabled={finalAttSelectedKeys.length === 0}
                >
                  批量删除
                </Button>
              </Space>
            }
          >
            <Upload.Dragger {...uploadProps} style={{ padding: 12 }}>
              <p className="ant-upload-drag-icon">
                <InboxOutlined />
              </p>
              <p className="ant-upload-text">点击或拖拽上传终审附件</p>
              <p className="ant-upload-hint">这些附件将作为终审留档，不会修改执行数据。</p>
            </Upload.Dragger>
            <Divider style={{ margin: '12px 0' }} />
            <Table
              size="small"
              rowKey="id"
              loading={attsLoading}
              columns={columns as any}
              dataSource={attachments}
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100', '200'],
                showQuickJumper: true,
                showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
              }}
              tableLayout="fixed"
            />
          </Card>

          <Card
            className="final-review-charges"
            style={{ border: '2px solid #722ed1' }}
            title={reviewSectionTitle(3, '收费事实', '按执行结果自动聚合 · 可保存草稿', '#722ed1')}
            headStyle={{ background: '#f9f0ff', borderColor: '#d3adf7' }}
            bodyStyle={{ background: '#f9f0ff', borderColor: '#d3adf7' }}
            extra={
              <Space>
                <Button size="small" icon={<ReloadOutlined />} onClick={() => { void loadFinalPricingPreview(); }}>
                  刷新
                </Button>
                <Button size="small" onClick={() => { void saveFinalPricingDraft(); }} disabled={!instrId}>
                  保存草稿
                </Button>
              </Space>
            }
          >
            {pricingBlockingIssues.length > 0 ? (
              <Alert
                type="error"
                showIcon
                message={`发现 ${pricingBlockingIssues.length} 个严重配置问题：已阻断自动出账`}
                description={
                  <div style={{ marginTop: 6 }}>
                    <div style={{ marginBottom: 6, color: '#8c8c8c' }}>
                      请先修复 BillingRule 的计费单位与 tier_basis/口径key 一致性（否则会用错字段出账）。
                    </div>
                    <div style={{ maxHeight: 140, overflow: 'auto' }}>
                      {(pricingBlockingIssues || []).slice(0, 20).map((it: any, idx: number) => (
                        <div key={idx} style={{ fontFamily: 'monospace' }}>
                          {String(it?.message || it?.issue_type || 'blocking')}
                        </div>
                      ))}
                    </div>
                  </div>
                }
              />
            ) : (pricingWarnings.length > 0 ? (
              <Alert
                type="warning"
                showIcon
                message={`计费提示（${pricingWarnings.length}）`}
                description={
                  <div style={{ maxHeight: 140, overflow: 'auto', fontFamily: 'monospace' }}>
                    {(pricingWarnings || []).slice(0, 30).map((w, idx) => <div key={idx}>{String(w)}</div>)}
                  </div>
                }
              />
            ) : null)}

            <div style={{ marginTop: 10 }}>
              <Table
                size="small"
                rowKey={(r: any) => String(r?.work_order_id || r?.rule_system_code || Math.random())}
                loading={pricingLoading}
                columns={pricingColumns as any}
                dataSource={pricingRows}
                pagination={{
                  defaultPageSize: 20,
                  showSizeChanger: true,
                  pageSizeOptions: ['5', '10', '20', '50', '100'],
                  showQuickJumper: true,
                  showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                }}
                scroll={{ x: 'max-content' }}
                tableLayout="fixed"
              />
            </div>

            <Divider style={{ margin: '10px 0' }} />
            <div>
              <Space size={10} wrap>
                <Text type="secondary">证据来源</Text>
                <Button size="small" onClick={() => setPricingEvidenceOpen(true)} disabled={!pricingEvidence || Object.keys(pricingEvidence || {}).length === 0}>
                  查看证据来源
                </Button>
                {(() => {
                  try {
                    const comp = (pricingEvidence?.computed && typeof pricingEvidence.computed === 'object') ? (pricingEvidence.computed as AnyObj) : {};
                    const relabelItems = comp?.relabel_item_qty ?? comp?.new_barcode_total_qty;
                    const relabelTypes = comp?.relabel_new_barcode_types ?? comp?.new_barcode_types;
                    const iocCnt = comp?.ioc_normal_count;
                    const volM3 = comp?.ioc_normal_volume_m3_total;
                    const wtKg = comp?.ioc_normal_weight_kg_total;
                    const usedMetricKeys = pricingEvidenceSummaryFlags.usedMetricKeys;
                    const parts: string[] = [];
                    if (
                      Number.isFinite(Number(relabelItems)) &&
                      Number(relabelItems) !== 0 &&
                      pricingEvidenceSummaryFlags.usesRelabel
                    ) parts.push(`换标件数=${Number(relabelItems)}`);
                    if (
                      Number.isFinite(Number(relabelTypes)) &&
                      Number(relabelTypes) !== 0 &&
                      pricingEvidenceSummaryFlags.usesRelabel
                    ) parts.push(`条码种类=${Number(relabelTypes)}`);
                    if (
                      Number.isFinite(Number(iocCnt)) &&
                      Number(iocCnt) !== 0 &&
                      (pricingEvidenceSummaryFlags.usesIocCount || usedMetricKeys.has('quantity'))
                    ) parts.push(`正常IOC数=${Number(iocCnt)}`);
                    if (
                      Number.isFinite(Number(volM3)) &&
                      Number(volM3) !== 0 &&
                      (usedMetricKeys.has('volume_m3') || usedMetricKeys.has('volume_cc') || usedMetricKeys.has('volume_cuft'))
                    ) parts.push(`总体积m³=${Number(volM3)}`);
                    if (
                      Number.isFinite(Number(wtKg)) &&
                      Number(wtKg) !== 0 &&
                      (usedMetricKeys.has('weight_kg') || usedMetricKeys.has('weight_g'))
                    ) parts.push(`总重量kg=${Number(wtKg)}`);
                    return parts.length ? <Text type="secondary">{parts.join(' ｜ ')}</Text> : null;
                  } catch { return null; }
                })()}
              </Space>
            </div>
          </Card>

          <Card
            className="final-review-submit"
            style={{ border: '2px solid #fa8c16' }}
            title={reviewSectionTitle(4, '备注与提交', '提交终审 / 保存终审结果', '#fa8c16')}
            headStyle={{ background: '#fff7e6', borderColor: '#ffd591' }}
            bodyStyle={{ background: '#fff7e6', borderColor: '#ffd591' }}
          >
            <Form layout="vertical">
              <Form.Item label="备注信息（可选）">
                <Input.TextArea
                  rows={5}
                  value={notes}
                  placeholder="可填写对客户/内部的说明（仅记录，不影响执行数据）"
                  onChange={(e) => setNotes(e.target.value)}
                  showCount
                  maxLength={2000}
                />
              </Form.Item>
            </Form>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              {String((instr as any)?.status || '').trim() === 'completed' ? (
                <Space>
                  <Button onClick={submitFinalReview} disabled={!instrId || pricingBlockingIssues.length > 0}>
                    保存终审结果（不重算）
                  </Button>
                  <Button
                    type="primary"
                    danger
                    icon={<ThunderboltOutlined />}
                    onClick={submitFinalReviewWithRecalc}
                    disabled={!instrId || pricingBlockingIssues.length > 0}
                  >
                    保存并重算收费记录
                  </Button>
                </Space>
              ) : (
                <Button type="primary" danger onClick={submitFinalReview} disabled={!instrId || pricingBlockingIssues.length > 0}>
                  终审提交
                </Button>
              )}
            </div>
          </Card>
        </div>
      </div>

      <Modal
        open={previewOpen}
        title={previewTitle}
        onCancel={() => { cleanupPreviewObjUrl(); clearPreviewXlsx(); setPreviewOpen(false); }}
        footer={[
          <Button key="close" onClick={() => { cleanupPreviewObjUrl(); clearPreviewXlsx(); setPreviewOpen(false); }}>关闭</Button>,
        ]}
        width={previewKind === 'table' ? 1480 : 1380}
        destroyOnClose
      >
        {previewKind === 'image' ? (
          <div style={{ textAlign: 'center' }}>
            <img src={previewUrl} style={{ maxWidth: '100%', maxHeight: 560 }} />
          </div>
        ) : null}
        {previewKind === 'pdf' ? (
          <iframe src={previewUrl} style={{ width: '100%', height: 640, border: 'none' }} />
        ) : null}
        {previewKind === 'video' ? (
          <video src={previewUrl} style={{ width: '100%' }} controls />
        ) : null}
        {previewKind === 'table' && previewTable ? (
          <div>
            {previewXlsxSheets.length > 1 ? (
              <Tabs
                size="small"
                activeKey={previewXlsxActiveSheet || previewXlsxSheets[0]}
                onChange={(k) => {
                  const name = String(k || '').trim();
                  setPreviewXlsxActiveSheet(name);
                  applyXlsxSheetToPreview(name);
                }}
                items={previewXlsxSheets.map((name) => ({
                  key: name,
                  label: <span style={{ fontFamily: 'monospace' }}>{name}</span>,
                }))}
              />
            ) : null}
            <Table
              size="small"
              tableLayout="fixed"
              columns={(previewTable.columns || []).map((c: any) => {
                const titleText = String(c?.title || '').trim();
                const dataIndexText = String(c?.dataIndex || '').trim();
                const titleLower = titleText.toLowerCase();
                const dataLower = dataIndexText.toLowerCase();
                const isMatchInfoColumn = (
                  titleText.includes('操作对象匹配')
                  || dataIndexText.includes('操作对象匹配')
                  || titleLower === 'match_targets'
                  || dataLower === 'match_targets'
                  || titleLower.includes('match_target')
                  || dataLower.includes('match_target')
                );
                const isQtyAllocColumn = (
                  titleText.includes('数量分配')
                  || dataIndexText.includes('数量分配')
                  || titleLower.includes('qty_alloc')
                  || dataLower.includes('qty_alloc')
                  || titleLower.includes('allocation')
                  || dataLower.includes('allocation')
                );
                if (isMatchInfoColumn) {
                  return {
                    ...c,
                    width: 360,
                    ellipsis: true,
                    render: (v: any) => {
                      const text = String(v ?? '').trim();
                      if (!text) return <span style={{ color: '#999' }}>-</span>;
                      return (
                        <Tooltip placement="topLeft" title={<div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxWidth: 760 }}>{text}</div>}>
                          <span style={{ display: 'inline-block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {text}
                          </span>
                        </Tooltip>
                      );
                    },
                  };
                }
                if (isQtyAllocColumn) {
                  return {
                    ...c,
                    width: 120,
                    ellipsis: true,
                  };
                }
                return {
                  ...c,
                  width: c?.width || 160,
                  ellipsis: true,
                };
              })}
              dataSource={previewTable.data}
              pagination={{
                current: previewTablePage,
                pageSize: previewTablePageSize,
                showSizeChanger: true,
                pageSizeOptions: ['10', '20', '50', '100', '200'],
                showQuickJumper: true,
                showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                onChange: (page: number, size: number) => {
                  setPreviewTablePage(page);
                  if (size !== previewTablePageSize) {
                    setPreviewTablePageSize(size);
                    setPreviewTablePage(1);
                  }
                },
              }}
              scroll={{ y: 520 }}
            />
          </div>
        ) : null}
        {previewKind === 'json' ? (
          <Tabs
            items={[
              {
                key: 'readable',
                label: '可读视图',
                children: (() => {
                  const info = previewStageReadable;
                  if (!info) return <Text type="secondary">（暂无可读视图）</Text>;
                  const sk = String(info?.stage_key || '').toUpperCase();
                  const snap = (info?.snapshot || {}) as AnyObj;

                  // PICK
                  if (sk === 'PICK') {
                    const stats = (snap?.stats || {}) as AnyObj;
                    const logs = Array.isArray(snap?.pick_logs) ? snap.pick_logs : [];
                    const uiCtx = (snap?.ui_context && typeof snap.ui_context === 'object') ? (snap.ui_context as AnyObj) : {};
                    const pickMode = String((snap as any)?.pick_mode || (uiCtx as any)?.pick_mode || '').trim().toLowerCase();
                    const isIocPick = pickMode === 'ioc' || !!(uiCtx as any)?.ioc_snapshot_preview;

                    // PICK（历史指令产出）专用可读视图：对齐执行页拣货快照样式与字段
                    if (isIocPick) {
                      const previewInSnap = ((uiCtx as any)?.ioc_snapshot_preview && typeof (uiCtx as any).ioc_snapshot_preview === 'object')
                        ? ((uiCtx as any).ioc_snapshot_preview as AnyObj)
                        : ({} as AnyObj);
                      const pickedSet = new Set<string>(
                        (Array.isArray(logs) ? logs : [])
                          .map((r: any) => String(r?.container_code || '').trim())
                          .filter(Boolean),
                      );
                      const iocRowsRaw = Array.isArray(previewInSnap?.rows) ? previewInSnap.rows : [];
                      const iocRows = (() => {
                        if (iocRowsRaw.length > 0) {
                          return iocRowsRaw.map((r: any, idx: number) => {
                            const code = String(r?.code || r?.output_code || r?.display_code || r?.container_code || `IOC-${idx + 1}`).trim();
                            return {
                              __k: `${code}-${idx}`,
                              code,
                              location: String(r?.location || r?.location_code || '-'),
                              instructionCode: String(r?.instructionCode || r?.instruction_code || '-'),
                              containerType: String(r?.containerType || r?.container_type_display || '-'),
                              contentType: String(r?.contentType || r?.output_content_kind_display || '-'),
                              contentSummary: String(r?.contentSummary || r?.output_contents_summary || '-'),
                              contentDetail: String(r?.contentDetail || r?.output_contents_detail || '-'),
                              picked: pickedSet.has(code) || !!r?.picked,
                            };
                          });
                        }
                        const uniq = Array.from(new Set((Array.isArray(logs) ? logs : []).map((r: any) => String(r?.container_code || '').trim()).filter(Boolean)));
                        return uniq.map((code: string, idx: number) => ({
                          __k: `${code}-${idx}`,
                          code,
                          location: '-',
                          instructionCode: '-',
                          containerType: '-',
                          contentType: '-',
                          contentSummary: '-',
                          contentDetail: '-',
                          picked: pickedSet.has(code),
                        }));
                      })();
                      const iocStats = (() => {
                        const total = Number.isFinite(Number(previewInSnap?.total)) ? Math.floor(Number(previewInSnap.total)) : iocRows.length;
                        const picked = Number.isFinite(Number(previewInSnap?.picked))
                          ? Math.floor(Number(previewInSnap.picked))
                          : iocRows.filter((r: any) => !!r?.picked).length;
                        const todo = Number.isFinite(Number(previewInSnap?.todo))
                          ? Math.floor(Number(previewInSnap.todo))
                          : Math.max(total - picked, 0);
                        return { total, picked, todo };
                      })();
                      const parseContentLines = (raw: any): string[] => {
                        const txt = String(raw || '').trim();
                        if (!txt || txt === '-') return [];
                        const byLine = txt.split(/\r?\n/).map((s) => String(s || '').trim()).filter(Boolean);
                        if (byLine.length > 1) return byLine;
                        const bySemi = txt.split(/[；;]+/).map((s) => String(s || '').trim()).filter(Boolean);
                        if (bySemi.length > 1) return bySemi;
                        const byPipe = txt.split(/\s*\|\s*/).map((s) => String(s || '').trim()).filter(Boolean);
                        if (byPipe.length > 1) return byPipe;
                        return [txt];
                      };
                      return (
                        <div style={{ lineHeight: 1.7, display: 'flex', flexDirection: 'column', gap: 12 }}>
                          <div style={{ background: '#f0f9ff', border: '1px solid #91caff', borderRadius: 8, padding: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                              <div style={{ fontWeight: 700 }}>仓位表与拣货结果</div>
                              <div style={{ color: '#595959' }}>
                                目标IOC {iocStats.total} ｜ 已拣出 {iocStats.picked} ｜ 待拣出 {iocStats.todo} ｜ 记录 {(Array.isArray(logs) ? logs.length : 0)}
                              </div>
                            </div>
                            <Table
                              size="small"
                              tableLayout="fixed"
                              rowKey={(r: any) => String(r?.__k || r?.code || '')}
                              dataSource={iocRows}
                              onRow={(record: any) => ({ style: record?.picked ? { background: '#f6ffed' } : undefined })}
                              pagination={{
                                defaultPageSize: 20,
                                showSizeChanger: true,
                                pageSizeOptions: ['10', '20', '50', '100'],
                                showQuickJumper: true,
                                showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                              }}
                              columns={[
                                { title: 'IOC系统编码', dataIndex: 'code', key: 'code', width: 220, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                                { title: '仓位', dataIndex: 'location', key: 'location', width: 120, ellipsis: true },
                                { title: '来源历史指令', dataIndex: 'instructionCode', key: 'instructionCode', width: 180, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                                { title: '容器类型', dataIndex: 'containerType', key: 'containerType', width: 120, ellipsis: true },
                                { title: '内容类型', dataIndex: 'contentType', key: 'contentType', width: 120, ellipsis: true },
                                { title: '容器内容统计', dataIndex: 'contentSummary', key: 'contentSummary', width: 130, ellipsis: true },
                                {
                                  title: '容器内容',
                                  dataIndex: 'contentDetail',
                                  key: 'contentDetail',
                                  width: 320,
                                  render: (v: any, r: any) => {
                                    const lines = parseContentLines(v);
                                    if (lines.length === 0) return '-';
                                    const preview = lines.slice(0, 3);
                                    const hasMore = lines.length > 3;
                                    return (
                                      <div>
                                        {preview.map((line: string, idx: number) => (
                                          <div key={`${String(r?.code || '')}-line-${idx}`} style={{ marginBottom: idx === preview.length - 1 ? 0 : 2, wordBreak: 'break-all' }}>
                                            {line}
                                          </div>
                                        ))}
                                        {hasMore ? (
                                          <Button
                                            type="link"
                                            size="small"
                                            style={{ padding: 0, height: 'auto', lineHeight: 1.2, marginTop: 2 }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              Modal.info({
                                                title: `容器内容完整记录（${String(r?.code || '-')})`,
                                                width: 760,
                                                centered: true,
                                                content: (
                                                  <div style={{ maxHeight: 420, overflow: 'auto', paddingRight: 8 }}>
                                                    {lines.map((line: string, i: number) => (
                                                      <div key={`${String(r?.code || '-')}-${i}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                                                        {i + 1}. {line}
                                                      </div>
                                                    ))}
                                                  </div>
                                                ),
                                              });
                                            }}
                                          >
                                            [...]
                                          </Button>
                                        ) : null}
                                      </div>
                                    );
                                  },
                                },
                                {
                                  title: '拣出状态',
                                  dataIndex: 'picked',
                                  key: 'picked',
                                  width: 110,
                                  align: 'center' as const,
                                  render: (v: any) => <Tag color={v ? 'green' : 'default'}>{v ? '已拣出' : '待拣出'}</Tag>,
                                },
                              ]}
                              scroll={{ x: 1500, y: 360 }}
                            />
                          </div>

                          <div style={{ background: '#fff7e6', border: '1px solid #ffd591', borderRadius: 8, padding: 10 }}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                              <div style={{ fontWeight: 700 }}>拣货操作记录</div>
                              <div style={{ color: '#595959' }}>记录总数 {(Array.isArray(logs) ? logs.length : 0)}</div>
                            </div>
                            <Table
                              size="small"
                              tableLayout="fixed"
                              rowKey={(_, idx) => String(idx)}
                              dataSource={logs}
                              pagination={{
                                defaultPageSize: 10,
                                showSizeChanger: true,
                                pageSizeOptions: ['5', '10', '20', '50'],
                                showQuickJumper: true,
                                showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                              }}
                              columns={[
                                { title: '操作时间', dataIndex: 'operated_at', key: 'operated_at', width: 180, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{fmtDateTime(v)}</span> },
                                { title: 'IOC系统编码', dataIndex: 'container_code', key: 'container_code', width: 220, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                                { title: '动作', key: 'action', width: 100, align: 'center' as const, render: () => '扫码拣出' },
                              ]}
                              scroll={{ y: 260 }}
                            />
                          </div>
                        </div>
                      );
                    }

                    // 指令目标明细：用于终审端补齐目标数量（尤其是 use_all_available 导致 target_qty_map 为 null 的情况）
                    const instrTargets = Array.isArray((instr as any)?.targets_detail) ? ((instr as any).targets_detail as any[]) : [];
                    const expectedQtyMap = (() => {
                      const m = new Map<string, number>();
                      try {
                        instrTargets.forEach((r: any) => {
                          const snap0 = (r?.target?.snapshot || r?.snapshot || {}) as AnyObj;
                          const code =
                            String(
                              snap0?.system_barcode
                              || r?.inventory_unit_system_barcode
                              || r?.target?.inventory_unit_system_barcode
                              || ''
                            ).trim();
                          if (!code) return;
                          const v = r?.expected_qty;
                          const n = Math.floor(Number(v ?? 0));
                          if (Number.isFinite(n) && n >= 0) m.set(code, n);
                        });
                      } catch {}
                      return m;
                    })();
                    const useAllMapFromInstr = (() => {
                      const m = new Map<string, boolean>();
                      try {
                        instrTargets.forEach((r: any) => {
                          const snap0 = (r?.target?.snapshot || r?.snapshot || {}) as AnyObj;
                          const code =
                            String(
                              snap0?.system_barcode
                              || r?.inventory_unit_system_barcode
                              || r?.target?.inventory_unit_system_barcode
                              || ''
                            ).trim();
                          if (!code) return;
                          m.set(code, !!r?.use_all_available);
                        });
                      } catch {}
                      return m;
                    })();

                    // 目标数量：优先使用后端固化的 target_qty_map；没有时再尝试从 ui_context 推断
                    const targetQtyMap = (() => {
                      const m = new Map<string, number | null>();
                      try {
                        const raw = (snap as any)?.target_qty_map;
                        if (raw && typeof raw === 'object') {
                          Object.keys(raw).forEach((k) => {
                            const code = String(k || '').trim();
                            if (!code) return;
                            const v = (raw as any)[k];
                            if (v == null) {
                              m.set(code, null);
                              return;
                            }
                            const n = Math.floor(Number(v));
                            if (Number.isFinite(n) && n >= 0) m.set(code, n);
                          });
                          return m;
                        }
                      } catch {}
                      try {
                        const candRoots: any[] = [
                          (uiCtx as any)?.targetSummary,
                          (uiCtx as any)?.target_summary,
                          (uiCtx as any)?.snapshot?.targetSummary,
                          (uiCtx as any)?.snapshot?.target_summary,
                        ].filter(Boolean);
                        for (const root of candRoots) {
                          const rows = Array.isArray(root?.rows) ? root.rows : (Array.isArray(root) ? root : []);
                          for (const r of rows) {
                            const code = String(r?.target_code || r?.unit_code || r?.code || '').trim();
                            if (!code) continue;
                            const q = Number(
                              r?.target_qty ?? r?.targetQty ?? r?.expected_qty ?? r?.expectedQty ?? r?.expected ?? r?.qty_expected ?? r?.qty_target
                            );
                            if (Number.isFinite(q) && q >= 0) {
                              m.set(code, Math.floor(q));
                            }
                          }
                        }
                      } catch {}
                      return m;
                    })();

                    // 分拣标识：优先使用后端固化的 target_mark_map（对齐执行端 pick_rows.product_sorting_code 口径）
                    const targetMarkMap = (() => {
                      const m = new Map<string, string>();
                      try {
                        const raw = (snap as any)?.target_mark_map;
                        if (raw && typeof raw === 'object') {
                          Object.keys(raw).forEach((k) => {
                            const code = String(k || '').trim();
                            if (!code) return;
                            const v = String((raw as any)[k] || '').trim();
                            if (v) m.set(code, v);
                          });
                          return m;
                        }
                      } catch {}
                      return m;
                    })();

                    // 是否 use_all_available：优先使用后端固化的 target_use_all_map；没有则用指令 targets_detail 兜底
                    const targetUseAllMap = (() => {
                      const m = new Map<string, boolean>();
                      try {
                        const raw = (snap as any)?.target_use_all_map;
                        if (raw && typeof raw === 'object') {
                          Object.keys(raw).forEach((k) => {
                            const code = String(k || '').trim();
                            if (!code) return;
                            m.set(code, !!(raw as any)[k]);
                          });
                          return m;
                        }
                      } catch {}
                      return m;
                    })();

                    const sumBy = <T extends AnyObj>(arr: T[], keyFn: (x: T) => string) => {
                      const m = new Map<string, { key: string; qty: number; rows: T[]; off_shelf_count: number }>();
                      (arr || []).forEach((r) => {
                        const k = String(keyFn(r) || '').trim() || '-';
                        const q = Math.floor(Number((r as any)?.qty || 0) || 0);
                        const off = !!(r as any)?.off_shelf;
                        if (!m.has(k)) m.set(k, { key: k, qty: 0, rows: [], off_shelf_count: 0 });
                        const it = m.get(k)!;
                        it.qty += (Number.isFinite(q) ? q : 0);
                        if (off) it.off_shelf_count += 1;
                        it.rows.push(r);
                      });
                      return Array.from(m.values()).sort((a, b) => (b.qty - a.qty) || a.key.localeCompare(b.key, undefined, { numeric: true, sensitivity: 'base' }));
                    };
                    const byContainer = sumBy(logs as any[], (r: any) => String(r?.container_code || '').trim());
                    const byTarget = sumBy(logs as any[], (r: any) => String(r?.target_code || '').trim());
                    const pickTotalQtyNum = (() => {
                      const n = Number((stats as any)?.total_qty ?? (stats as any)?.totalQty ?? 0);
                      return Number.isFinite(n) ? Math.floor(n) : 0;
                    })();
                    // 拣货目标总数量：严格复用“按目标汇总”里“目标数量”列的展示口径求和
                    // - 优先 target_qty_map（数值）
                    // - 若为 null/缺失：用指令 targets_detail.expected_qty 兜底（use_all_available 场景会落到这里）
                    // 注意：按用户要求，此处以“按目标汇总（byTarget）”表格的目标集合为准求和。
                    const targetTotalQtyNum = (() => {
                      let s = 0;
                      try {
                        (byTarget || []).forEach((row: any) => {
                          const code = String(row?.key || '').trim();
                          if (!code) return;
                          const q0 = targetQtyMap.get(code);
                          const q = (q0 == null ? expectedQtyMap.get(code) : q0);
                          if (Number.isFinite(q as any)) s += Math.floor(Number(q));
                        });
                      } catch {}
                      return s;
                    })();
                    const pickDiffNum = pickTotalQtyNum - targetTotalQtyNum;
                    const diffUI = (() => {
                      const base = { borderRadius: 4, padding: '1px 8px', fontWeight: 800, display: 'inline-block' } as React.CSSProperties;
                      if (!Number.isFinite(pickDiffNum)) return { label: '拣货总差额', contentStyle: undefined as any, tag: null as any };
                      if (pickDiffNum < 0) {
                        const bg = '#e6f4ff', bd = '#91caff', fg = '#0958d9';
                        return {
                          label: '拣货总差额',
                          contentStyle: { backgroundColor: bg, border: `1px solid ${bd}`, color: fg, fontWeight: 800 } as React.CSSProperties,
                          tag: <span style={{ ...base, backgroundColor: bg, border: `1px solid ${bd}`, color: fg }}>拣货不足</span>,
                        };
                      }
                      if (pickDiffNum === 0) {
                        const bg = '#f6ffed', bd = '#b7eb8f', fg = '#389e0d';
                        return {
                          label: '拣货总差额',
                          contentStyle: { backgroundColor: bg, border: `1px solid ${bd}`, color: fg, fontWeight: 800 } as React.CSSProperties,
                          tag: <span style={{ ...base, backgroundColor: bg, border: `1px solid ${bd}`, color: fg }}>正好完成拣货目标</span>,
                        };
                      }
                      {
                        const bg = '#fff2e8', bd = '#ffbb96', fg = '#d4380d';
                        return {
                          label: '拣货总差额',
                          contentStyle: { backgroundColor: bg, border: `1px solid ${bd}`, color: fg, fontWeight: 800 } as React.CSSProperties,
                          tag: <span style={{ ...base, backgroundColor: bg, border: `1px solid ${bd}`, color: fg }}>超额拣货</span>,
                        };
                      }
                    })();
                    return (
                      <div style={{ lineHeight: 1.75 }}>
                        <Descriptions size="small" column={2} bordered>
                          <Descriptions.Item label="阶段">{stageKeyToCN(sk)}</Descriptions.Item>
                          <Descriptions.Item label="提交时间">{fmtDateTime(snap?.submitted_at)}</Descriptions.Item>
                          <Descriptions.Item label="拣货涉及仓储容器">{String(stats?.containers ?? '-')}</Descriptions.Item>
                          <Descriptions.Item label="拣货记录数">{String(Array.isArray(logs) ? logs.length : 0)}</Descriptions.Item>
                          <Descriptions.Item label="拣货目标总数量">{String(targetTotalQtyNum)}</Descriptions.Item>
                          <Descriptions.Item label="拣货目标种类">{String(stats?.kinds ?? '-')}</Descriptions.Item>
                          <Descriptions.Item label="拣货总数量">{String(pickTotalQtyNum)}</Descriptions.Item>
                          <Descriptions.Item
                            label={
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                                <span>{diffUI.label}</span>
                                {diffUI.tag}
                              </span>
                            }
                            contentStyle={diffUI.contentStyle}
                          >
                            {String(pickDiffNum)}
                          </Descriptions.Item>
                        </Descriptions>
                        {Object.keys(uiCtx || {}).length ? (
                          <>
                            <Divider style={{ margin: '10px 0' }} />
                            <div style={{ fontWeight: 800, marginBottom: 6 }}>UI上下文</div>
                            <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 220, overflowY: 'auto', overflowX: 'hidden', background: '#f6f6f6', padding: 10, borderRadius: 6 }}>
                              {JSON.stringify(uiCtx, null, 2)}
                            </pre>
                          </>
                        ) : null}
                        <Divider style={{ margin: '10px 0' }} />
                        <div style={{ fontWeight: 800, marginBottom: 6 }}>按仓储容器汇总</div>
                        <Table
                          size="small"
                          tableLayout="fixed"
                          rowKey={(r: any) => String(r?.key || '')}
                          pagination={{
                            defaultPageSize: 10,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showQuickJumper: true,
                            showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                          }}
                          dataSource={byContainer}
                          columns={[
                            { title: '容器', dataIndex: 'key', key: 'key', width: 220, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                            { title: '数量合计', dataIndex: 'qty', key: 'qty', width: 110 },
                            {
                              title: '已下架',
                              dataIndex: 'off_shelf_count',
                              key: 'off_shelf_count',
                              width: 110,
                              render: (v: any) => {
                                const n = Math.floor(Number(v || 0) || 0);
                                if (!Number.isFinite(n) || n <= 0) return <span />;
                                return <span style={{ color: '#cf1322', fontWeight: 800 }}>已下架</span>;
                              }
                            },
                          ]}
                          scroll={{ y: 240 }}
                        />

                        <Divider style={{ margin: '10px 0' }} />
                        <div style={{ fontWeight: 800, marginBottom: 6 }}>按目标汇总</div>
                        <Table
                          size="small"
                          tableLayout="fixed"
                          rowKey={(r: any) => String(r?.key || '')}
                          pagination={{
                            defaultPageSize: 10,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100'],
                            showQuickJumper: true,
                            showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                          }}
                          dataSource={byTarget}
                          columns={[
                            { title: '目标(库存单元)', dataIndex: 'key', key: 'key', width: 220, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                            {
                              title: '分拣标识',
                              key: 'sorting_mark',
                              width: 160,
                              ellipsis: true,
                              render: (_: any, r: any) => {
                                const code = String(r?.key || '').trim();
                                const m0 = targetMarkMap.get(code);
                                if (m0) return <span style={{ fontFamily: 'monospace' }}>{m0}</span>;
                                // 兜底：若快照未固化 target_mark_map，则从拣货记录的 sorting_mark 聚合
                                const marks = Array.from(new Set((r?.rows || []).map((x:any)=> String(x?.sorting_mark || '').trim()).filter(Boolean)));
                                if (!marks.length) return <span style={{ color: '#999' }}>-</span>;
                                const txt = marks.slice(0, 3).join('、');
                                return <span style={{ fontFamily: 'monospace' }}>{txt}{marks.length > 3 ? `…(+${marks.length - 3})` : ''}</span>;
                              }
                            },
                            {
                              title: '是否操作全部库存',
                              key: 'use_all_available',
                              width: 140,
                              render: (_: any, r: any) => {
                                const code = String(r?.key || '').trim();
                                const isAll = (targetUseAllMap.has(code) ? targetUseAllMap.get(code) : useAllMapFromInstr.get(code)) || false;
                                const style: React.CSSProperties = isAll
                                  ? { backgroundColor: '#f6ffed', color: '#389e0d', border: '1px solid #b7eb8f', borderRadius: 4, padding: '1px 8px', fontWeight: 700, display: 'inline-block' }
                                  : { backgroundColor: '#fff0f6', color: '#c41d7f', border: '1px solid #ffadd2', borderRadius: 4, padding: '1px 8px', fontWeight: 700, display: 'inline-block' };
                                return <span style={style}>{isAll ? '是' : '否'}</span>;
                              }
                            },
                            {
                              title: '目标数量',
                              key: 'target_qty',
                              width: 110,
                              render: (_: any, r: any) => {
                                const code = String(r?.key || '').trim();
                                const q0 = targetQtyMap.get(code);
                                if (q0 == null) {
                                  // 终审端补齐：若 target_qty_map 为 null/缺失，则用指令 targets_detail.expected_qty 兜底展示
                                  const fb = expectedQtyMap.get(code);
                                  if (Number.isFinite(fb as any)) return <span style={{ fontFamily: 'monospace' }}>{String(fb)}</span>;
                                  return <span style={{ color: '#999' }}>-</span>;
                                }
                                return Number.isFinite(q0 as any) ? <span style={{ fontFamily: 'monospace' }}>{String(q0)}</span> : <span style={{ color: '#999' }}>-</span>;
                              }
                            },
                            { title: '已拣货数量', dataIndex: 'qty', key: 'qty', width: 110 },
                            {
                              title: '拣货差额',
                              key: 'diff_qty',
                              width: 110,
                              render: (_: any, r: any) => {
                                const code = String(r?.key || '').trim();
                                const tgt0 = targetQtyMap.get(code);
                                const tgt = (tgt0 == null ? expectedQtyMap.get(code) : tgt0);
                                const done = Math.floor(Number(r?.qty || 0) || 0);
                                if (tgt == null || !Number.isFinite(tgt as any)) return <span style={{ color: '#999' }}>-</span>;
                                const diff = Number(tgt) - done; // 与执行端一致：差额=目标-已拣（可为负）
                                const color = diff > 0 ? '#cf1322' : (diff < 0 ? '#fa8c16' : '#389e0d');
                                return <span style={{ fontFamily: 'monospace', fontWeight: 800, color }}>{String(diff)}</span>;
                              }
                            },
                          ]}
                          scroll={{ y: 240 }}
                        />

                        <Divider style={{ margin: '10px 0' }} />
                        <div style={{ fontWeight: 800, marginBottom: 6 }}>拣货操作记录</div>
                        <Table
                          size="small"
                          tableLayout="fixed"
                          rowKey={(_, idx) => String(idx)}
                          pagination={{
                            defaultPageSize: 20,
                            showSizeChanger: true,
                            pageSizeOptions: ['20', '50', '100', '200'],
                            showQuickJumper: true,
                            showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                          }}
                          dataSource={logs}
                          columns={[
                            { title: '容器', dataIndex: 'container_code', key: 'container_code', width: 160, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                            { title: '目标(库存单元)', dataIndex: 'target_code', key: 'target_code', width: 180, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                            { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', width: 140, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                            { title: '数量', dataIndex: 'qty', key: 'qty', width: 90 },
                            { title: '下架', dataIndex: 'off_shelf', key: 'off_shelf', width: 90, render: (v: any) => (v ? '是' : '否') },
                            { title: '操作时间', dataIndex: 'operated_at', key: 'operated_at', width: 180, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{fmtDateTime(v)}</span> },
                          ]}
                          scroll={{ y: 420 }}
                        />
                      </div>
                    );
                  }

                  // SORT_COUNT / SORT_AND_COUNT
                  if (sk === 'SORT_COUNT' || sk === 'SORT_AND_COUNT') {
                    const root = (snap || {}) as AnyObj;
                    const real = (root?.snapshot && typeof root.snapshot === 'object') ? (root.snapshot as AnyObj) : root;
                    const stats = (root?.stats && typeof root.stats === 'object') ? (root.stats as AnyObj) : ((real?.stats || {}) as AnyObj);
                    const ctx = (real?.instruction_context && typeof real.instruction_context === 'object')
                      ? (real.instruction_context as AnyObj)
                      : ({} as AnyObj);
                    const sections = (real?.sections && typeof real.sections === 'object')
                      ? (real.sections as AnyObj)
                      : ({} as AnyObj);
                    const targetRows = Array.isArray(sections?.operation_targets?.rows) ? sections.operation_targets.rows : [];
                    const sortDatabaseRows = Array.isArray(sections?.sort_database_rows?.rows) ? sections.sort_database_rows.rows : [];
                    const operationRows = Array.isArray(sections?.operation_records?.rows) ? sections.operation_records.rows : [];
                    const byMarkRows = Array.isArray(sections?.stats_by_mark?.rows) ? sections.stats_by_mark.rows : [];
                    const generatedContainerRows = Array.isArray(sections?.generated_containers?.rows) ? sections.generated_containers.rows : [];
                    const generatedDetailRows = Array.isArray(sections?.generated_containers?.detail_rows) ? sections.generated_containers.detail_rows : [];
                    const renderSortCountTargetContentItems = (row: any) => {
                      const extractItems = (): Array<{ barcode: string; qty: number; productName?: string; customCode?: string }> => {
                        try {
                          const snapObj = row?.target?.snapshot || row?.snapshot || {};
                          const fromInventoryContentItems = Array.isArray(row?.inventory_content_items) ? row.inventory_content_items : [];
                          if (fromInventoryContentItems.length > 0) {
                            return fromInventoryContentItems.map((it: any) => {
                              const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
                              const productName = String(it?.product_name || it?.productName || it?.name || '').trim();
                              const customCode = String(it?.product_custom_code || it?.customCode || it?.product_code || '').trim();
                              let barcode = '';
                              try {
                                const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                                if (sc.length > 0) {
                                  const codes = sc.map((c: any) => String(c?.code || '')).filter(Boolean);
                                  if (codes.length > 0) barcode = codes.join('/');
                                }
                              } catch {}
                              if (!barcode) {
                                barcode = String(
                                  it?.product_sorting_barcode
                                  || it?.sorting_barcode
                                  || it?.barcode
                                  || it?.product_system_code
                                  || it?.system_code
                                  || it?.product_code
                                  || ''
                                ).trim();
                              }
                              return { barcode, qty, productName, customCode };
                            }).filter((x: any) => x.barcode || x.qty);
                          }
                          const candidates: any[] = [
                            snapObj?.content?.items,
                            row?.content?.items,
                            row?.target?.content?.items,
                            row?.items,
                          ].filter(Boolean);
                          for (const cand of candidates) {
                            const arr = Array.isArray(cand) ? cand : [];
                            if (arr.length > 0) {
                              return arr.map((it: any) => {
                                const p = it?.product || {};
                                const qty = Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0;
                                const productName = String(it?.product_name || p?.name || '').trim();
                                const customCode = String(
                                  it?.product_custom_code
                                  || p?.custom_code
                                  || it?.product_code
                                  || ''
                                ).trim();
                                let barcode = '';
                                try {
                                  const sc = Array.isArray(it?.sorting_codes) ? it.sorting_codes : [];
                                  if (sc.length > 0) {
                                    const codes = sc.map((c: any) => String(c?.code || '')).filter(Boolean);
                                    if (codes.length > 0) barcode = codes.join('/');
                                  }
                                } catch {}
                                if (!barcode) {
                                  barcode = String(
                                    it?.product_system_code
                                    || it?.system_code
                                    || it?.product_code
                                    || ''
                                  ).trim();
                                }
                                return { barcode, qty, productName, customCode };
                              }).filter((x: any) => x.barcode || x.qty);
                            }
                          }
                        } catch {}
                        return [];
                      };
                      const items = extractItems();
                      if (!items || items.length === 0) return <span style={{ color: '#999' }}>-</span>;
                      return (
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {items.map((it: any, idx: number) => {
                            const productName = String(it?.productName || '').trim();
                            const customCode = String(it?.customCode || '').trim();
                            const barcode = String(it?.barcode || '').trim();
                            const meta = [customCode, barcode].filter(Boolean).join(' ');
                            let head = '-';
                            if (productName && meta) head = `${productName}(${meta})`;
                            else if (productName) head = productName;
                            else if (meta) head = meta;
                            return (
                              <div key={`sort-target-item-${idx}`}>
                                {`${head} × ${Math.max(0, Number(it?.qty || 0))}`}
                              </div>
                            );
                          })}
                        </div>
                      );
                    };
                    const extractSortCountTargetItems = (row: any): Array<{ qty: number }> => {
                      try {
                        const snapObj = row?.target?.snapshot || row?.snapshot || {};
                        const fromInventoryContentItems = Array.isArray(row?.inventory_content_items) ? row.inventory_content_items : [];
                        if (fromInventoryContentItems.length > 0) {
                          return fromInventoryContentItems.map((it: any) => ({ qty: Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0 }));
                        }
                        const candidates: any[] = [
                          snapObj?.content?.items,
                          row?.content?.items,
                          row?.target?.content?.items,
                          row?.items,
                        ].filter(Boolean);
                        for (const cand of candidates) {
                          const arr = Array.isArray(cand) ? cand : [];
                          if (arr.length > 0) {
                            return arr.map((it: any) => ({ qty: Number(it?.quantity ?? it?.qty ?? it?.count ?? 0) || 0 }));
                          }
                        }
                      } catch {}
                      return [];
                    };
                    const calcEstimatedPendingQtyForTarget = (row: any): number | null => {
                      const unitQty = Math.max(0, Number(row?.expected_qty ?? row?.target_qty ?? 0) || 0);
                      const items = extractSortCountTargetItems(row);
                      if (!Array.isArray(items) || items.length <= 0) return null;
                      const perUnit = items.reduce((acc: number, it: any) => {
                        const q = Number(it?.qty ?? 0);
                        if (!Number.isFinite(q) || q <= 0) return acc;
                        return acc + q;
                      }, 0);
                      return Math.max(0, Math.floor(perUnit * unitQty));
                    };
                    const getSnapshotTargetUnitType = (row: any): string => {
                      const snap = row?.target?.snapshot || row?.snapshot || {};
                      const direct = String(
                        row?.unit_type
                        || row?.unit_type_display
                        || snap?.unit_type
                        || snap?.unit_type_display
                        || '',
                      ).trim().toUpperCase();
                      if (direct === 'BIU' || direct.includes('箱库存单元')) return 'BIU';
                      if (direct === 'LIU' || direct.includes('散货库存单元')) return 'LIU';
                      if (direct === 'PIU' || direct.includes('托盘库存单元')) return 'PIU';
                      const iuCode = String(
                        row?.inventory_unit_system_barcode
                        || row?.inventory_unit_code
                        || row?.target_code
                        || snap?.system_barcode
                        || snap?.system_code
                        || '',
                      ).trim().toUpperCase();
                      if (iuCode.startsWith('BIU')) return 'BIU';
                      if (iuCode.startsWith('LIU')) return 'LIU';
                      if (iuCode.startsWith('PIU')) return 'PIU';
                      return '';
                    };
                    const calcEstimatedOperationTargetQtyForSnapshotTarget = (row: any): number | null => {
                      const demandMode = String(ctx?.demand_mode || '').trim();
                      if (demandMode === 'need1' && getSnapshotTargetUnitType(row) === 'BIU') {
                        const v = Number(row?.expected_qty ?? row?.target_qty ?? 0);
                        if (!Number.isFinite(v)) return 0;
                        return Math.floor(Math.max(0, v));
                      }
                      return calcEstimatedPendingQtyForTarget(row);
                    };
                    const renderSortCountHeaderDescription = () => {
                      const text = String(ctx?.header_display || '').trim();
                      if (!text) return '该快照固化提交时的分拣清点操作结果。';
                      const parts = text
                        .split('|')
                        .map((x: string) => String(x || '').trim())
                        .filter((x: string) => !!x && !x.includes('库存转换'));
                      if (parts.length <= 0) return '该快照固化提交时的分拣清点操作结果。';
                      return (
                        <span>
                          {parts.map((part: string, idx: number) => (
                            <React.Fragment key={`${part}-${idx}`}>
                              {idx > 0 ? <span style={{ margin: '0 10px', color: '#8c8c8c' }}>|</span> : null}
                              <span>{part}</span>
                            </React.Fragment>
                          ))}
                        </span>
                      );
                    };
                    const containerInfoMap = new Map<string, any>();
                    generatedContainerRows.forEach((c: any) => {
                      const code = String(c?.system_code || '').trim();
                      if (code) containerInfoMap.set(code, c);
                    });
                    const containerGroupKeys = Array.from(new Set(
                      (Array.isArray(generatedDetailRows) ? generatedDetailRows : [])
                        .map((r: any) => String(r?.system_code || r?.id || '').trim())
                        .filter(Boolean),
                    ));
                    const effectiveContainerGroupKeys: string[] = containerGroupKeys.length > 0
                      ? containerGroupKeys
                      : Array.from(new Set((generatedContainerRows || []).map((r: any) => String(r?.system_code || '').trim()).filter(Boolean)));
                    const containerGroupsForSnapshot = effectiveContainerGroupKeys.map((groupKey: string, groupIdx: number) => {
                      const rows = (Array.isArray(generatedDetailRows) ? generatedDetailRows : [])
                        .filter((r: any) => String(r?.system_code || r?.id || '').trim() === groupKey);
                      const first = rows[0] || {};
                      const code = String(first?.system_code || groupKey || '').trim();
                      const info = code ? containerInfoMap.get(code) : null;
                      const totalQty = Math.max(
                        0,
                        Number(first?.total_qty || info?.total_qty || rows.reduce((sum: number, r: any) => sum + Math.max(0, Number(r?.qty || 0)), 0)),
                      );
                      return {
                        key: groupKey,
                        seq: groupIdx + 1,
                        bg: groupIdx % 2 === 0 ? '#f0f9ff' : '#f6ffed',
                        system_code: code,
                        container_type: String(first?.container_type || info?.container_type_display || info?.container_type || ''),
                        total_qty: totalQty,
                        rows,
                      };
                    });
                    const containerDisplayRows = containerGroupsForSnapshot.flatMap((group: any) => {
                      const rows = Array.isArray(group?.rows) && group.rows.length > 0 ? group.rows : [{}];
                      return rows.map((r: any, idx: number) => ({
                        ...(r || {}),
                        __containerGroupKey: group.key,
                        __containerSeq: group.seq,
                        __containerRowSpanForSnapshot: idx === 0 ? rows.length : 0,
                        __containerBg: group.bg,
                        system_code: String(r?.system_code || group.system_code || '').trim(),
                        container_type: group.container_type,
                        total_qty: group.total_qty,
                      }));
                    });
                    const demandModeText = (() => {
                      const v = String(ctx?.demand_mode || '').trim();
                      if (v === 'need1') return '需求1';
                      if (v === 'need2') return '需求2';
                      if (v === 'need3') return '需求3';
                      return v || '-';
                    })();
                    const sortGroupCount = Array.from(new Set(
                      (Array.isArray(sortDatabaseRows) ? sortDatabaseRows : [])
                        .map((r: any) => String(r?.group_code || r?.sorting_group || r?.sorting_group_code || '').trim())
                        .filter(Boolean),
                    )).length;
                    const estimatedPendingProductTotal = (() => {
                      let sum = 0;
                      let missing = false;
                      (Array.isArray(targetRows) ? targetRows : []).forEach((row: any) => {
                        const v = calcEstimatedOperationTargetQtyForSnapshotTarget(row);
                        if (v == null) {
                          missing = true;
                          return;
                        }
                        sum += v;
                      });
                      if (missing) return null;
                      return Math.max(0, Math.floor(sum));
                    })();
                    const sortedTotal = Number(stats?.sorted_total_qty ?? operationRows.length ?? 0);
                    const diffQty = estimatedPendingProductTotal == null ? null : Math.floor(estimatedPendingProductTotal - sortedTotal);
                    const snapshotLocationBadgeStyle: React.CSSProperties = {
                      display: 'inline-block',
                      minWidth: 44,
                      padding: '2px 10px',
                      borderRadius: 6,
                      background: '#ffeb3b',
                      border: '1px solid #fadb14',
                      color: '#cf1322',
                      fontWeight: 800,
                      lineHeight: 1.2,
                      textAlign: 'center',
                    };
                    const snapshotSortMarkBadgeStyle: React.CSSProperties = {
                      display: 'inline-block',
                      minWidth: 64,
                      padding: '2px 10px',
                      borderRadius: 6,
                      background: '#e6f7ff',
                      border: '1px solid #91d5ff',
                      color: '#0050b3',
                      fontWeight: 700,
                      lineHeight: 1.2,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      verticalAlign: 'middle',
                    };
                    const renderSnapshotLocationBadge = (v: any) => {
                      const txt = String(v ?? '').trim();
                      if (!txt) return '-';
                      return <span style={snapshotLocationBadgeStyle}>{txt}</span>;
                    };
                    const renderSnapshotSortMarkBadge = (v: any) => {
                      const txt = String(v ?? '').trim().toUpperCase();
                      if (!txt) return '-';
                      const shown = txt.length > 20 ? `${txt.slice(0, 10)}...${txt.slice(-10)}` : txt;
                      return (
                        <span style={snapshotSortMarkBadgeStyle} title={txt}>
                          {shown}
                        </span>
                      );
                    };
                    return (
                      <div style={{ lineHeight: 1.75 }}>
                        <Alert
                          type={Number(stats?.location_retention_total_qty || 0) > 0 ? 'warning' : 'success'}
                          showIcon
                          style={{ marginBottom: 12 }}
                          message={`分拣清点阶段快照 | ${demandModeText}`}
                          description={renderSortCountHeaderDescription()}
                        />
                        <Descriptions size="small" column={4} bordered>
                          <Descriptions.Item label="预估操作对象数量">{String(stats?.operation_target_expected_qty_total ?? targetRows.length ?? 0)}</Descriptions.Item>
                          <Descriptions.Item label="预估分拣目标总数量">{estimatedPendingProductTotal == null ? '-' : String(estimatedPendingProductTotal)}</Descriptions.Item>
                          <Descriptions.Item label="已分拣目标总数量">{String(sortedTotal)}</Descriptions.Item>
                          <Descriptions.Item label="差额">
                            {diffQty == null ? '-' : <span style={{ background: '#ffeb3b', border: '1px solid #fadb14', color: '#cf1322', fontWeight: 800, padding: '1px 8px', borderRadius: 4 }}>{String(diffQty)}</span>}
                          </Descriptions.Item>
                          <Descriptions.Item label="分拣标识数量">{String(stats?.by_mark_count ?? byMarkRows.length ?? 0)}</Descriptions.Item>
                          <Descriptions.Item label="分拣组数量">{String(sortGroupCount)}</Descriptions.Item>
                          <Descriptions.Item label="已装入容器" span={2}>
                            {`${Number(stats?.generated_container_count || generatedContainerRows.length || 0)}容器 ${Number(stats?.generated_container_total_qty || 0)}件`}
                          </Descriptions.Item>
                        </Descriptions>
                        <Divider style={{ margin: '10px 0' }} />
                        <Tabs
                          size="small"
                          items={[
                            {
                              key: 'targets',
                              label: `操作对象(${targetRows.length})`,
                              children: (
                                <Table
                                  size="small"
                                  tableLayout="fixed"
                                  rowKey={(_: any, idx?: number) => String(idx ?? 0)}
                                  dataSource={targetRows}
                                  pagination={{
                                    defaultPageSize: 10,
                                    showSizeChanger: true,
                                    pageSizeOptions: ['10', '20', '50', '100'],
                                    showQuickJumper: true,
                                    showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                                  }}
                                  columns={[
                                    { title: '库存单元系统编码', key: 'inventory_unit', width: 220, ellipsis: true, render: (_: any, row: any) => <span style={{ fontFamily: 'monospace' }}>{String(row?.inventory_unit_system_barcode || row?.snapshot?.system_barcode || row?.inventory_unit_code || '-')}</span> },
                                    { title: '分拣标识', key: 'sorting_mark', width: 200, ellipsis: true, render: (_: any, row: any) => <span style={{ fontFamily: 'monospace' }}>{String(row?.sorting_mark || row?.mark || '-')}</span> },
                                    { title: '库存单元内容项', key: 'inventory_unit_items', width: 420, render: (_: any, row: any) => renderSortCountTargetContentItems(row) },
                                    { title: '预估库存单元数量', key: 'expected_qty', width: 120, render: (_: any, row: any) => String(row?.expected_qty ?? row?.target_qty ?? '-') },
                                    { title: '预估分拣目标数量', key: 'estimated_pending_product_qty', width: 150, render: (_: any, row: any) => {
                                      const v = calcEstimatedOperationTargetQtyForSnapshotTarget(row);
                                      return v == null ? '-' : String(v);
                                    } },
                                  ]}
                                  scroll={{ y: 320 }}
                                  locale={{ emptyText: '暂无操作对象快照' }}
                                />
                              ),
                            },
                            {
                              key: 'sort_db',
                              label: `分拣数据库与仓位表(${sortDatabaseRows.length})`,
                              children: (
                                <Table
                                  size="small"
                                  tableLayout="fixed"
                                  rowKey={(r: any, idx?: number) => String(r?.id || `${String(r?.sorting_mark || '').trim()}-${String(r?.sort_id || '').trim()}-${idx ?? 0}`)}
                                  dataSource={sortDatabaseRows}
                                  pagination={{
                                    defaultPageSize: 20,
                                    showSizeChanger: true,
                                    pageSizeOptions: ['20', '50', '100', '200'],
                                    showQuickJumper: true,
                                    showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                                  }}
                                  columns={[
                                    {
                                      title: '仓位',
                                      dataIndex: 'location_no',
                                      key: 'location_no',
                                      width: 110,
                                      align: 'center' as const,
                                      render: (v: any) => renderSnapshotLocationBadge(v),
                                    },
                                    {
                                      title: '分拣标识',
                                      dataIndex: 'sorting_mark',
                                      key: 'sorting_mark',
                                      width: 220,
                                      ellipsis: true,
                                      render: (v: any) => renderSnapshotSortMarkBadge(v),
                                    },
                                    {
                                      title: '分拣组',
                                      dataIndex: 'group_code',
                                      key: 'group_code',
                                      width: 120,
                                      align: 'center' as const,
                                      render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>,
                                    },
                                    {
                                      title: '分拣ID',
                                      dataIndex: 'sort_id',
                                      key: 'sort_id',
                                      width: 110,
                                      align: 'center' as const,
                                      render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>,
                                    },
                                    {
                                      title: '预设分组编号',
                                      dataIndex: 'preset_group_no',
                                      key: 'preset_group_no',
                                      width: 140,
                                      align: 'center' as const,
                                      render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>,
                                    },
                                  ]}
                                  scroll={{ y: 320, x: 920 }}
                                  locale={{ emptyText: '暂无分拣数据库与仓位表快照' }}
                                />
                              ),
                            },
                            {
                              key: 'by_mark',
                              label: `按分拣标识统计(${byMarkRows.length})`,
                              children: (
                                <Table
                                  size="small"
                                  tableLayout="fixed"
                                  rowKey={(_: any, idx?: number) => String(idx ?? 0)}
                                  dataSource={byMarkRows}
                                  pagination={{
                                    defaultPageSize: 10,
                                    showSizeChanger: true,
                                    pageSizeOptions: ['10', '20', '50', '100'],
                                    showQuickJumper: true,
                                    showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                                  }}
                                  columns={[
                                    { title: '分拣标识', key: 'mark', width: 200, ellipsis: true, render: (_: any, row: any) => renderSnapshotSortMarkBadge(row?.sorting_mark || row?.mark || row?.sorting_code) },
                                    { title: '数量', key: 'qty', width: 120, render: (_: any, row: any) => String(row?.qty ?? row?.total_qty ?? row?.count ?? 0) },
                                  ]}
                                  scroll={{ y: 320 }}
                                  locale={{ emptyText: '暂无分拣标识统计快照' }}
                                />
                              ),
                            },
                            {
                              key: 'records',
                              label: `操作记录(${operationRows.length})`,
                              children: (
                                <Table
                                  size="small"
                                  tableLayout="fixed"
                                  rowKey={(_: any, idx?: number) => String(idx ?? 0)}
                                  dataSource={operationRows}
                                  pagination={{
                                    defaultPageSize: 20,
                                    showSizeChanger: true,
                                    pageSizeOptions: ['20', '50', '100', '200'],
                                    showQuickJumper: true,
                                    showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                                  }}
                                  columns={[
                                    { title: '分拣标识', dataIndex: 'sorting_mark', key: 'sorting_mark', width: 150, ellipsis: true, render: (v: any) => renderSnapshotSortMarkBadge(v) },
                                    { title: '数量', dataIndex: 'qty', key: 'qty', width: 100 },
                                    { title: '操作时间', dataIndex: 'time_text', key: 'time_text', width: 180, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                                  ]}
                                  scroll={{ y: 360 }}
                                  locale={{ emptyText: '暂无操作记录快照' }}
                                />
                              ),
                            },
                            {
                              key: 'containers',
                              label: `已生成容器(${containerGroupsForSnapshot.length})`,
                              children: (
                                <Table
                                  size="small"
                                  tableLayout="fixed"
                                  rowKey={(r: any, i?: number) => String(r?.__rowKey || `${r?.system_code || r?.__containerGroupKey || ''}-${i || 0}`)}
                                  dataSource={containerDisplayRows}
                                  onRow={(row: any) => ({
                                    style: { background: row?.__containerBg || undefined },
                                  })}
                                  pagination={{
                                    defaultPageSize: 10,
                                    showSizeChanger: true,
                                    pageSizeOptions: ['10', '20', '50', '100'],
                                    showQuickJumper: true,
                                    showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                                  }}
                                  columns={[
                                    {
                                      title: '序号',
                                      dataIndex: '__containerSeq',
                                      key: '__containerSeq',
                                      width: 70,
                                      align: 'center' as const,
                                      onCell: (row: any) => ({
                                        rowSpan: Number(row?.__containerRowSpanForSnapshot || 0),
                                        style: { background: row?.__containerBg || undefined },
                                      }),
                                    },
                                    {
                                      title: '容器系统编码',
                                      dataIndex: 'system_code',
                                      key: 'system_code',
                                      width: 180,
                                      ellipsis: true,
                                      render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>,
                                      onCell: (row: any) => ({
                                        rowSpan: Number(row?.__containerRowSpanForSnapshot || 0),
                                        style: { background: row?.__containerBg || undefined },
                                      }),
                                    },
                                    {
                                      title: '容器类型',
                                      key: 'container_type',
                                      width: 120,
                                      ellipsis: true,
                                      render: (_: any, row: any) => String(row?.container_type_display || row?.container_type || '-'),
                                      onCell: (row: any) => ({
                                        rowSpan: Number(row?.__containerRowSpanForSnapshot || 0),
                                        style: { background: row?.__containerBg || undefined },
                                      }),
                                    },
                                    {
                                      title: '库存单元',
                                      dataIndex: 'unit_code',
                                      key: 'unit_code',
                                      width: 170,
                                      ellipsis: true,
                                      render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>,
                                      onCell: (row: any) => ({ style: { background: row?.__containerBg || undefined } }),
                                    },
                                    {
                                      title: '分拣标识',
                                      dataIndex: 'sorting_mark',
                                      key: 'sorting_mark',
                                      width: 170,
                                      ellipsis: true,
                                      render: (v: any) => renderSnapshotSortMarkBadge(v),
                                      onCell: (row: any) => ({ style: { background: row?.__containerBg || undefined } }),
                                    },
                                    {
                                      title: '数量',
                                      dataIndex: 'qty',
                                      key: 'qty',
                                      width: 100,
                                      align: 'center' as const,
                                      onCell: (row: any) => ({ style: { background: row?.__containerBg || undefined } }),
                                    },
                                  ]}
                                  scroll={{ y: 320 }}
                                  locale={{ emptyText: '暂无容器快照' }}
                                />
                              ),
                            },
                          ]}
                        />
                      </div>
                    );
                  }

                  // RELABEL_PACK
                  if (sk === 'RELABEL_PACK') {
                    const stats = (snap?.stats || {}) as AnyObj;
                    const inner = (snap?.snapshot && typeof snap.snapshot === 'object') ? (snap.snapshot as AnyObj) : (snap as AnyObj);
                    const groups: any[] = Array.isArray(inner?.groups) ? inner.groups : [];
                    const g0 = groups[0] || {};
                    const progress = (g0?.progress_snapshot && typeof g0.progress_snapshot === 'object') ? g0.progress_snapshot : {};
                    const overall = progress?.overall || null;
                    const mappingRows: any[] = Array.isArray(progress?.mapping_rows) ? progress.mapping_rows : [];
                    const boxRows: any[] = Array.isArray(progress?.boxes_rows) ? progress.boxes_rows : [];
                    const pool = (progress?.pool_summary && typeof progress.pool_summary === 'object') ? progress.pool_summary : null;
                    const records: any[] = Array.isArray(g0?.records_preview) ? g0.records_preview : [];
                    const outputDrafts: any[] = Array.isArray(g0?.output_container_drafts) ? g0.output_container_drafts : [];
                    const genLabels = (inner?.generated_labels && typeof inner.generated_labels === 'object') ? (inner.generated_labels as AnyObj) : {};

                    const parseTs = (v: any): number => {
                      if (v == null) return 0;
                      const n = Number(v);
                      if (Number.isFinite(n) && n > 0) return n;
                      try {
                        const d = new Date(String(v));
                        const t = d.getTime();
                        if (Number.isFinite(t) && t > 0) return t;
                      } catch {}
                      return 0;
                    };
                    const fmtDurationMs = (msRaw: any): string => {
                      const ms = Math.floor(Number(msRaw || 0) || 0);
                      if (!Number.isFinite(ms) || ms <= 0) return '-';
                      const totalSec = Math.floor(ms / 1000);
                      const hh = Math.floor(totalSec / 3600);
                      const mm = Math.floor((totalSec % 3600) / 60);
                      const ss = totalSec % 60;
                      const pad2 = (x: number) => String(x).padStart(2, '0');
                      if (hh > 0) return `${hh}:${pad2(mm)}:${pad2(ss)}`;
                      return `${mm}:${pad2(ss)}`;
                    };
                    // 固定格式：小时:分钟:秒（不补齐两位，示例：0:0:24、0:1:2）
                    const fmtDurationHMS = (msRaw: any): string => {
                      const ms = Math.floor(Number(msRaw || 0) || 0);
                      if (!Number.isFinite(ms) || ms <= 0) return '-';
                      const totalSec = Math.floor(ms / 1000);
                      const hh = Math.floor(totalSec / 3600);
                      const mm = Math.floor((totalSec % 3600) / 60);
                      const ss = totalSec % 60;
                      return `${hh}:${mm}:${ss}`;
                    };

                    // 兜底：按“全量操作记录 records_preview”聚合每个容器的开始/结束时间（boxRows 未必包含 start/end 字段）
                    const containerTimeMap = (() => {
                      const m = new Map<string, { start: number; end: number }>();
                      try {
                        (records || []).forEach((r: any) => {
                          const cc = String(r?.container_code || r?.containerCode || '').trim();
                          if (!cc) return;
                          const t = parseTs(r?.ts || r?.time || r?.created_at || r?.createdAt);
                          if (!(t > 0)) return;
                          if (!m.has(cc)) {
                            m.set(cc, { start: t, end: t });
                            return;
                          }
                          const it = m.get(cc)!;
                          if (t < it.start) it.start = t;
                          if (t > it.end) it.end = t;
                        });
                      } catch {}
                      return m;
                    })();

                    // 标题区统计：按条码映射统计
                    const mappingStats = (() => {
                      try {
                        const rows = Array.isArray(mappingRows) ? mappingRows : [];
                        const kinds = new Set<string>();
                        let doneSum = 0;
                        rows.forEach((r: any) => {
                          const newer = String(r?.newer || '').trim();
                          if (newer) kinds.add(newer);
                          doneSum += (Number(r?.used || 0) || 0);
                        });
                        return { newKinds: kinds.size, doneSum };
                      } catch {
                        return { newKinds: 0, doneSum: 0 };
                      }
                    })();

                    // 标题区统计：按产出容器统计（不含调整池）
                    const isCancelledContainerRow = (row: any): boolean => {
                      const st = String(row?.status || row?.container_status || row?.containerStatus || '').trim().toLowerCase();
                      const stZh = String(row?.status_zh || row?.statusZh || '').trim();
                      return st === 'cancelled' || stZh.includes('作废');
                    };
                    const boxStats = (() => {
                      try {
                        const rows = Array.isArray(boxRows) ? boxRows : [];
                        const totalContainers = new Set<string>();
                        let loadedQtySum = 0;
                        rows.forEach((r: any) => {
                          if (isCancelledContainerRow(r)) return;
                          const cc = String(r?.container_code || '').trim();
                          if (cc) totalContainers.add(cc);
                          const q = Number(r?.qty || 0) || 0;
                          if (q > 0) loadedQtySum += q;
                        });
                        // 总耗时：全局最早开始 ~ 最晚结束
                        let minStart = 0;
                        let maxEnd = 0;
                        containerTimeMap.forEach((v) => {
                          if (v?.start > 0 && (minStart === 0 || v.start < minStart)) minStart = v.start;
                          if (v?.end > 0 && v.end > maxEnd) maxEnd = v.end;
                        });
                        const durationMs = (minStart > 0 && maxEnd > 0 && maxEnd > minStart) ? (maxEnd - minStart) : 0;
                        return {
                          totalContainers: totalContainers.size,
                          loadedQtySum,
                          durationMs,
                        };
                      } catch {
                        return { totalContainers: 0, loadedQtySum: 0, durationMs: 0 };
                      }
                    })();

                    const renderMatchTargets = (txtRaw: any) => {
                      const txt = String(txtRaw ?? '').trim();
                      if (!txt || txt === '-' || txt === '[]') return <span style={{ color: '#cf1322', fontWeight: 700 }}>-</span>;
                      let arr: any[] = [];
                      try { arr = JSON.parse(txt); } catch { arr = []; }
                      const list = Array.isArray(arr) ? arr : [];
                      if (!list.length) return <span style={{ color: '#cf1322', fontWeight: 700 }}>-</span>;
                      const nodes = list.slice(0, 3).map((x: any, i: number) => {
                        const sys = String(x?.system_barcode || (x?.iu_id ? `IU#${x.iu_id}` : '') || '').trim() || '-';
                        const useAll = !!x?.use_all_available;
                        const eq = (x?.expected_qty == null || x?.expected_qty === '') ? null : Number(x.expected_qty);
                        const eqTxt = (eq == null || !Number.isFinite(eq)) ? '-' : String(eq);
                        const bg = useAll ? '#e6f4ff' : '#ff7a45';
                        const bd = useAll ? '#91caff' : '#ff7a45';
                        const fg = useAll ? '#0958d9' : '#ffffff';
                        return (
                          <Tag
                            key={`mt_${i}`}
                            style={{ marginInlineEnd: 0, marginBottom: 4, background: bg, borderColor: bd, color: fg, borderRadius: 999, fontWeight: 700 }}
                          >
                            <span style={{ fontFamily: 'monospace' }}>{sys}</span>
                            <span style={{ marginLeft: 6 }}>{useAll ? '是' : '否'}</span>
                            <span style={{ marginLeft: 6 }}>{`预${eqTxt}`}</span>
                          </Tag>
                        );
                      });
                      const more = list.length > 3 ? list.slice(3) : [];
                      if (!more.length) return <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>{nodes}</span>;
                      return (
                        <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                          {nodes}
                          <Tooltip
                            title={
                              <div style={{ maxWidth: 520, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                {list.map((x: any, idx: number) => {
                                  const sys = String(x?.system_barcode || (x?.iu_id ? `IU#${x.iu_id}` : '') || '').trim() || '-';
                                  return <Tag key={`mt_full_${idx}`} style={{ marginInlineEnd: 0, borderRadius: 999, fontWeight: 700 }}>{sys}</Tag>;
                                })}
                              </div>
                            }
                          >
                            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, cursor: 'pointer', color: '#0958d9', borderColor: '#91caff', background: '#e6f4ff', fontWeight: 700 }}>
                              +{more.length}
                            </Tag>
                          </Tooltip>
                        </span>
                      );
                    };

                    const outputDraftByCode = (() => {
                      const m = new Map<string, any>();
                      try {
                        (Array.isArray(outputDrafts) ? outputDrafts : []).forEach((d: any) => {
                          const cc = String(d?.code || '').trim();
                          if (cc) m.set(cc, d);
                        });
                      } catch {}
                      return m;
                    })();

                    const parseDimsCmText = (raw: any): [number, number, number] | null => {
                      const s = String(raw || '').trim();
                      if (!s) return null;
                      const parts = s.split(/[×xX]/g).map((x: string) => Number(String(x).trim()));
                      if (parts.length !== 3) return null;
                      if (!parts.every((n) => Number.isFinite(n) && n > 0)) return null;
                      return [Math.floor(parts[0]), Math.floor(parts[1]), Math.floor(parts[2])];
                    };

                    const openRelabelPackBoxFinal = (containerCodeRaw: any) => {
                      const cc = String(containerCodeRaw || '').trim();
                      if (!cc) return;
                      const hitBox = (Array.isArray(boxRows) ? boxRows : []).find((x: any) => String(x?.container_code || '').trim() === cc) || {};
                      const hitDraft = outputDraftByCode.get(cc) || {};
                      const ops = (Array.isArray(records) ? records : []).filter((r: any) => String(r?.container_code || '').trim() === cc);
                      const byNew = new Map<string, number>();
                      (ops || []).forEach((r: any) => {
                        const newCode = String(r?.new_code || '').trim().toUpperCase();
                        if (!newCode || newCode === '-') return;
                        const q = Number(r?.qty ?? 0) || 0;
                        if (!Number.isFinite(q) || q === 0) return;
                        byNew.set(newCode, (byNew.get(newCode) || 0) + q);
                      });
                      const rows = Array.from(byNew.entries())
                        .map(([new_code, qty], idx) => ({
                          __k: `${idx}-${new_code}`,
                          new_code,
                          qty: Math.max(0, Math.floor(Number(qty || 0) || 0)),
                        }))
                        .filter((x: any) => Number(x?.qty || 0) > 0)
                        .sort((a: any, b: any) => Number(b?.qty || 0) - Number(a?.qty || 0));
                      const totalQty = rows.reduce((s: number, x: any) => s + Math.max(0, Math.floor(Number(x?.qty || 0) || 0)), 0);
                      const startTs = Number(containerTimeMap.get(cc)?.start || 0);
                      const endTs = Number(containerTimeMap.get(cc)?.end || 0);
                      const durationMs = (startTs > 0 && endTs > 0 && endTs > startTs) ? (endTs - startTs) : 0;
                      const dimsFromDraft = (() => {
                        const l = Math.floor(Number(hitDraft?.length_cm || 0) || 0);
                        const w = Math.floor(Number(hitDraft?.width_cm || 0) || 0);
                        const h = Math.floor(Number(hitDraft?.height_cm || 0) || 0);
                        if (l > 0 && w > 0 && h > 0) return [l, w, h] as [number, number, number];
                        return null;
                      })();
                      const dims = dimsFromDraft || parseDimsCmText(hitBox?.dims_cm_text);
                      const dimsText = (() => {
                        if (!dims) return '-';
                        const [l, w, h] = dims;
                        const li = Math.ceil(l / 2.54);
                        const wi = Math.ceil(w / 2.54);
                        const hi = Math.ceil(h / 2.54);
                        return `${l}×${w}×${h}cm | ${li}×${wi}×${hi}inch`;
                      })();
                      const weightText = (() => {
                        const wg = Number(hitDraft?.weight_g);
                        if (Number.isFinite(wg) && wg > 0) {
                          const kg = Number((wg / 1000).toFixed(2));
                          const lb = Number((kg * 2.2046226218).toFixed(2));
                          return `${kg}kg | ${lb}lb`;
                        }
                        const kg2 = Number(hitBox?.weight_kg);
                        if (Number.isFinite(kg2) && kg2 > 0) {
                          const lb2 = Number((kg2 * 2.2046226218).toFixed(2));
                          return `${Number(kg2.toFixed(2))}kg | ${lb2}lb`;
                        }
                        return '-';
                      })();
                      const containerTypeText = (() => {
                        const code = cc.toUpperCase();
                        const seg = code.split('-');
                        const mid = String(seg?.[1] || '').trim().toUpperCase();
                        if (mid === 'B') return '纸箱';
                        if (mid === 'P') return '托盘';
                        if (mid === 'O') return '其他';
                        return mid || '-';
                      })();
                      setRelabelPackBoxFinalData({
                        container_code: cc,
                        container_type_text: containerTypeText,
                        total_qty: totalQty,
                        new_kinds: rows.length,
                        start_ts: startTs,
                        end_ts: endTs,
                        duration_ms: durationMs,
                        duration_text: durationMs > 0 ? fmtDurationHMS(durationMs) : '-',
                        dims_text: dimsText,
                        weight_text: weightText,
                        rows,
                      });
                      setRelabelPackBoxFinalOpen(true);
                    };

                    const mappingCols = [
                      { title: '旧条码', dataIndex: 'old', key: 'old', width: 140, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                      { title: '新条码', dataIndex: 'newer', key: 'newer', width: 140, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                      { title: '已完成', dataIndex: 'used', key: 'used', width: 90 },
                      { title: '备注', dataIndex: 'remark', key: 'remark', width: 140, ellipsis: true },
                      { title: '操作对象匹配', dataIndex: 'matchTargets', key: 'matchTargets', width: 240, render: (v: any) => renderMatchTargets(v) },
                      {
                        title: '系统自动生成新标签(PDF)',
                        key: 'pdf',
                        width: 200,
                        render: (_: any, r: any) => {
                          const code = String(r?.newer || '').trim().toUpperCase();
                          const gl = (genLabels as any)?.[code];
                          const u = String(gl?.file_url || gl?.pdf_file || '').trim();
                          if (!u) return <span style={{ color: '#999' }}>-</span>;
                          const url = resolveBackendFileUrl(u) || u;
                          return (
                            <Space size="small">
                              <Button size="small" type="link" onClick={() => { void openLabelPdfPreview(url, `${code}.pdf`); }}>预览</Button>
                              <Button size="small" onClick={() => {
                                try {
                                  const a = document.createElement('a');
                                  a.href = url;
                                  a.download = `${code}.pdf`;
                                  document.body.appendChild(a);
                                  a.click();
                                  document.body.removeChild(a);
                                } catch {}
                              }}>下载</Button>
                            </Space>
                          );
                        }
                      },
                    ];

                    const boxCols = [
                      {
                        title: '产出容器系统编码',
                        dataIndex: 'container_code',
                        key: 'container_code',
                        width: 250,
                        ellipsis: true,
                        render: (v: any, r: any) => {
                          const cancelled = isCancelledContainerRow(r);
                          return (
                            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                              <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>
                              {cancelled ? (
                                <span
                                  style={{
                                    padding: '0 8px',
                                    borderRadius: 999,
                                    border: '1px solid #ff4d4f',
                                    color: '#cf1322',
                                    background: '#fff1f0',
                                    fontWeight: 700,
                                    lineHeight: '18px',
                                    fontSize: 12,
                                  }}
                                >
                                  已作废
                                </span>
                              ) : null}
                            </span>
                          );
                        },
                      },
                      { title: '产品总数量', dataIndex: 'qty', key: 'qty', width: 110 },
                      { title: '新标签种类', dataIndex: 'newKinds', key: 'newKinds', width: 110 },
                      { title: '重量(kg)', dataIndex: 'weight_kg', key: 'weight_kg', width: 110 },
                      { title: '尺寸(cm)', dataIndex: 'dims_cm_text', key: 'dims_cm_text', width: 160, ellipsis: true },
                      {
                        title: '开始时间',
                        key: 'start_time',
                        width: 180,
                        ellipsis: true,
                        render: (_: any, r: any) => {
                          const cc = String(r?.container_code || '').trim();
                          const t0 =
                            parseTs(r?.firstTs)
                            || parseTs(r?.startTs)
                            || parseTs(r?.beginTs)
                            || parseTs(r?.created_at)
                            || parseTs(r?.createdAt)
                            || (cc ? (containerTimeMap.get(cc)?.start ?? 0) : 0);
                          if (!(t0 > 0)) return <span style={{ color: '#999' }}>-</span>;
                          return <span style={{ fontFamily: 'monospace' }}>{fmtDateTime(t0)}</span>;
                        }
                      },
                      {
                        title: '结束时间',
                        key: 'end_time',
                        width: 180,
                        ellipsis: true,
                        render: (_: any, r: any) => {
                          const cc = String(r?.container_code || '').trim();
                          const t1 =
                            parseTs(r?.lastTs)
                            || parseTs(r?.endTs)
                            || parseTs(r?.finishTs)
                            || parseTs(r?.closedTs)
                            || parseTs(r?.updated_at)
                            || parseTs(r?.updatedAt)
                            || (cc ? (containerTimeMap.get(cc)?.end ?? 0) : 0);
                          if (!(t1 > 0)) return <span style={{ color: '#999' }}>-</span>;
                          return <span style={{ fontFamily: 'monospace' }}>{fmtDateTime(t1)}</span>;
                        }
                      },
                      {
                        title: '耗时(小时:分钟:秒)',
                        key: 'duration',
                        width: 120,
                        render: (_: any, r: any) => {
                          const cc = String(r?.container_code || '').trim();
                          const t0 =
                            parseTs(r?.firstTs)
                            || parseTs(r?.startTs)
                            || parseTs(r?.beginTs)
                            || parseTs(r?.created_at)
                            || parseTs(r?.createdAt)
                            || (cc ? (containerTimeMap.get(cc)?.start ?? 0) : 0);
                          const t1 =
                            parseTs(r?.lastTs)
                            || parseTs(r?.endTs)
                            || parseTs(r?.finishTs)
                            || parseTs(r?.closedTs)
                            || parseTs(r?.updated_at)
                            || parseTs(r?.updatedAt)
                            || (cc ? (containerTimeMap.get(cc)?.end ?? 0) : 0);
                          if (!(t0 > 0) || !(t1 > 0) || t1 <= t0) return <span style={{ color: '#999' }}>-</span>;
                          return <span style={{ fontFamily: 'monospace' }}>{fmtDurationHMS(t1 - t0)}</span>;
                        }
                      },
                      {
                        title: '操作',
                        key: 'ops',
                        width: 140,
                        render: (_: any, r: any) => {
                          const cc = String(r?.container_code || '').trim();
                          if (!cc) return <span style={{ color: '#999' }}>-</span>;
                          const cancelled = isCancelledContainerRow(r);
                          return (
                            <Button
                              size="small"
                              disabled={cancelled}
                              onClick={() => openRelabelPackBoxFinal(cc)}
                            >
                              最终装箱数据
                            </Button>
                          );
                        }
                      }
                    ];

                    const recordCols = [
                      { title: '容器', dataIndex: 'container_code', key: 'container_code', width: 160, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                      { title: '事件', dataIndex: 'event', key: 'event', width: 110, ellipsis: true },
                      { title: '旧条码', dataIndex: 'old_code', key: 'old_code', width: 140, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                      { title: '新条码', dataIndex: 'new_code', key: 'new_code', width: 140, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                      { title: '数量', dataIndex: 'qty', key: 'qty', width: 90 },
                      {
                        title: '操作对象IU',
                        key: 'op_iu_code',
                        width: 220,
                        ellipsis: true,
                        render: (_: any, r: any) => {
                          const iuId = Math.floor(Number(r?.op_iu_id || 0) || 0);
                          const cached = iuId > 0 ? String(iuSysCodeById[String(iuId)] || '').trim() : '';
                          const code = String(
                            // 执行端快照 records_preview 通常直接提供该字段（CombinedRelabelBoxRenderer 会补齐）
                            r?.op_iu_code
                            || r?.opIuCode
                            || r?.inventory_unit_system_barcode
                            || r?.inventory_unit_system_code
                            || r?.inventory_unit_code
                            || r?.system_code
                            || r?.systemCode
                            || r?.system_barcode
                            || r?.systemBarcode
                            || r?.unit_code
                            || r?.op_iu_system_barcode
                            || r?.op_inventory_unit_system_barcode
                            || r?.op_unit_code
                            || cached
                            || ''
                          ).trim();
                          if (!code) {
                            if (iuId > 0) return <span style={{ color: '#999' }}>查询中...</span>;
                            return <span style={{ color: '#999' }}>-</span>;
                          }
                          return <span style={{ fontFamily: 'monospace' }}>{code}</span>;
                        }
                      },
                      { title: '时间', dataIndex: 'ts', key: 'ts', width: 180, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{fmtDateTime(v)}</span> },
                    ];

                    const auditZip = (snap as any)?.audit_baselines_zip;
                    const auditZipErr = (snap as any)?.audit_baselines_zip_error;
                    return (
                      <div style={{ lineHeight: 1.75 }}>
                        {/* 顺序对齐执行页：审计包 -> RelabelPackSnapshotBlock(总进度/映射/容器/调整池/标签) -> Records */}
                        <div style={{ marginBottom: 10, padding: '10px 12px', border: '1px solid #91caff', background: '#e6f4ff', borderRadius: 8 }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                            <div style={{ fontWeight: 700, color: '#0958d9' }}>基线文件审计包（zip）</div>
                            {auditZip && auditZip.file ? (
                              <Button
                                size="small"
                                type="primary"
                                onClick={() => {
                                  const u = String(auditZip?.file || '').trim();
                                  const url = resolveBackendFileUrl(u) || u;
                                  try { window.open(url, '_blank'); } catch {}
                                }}
                              >
                                下载审计包
                              </Button>
                            ) : (
                              <span style={{ color: '#8c8c8c' }}>该快照未生成审计包</span>
                            )}
                          </div>
                          {auditZip && auditZip.file ? (
                            <div style={{ marginTop: 6, color: '#595959', fontSize: 12 }}>
                              文件名：<span style={{ fontFamily: 'monospace' }}>{String(auditZip?.file_name || 'audit.zip')}</span>
                              {Number.isFinite(Number(auditZip?.count)) ? <span style={{ marginLeft: 10 }}>包含基线文件：{Number(auditZip?.count)} 个</span> : null}
                              {auditZip?.generated_at ? <span style={{ marginLeft: 10 }}>生成时间：{fmtDateTime(auditZip?.generated_at)}</span> : null}
                            </div>
                          ) : null}
                          {auditZipErr ? (
                            <div style={{ marginTop: 6, color: '#cf1322', fontSize: 12 }}>审计包生成失败：{String(auditZipErr || '')}</div>
                          ) : null}
                        </div>

                        <Descriptions size="small" column={2} bordered>
                          <Descriptions.Item label="阶段">{stageKeyToCN(sk)}</Descriptions.Item>
                          <Descriptions.Item label="提交时间">{fmtDateTime(snap?.submitted_at)}</Descriptions.Item>
                          <Descriptions.Item label="操作记录数">{String(stats?.records_count ?? '-')}</Descriptions.Item>
                          <Descriptions.Item label="指令产出容器总数(不含已作废)">{String(boxStats.totalContainers ?? '-')}</Descriptions.Item>
                          <Descriptions.Item label="已结束容器数量">{String(stats?.containers_closed ?? '-')}</Descriptions.Item>
                        </Descriptions>

                        <Divider style={{ margin: '10px 0' }} />
                        <div style={{ fontWeight: 800, marginBottom: 6 }}>总进度概览（含调整池）</div>
                        <Descriptions size="small" column={2} bordered>
                          <Descriptions.Item label="目标数/完成数">{String(overall?.targetQtyText ?? '-')}/{String(overall?.doneQty ?? '-')}</Descriptions.Item>
                          <Descriptions.Item label="完成率">{String(overall?.rateText ?? '-')}</Descriptions.Item>
                          <Descriptions.Item label="容器总重量(kg)">{String(overall?.kgText ?? '-')}</Descriptions.Item>
                          <Descriptions.Item label="容器总体积(m³)">{String(overall?.volM3Text ?? '-')}</Descriptions.Item>
                        </Descriptions>

                        <Divider style={{ margin: '10px 0' }} />
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                          <div style={{ fontWeight: 800 }}>按条码映射统计</div>
                          <div style={{ color: '#595959', fontSize: 12 }}>
                            新条码种类：<span style={{ fontFamily: 'monospace', fontWeight: 800 }}>{String(mappingStats.newKinds)}</span>
                            <span style={{ marginLeft: 12 }}>
                              已完成总数量：<span style={{ fontFamily: 'monospace', fontWeight: 800 }}>{String(mappingStats.doneSum)}</span>
                            </span>
                          </div>
                        </div>
                        <Table
                          size="small"
                          tableLayout="fixed"
                          rowKey={(_x, idx) => String(idx)}
                          pagination={{
                            defaultPageSize: 20,
                            showSizeChanger: true,
                            pageSizeOptions: ['20', '50', '100', '200', '500'],
                            showQuickJumper: true,
                            showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                          }}
                          dataSource={mappingRows}
                          columns={mappingCols as any}
                          scroll={{ y: 360 }}
                        />

                        <Divider style={{ margin: '10px 0' }} />
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                          <div style={{ fontWeight: 800 }}>按产出容器统计(不含调整池)</div>
                          <div style={{ color: '#595959', fontSize: 12 }}>
                            有效产出容器总数量：<span style={{ fontFamily: 'monospace', fontWeight: 800 }}>{String(boxStats.totalContainers)}</span>
                            <span style={{ marginLeft: 12 }}>
                              已装入容器总数量：<span style={{ fontFamily: 'monospace', fontWeight: 800 }}>{String(boxStats.loadedQtySum)}</span>
                            </span>
                            <span style={{ marginLeft: 12 }}>
                              总耗时：<span style={{ fontFamily: 'monospace', fontWeight: 800 }}>{boxStats.durationMs > 0 ? fmtDurationHMS(boxStats.durationMs) : '-'}</span>
                            </span>
                          </div>
                        </div>
                        <Table
                          size="small"
                          tableLayout="fixed"
                          rowKey={(_x, idx) => String(idx)}
                          pagination={{
                            defaultPageSize: 20,
                            showSizeChanger: true,
                            pageSizeOptions: ['20', '50', '100', '200', '500'],
                            showQuickJumper: true,
                            showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                          }}
                          dataSource={boxRows}
                          columns={boxCols as any}
                          scroll={{ y: 360 }}
                        />

                        <Divider style={{ margin: '10px 0' }} />
                        <div style={{ fontWeight: 800, marginBottom: 6 }}>调整池</div>
                        {pool ? (
                          <Descriptions size="small" column={2} bordered>
                            <Descriptions.Item label="Pool">{String(pool?.pool ?? '-')}</Descriptions.Item>
                            <Descriptions.Item label="totalPos">{String(pool?.totalPos ?? '-')}</Descriptions.Item>
                            <Descriptions.Item label="newKinds">{String(pool?.newKinds ?? '-')}</Descriptions.Item>
                            <Descriptions.Item label="iuKinds">{String(pool?.iuKinds ?? '-')}</Descriptions.Item>
                          </Descriptions>
                        ) : (
                          <Text type="secondary">（无调整池数据）</Text>
                        )}

                        <Divider style={{ margin: '10px 0' }} />
                        <div style={{ fontWeight: 800, marginBottom: 6 }}>操作记录（全量）</div>
                        <Table
                          size="small"
                          tableLayout="fixed"
                          rowKey={(r: any) => String(r?.id || Math.random())}
                          pagination={{
                            defaultPageSize: 30,
                            showSizeChanger: true,
                            pageSizeOptions: ['30', '50', '100', '200', '500'],
                            showQuickJumper: true,
                            showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                          }}
                          dataSource={records}
                          columns={recordCols as any}
                          scroll={{ y: 420 }}
                        />
                      </div>
                    );
                  }

                  // REGULAR_OUTBOUND_PACK
                  if (sk === 'REGULAR_OUTBOUND_PACK') {
                    return <RegularOutboundSnapshotForReview snapshotEnvelope={snap} />;
                  }

                  // SINGLE_DROPSHIP_PACK
                  if (sk === 'SINGLE_DROPSHIP_PACK') {
                    const root = (snap || {}) as AnyObj;
                    const real = (root?.snapshot && typeof root.snapshot === 'object') ? (root.snapshot as AnyObj) : root;
                    const stats = (root?.stats || {}) as AnyObj;
                    const metrics = (real?.metrics || {}) as AnyObj;
                    const opRows: any[] = Array.isArray(metrics?.operation_records) ? metrics.operation_records : [];

                    const processRaw = String(metrics?.process_result_status || '').trim().toLowerCase();
                    const processText = processRaw === 'completed' ? '已完成' : (processRaw === 'unable' ? '无法操作' : '未标注');
                    const processStyle: React.CSSProperties = processRaw === 'completed'
                      ? { background: '#52c41a', color: '#fff', border: '1px solid #52c41a' }
                      : (processRaw === 'unable'
                        ? { background: '#ff4d4f', color: '#fff', border: '1px solid #ff4d4f' }
                        : { background: '#f5f5f5', color: '#595959', border: '1px solid #d9d9d9' });
                    const hasWaybill = !!(String(metrics?.waybill_carrier || '').trim() && String(metrics?.waybill_tracking_no || '').trim())
                      || !!stats?.has_waybill;
                    const packText = (
                      metrics?.actual_pack_length_cm
                      && metrics?.actual_pack_width_cm
                      && metrics?.actual_pack_height_cm
                      && metrics?.actual_gross_weight_g
                    )
                      ? `${metrics.actual_pack_length_cm}×${metrics.actual_pack_width_cm}×${metrics.actual_pack_height_cm} cm, ${metrics.actual_gross_weight_g} g`
                      : '-';

                    return (
                      <div style={{ lineHeight: 1.75 }}>
                        <Descriptions size="small" column={2} bordered>
                          <Descriptions.Item label="阶段">{stageKeyToCN(sk)}</Descriptions.Item>
                          <Descriptions.Item label="提交时间">{fmtDateTime(root?.submitted_at)}</Descriptions.Item>
                          <Descriptions.Item label="处理结果">
                            <span style={{ ...processStyle, borderRadius: 6, padding: '2px 10px', fontWeight: 700 }}>
                              {processText}
                            </span>
                          </Descriptions.Item>
                          <Descriptions.Item label="操作记录数">{String(stats?.operation_records_count ?? opRows.length ?? 0)}</Descriptions.Item>
                        </Descriptions>

                        <div style={{ marginTop: 10, marginBottom: 10, padding: '10px 12px', border: '1px solid #91caff', borderRadius: 8, background: '#f0f5ff' }}>
                          <div style={{ fontWeight: 700, color: '#0958d9', marginBottom: 6 }}>运单信息</div>
                          <div>
                            <span style={{ marginRight: 20 }}>运单：{hasWaybill ? '已填写' : '未填写'}</span>
                            <span style={{ marginRight: 20 }}>承运商：{String(metrics?.waybill_carrier || '-')}</span>
                            <span>物流跟踪号：{String(metrics?.waybill_tracking_no || '-')}</span>
                          </div>
                          <div style={{ marginTop: 4 }}>
                            <span style={{ marginRight: 20 }}>
                              运单金额：{String(metrics?.waybill_charge_amount || '').trim()
                                ? `${String(metrics?.waybill_charge_amount || '').trim()} ${String(metrics?.waybill_charge_currency || '').trim() || 'USD'}`
                                : '-'}
                            </span>
                            <span>费用备注：{String(metrics?.waybill_charge_note || '').trim() || '-'}</span>
                          </div>
                        </div>

                        <div style={{ marginBottom: 10, padding: '10px 12px', border: '1px solid #b7eb8f', borderRadius: 8, background: '#f6ffed' }}>
                          <div style={{ fontWeight: 700, color: '#389e0d', marginBottom: 6 }}>包装数据与指令产出容器信息</div>
                          <div style={{ marginBottom: 4 }}>包装数据：{packText}</div>
                          <div>指令产出容器：{String(metrics?.created_ioc_code || stats?.ioc_code || '-')}</div>
                        </div>

                        <div style={{ padding: '10px 12px', border: '1px solid #ffd591', borderRadius: 8, background: '#fff7e6' }}>
                          <div style={{ fontWeight: 700, color: '#d46b08', marginBottom: 6 }}>
                            操作记录（共 {Number(stats?.operation_records_count || opRows.length || 0)} 条）
                          </div>
                          <Table
                            size="small"
                            rowKey={(r:any, idx?:number) => String(r?.time || `${idx || 0}`)}
                            pagination={{
                              defaultPageSize: 10,
                              showSizeChanger: true,
                              pageSizeOptions: ['5', '10', '20', '50'] as any,
                              showQuickJumper: true,
                              showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                            }}
                            locale={{ emptyText: '暂无操作记录快照' }}
                            dataSource={opRows.map((r:any, idx:number) => ({
                              key: `${idx}-${String(r?.time || '')}`,
                              time: String(r?.time || '-'),
                              action: String(r?.action || '-'),
                              remark: String(r?.remark || '-'),
                            }))}
                            columns={[
                              { title: '时间', dataIndex: 'time', key: 'time', width: 170 },
                              { title: '操作', dataIndex: 'action', key: 'action', width: 180 },
                              { title: '备注', dataIndex: 'remark', key: 'remark' },
                            ] as any}
                            scroll={{ y: 360 }}
                          />
                        </div>
                      </div>
                    );
                  }

                  // WRAP_UP
                  if (sk === 'WRAP_UP') {
                    const stats = (snap?.stats || {}) as AnyObj;
                    const inner = (snap?.snapshot && typeof snap.snapshot === 'object') ? (snap.snapshot as AnyObj) : (snap as AnyObj);
                    const sections = (inner?.sections || {}) as AnyObj;
                    const s1 = (sections?.step1_unoperable || {}) as AnyObj;
                    const s2 = (sections?.step2_overpick || {}) as AnyObj;
                    const s3 = (sections?.step3_put_away || {}) as AnyObj;
                    const s4 = (sections?.step4_remarks_attachments || {}) as AnyObj;
                    const atts = Array.isArray(s4?.attachments) ? s4.attachments : [];
                    const exceptionContainers: any[] = Array.isArray(s1?.exception_containers) ? s1.exception_containers : [];
                    const overGroups: any[] = Array.isArray(s2?.over_pick_groups) ? s2.over_pick_groups : [];
                    const putAwayOps: any[] = Array.isArray(s3?.put_away_ops) ? s3.put_away_ops : [];
                    const iocNormalCodes: string[] = Array.isArray((s3 as any)?.ioc_normal_codes) ? (s3 as any).ioc_normal_codes : [];
                    // “无法操作产品数量”口径：所有放入异常容器IOC中的产品总和
                    // 优先：exception_containers[].total_qty；兜底：allocations[].quantity 求和
                    const unoperableCount = (() => {
                      let sum = 0;
                      try {
                        (exceptionContainers || []).forEach((c: any) => {
                          const tq = Number(c?.total_qty);
                          if (Number.isFinite(tq) && tq > 0) {
                            sum += tq;
                            return;
                          }
                          const arr = Array.isArray(c?.allocations) ? c.allocations : [];
                          arr.forEach((a: any) => {
                            const q = Number(a?.quantity ?? a?.qty ?? 0) || 0;
                            if (q) sum += Math.abs(q);
                          });
                        });
                      } catch {}
                      return Math.floor(sum);
                    })();
                    const exceptionIocCount = (() => {
                      const n = Number(stats?.exception_containers_count);
                      if (Number.isFinite(n) && n >= 0) return Math.floor(n);
                      return Array.isArray(exceptionContainers) ? exceptionContainers.length : 0;
                    })();
                    const overpickProductsQty = (() => {
                      let sum = 0;
                      try {
                        (overGroups || []).forEach((g: any) => {
                          const items = Array.isArray(g?.items) ? g.items : [];
                          items.forEach((x: any) => {
                            const q = Number(x?.qty || 0) || 0;
                            if (q) sum += Math.abs(q);
                          });
                        });
                      } catch {}
                      return Math.floor(sum);
                    })();
                    const overpickCreatedScCount = (() => {
                      const s = new Set<string>();
                      try {
                        (overGroups || []).forEach((g: any) => {
                          const cc = String(g?.created_container_code || '').trim();
                          if (cc) s.add(cc);
                        });
                      } catch {}
                      return s.size;
                    })();
                    const putAwayOpsCount = (() => {
                      const n = Number(stats?.put_away_ops_count);
                      if (Number.isFinite(n) && n >= 0) return Math.floor(n);
                      return Array.isArray(putAwayOps) ? putAwayOps.length : 0;
                    })();
                    const exceptionIocSet = new Set<string>((exceptionContainers || []).map((x:any)=> String(x?.code || '').trim()).filter(Boolean));
                    const normalIocSet = new Set<string>((iocNormalCodes || []).map((x:any)=> String(x || '').trim()).filter(Boolean));
                    const renderContainerTypeTag = (codeRaw: any) => {
                      const code = String(codeRaw || '').trim();
                      if (!code) return <span style={{ color: '#999' }}>-</span>;
                      // 优先判定：是否为 IOC（正常/异常）
                      if (normalIocSet.has(code)) {
                        return (
                          <span style={{ background: '#f6ffed', color: '#389e0d', border: '1px solid #b7eb8f', borderRadius: 4, padding: '1px 8px', fontWeight: 700, display: 'inline-block' }}>
                            指令产出容器(正常内容)
                          </span>
                        );
                      }
                      if (exceptionIocSet.has(code)) {
                        return (
                          <span style={{ background: '#fff2e8', color: '#d4380d', border: '1px solid #ffbb96', borderRadius: 4, padding: '1px 8px', fontWeight: 700, display: 'inline-block' }}>
                            指令产出容器(异常内容)
                          </span>
                        );
                      }
                      // 其余视为仓储容器
                      return (
                        <span style={{ background: '#f0f5ff', color: '#1d39c4', border: '1px solid #adc6ff', borderRadius: 4, padding: '1px 8px', fontWeight: 700, display: 'inline-block' }}>
                          仓储容器
                        </span>
                      );
                    };

                    // 步骤4：附件表稳定 key + 勾选（用于批量下载/批量加入终审附件）
                    const step4Rows = (atts || []).map((x: any, idx: number) => ({
                      ...x,
                      __k: String(x?.id || x?.file || x?.file_name || `row_${idx}`),
                    }));
                    const allStep4Keys = step4Rows.map((r: any) => String(r?.__k || '')).filter(Boolean);
                    const selectedStep4Rows = step4Rows.filter((r: any) => wrapStep4SelectedKeys.includes(String(r?.__k || '')));
                    const selectedStep4Ids = selectedStep4Rows
                      .map((r: any) => Math.floor(Number(r?.id || 0) || 0))
                      .filter((n: number) => n > 0);
                    const doBatchDownloadStep4 = async () => {
                      if (!selectedStep4Rows.length) { message.warning('请先勾选附件'); return; }
                      for (const r of selectedStep4Rows) {
                        try {
                          const raw = String(r?.file || r?.file_url || '').trim();
                          const url = resolveBackendFileUrl(raw) || raw;
                          const name = String(r?.file_name || r?.file || '').trim();
                          if (!url) continue;
                          await downloadWrapAtt(url, name);
                        } catch {}
                      }
                    };
                    const doBatchAddStep4 = async () => {
                      if (!selectedStep4Ids.length) { message.warning('未选择可加入的附件'); return; }
                      await addStepAttachmentBatchToFinalReview(selectedStep4Ids);
                    };
                    return (
                      <div style={{ lineHeight: 1.75 }}>
                        <Descriptions size="small" column={2} bordered>
                          <Descriptions.Item label="阶段">{stageKeyToCN(sk)}</Descriptions.Item>
                          <Descriptions.Item label="提交时间">{fmtDateTime(snap?.submitted_at)}</Descriptions.Item>
                          <Descriptions.Item label="无法操作产品数量">{String(unoperableCount)}</Descriptions.Item>
                          <Descriptions.Item label="异常容器IOC数量">{String(exceptionIocCount)}</Descriptions.Item>
                          <Descriptions.Item label="多拣货产品数量">{String(overpickProductsQty)}</Descriptions.Item>
                          <Descriptions.Item label="多拣货生成仓储容器SC数量">{String(overpickCreatedScCount)}</Descriptions.Item>
                          <Descriptions.Item label="放入仓位记录">{String(putAwayOpsCount)}</Descriptions.Item>
                          <Descriptions.Item label="提交附件数">{String(Array.isArray(atts) ? atts.length : 0)}</Descriptions.Item>
                        </Descriptions>
                        <Divider style={{ margin: '10px 0' }} />
                        <div style={{ fontWeight: 800, marginBottom: 6 }}>步骤1：无法操作产品生成容器</div>
                        <Table
                          size="small"
                          tableLayout="fixed"
                          rowKey={(_x, idx) => String(idx)}
                          pagination={{ pageSize: 10 }}
                          dataSource={exceptionContainers}
                          columns={[
                            { title: '容器', dataIndex: 'code', key: 'code', width: 160, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                            { title: '总数量', dataIndex: 'total_qty', key: 'total_qty', width: 90 },
                            {
                              title: '分配明细',
                              key: 'alloc',
                              render: (_: any, r: AnyObj) => {
                                const arr = Array.isArray(r?.allocations) ? r.allocations : [];
                                return (
                                  <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
                                    {arr.map((x: any, i: number) => (
                                      <Tag key={i} style={{ marginInlineEnd: 0, borderRadius: 999 }}>
                                        <span style={{ fontFamily: 'monospace' }}>{String(x?.unit_code || `IU#${x?.inventory_unit_id}`)}</span>
                                        <span style={{ marginLeft: 6, fontWeight: 800, color: '#1677ff' }}>{String(x?.quantity ?? '-')}</span>
                                      </Tag>
                                    ))}
                                  </span>
                                );
                              }
                            },
                          ]}
                          locale={{ emptyText: '无记录' }}
                        />

                        <Divider style={{ margin: '10px 0' }} />
                        <div style={{ fontWeight: 800, marginBottom: 6 }}>步骤2：多拣货产品生成记录</div>
                        <Table
                          size="small"
                          tableLayout="fixed"
                          rowKey={(r: any) => String(r?.group_id || Math.random())}
                          pagination={{ pageSize: 10 }}
                          dataSource={overGroups}
                          columns={[
                            { title: '生成容器', dataIndex: 'created_container_code', key: 'created_container_code', width: 160, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                            { title: '分拣组', dataIndex: 'group_code', key: 'group_code', width: 160, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                            {
                              title: '明细',
                              key: 'items',
                              render: (_: any, r: AnyObj) => {
                                const items = Array.isArray(r?.items) ? r.items : [];
                                return (
                                  <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 6 }}>
                                    {items.map((x: any, i: number) => (
                                      <Tag key={i} style={{ marginInlineEnd: 0, borderRadius: 999 }}>
                                        <span style={{ fontFamily: 'monospace' }}>{String(x?.old_code || '-')}</span>
                                        <span style={{ marginLeft: 6, fontFamily: 'monospace' }}>{String(x?.inventory_unit_code || `IU#${x?.inventory_unit_id}`)}</span>
                                        <span style={{ marginLeft: 6, fontWeight: 800, color: '#1677ff' }}>{String(x?.qty ?? '-')}</span>
                                      </Tag>
                                    ))}
                                  </span>
                                );
                              }
                            },
                          ]}
                          locale={{ emptyText: '无记录' }}
                        />

                        <Divider style={{ margin: '10px 0' }} />
                        <div style={{ fontWeight: 800, marginBottom: 6 }}>步骤3：容器放入仓位信息</div>
                        <Table
                          size="small"
                          tableLayout="fixed"
                          rowKey={(_x, idx) => String(idx)}
                          pagination={{
                            defaultPageSize: 10,
                            showSizeChanger: true,
                            pageSizeOptions: ['10', '20', '50', '100', '200'],
                            showQuickJumper: true,
                            showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                          }}
                          dataSource={putAwayOps}
                          columns={[
                            { title: '容器', dataIndex: 'container_code', key: 'container_code', width: 180, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                            { title: '容器类型', key: 'container_type', width: 220, render: (_: any, r: AnyObj) => renderContainerTypeTag(r?.container_code) },
                            { title: '仓位', dataIndex: 'location_code', key: 'location_code', width: 140, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                          ]}
                          locale={{ emptyText: '无记录' }}
                        />

                        <Divider style={{ margin: '10px 0' }} />
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                          <div style={{ fontWeight: 800 }}>步骤4：备注与附件</div>
                          <Space>
                            <Button
                              size="small"
                              icon={<DownloadOutlined />}
                              onClick={() => { void doBatchDownloadStep4(); }}
                              disabled={selectedStep4Rows.length === 0}
                            >
                              批量下载
                            </Button>
                            <Button
                              size="small"
                              type="primary"
                              onClick={() => { void doBatchAddStep4(); }}
                              disabled={selectedStep4Ids.length === 0}
                            >
                              批量添加为终审附件
                            </Button>
                          </Space>
                        </div>
                        {String(s4?.remarks || '').trim() ? (
                          <div style={{ marginBottom: 10, padding: 10, border: '1px solid #f0f0f0', borderRadius: 8, background: '#fafafa' }}>
                            <div style={{ fontWeight: 700, marginBottom: 6 }}>备注</div>
                            <div style={{ whiteSpace: 'pre-wrap', color: '#d4380d', fontWeight: 700 }}>{String(s4.remarks)}</div>
                          </div>
                        ) : null}
                        {atts && atts.length ? (
                          <Table
                            size="small"
                            tableLayout="fixed"
                            rowKey="__k"
                            pagination={{
                              defaultPageSize: 10,
                              showSizeChanger: true,
                              pageSizeOptions: ['10', '20', '50', '100', '200'],
                              showQuickJumper: true,
                              showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                            }}
                            dataSource={step4Rows}
                            rowSelection={{
                              columnTitle: (() => {
                                const total = allStep4Keys.length;
                                const sel = wrapStep4SelectedKeys.length;
                                const checked = total > 0 && sel === total;
                                const indeterminate = sel > 0 && total > 0 && sel < total;
                                return (
                                  <Checkbox
                                    checked={checked}
                                    indeterminate={indeterminate}
                                    onChange={() => {
                                      if (checked) {
                                        setWrapStep4SelectedKeys([]);
                                      } else {
                                        setWrapStep4SelectedKeys(allStep4Keys);
                                      }
                                    }}
                                  >
                                    全选
                                  </Checkbox>
                                );
                              })(),
                              columnWidth: 70,
                              selectedRowKeys: wrapStep4SelectedKeys,
                              onChange: (keys: any[]) => setWrapStep4SelectedKeys((keys || []).map((k) => String(k))),
                            }}
                            columns={[
                              { title: '文件名', key: 'name', width: 260, ellipsis: true, render: (_: any, x: AnyObj) => <span style={{ fontFamily: 'monospace' }}>{String(x?.file_name || x?.file || '-')}</span> },
                              { title: '类型', dataIndex: 'mime_type', key: 'mime_type', width: 220, ellipsis: true },
                              { title: '上传时间', dataIndex: 'created_at', key: 'created_at', width: 180, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{fmtDateTime(v)}</span> },
                              {
                                title: '操作',
                                key: 'op',
                                width: 300,
                                render: (_: any, x: AnyObj) => {
                                  const raw = String(x?.file || x?.file_url || '').trim();
                                  const url = resolveBackendFileUrl(raw) || raw;
                                  const name = String(x?.file_name || x?.file || '').trim();
                                  return (
                                    <Space size="small">
                                      <Button size="small" onClick={() => { void openWrapAttPreview(url, name); }} disabled={!url}>预览</Button>
                                      <Button
                                        size="small"
                                        icon={<DownloadOutlined />}
                                        onClick={() => {
                                          if (!url) return;
                                          void downloadWrapAtt(url, name);
                                        }}
                                        disabled={!url}
                                      >
                                        下载
                                      </Button>
                                      <Button size="small" type="primary" onClick={() => addStepAttachmentToFinalReview(Number(x?.id || 0))} disabled={!x?.id}>加入终审附件</Button>
                                    </Space>
                                  );
                                },
                              },
                            ]}
                          />
                        ) : (
                          <Text type="secondary">（该收尾快照未包含附件）</Text>
                        )}
                      </div>
                    );
                  }

                  return <Text type="secondary">（该阶段暂无可读视图，建议查看 JSON）</Text>;
                })(),
              },
              {
                key: 'json',
                label: 'JSON',
                children: (
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
                      <Button
                        size="small"
                        icon={<CopyOutlined />}
                        onClick={() => {
                          const txt = String(previewText || '').trim();
                          if (!txt) { message.warning('无可复制内容'); return; }
                          const run = async () => {
                            try {
                              await navigator.clipboard.writeText(txt);
                              message.success('已复制');
                              return;
                            } catch {}
                            try {
                              const ta = document.createElement('textarea');
                              ta.value = txt;
                              ta.style.position = 'fixed';
                              ta.style.left = '-9999px';
                              document.body.appendChild(ta);
                              ta.focus();
                              ta.select();
                              document.execCommand('copy');
                              document.body.removeChild(ta);
                              message.success('已复制');
                            } catch {
                              message.error('复制失败');
                            }
                          };
                          void run();
                        }}
                      >
                        复制
                      </Button>
                    </div>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 640, overflow: 'auto', background: '#f6f6f6', padding: 12, borderRadius: 6 }}>
                      {previewText || '-'}
                    </pre>
                  </div>
                ),
              },
            ]}
          />
        ) : null}
        {previewKind === 'text' ? (
          <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: 640, overflow: 'auto', background: '#f6f6f6', padding: 12, borderRadius: 6 }}>
            {previewText || '-'}
          </pre>
        ) : null}
        {previewKind === 'other' ? (
          <div style={{ color: '#8c8c8c' }}>该类型暂不支持内嵌预览，可点击“下载”或在新标签页打开。</div>
        ) : null}
      </Modal>

      <Modal
        open={pricingEvidenceOpen}
        title="证据来源（evidence_json）"
        onCancel={() => setPricingEvidenceOpen(false)}
        footer={[
          <Button key="close" onClick={() => setPricingEvidenceOpen(false)}>关闭</Button>,
        ]}
        width={980}
      >
        <Tabs
          size="small"
          items={[
            {
              key: 'readable',
              label: '可读视图',
              children: (
                <div style={{ display: 'grid', gap: 10, maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
                  {pricingEvidenceResolverSummary.visible ? (
                    <Alert
                      type={pricingEvidenceResolverSummary.used ? 'success' : 'info'}
                      showIcon
                      message={`IOC上游追溯解析：${pricingEvidenceResolverSummary.used ? '已启用' : '未启用'}（hops=${pricingEvidenceResolverSummary.hops}，新增IOC=${pricingEvidenceResolverSummary.added}）`}
                    />
                  ) : null}
                  <Descriptions size="small" bordered column={2}>
                    <Descriptions.Item label="来源条目数">{String(Array.isArray(pricingEvidenceReadable.sources) ? pricingEvidenceReadable.sources.length : 0)}</Descriptions.Item>
                    <Descriptions.Item label="工单证据数">{String(Array.isArray(pricingEvidenceReadable.byWorkOrderRows) ? pricingEvidenceReadable.byWorkOrderRows.length : 0)}</Descriptions.Item>
                    <Descriptions.Item label="审计矩阵行数">{String(Array.isArray(pricingEvidenceReadable.sourceAuditRows) ? pricingEvidenceReadable.sourceAuditRows.length : 0)}</Descriptions.Item>
                    <Descriptions.Item label="审计来源类别">{String(Array.isArray(pricingEvidenceReadable.sourceAuditNowCountRows) ? pricingEvidenceReadable.sourceAuditNowCountRows.length : 0)}</Descriptions.Item>
                  </Descriptions>
                  <div style={{ fontWeight: 700 }}>来源分类统计</div>
                  <div style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                    <Table
                      size="small"
                      rowKey={(r: any) => String(r?.__k || r?.kind || '')}
                      pagination={false}
                      dataSource={pricingEvidenceReadable.sourceCounts}
                      columns={[
                        { title: '来源kind', dataIndex: 'kind', key: 'kind', width: 360, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        { title: '数量', dataIndex: 'count', key: 'count', width: 120, align: 'right' as const },
                      ]}
                      locale={{ emptyText: '暂无来源统计' }}
                      scroll={{ x: 520 }}
                    />
                  </div>
                  <div style={{ fontWeight: 700 }}>按工单证据摘要</div>
                  <div style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                    <Table
                      size="small"
                      rowKey={(r: any) => String(r?.__k || r?.work_order_id || '')}
                      pagination={{ defaultPageSize: 8, showSizeChanger: true, pageSizeOptions: ['8', '20', '50'] }}
                      dataSource={pricingEvidenceReadable.byWorkOrderRows}
                      columns={[
                        { title: '工单ID', dataIndex: 'work_order_id', key: 'work_order_id', width: 90, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        { title: '工单名称', dataIndex: 'work_order_name', key: 'work_order_name', width: 200, ellipsis: true },
                        { title: '来源', dataIndex: 'from', key: 'from', width: 340, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        { title: '口径', dataIndex: 'metric_key', key: 'metric_key', width: 110, ellipsis: true },
                        { title: '语义', dataIndex: 'quantity_semantic', key: 'quantity_semantic', width: 220, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        { title: 'basis', dataIndex: 'basis_value', key: 'basis_value', width: 100, align: 'right' as const, render: (v: any) => (v == null ? '-' : String(v)) },
                        { title: '单价', dataIndex: 'unit_price', key: 'unit_price', width: 100, align: 'right' as const, render: (v: any) => (v == null ? '-' : String(v)) },
                        { title: '金额', dataIndex: 'amount', key: 'amount', width: 100, align: 'right' as const, render: (v: any) => (v == null ? '-' : String(v)) },
                        { title: '缺失原因', dataIndex: 'missing_reason', key: 'missing_reason', width: 260, ellipsis: true },
                        { title: '公式', dataIndex: 'formula', key: 'formula', width: 280, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                      ]}
                      locale={{ emptyText: '暂无工单证据摘要' }}
                      scroll={{ x: 1900, y: 280 }}
                    />
                  </div>
                  <div style={{ fontWeight: 700 }}>来源审计矩阵</div>
                  <div style={{ width: '100%', maxWidth: '100%', overflowX: 'auto' }}>
                    <Table
                      size="small"
                      rowKey={(r: any, idx?: number) => String(r?.__k || `${idx || 0}-${r?.rule_key?.work_order_id || ''}`)}
                      pagination={{ defaultPageSize: 8, showSizeChanger: true, pageSizeOptions: ['8', '20', '50'] }}
                      dataSource={pricingEvidenceReadable.sourceAuditRows}
                      columns={[
                        { title: '工单ID', key: 'wo', width: 90, render: (_: any, r: any) => <span style={{ fontFamily: 'monospace' }}>{String(r?.rule_key?.work_order_id || '-')}</span> },
                        { title: '操作类型', key: 'op', width: 190, ellipsis: true, render: (_: any, r: any) => <span style={{ fontFamily: 'monospace' }}>{String(r?.rule_key?.operation_type_system_code || '-')}</span> },
                        { title: 'variant', key: 'vk', width: 170, ellipsis: true, render: (_: any, r: any) => <span style={{ fontFamily: 'monospace' }}>{String(r?.rule_key?.variant_key || '-')}</span> },
                        { title: 'metric', dataIndex: 'metric_key', key: 'metric_key', width: 110, ellipsis: true },
                        { title: 'semantic', dataIndex: 'quantity_semantic', key: 'quantity_semantic', width: 220, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        { title: '当前来源', dataIndex: 'source_now', key: 'source_now', width: 300, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        { title: '目标来源', dataIndex: 'source_target', key: 'source_target', width: 280, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                        { title: 'defer安全', dataIndex: 'defer_safe', key: 'defer_safe', width: 100, align: 'center' as const, render: (v: any) => (v ? <Tag color="success">是</Tag> : <Tag color="warning">否</Tag>) },
                      ]}
                      locale={{ emptyText: '暂无来源审计矩阵' }}
                      scroll={{ x: 1700, y: 260 }}
                    />
                  </div>
                </div>
              ),
            },
            {
              key: 'raw_json',
              label: '原始JSON',
              children: (
                <pre style={{ maxHeight: 640, overflow: 'auto', background: 'rgba(0,0,0,0.02)', padding: 10 }}>
                  {(() => { try { return JSON.stringify(pricingEvidence || {}, null, 2); } catch { return '{}'; } })()}
                </pre>
              ),
            },
          ]}
        />
      </Modal>

      <Modal
        open={executionReservationHelpOpen}
        title="执行期占用结算说明"
        onCancel={() => setExecutionReservationHelpOpen(false)}
        footer={[
          <Button key="close" onClick={() => setExecutionReservationHelpOpen(false)}>关闭</Button>,
        ]}
        width={980}
      >
        <div style={{ lineHeight: 1.85, color: '#262626' }}>
          <div>执行期占用结算整体逻辑:</div>
          <div>1 执行期占用结算是终审库存结算的独立动作，目标是让执行期占用与终审确认后的应结算数量一致。</div>
          <div>2 结算顺序为：先做占用归一（释放超额执行期占用），再按模式执行最终动作（转消耗/转业务留存占用/释放）。</div>
          <div>3 无法操作数量不直接并入执行期占用；该部分走独立“无法操作转AIU”链路，执行期占用仅按最终应结算数量处理。</div>
          <div>4 常规出库与换标装箱在“实际结果&gt;当前执行期占用”时，新增 shortfall_compensated 补偿：转消耗场景补差额消耗，after_pack场景补差额业务留存占用。</div>
          <div>5 after_pack 补差额业务留存占用通过统一锁定服务写入，保证“库存锁定”与“当前库存(锁定/可用)”口径一致，不出现仅锁定表变化的偏差。</div>
          <div>6 一件代发/常规出库/换标装箱按模板策略与场景模式执行；分拣清点仍按 need1/need2/need3 既有规则处理，不受上述补偿逻辑影响。</div>
          <div>7 明细中出现“释放执行期占用（normalized_release）”表示归一步骤释放超额；出现“shortfall_compensated”表示对“目标量&gt;占用量”的差额补偿，二者可同时出现且不代表重复扣减。</div>
        </div>
      </Modal>

      <Modal
        open={groupAdjustmentHelpOpen}
        title="归组调整说明"
        onCancel={() => setGroupAdjustmentHelpOpen(false)}
        footer={[
          <Button key="close" onClick={() => setGroupAdjustmentHelpOpen(false)}>关闭</Button>,
        ]}
        width={980}
      >
        <div style={{ lineHeight: 1.85, color: '#262626' }}>
          <div>归组调整整体逻辑:</div>
          <div>1 归组调整用于将终审口径下的库存单元挂载到目标分拣组，保证后续库存结构与分拣结果一致。</div>
          <div>2 分拣清点 need1 场景按终审结果重建目标分组并重挂 IU，空旧分组保留，不执行自动清理。</div>
          <div>3 分拣清点非 need1 场景按操作阶段产出的 group_code 映射到项目分拣组执行调整。</div>
          <div>4 明细状态含义：updated=已调整；noop=目标组一致无需调整；skipped=因缺组/缺IU等原因跳过。</div>
          <div>5 归组调整明细仅展示本次终审实际执行记录；若出现 skipped，请按原因修复后重试终审。</div>
        </div>
      </Modal>

      <Modal
        open={quantityCorrectionHelpOpen}
        title="库存数量修正说明"
        onCancel={() => setQuantityCorrectionHelpOpen(false)}
        footer={[
          <Button key="close" onClick={() => setQuantityCorrectionHelpOpen(false)}>关闭</Button>,
        ]}
        width={980}
      >
        <div style={{ lineHeight: 1.85, color: '#262626' }}>
          <div>库存数量修正整体逻辑:</div>
          <div>1 指的是理论库存与实际库存的差异</div>
          <div>2 本应在执行库存盘点时才进行修正</div>
          <div>3 如果是在指令操作阶段发现的库存数量差异,应该是只适用于操作全部可用库存时的场景,否则这种差异可能只是拣货差额造成的</div>
          <div>4 指令操作阶段体现的数量差异=可解释的已操作IU+无法操作IU-操作目标IU. 其中一件代发仍不适用库存数量修正; 常规出库仅在"操作对象=当前可用库存 + 操作全部可用库存 + 需重新包装"同时满足时适用, explained 来自最新打包阶段快照的 by_target_rows 映射结果.</div>
          <div>5 库存数量修正仅适用于以下指令及场景: A 换标+装箱指令 且 操作全部库存  B 分拣清点指令 need1 且 操作全部库存  C 分拣清点指令 need3 且 操作全部库存  D 常规出库指令 且 当前可用库存对象 + 全部可用库存 + 需重新包装</div>
          <div>6 在以上适用场景中,统一按公式 diff=explained+unoperable-target 计算修正量并落库; 其中常规出库的 explained 按最新打包快照中 by_target_rows 的可映射 IU 数量计算,其余指令沿用各自既有 explained 口径.</div>
        </div>
      </Modal>

      <Modal
        open={unoperableToAiuHelpOpen}
        title="无法操作IU转AIU说明"
        onCancel={() => setUnoperableToAiuHelpOpen(false)}
        footer={[
          <Button key="close" onClick={() => setUnoperableToAiuHelpOpen(false)}>关闭</Button>,
        ]}
        width={980}
      >
        <div style={{ lineHeight: 1.85, color: '#262626' }}>
          <div>无法操作IU转换为AIU的整体逻辑是:</div>
          <div>1 指的是将可确定来源的IU转换为异常库存单元AIU</div>
          <div>2 本应在执行库存盘点时才进行转换</div>
          <div>3 如果是在指令操作阶段发现的无法操作产品,应该是只适用于可确定源IU的场景,否则无法执行转换</div>
          <div>4 指令执行阶段确定的无法操作产品源IU和数量分配,是在收尾阶段步骤1中进行.其中常规出库在收尾步骤1已记录来源IU分配事实时,终审可执行AIU转换;一件代发当前仍不适用该转换</div>
          <div>5 无法操作IU转AIU适用于以下指令及场景: A 换标+装箱指令  B 常规出库指令(收尾步骤1已形成来源IU分配)  C 分拣清点指令 need1/need3(按照收尾阶段1自动分配源IU)</div>
          <div>6 分拣清点指令need2时,都是新生成的AIU,无需确定源IU(收尾阶段步骤1 已经记录AIU的数量和总重量)</div>
          <div>7 在以上适用于无法操作IU转AIU的场景中,通过建立特定转换记录的方式调整源IU和AIU库存,并在终审明细中保留转换审计记录</div>
        </div>
      </Modal>

      <Modal
        open={finalReviewHelpOpen}
        title="终审库存落库说明（按指令模板）"
        onCancel={() => setFinalReviewHelpOpen(false)}
        footer={[
          <Button key="close" onClick={() => setFinalReviewHelpOpen(false)}>关闭</Button>,
        ]}
        width={980}
      >
        <div style={{ lineHeight: 1.85, color: '#262626' }}>
          <div style={{ marginBottom: 8 }}>
            统一原则：指令操作对象只允许“当前可用库存(IU)”或“历史指令产出IOC”二选一；终审按操作对象进入独立分支执行库存落库，不混用口径。
          </div>
          <ul style={{ margin: '6px 0 0 18px' }}>
            <li><strong>分支A：当前可用库存(IU)为操作对象</strong>：沿用现有完整链路，按模板/场景命中“库存数量修正、无法操作转AIU、执行期占用结算、分组调整、初始库存与库龄入账”等动作；各模板按既有规则执行。</li>
            <li><strong>分支B：历史指令产出IOC为操作对象</strong>：采用 IOC 专属结算链路，不复用 IU 目标公式；核心动作按场景可包含“IOC内容映射到IU的消耗/保留、IOC占用结算、IOC状态流转、IOC层级同步与审计记录”。</li>
            <li><strong>IOC分支按模板+场景策略化</strong>：同一模板在不同场景可触发不同库存动作，不是一套固定流程。示例：常规出库直接出库(IOC目标)与常规出库 after_pack(IOC目标)使用不同结算策略。</li>
            <li><strong>当前优先实现口径</strong>：当前库存目标分支功能相对完整；IOC目标分支按“指令类型+业务场景”逐步补齐，确保每条策略可审计、可回放、可幂等重试。</li>
            <li><strong>已规划的IOC消耗型场景口径</strong>：当场景定义为“消耗性处理”（如直接出库/销毁）时，终审应执行“IOC内容对应IU转消耗 + IOC状态转已消耗并不可再次作为操作对象”。</li>
            <li><strong>IOC非消耗型场景口径</strong>：当场景定义为“非消耗处理”（如再次重包装留待后续业务）时，终审应按该场景策略执行占用迁移/保留，不做消耗型动作。</li>
            <li><strong>批量处理</strong>：批次层不直接写库存账；终审按单指令独立事务与幂等键执行，确保失败可隔离、可重试。</li>
          </ul>
          <div style={{ marginTop: 8, color: '#8c8c8c' }}>
            备注：最终命中动作以“操作对象类型 + 指令模板 + 场景策略 + 终审快照事实”为准；若明细出现 normalized_release 与 shortfall_compensated，分别表示“释放超额占用”与“补齐占用缺口”，并非重复执行。
          </div>
        </div>
      </Modal>

      <Modal
        open={labelPdfOpen}
        title={labelPdfTitle}
        onCancel={() => { cleanupLabelPdfObjUrl(); setLabelPdfOpen(false); }}
        footer={[
          <Button key="close" onClick={() => { cleanupLabelPdfObjUrl(); setLabelPdfOpen(false); }}>关闭</Button>,
        ]}
        width={1180}
        zIndex={1200}
      >
        <iframe src={labelPdfUrl} style={{ width: '100%', height: 680, border: 'none' }} />
      </Modal>

      <Modal
        open={wrapAttOpen}
        title={wrapAttTitle}
        onCancel={() => { cleanupWrapAttObjUrl(); setWrapAttOpen(false); }}
        footer={[
          <Button key="close" onClick={() => { cleanupWrapAttObjUrl(); setWrapAttOpen(false); }}>关闭</Button>,
        ]}
        width={1180}
        zIndex={1300}
        destroyOnClose
      >
        {wrapAttKind === 'image' ? (
          <div style={{ textAlign: 'center' }}>
            <img src={wrapAttUrl} style={{ maxWidth: '100%', maxHeight: 640 }} />
          </div>
        ) : null}
        {wrapAttKind === 'pdf' ? (
          <iframe src={wrapAttUrl} style={{ width: '100%', height: 680, border: 'none' }} />
        ) : null}
        {wrapAttKind === 'video' ? (
          <video src={wrapAttUrl} style={{ width: '100%' }} controls />
        ) : null}
        {wrapAttKind === 'other' ? (
          <div style={{ color: '#8c8c8c' }}>
            该类型暂不支持内嵌预览，可点击
            {' '}
            <a href={wrapAttUrl} target="_blank" rel="noreferrer">下载/新标签页打开</a>
            {' '}
            查看。
          </div>
        ) : null}
      </Modal>

      <Modal
        open={iocContentOpen}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', paddingRight: 56 }}>
            <div style={{ fontWeight: 700 }}>{iocContentTitle}</div>
            <div
              style={{
                marginLeft: 'auto',
                color: '#595959',
                fontFamily: 'monospace',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              条码种类 {iocContentStats.kinds} ｜ 产品总数量 {iocContentStats.total}
            </div>
          </div>
        }
        onCancel={() => { setIocContentOpen(false); setIocContentRows([]); }}
        footer={[
          <Button key="close" onClick={() => { setIocContentOpen(false); setIocContentRows([]); }}>关闭</Button>,
        ]}
        width={860}
        zIndex={1400}
      >
        <Table
          size="small"
          tableLayout="fixed"
          rowKey="__k"
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条,共${total}条记录`,
          }}
          dataSource={iocContentRows}
          columns={[
            { title: '新条码', dataIndex: 'new_code', key: 'new_code', width: 340, ellipsis: true, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
            { title: '数量', dataIndex: 'quantity', key: 'quantity', width: 120, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v ?? 0)}</span> },
          ]}
          locale={{ emptyText: '（无内容物）' }}
          scroll={{ y: 520 }}
        />
      </Modal>

      <Modal
        open={regularOutboundPackingOpen}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', paddingRight: 56 }}>
            <div style={{ fontWeight: 700 }}>
              指令:
              <span style={{ color: '#1677ff' }}>{String((instr as any)?.system_code || instrId || '-')}</span>
              {' '}常规出库总包装数据
            </div>
            <div
              style={{
                marginLeft: 'auto',
                color: '#595959',
                fontFamily: 'monospace',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              容器总数 {Number(regularOutboundPackingPayload?.stats?.containers || 0)}
              {' ｜ '}
              可用 {Number(regularOutboundPackingPayload?.stats?.activeContainers || 0)}
              {' ｜ '}
              已作废 {Number(regularOutboundPackingPayload?.stats?.cancelledContainers || 0)}
              {' ｜ '}
              已装载操作对象 {Number(regularOutboundPackingPayload?.stats?.aggregatedQty || 0)}
            </div>
          </div>
        }
        onCancel={() => { setRegularOutboundPackingOpen(false); }}
        footer={[
          <Button
            key="download"
            style={{ background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', fontWeight: 700 }}
            loading={regularOutboundPackingExporting}
            onClick={() => { void downloadRegularOutboundPackingExcel(); }}
            disabled={!regularOutboundContainerTable.rows.length}
          >
            下载
          </Button>,
          <Button
            key="add"
            type="primary"
            style={{ background: '#52c41a', borderColor: '#52c41a', fontWeight: 700 }}
            loading={regularOutboundPackingAdding}
            onClick={() => { void addRegularOutboundPackingExcelToFinalReview(); }}
            disabled={!regularOutboundContainerTable.rows.length}
          >
            添加到终审附件
          </Button>,
          <Button key="close" onClick={() => { setRegularOutboundPackingOpen(false); }}>关闭</Button>,
        ]}
        width={1320}
        zIndex={1450}
      >
        <Tabs
          size="small"
          items={[
            {
              key: 'regular_by_container',
              label: '按容器统计',
              children: (
                <Table
                  size="small"
                  rowKey={(r: any, idx?: number) => String(r?.__k || r?.container_code || r?.code || idx || 0)}
                  pagination={{
                    defaultPageSize: 20,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100', '200'],
                    showQuickJumper: true,
                    showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${total}条记录`,
                  }}
                  dataSource={regularOutboundContainerTable.rows}
                  onRow={(record: any) => {
                    const gi = Number(record?.__group_index || 0) || 0;
                    return { style: { background: gi % 2 === 0 ? '#f0f5ff' : '#f6ffed' } };
                  }}
                  columns={(() => {
                      const hasHierarchy = !!regularOutboundContainerTable.hasHierarchy;
                      const cols: any[] = [
                        {
                          title: regularOutboundContainerTable.hasHierarchy ? '外层容器编码' : '容器编码',
                          dataIndex: 'parent_code',
                          key: 'parent_code',
                          width: 240,
                          ellipsis: true,
                          render: (v: any, r: any) => {
                            const rowSpan = Number(r?.__parent_row_span ?? 1);
                            const txt = formatContainerCodeWithType(v, r?.parent_type);
                            const node = (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                                <span style={{ fontFamily: 'monospace', color: '#1677ff' }}>{txt}</span>
                              </span>
                            );
                            return { children: node, props: { rowSpan: rowSpan > 0 ? rowSpan : 0, style: { verticalAlign: 'middle' } } } as any;
                          },
                        },
                        {
                          title: hasHierarchy ? '外层容器重量(kg | lb)' : '容器重量(kg | lb)',
                          dataIndex: 'outer_weight',
                          key: 'outer_weight',
                          width: 170,
                          render: (v: any, r: any) => {
                            const rowSpan = Number(r?.__parent_row_span ?? 1);
                            const displayVal = hasHierarchy
                              ? String(v || '-')
                              : String((v && String(v).trim() !== '-') ? v : (r?.inner_weight || '-'));
                            return { children: displayVal, props: { rowSpan: rowSpan > 0 ? rowSpan : 0, style: { verticalAlign: 'middle' } } } as any;
                          }
                        },
                        {
                          title: hasHierarchy ? '外层容器尺寸(cm | inch)' : '容器尺寸(cm | inch)',
                          dataIndex: 'outer_dims',
                          key: 'outer_dims',
                          width: 220,
                          render: (v: any, r: any) => {
                            const rowSpan = Number(r?.__parent_row_span ?? 1);
                            const displayVal = hasHierarchy
                              ? String(v || '-')
                              : String((v && String(v).trim() !== '-') ? v : (r?.inner_dims || '-'));
                            return { children: displayVal, props: { rowSpan: rowSpan > 0 ? rowSpan : 0, style: { verticalAlign: 'middle' } } } as any;
                          }
                        },
                      ];
                      if (hasHierarchy) {
                        cols.push({
                          title: '内层容器编码',
                          dataIndex: 'child_label',
                          key: 'child_label',
                          width: 220,
                          ellipsis: true,
                          render: (v: any, r: any) => {
                            const rowSpan = Number(r?.__child_row_span ?? 1);
                            const isDirect = !!r?.is_parent_direct;
                            const text = isDirect ? String(v || '-') : formatContainerCodeWithType(v, r?.child_type);
                            const showAsCode = !isDirect && text !== '-';
                            const node = (
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, paddingLeft: isDirect ? 0 : 10 }}>
                                <span style={{ fontFamily: showAsCode ? 'monospace' : 'inherit', color: showAsCode ? '#1677ff' : undefined }}>{text}</span>
                              </span>
                            );
                            return { children: node, props: { rowSpan: rowSpan > 0 ? rowSpan : 0, style: { verticalAlign: 'middle' } } } as any;
                          },
                        });
                        cols.push(
                          {
                            title: '内层容器重量(kg | lb)',
                            dataIndex: 'inner_weight',
                            key: 'inner_weight',
                            width: 170,
                            render: (v: any, r: any) => {
                              const rowSpan = Number(r?.__child_row_span ?? 1);
                              return { children: String(v || '-'), props: { rowSpan: rowSpan > 0 ? rowSpan : 0, style: { verticalAlign: 'middle' } } } as any;
                            }
                          },
                          {
                            title: '内层容器尺寸(cm | inch)',
                            dataIndex: 'inner_dims',
                            key: 'inner_dims',
                            width: 220,
                            render: (v: any, r: any) => {
                              const rowSpan = Number(r?.__child_row_span ?? 1);
                              return { children: String(v || '-'), props: { rowSpan: rowSpan > 0 ? rowSpan : 0, style: { verticalAlign: 'middle' } } } as any;
                            }
                          },
                        );
                      }
                      cols.push(
                        {
                          title: '操作对象',
                          dataIndex: 'target_code',
                          key: 'target_code',
                          width: 220,
                          ellipsis: true,
                          render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span>,
                        },
                        {
                          title: '操作对象所含内容物',
                          dataIndex: 'target_content_info',
                          key: 'target_content_info',
                          width: 320,
                          ellipsis: true,
                          render: (v: any, r: any) => {
                            const full = String(v || '-').trim() || '-';
                            const lines = splitContentInfoLines(full);
                            if (lines.length <= 3) {
                              return (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                  {(lines.length ? lines : [full]).map((line: string, i: number) => (
                                    <span key={`${line}-${i}`} style={{ color: String(r?.target_content_mode || '') === 'ioc' ? '#595959' : undefined }}>
                                      {line}
                                    </span>
                                  ))}
                                </div>
                              );
                            }
                            const shown = lines.slice(0, 3);
                            return (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {shown.map((line: string, i: number) => (
                                  <span key={`${line}-${i}`} style={{ color: String(r?.target_content_mode || '') === 'ioc' ? '#595959' : undefined }}>
                                    {line}
                                  </span>
                                ))}
                                <Button
                                  type="link"
                                  size="small"
                                  style={{ padding: 0, height: 'auto', width: 'fit-content' }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    Modal.info({
                                      title: '内容物完整信息',
                                      width: 760,
                                      centered: true,
                                      content: (
                                        <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                                          {lines.map((x: string, i: number) => (
                                            <div key={`${x}-${i}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                                              {i + 1}. {x}
                                            </div>
                                          ))}
                                        </div>
                                      ),
                                    });
                                  }}
                                >
                                  [...]
                                </Button>
                              </div>
                            );
                          },
                        },
                        { title: '操作对象数量', dataIndex: 'target_qty', key: 'target_qty', width: 120, align: 'center' as const, render: (v: any) => Math.max(0, Math.floor(Number(v || 0) || 0)) },
                      );
                      return cols;
                    })()}
                  summary={() => {
                    const leadCols = regularOutboundContainerTable.hasHierarchy ? 8 : 5;
                    return (
                      <>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={leadCols} align="center">
                            <span style={{ fontWeight: 700 }}>TOTAL</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={leadCols} align="center">
                            <span style={{ color: '#cf1322', fontWeight: 700 }}>
                              {Math.max(0, Math.floor(Number(regularOutboundContainerTable.totalQty || 0) || 0))}
                            </span>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={leadCols} align="center">
                            <span style={{ color: '#d4380d', fontWeight: 700 }}>无法操作产品总数</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={leadCols} align="center">
                            <span style={{ color: '#fa541c', fontWeight: 700 }}>
                              {Math.max(0, Math.floor(Number(exceptionIocTotalQty || 0) || 0))}
                            </span>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    );
                  }}
                  locale={{ emptyText: '暂无常规出库按容器统计快照数据' }}
                  scroll={{ x: regularOutboundContainerTable.hasHierarchy ? 2160 : 1840 }}
                />
              ),
            },
            {
              key: 'regular_by_target',
              label: '按操作对象统计',
              children: (
                <Table
                  size="small"
                  rowKey={(r: any, idx?: number) => String(r?.key || r?.target_code || r?.unit_code || r?.output_code || idx || 0)}
                  pagination={{
                    defaultPageSize: 20,
                    showSizeChanger: true,
                    pageSizeOptions: ['20', '50', '100', '200'],
                    showQuickJumper: true,
                    showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${total}条记录`,
                  }}
                  dataSource={Array.isArray(regularOutboundPackingPayload?.byTargetRows) ? regularOutboundPackingPayload?.byTargetRows : []}
                  columns={[
                    { title: '操作对象编码', key: 'target_code', width: 260, ellipsis: true, render: (_: any, r: any) => <span style={{ fontFamily: 'monospace' }}>{resolveRegularOutboundTargetCode(r)}</span> },
                    {
                      title: '操作对象所含内容物',
                      key: 'target_content_info',
                      width: 360,
                      ellipsis: true,
                      render: (_: any, r: any) => {
                        const full = resolveRegularOutboundTargetContentInfo(r);
                        const lines = splitContentInfoLines(full);
                        if (lines.length <= 3) {
                          return (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              {(lines.length ? lines : [full]).map((line: string, i: number) => (
                                <span key={`${line}-${i}`}>{line}</span>
                              ))}
                            </div>
                          );
                        }
                        const shown = lines.slice(0, 3);
                        return (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {shown.map((line: string, i: number) => <span key={`${line}-${i}`}>{line}</span>)}
                            <Button
                              type="link"
                              size="small"
                              style={{ padding: 0, height: 'auto', width: 'fit-content' }}
                              onClick={(e) => {
                                e.stopPropagation();
                                Modal.info({
                                  title: `操作对象所含内容物（${resolveRegularOutboundTargetCode(r)}）`,
                                  width: 760,
                                  centered: true,
                                  content: (
                                    <div style={{ maxHeight: 420, overflowY: 'auto' }}>
                                      {lines.map((x: string, idx: number) => (
                                        <div key={`${x}-${idx}`} style={{ marginBottom: 6, wordBreak: 'break-all' }}>
                                          {idx + 1}. {x}
                                        </div>
                                      ))}
                                    </div>
                                  ),
                                });
                              }}
                            >
                              [...]
                            </Button>
                          </div>
                        );
                      },
                    },
                    { title: '目标数量', key: 'target_qty', width: 120, align: 'center' as const, render: (_: any, r: any) => String(r?.target_qty ?? '-') },
                    { title: '已装入容器', key: 'done_qty', width: 130, align: 'center' as const, render: (_: any, r: any) => String(r?.done_qty ?? r?.picked_qty ?? '-') },
                  ] as any}
                  summary={() => {
                    const payload = (regularOutboundPackingPayload && typeof regularOutboundPackingPayload === 'object')
                      ? regularOutboundPackingPayload
                      : ({} as AnyObj);
                    const rows = Array.isArray(payload?.byTargetRows) ? payload.byTargetRows : [];
                    const targetQtyTotal = rows.reduce((s: number, r: AnyObj) => s + resolveRegularOutboundTargetQtyValue(r), 0);
                    const doneQtyTotal = rows.reduce((s: number, r: AnyObj) => s + resolveRegularOutboundDoneQtyValue(r), 0);
                    return (
                      <>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={2} align="center">
                            <span style={{ fontWeight: 700 }}>TOTAL</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="center">
                            <span style={{ color: '#cf1322', fontWeight: 700 }}>{targetQtyTotal}</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={3} align="center">
                            <span style={{ color: '#0958d9', fontWeight: 700 }}>{doneQtyTotal}</span>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={2} align="center">
                            <span style={{ color: '#d4380d', fontWeight: 700 }}>无法操作产品总数</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2} />
                          <Table.Summary.Cell index={3} align="center">
                            <span style={{ color: '#fa541c', fontWeight: 700 }}>
                              {Math.max(0, Math.floor(Number(exceptionIocTotalQty || 0) || 0))}
                            </span>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    );
                  }}
                  locale={{ emptyText: '暂无常规出库按操作对象统计快照数据' }}
                />
              ),
            },
          ]}
        />
      </Modal>

      <Modal
        open={iocNewBarcodeFacetOpen}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, width: '100%', paddingRight: 56 }}>
            <div style={{ fontWeight: 700 }}>
              指令:{String((instr as any)?.system_code || instrId || '-')} 换标装箱总包装数据
            </div>
            <div
              style={{
                marginLeft: 'auto',
                color: '#595959',
                fontFamily: 'monospace',
                fontWeight: 700,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              正常IOC容器 {iocNewBarcodeFacetStats.containers} ｜ 条码种类数 {iocNewBarcodeFacetStats.barcodes} ｜ 正常产品总数量 {iocNewBarcodeFacetStats.totalQty}
            </div>
          </div>
        }
        onCancel={() => { setIocNewBarcodeFacetOpen(false); }}
        footer={[
          <Button
            key="download"
            style={{ background: '#e6f4ff', borderColor: '#91caff', color: '#0958d9', fontWeight: 700 }}
            loading={iocNewBarcodeFacetExporting}
            onClick={() => { void downloadIocNewBarcodeExcel(); }}
            disabled={(!iocNewBarcodeFacetRows.length && Number(exceptionIocTotalQty || 0) <= 0)}
          >
            下载
          </Button>,
          <Button
            key="add"
            type="primary"
            style={{ background: '#52c41a', borderColor: '#52c41a', fontWeight: 700 }}
            loading={iocNewBarcodeFacetAdding}
            onClick={() => { void addIocNewBarcodeExcelToFinalReview(); }}
            disabled={(!iocNewBarcodeFacetRows.length && Number(exceptionIocTotalQty || 0) <= 0)}
          >
            添加到终审附件
          </Button>,
          <Button key="close" onClick={() => { setIocNewBarcodeFacetOpen(false); }}>关闭</Button>,
        ]}
        width={1320}
        zIndex={1450}
      >
        <div style={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
        <Tabs
          size="small"
          items={[
            {
              key: 'sheet_total_packaging',
              label: `总装箱数据（正常:${Math.max(0, Math.floor(Number(normalIocTotalQty || 0) || 0))}件; 异常:${Math.max(0, Math.floor(Number(exceptionIocTotalQty || 0) || 0))}件）`,
              children: (
        <div style={{ display: 'grid', gap: 12, width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'hidden' }}>
        <div style={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto' }}>
        <Table
          size="small"
          loading={iocNewBarcodeFacetLoading}
          tableLayout="fixed"
          rowKey="__k"
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            pageSizeOptions: ['20', '50', '100', '200'],
            showQuickJumper: true,
            showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
          }}
          dataSource={iocNewBarcodeFacetRowsWithUnoperableSummary.filter((r: any) => !r?.__is_summary && String(r?.__ioc_kind || '') !== 'exception')}
          onRow={(record: any) => {
            // 正常内容 IOC 统一浅绿色背景
            return { style: { background: '#f6ffed' } };
          }}
          columns={[
            {
              title: '容器序号',
              key: '__idx',
              width: 70,
              render: (_: any, r: any) => {
                const rowSpan = Number(r?.__row_span ?? 1);
                const seq = Math.floor(Number(r?.__container_seq ?? 0) || 0);
                if (r?.__is_unoperable_summary || r?.__is_normal_summary) {
                  return { children: <span />, props: { rowSpan: 1 } } as any;
                }
                return {
                  children: <span style={{ fontFamily: 'monospace' }}>{String(seq > 0 ? seq : '-')}</span>,
                  props: { rowSpan: (rowSpan > 0 ? rowSpan : 0) },
                } as any;
              },
            },
            {
              title: '指令产出容器(IOC)编码',
              dataIndex: 'container_code',
              key: 'container_code',
              width: 200,
              ellipsis: true,
              render: (v: any, r: any) => {
                const rowSpan = Number(r?.__row_span ?? 1);
                if (r?.__is_normal_summary) {
                  return {
                    children: <span style={{ fontWeight: 800, color: '#389e0d' }}>{String(v || '正常产品总数')}</span>,
                    props: { rowSpan: 1 },
                  } as any;
                }
                if (r?.__is_unoperable_summary) {
                  return {
                    children: <span style={{ fontWeight: 800, color: '#d4380d' }}>{String(v || '无法操作产品总数')}</span>,
                    props: { rowSpan: 1 },
                  } as any;
                }
                return {
                  children: <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#1677ff' }}>{formatContainerCodeWithType(v, r?.container_type_text)}</span>,
                  props: { rowSpan: (rowSpan > 0 ? rowSpan : 0) },
                } as any;
              },
            },
            {
              title: 'IOC类型',
              key: 'ioc_kind',
              width: 120,
              render: (_: any, r: any) => {
                const rowSpan = Number(r?.__row_span ?? 1);
                if (r?.__is_normal_summary || r?.__is_unoperable_summary) {
                  return { children: <span style={{ color: '#999' }}>-</span>, props: { rowSpan: 1 } } as any;
                }
                if (String(r?.__ioc_kind || '') === 'exception') {
                  return (
                    {
                      children: (
                        <Tag style={{ marginInlineEnd: 0, background: '#fa541c', borderColor: '#fa541c', color: '#fff', fontWeight: 700 }}>
                          异常内容容器
                        </Tag>
                      ),
                      props: { rowSpan: (rowSpan > 0 ? rowSpan : 0) },
                    }
                  ) as any;
                }
                return (
                  {
                    children: (
                      <Tag style={{ marginInlineEnd: 0, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d', fontWeight: 700 }}>
                        正常内容容器
                      </Tag>
                    ),
                    props: { rowSpan: (rowSpan > 0 ? rowSpan : 0) },
                  }
                ) as any;
              },
            },
            {
              title: '条码',
              dataIndex: 'new_barcode',
              key: 'new_barcode',
              width: 190,
              ellipsis: true,
              render: (v: any, r: any) => {
                if (r?.__is_unoperable_summary || r?.__is_normal_summary) return <span style={{ color: '#999' }}>-</span>;
                return (
                  <Typography.Text style={{ fontFamily: 'monospace' }} copyable={{ text: String(v || '') }}>
                    {String(v || '-')}
                  </Typography.Text>
                );
              },
            },
            {
              title: '产品数量',
              dataIndex: 'total_quantity',
              key: 'total_quantity',
              width: 120,
              render: (v: any, r: any) => {
                const n = Math.floor(Number(v ?? 0) || 0);
                if (r?.__is_normal_summary) {
                  return <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#389e0d' }}>{String(n)}</span>;
                }
                if (r?.__is_unoperable_summary) {
                  return <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#fa541c' }}>{String(n)}</span>;
                }
                return <span style={{ fontFamily: 'monospace' }}>{String(n)}</span>;
              },
            },
            {
              title: '容器重量(kg/lb)',
              dataIndex: 'weight_kg',
              key: 'weight_kg',
              width: 140,
              render: (_: any, r: any) => {
                const rowSpan = Number(r?.__row_span ?? 1);
                if (r?.__is_unoperable_summary || r?.__is_normal_summary) return { children: <span style={{ color: '#999' }}>-</span>, props: { rowSpan: 1 } } as any;
                const ceil1 = (v: any): number | null => {
                  const n = Number(v);
                  if (!Number.isFinite(n) || n <= 0) return null;
                  return Math.ceil(n * 10) / 10;
                };
                const kg = r?.weight_kg;
                const kgNum = ceil1(kg);
                const lbNum = (kgNum == null) ? null : ceil1(kgNum * 2.2046226218);
                const txt = (kgNum == null || lbNum == null)
                  ? '-'
                  : `${kgNum.toFixed(1)} / ${lbNum.toFixed(1)}`;
                return {
                  children: <span style={{ fontFamily: 'monospace' }}>{txt}</span>,
                  props: { rowSpan: (rowSpan > 0 ? rowSpan : 0) },
                } as any;
              },
            },
            {
              title: '容器尺寸(cm/in)',
              dataIndex: 'dims_cm',
              key: 'dims_cm',
              width: 180,
              render: (_: any, r: any) => {
                const rowSpan = Number(r?.__row_span ?? 1);
                if (r?.__is_unoperable_summary || r?.__is_normal_summary) return { children: <span style={{ color: '#999' }}>-</span>, props: { rowSpan: 1 } } as any;
                const ceil1 = (v: any): number | null => {
                  const n = Number(v);
                  if (!Number.isFinite(n) || n <= 0) return null;
                  return Math.ceil(n * 10) / 10;
                };
                const raw = String(r?.dims_cm || '').trim();
                const parts = raw ? raw.split(/[×xX]/g).map((s: string) => Number(String(s).trim())) : [];
                const ok = parts.length === 3 && parts.every((n) => Number.isFinite(n) && n > 0);
                const txt = (() => {
                  if (!ok) return (raw || '-');
                  const [L, W, H] = parts;
                  const inch = (cm: number) => (cm / 2.54);
                  const Lc = ceil1(L); const Wc = ceil1(W); const Hc = ceil1(H);
                  const Li = (Lc == null) ? null : ceil1(inch(Lc));
                  const Wi = (Wc == null) ? null : ceil1(inch(Wc));
                  const Hi = (Hc == null) ? null : ceil1(inch(Hc));
                  if ([Lc, Wc, Hc, Li, Wi, Hi].some((x) => x == null)) return (raw || '-');
                  return `${Lc!.toFixed(1)}×${Wc!.toFixed(1)}×${Hc!.toFixed(1)} / ${Li!.toFixed(1)}×${Wi!.toFixed(1)}×${Hi!.toFixed(1)}`;
                })();
                return {
                  children: <span style={{ fontFamily: 'monospace' }}>{txt}</span>,
                  props: { rowSpan: (rowSpan > 0 ? rowSpan : 0) },
                } as any;
              },
            },
            {
              title: '容积(m³/ft³)',
              key: 'volume',
              width: 160,
              render: (_: any, r: any) => {
                const rowSpan = Number(r?.__row_span ?? 1);
                if (r?.__is_unoperable_summary || r?.__is_normal_summary) return { children: <span style={{ color: '#999' }}>-</span>, props: { rowSpan: 1 } } as any;
                const ceil2 = (v: any): number | null => {
                  const n = Number(v);
                  if (!Number.isFinite(n) || n <= 0) return null;
                  return Math.ceil(n * 100) / 100;
                };
                const raw = String(r?.dims_cm || '').trim();
                const parts = raw ? raw.split(/[×xX]/g).map((s: string) => Number(String(s).trim())) : [];
                const ok = parts.length === 3 && parts.every((n) => Number.isFinite(n) && n > 0);
                const txt = (() => {
                  if (!ok) return '-';
                  const [L, W, H] = parts; // cm
                  const m3Raw = (L * W * H) / 1_000_000; // cm^3 -> m^3
                  const m3 = ceil2(m3Raw);
                  const ft3 = ceil2(m3Raw * 35.314666721);
                  if (m3 == null || ft3 == null) return '-';
                  return `${m3.toFixed(2)} / ${ft3.toFixed(2)}`;
                })();
                return {
                  children: <span style={{ fontFamily: 'monospace' }}>{txt}</span>,
                  props: { rowSpan: (rowSpan > 0 ? rowSpan : 0) },
                } as any;
              },
            },
          ]}
          summary={() => (
            <>
              <Table.Summary.Row style={{ background: '#f6ffed' }}>
                <Table.Summary.Cell index={0} />
                <Table.Summary.Cell index={1} colSpan={3}>
                  <span style={{ fontWeight: 800, color: '#389e0d' }}>正常产品总数量</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#389e0d' }}>
                    {String(Math.floor(Number(normalIocTotalQty || 0) || 0))}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} colSpan={3}>
                  <span style={{ color: '#999' }}>-</span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
              <Table.Summary.Row style={{ background: '#fff2e8' }}>
                <Table.Summary.Cell index={0} />
                <Table.Summary.Cell index={1} colSpan={3}>
                  <span style={{ fontWeight: 800, color: '#d4380d' }}>无法操作产品总数</span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={4}>
                  <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#fa541c' }}>
                    {String(Math.floor(Number(exceptionIocTotalQty || 0) || 0))}
                  </span>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={5} colSpan={3}>
                  <span style={{ color: '#999' }}>-</span>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          )}
          locale={{ emptyText: '（暂无统计数据：请确认已执行“最终确认/固化IOC内容”的流程）' }}
          scroll={{ x: 1400, y: 360 }}
                />
                </div>
        </div>
              ),
            },
            {
              key: 'sheet_barcode_summary',
              label: `按条码汇总（${Number(iocBarcodeSummaryStats.barcodeKinds || 0)}种）`,
              children: (
                <div style={{ width: '100%', maxWidth: '100%', minWidth: 0, overflowX: 'auto' }}>
                  <Table
                    size="small"
                    loading={iocNewBarcodeFacetLoading}
                    tableLayout="fixed"
                    rowKey="__k"
                    pagination={{
                      defaultPageSize: 20,
                      showSizeChanger: true,
                      pageSizeOptions: ['20', '50', '100', '200'],
                      showQuickJumper: true,
                      showTotal: (total: number, range: [number, number]) => `共 ${total} 条（${range[0]}-${range[1]}）`,
                    }}
                    dataSource={iocBarcodeSummaryRows}
                    columns={[
                    {
                      title: '序号',
                      key: '__idx',
                      width: 80,
                      render: (_: any, __: any, idx: number) => <span style={{ fontFamily: 'monospace' }}>{String(idx + 1)}</span>,
                    },
                    {
                      title: '条码',
                      dataIndex: 'new_barcode',
                      key: 'new_barcode',
                      width: 320,
                      ellipsis: true,
                      render: (v: any) => (
                        <Typography.Text style={{ fontFamily: 'monospace' }} copyable={{ text: String(v || '') }}>
                          {String(v || '-')}
                        </Typography.Text>
                      ),
                    },
                    {
                      title: '产品数量',
                      dataIndex: 'total_quantity',
                      key: 'total_quantity',
                      width: 140,
                      render: (v: any) => <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{String(Math.max(0, Math.floor(Number(v || 0) || 0)))}</span>,
                    },
                    ]}
                    summary={() => (
                      <>
                        <Table.Summary.Row style={{ background: '#fffbe6' }}>
                          <Table.Summary.Cell index={0} colSpan={2}>
                            <span style={{ fontWeight: 700, color: '#d48806' }}>Total</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#cf1322' }}>
                              {String(Math.max(0, Math.floor(Number(iocBarcodeSummaryStats.totalQty || 0) || 0)))}
                            </span>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                        <Table.Summary.Row style={{ background: '#fff2e8' }}>
                          <Table.Summary.Cell index={0} colSpan={2}>
                            <span style={{ fontWeight: 700, color: '#d4380d' }}>无法操作产品总数</span>
                          </Table.Summary.Cell>
                          <Table.Summary.Cell index={2}>
                            <span style={{ fontFamily: 'monospace', fontWeight: 800, color: '#fa541c' }}>
                              {String(Math.max(0, Math.floor(Number(exceptionIocTotalQty || 0) || 0)))}
                            </span>
                          </Table.Summary.Cell>
                        </Table.Summary.Row>
                      </>
                    )}
                    locale={{ emptyText: '暂无条码汇总数据' }}
                    scroll={{ x: 900, y: 560 }}
                  />
                </div>
              ),
            },
          ]}
        />
        </div>
      </Modal>

      <Modal
        open={relabelPackBoxFinalOpen}
        title={(
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', gap: 10, width: '100%', flexWrap: 'wrap' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span>最终装箱数据</span>
              {relabelPackBoxFinalData?.container_code ? (
                <>
                  <span style={{ color: '#d9d9d9' }}>·</span>
                  <span style={{ color: '#fa541c', fontWeight: 800 }}>{String(relabelPackBoxFinalData?.container_code || '')}</span>
                </>
              ) : null}
            </span>
            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f9f0ff', borderColor: '#d3adf7', color: '#531dab' }}>
              <span>新条码种类{Number(relabelPackBoxFinalData?.new_kinds || 0)}</span>
            </Tag>
            <Tag style={{ marginInlineEnd: 0, borderRadius: 999, background: '#f6ffed', borderColor: '#b7eb8f', color: '#389e0d' }}>
              <span>产品总数{Number(relabelPackBoxFinalData?.total_qty || 0)}</span>
            </Tag>
          </div>
        )}
        onCancel={() => { setRelabelPackBoxFinalOpen(false); setRelabelPackBoxFinalData(null); }}
        footer={<Button onClick={() => { setRelabelPackBoxFinalOpen(false); setRelabelPackBoxFinalData(null); }}>关闭</Button>}
        width={980}
        zIndex={1451}
        destroyOnClose
      >
        {relabelPackBoxFinalData ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Descriptions size="small" bordered column={2}>
              <Descriptions.Item label="容器系统编码">
                <span style={{ fontFamily: 'monospace' }}>{String(relabelPackBoxFinalData?.container_code || '-')}</span>
              </Descriptions.Item>
              <Descriptions.Item label="容器类型">{String(relabelPackBoxFinalData?.container_type_text || '-')}</Descriptions.Item>
              <Descriptions.Item label="开始时间">{relabelPackBoxFinalData?.start_ts ? fmtDateTime(relabelPackBoxFinalData.start_ts) : '-'}</Descriptions.Item>
              <Descriptions.Item label="结束时间">{relabelPackBoxFinalData?.end_ts ? fmtDateTime(relabelPackBoxFinalData.end_ts) : '-'}</Descriptions.Item>
              <Descriptions.Item label="总操作时间">{String(relabelPackBoxFinalData?.duration_text || '-')}</Descriptions.Item>
              <Descriptions.Item label="统计">{`新标签种类 ${Number(relabelPackBoxFinalData?.new_kinds || 0)} | 产品总数 ${Number(relabelPackBoxFinalData?.total_qty || 0)}`}</Descriptions.Item>
              <Descriptions.Item label="尺寸(cm | inch)">{String(relabelPackBoxFinalData?.dims_text || '-')}</Descriptions.Item>
              <Descriptions.Item label="重量(kg | lb)">{String(relabelPackBoxFinalData?.weight_text || '-')}</Descriptions.Item>
            </Descriptions>
            <Divider style={{ margin: '8px 0' }} />
            <div style={{ fontWeight: 700, marginBottom: 2 }}>容器内容物统计</div>
            <Table
              size="small"
              rowKey={(r: any) => String(r?.__k || r?.new_code || '')}
              dataSource={Array.isArray(relabelPackBoxFinalData?.rows) ? relabelPackBoxFinalData.rows : []}
              pagination={{
                defaultPageSize: 20,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: ['20', '50', '100', '200'] as any,
                showTotal: (total: number, range: [number, number]) => `第${range[0]}-${range[1]}条 共${total}条记录`,
              }}
              columns={[
                { title: '新条码', dataIndex: 'new_code', key: 'new_code', width: 320, render: (v: any) => <span style={{ fontFamily: 'monospace' }}>{String(v || '-')}</span> },
                { title: '数量', dataIndex: 'qty', key: 'qty', width: 120, align: 'center' as const, render: (v: any) => Math.max(0, Math.floor(Number(v || 0) || 0)) },
              ] as any}
              locale={{ emptyText: '暂无内容明细' }}
            />
          </div>
        ) : (
          <div style={{ color: '#8c8c8c' }}>暂无最终装箱数据</div>
        )}
      </Modal>

      <Modal
        title={`关联项目（共 ${relatedProjects.length} 个）`}
        open={relatedProjectsOpen}
        onCancel={() => setRelatedProjectsOpen(false)}
        footer={null}
        width={760}
        destroyOnClose
      >
        <Table
          size="small"
          rowKey={(r: any, index?: number) => String(r?.__k || r?.id || index)}
          dataSource={Array.isArray(relatedProjects) ? relatedProjects : []}
          pagination={false}
          columns={[
            { title: '项目编码', dataIndex: 'project_code', key: 'project_code', render: (v: any) => String(v || '-') },
            { title: '项目名称', dataIndex: 'name', key: 'name', render: (v: any) => String(v || '-') },
            { title: '项目ID', dataIndex: 'id', key: 'id', render: (v: any) => String(v || '-') },
          ]}
          locale={{ emptyText: '暂无关联项目' }}
        />
      </Modal>
    </div>
  );
};

export default InstructionFinalReviewPage;


